/**
 * Orquestra tudo: loop, máquina de estados (menu/playing/paused/settings/dead/altar),
 * regiões da Forgotten Forest, gate dos Elites, o Boss (Void Alpha), FX de combate,
 * áudio, carregamento/salvamento (v2) e render. Cada sistema vive em seu módulo.
 */
import * as THREE from "three";
import { GrimoireScreen } from "../ui/GrimoireScreen"; // 10.9FC
import { IndexScreen } from "../ui/IndexScreen"; // 10.9GA
import { GuideTour, type TourStep } from "../ui/GuideTour"; // 10.9IH
import { HowToPlay } from "../ui/HowToPlay"; // 10.9IH
import { AccountGate } from "../ui/AccountGate";
import { GameConfig, type EnemyDef } from "../config/GameConfig";
import { events } from "./Events";
import { InputManager } from "./InputManager";
import { viewport, type ViewportState } from "./ViewportManager";
import { Accessibility, clampCamZoom, type UiScaleKey } from "./Accessibility";
import { i18n, type Locale } from "../i18n/i18n";
import { fmtDuration } from "../utils/format"; // 10.9BU
import { Renderer } from "../world/Renderer";
import { VOID_GRADE } from "../world/GradePass";
import { armorBaseOf } from "../items/Stats";
import { DIVIDE_AMBIENT } from "../world/Divides";
import { SpellVfx } from "../fx/SpellVfx";
import { bagUpgradeInfo } from "../items/BagUpgrade";
import { quality, type QualityPreset } from "../core/Quality";
import { Threat } from "../progression/Threat";
import { AreaGoals } from "../progression/AreaGoals";
import { TerritoryProgress } from "../progression/TerritoryProgress";
import { DailyMission, type DailyKind } from "../progression/DailyMission";
import { TerritoryGates, wallBetween } from "../world/TerritoryGates";
import { Arena } from "../world/Arena";
import { TerritoryManager } from "../world/Territories";
import { Weather } from "../world/Weather";
import { CameraRig } from "../world/CameraRig";
import { VoidAltar } from "../world/VoidAltar";
import { Regions } from "../world/Regions";
import { ShatteredWilds } from "../world/ShatteredWilds";
import { TempestReach } from "../world/TempestReach";
import { RiftGate, riftVariantFor } from "../world/RiftGate";
import { RegionManager } from "../world/RegionManager";
import { EnemyProjectiles } from "../combat/EnemyProjectiles";
import { ImpactFX } from "../fx/ImpactFX";
import { SlashFx } from "../fx/SlashFx";
import { RARITY_COLOR, RARITIES, rarityIndex } from "../items/Rarity";
import { VoidBarrier } from "../world/VoidBarrier";
import { Sanctuary, type SanctuaryZone } from "../world/Sanctuary";
import { GroundLoot } from "../world/GroundLoot";
import { ChestManager, type ChestType } from "../world/ChestManager";
import { FogOfWar } from "../world/FogOfWar";
import { Player } from "../player/Player";
import { Pet } from "../player/Pet";
import { MinionManager } from "../player/Minions";
import type { MinionConfig } from "../player/Minions";
import { RiftSeer } from "../boss/RiftSeer";
import { BossZone, ZonePortal } from "../world/BossZone"; // 10.9AJ — Zona dos Bosses
import type { RoomView } from "../world/BossZone";
import { ChainWeapons } from "../player/ChainWeapons";
import { HookSystem, type HookTarget } from "../player/HookSystem";
import { EnemyManager } from "../enemies/EnemyManager";
import { EnemySpawner } from "../enemies/EnemySpawner";
import { Enemy } from "../enemies/Enemy";
import { XPSystem } from "../progression/XPSystem";
import { GameProgress } from "../progression/GameProgress";
import { VoidTreeState } from "../progression/VoidTree";
import { RegionMastery } from "../progression/RegionMastery";
import { RiftSurge } from "../progression/RiftSurge";
import { MasteryScreen, type MasteryView, type MasteryRewardRow } from "../ui/MasteryScreen";
import { SurgeScreen, type SurgeView, type SurgeOption } from "../ui/SurgeScreen";
import { RankingScreen } from "../ui/RankingScreen";
import { syncProfile, deleteProfileChar, getName, type ProfileStats, setFallbackName } from "../net/CloudProfile";
import { PvpPortal } from "../world/PvpPortal";
import { PvpScreen } from "../ui/PvpScreen";
import { DuelArena3D } from "../ui/DuelArena3D"; // 10.9AH — duelo agora em ARENA 3D real
import { Ads, SimulatedAdProvider } from "../ads/Ads"; // 10.9BE — anúncios recompensados
import { inFury } from "../boss/Fury"; // 10.9BF
import { TeamArena3D } from "../ui/TeamArena3D"; // 10.9BC — 3v3
import type { TeamRoom } from "../net/CloudDuel";
import { selfId, type LobbyPlayer, selfQ } from "../net/CloudPvp";
import { AutoTarget } from "../combat/AutoTarget";
import { ProjectileManager } from "../combat/ProjectileManager";
import { DamageNumbers } from "../combat/DamageNumbers";
import type { Damageable } from "../combat/Damageable";
import { Inventory } from "../items/Inventory";
import { computeLoadout } from "../items/Stats";
import { emptyMods, type EquipmentSlot, type WeaponType, type EquipmentInstance, type WeaponDefinition } from "../items/EquipmentTypes";
import { definitionById, isWeaponDef } from "../items/Catalog";
import { weaponDps, weaponBaseDamage } from "../items/Stats";
import { createInstance } from "../items/ItemFactory";
import { rollEnemyDrop, rollChest, rollBossWeapon, rollBossArmor, rollRaidUnique } from "../items/LootSystem"; // 10.9FX/GG
import { Vip } from "../progression/Vip";
import { EnemyHealthBars } from "../ui/EnemyHealthBars";
import { EquipmentBar } from "../ui/EquipmentBar";
import { InventoryScreen } from "../ui/InventoryScreen";
import { ShopScreen, shopItemPrice } from "../ui/ShopScreen";
import { WardrobeScreen } from "../ui/WardrobeScreen";
import { Minimap, type MinimapEntity } from "../ui/Minimap";
import { OrientationOverlay } from "../ui/OrientationOverlay";
import { MenuBar } from "../ui/MenuBar";
import { CharacterScreen, type CharacterData } from "../ui/CharacterScreen";
import { VoidTreeScreen, type TreeViewData, type TreeNodeView, type NodeState } from "../ui/VoidTreeScreen";
import { DevPanel } from "../ui/DevPanel"; // 10.9CT — painel de teste (só DEV)
import { routeDamageType } from "../items/DamageRoute";
import { setDisplayClass } from "../items/format";

// 10.9CO — os 3 nós de "tipo de dano" do Poder (pow_slash/pierce/blunt) NÃO fazem sentido fixos por
// tipo (ex.: "perfurante" pra um mago). Eles se adaptam ao dano REAL da classe. Este mapa liga cada
// um a um "slot" (0/1/2) e cada slot recebe o tipo certo via powerDmgRoute().
type PowerRoute = "slashing" | "piercing" | "blunt" | "arcane" | "damage";
const POW_TYPE_SLOT: Record<string, number | undefined> = { pow_slash: 0, pow_pierce: 1, pow_blunt: 2 };
const ROUTE_META: Record<PowerRoute, { name: string; icon: string; eff: string }> = {
  slashing: { name: "node.pow_slash", icon: "🗡️", eff: "treeeffect.dmgSlashPct" },
  piercing: { name: "node.pow_pierce", icon: "🏹", eff: "treeeffect.dmgPiercePct" },
  blunt: { name: "node.pow_blunt", icon: "🔨", eff: "treeeffect.dmgBluntPct" },
  arcane: { name: "node.pow_arcane", icon: "🔮", eff: "treeeffect.dmgArcanePct" },
  damage: { name: "node.pow_rawdmg", icon: "💢", eff: "treeeffect.damagePct" },
};
// 10.9CX — tipo de magia (usado pelos casts das magias 2/3) + emoji do botao por tipo
interface SpellLike { kind: string; nameKey: string; descKey?: string; cooldown: number; dmgMult: number; radius: number; knockback: number; color: number; damageType: import("../items/EquipmentTypes").DamageType; buff?: { seconds: number; dmgMult?: number; dr?: number; healFrac?: number; moveMult?: number; atkSpeedMult?: number; critChance?: number }; dashRange?: number; meteorDelay?: number; meteorRange?: number; field?: { seconds: number; tickEvery: number }; id?: string; unlockLevel?: number; waveLength?: number; trap?: { seconds: number; trigger: number }; chain?: { jumps: number; jumpRange: number }; slow?: number; drainPct?: number; ahead?: number }
const SPELL_EMOJI: Record<string, string> = { slam: "💥", nova: "🔮", volley: "🏹", summon: "💀", dash: "🌀", buff: "🛡️", meteor: "☄️", curse: "☠️", whirl: "🌪️", wave: "⚡", trap: "🪤", chain: "🌩️" }; // 10.9FC
const _spellTo = new THREE.Vector3();
const hexColor = (c: number): string => "#" + (c >>> 0).toString(16).padStart(6, "0").slice(-6); // 10.9CX
import { WorldScreen, type WorldRow, type WorldMapData, type MapPoi } from "../ui/WorldScreen";
import { VOID_NODES, nodeRequires, FLAT_EFFECTS } from "../progression/VoidTree";
import { Missions, type MissionState } from "../progression/Missions";
import { MissionsScreen, type MissionsData } from "../ui/MissionsScreen";
import { Tutorial, type TipId } from "./Tutorial";
import { MailSystem } from "../progression/Mail";
import { MailboxScreen } from "../ui/MailboxScreen";
import { TestPad } from "../world/TestPad";
import { TrainingDummy } from "../world/TrainingDummy"; // 10.9BY
import { GuardianArena } from "../world/GuardianArena"; // 10.9CC
import { TrainingScreen, type TrainingView } from "../ui/TrainingScreen"; // 10.9BY
import { SKILL_ICON, SKILL_IDS, addSkillXp, defenseSkillBonus, skillForWeapon, trainCostPerHour, trainDiamondCost, trainFinishCost, trainPendingXp, trainRatePerMin, weaponSkillBonus, xpFor as skillXpFor, type SkillId } from "../progression/Skills"; // 10.9BY
import { VipScreen } from "../ui/VipScreen";
import { itemName, itemIcon, rarityName } from "../items/format";
import { weaponTypeSvg } from "../items/icons"; // 10.9DO — ícone SVG da arma nas skills de treino
import { EssenceInventory } from "../essence/EssenceInventory";
import type { EssenceType } from "../essence/EssenceTypes";
import { EssenceManager } from "../essence/EssenceManager";
import { TelegraphSystem } from "../fx/Telegraph";
import { ParticleSystem } from "../fx/Particles";
import { AudioManager } from "../audio/AudioManager";
import { VoidAlpha } from "../boss/VoidAlpha";
import { AetherColossus } from "../boss/AetherColossus";
import { VoidKing } from "../boss/VoidKing"; // 10.9DR
import type { Boss } from "../boss/Boss";
import { ColossusGate } from "../world/ColossusGate";
import { BossHUD } from "../boss/BossHUD";
import { SaveManager, type SaveData } from "../save/SaveManager";
import { LocalStorageBackend } from "../save/Storage";
import { reportCrash, CloudBackedStorage } from "../net/CloudSave";
import { diag } from "./Diag"; // 10.9IT — trilha de diagnostico
import { BanScreen } from "../ui/BanScreen"; // 10.9IT
import { fetchBan, claimGrants, type Grant } from "../net/AdminGrants"; // 10.9IT
import { CharacterStorage } from "../save/CharacterStorage"; // 10.9X — personagens por classe
import { HUD } from "../ui/HUD";
import { MainMenu, PauseMenu, DeathScreen } from "../ui/Screens";
import { ClassSelectScreen } from "../ui/ClassSelectScreen"; // 10.9T
import { dealDamage } from "../combat/DamageSystem"; // 10.9U — magia da classe
import { SettingsPanel } from "../ui/SettingsPanel";
import { AltarPanel } from "../ui/AltarPanel";
import { LevelUpToast } from "../ui/LevelUpToast";
import { EssenceToast } from "../ui/EssenceToast";
import { DebugOverlay } from "../ui/DebugOverlay";
import { Banner } from "../ui/Banner";
import { LootToast } from "../ui/LootToast";
import { clamp } from "../utils/math";
import { TerritoryPopulation, type VirtualMob } from "../enemies/TerritoryPopulation"; // 10.9DX
import { RaidController, type RaidReward } from "../raid/RaidController"; // 10.9EO — raid (boss mundial)
import { RaidTitan } from "../raid/RaidTitan"; // 10.9EO
import { cloudUserId } from "../net/CloudProfile"; // 10.9EO (getName ja importado acima)
import { cloudPriority, wipeAccount } from "../net/CloudSave"; // 10.9EP — momentos importantes vao pra nuvem mais rapido; 10.9K7 — zerar conta
import { reportDrop, type DropSource } from "../net/DropFeed"; // 10.9K8 — drops raros pro site
import { openSite } from "../net/OpenSite"; // 10.9L1 — botão "Site oficial"
import { setSeasonMarkIO } from "../net/CloudSeason"; // 10.9K0 — marca da temporada por personagem
import { UpdateNotes } from "../ui/UpdateNotes"; // 10.9ES — painel de novidades
import { DepthIntro } from "../ui/DepthIntro"; // 10.9L20 — explicação da "Nova Volta" no Mapa
import { unseenUpdates, CHANGELOG } from "../config/Changelog"; // 10.9ES

type GameState = "menu" | "playing" | "paused" | "settings" | "dead" | "altar" | "inventory" | "character" | "tree" | "world" | "mastery" | "surge" | "shop" | "wardrobe" | "missions" | "mailbox" | "vip" | "pvp" | "duel" | "training";


/** 10.9IB — altura da plaquinha dos TELEPORTES. Os portais já têm um letreiro 3D próprio com o
 *  nome/destino por volta de y 4.4-4.9; a plaquinha de toque fica ACIMA dele pra não cobrir. */
const PORTAL_TAG_Y = 6.2;

export class Game {
  private readonly renderer: Renderer;
  private readonly arena: Arena;
  private readonly cameraRig: CameraRig;
  private readonly input: InputManager;
  private readonly player: Player;
  private readonly pet: Pet;
  private readonly minions: MinionManager; // 10.9AD — servos do Necromante
  private readonly chain: ChainWeapons; // 10.5H: armas da bolsa presas por corrente (atacam sozinhas)
  private readonly hook: HookSystem;
  private readonly enemies: EnemyManager;
  private readonly spawner: EnemySpawner;
  private readonly xp: XPSystem;
  private readonly damageNumbers: DamageNumbers;
  private readonly inventory: EssenceInventory;
  private readonly essence: EssenceManager;
  private readonly altar: VoidAltar;
  private readonly save: SaveManager;
  private readonly charStore: CharacterStorage; // 10.9X — personagens por classe

  // Fase 3
  private readonly regions: Regions;
  private readonly barrier: VoidBarrier;
  /** 10.6L — conquista de cada território + os muros/portas entre eles. */
  private readonly terrProgress = new TerritoryProgress();
  private readonly terrGates: TerritoryGates;
  /** 10.6M — missão diária (uma por dia, prêmio grande). */
  private readonly daily = new DailyMission(() => this.xp.level);
  private readonly sanctuary: Sanctuary;
  // Fase 8 — mundo/regiões
  private readonly wilds: ShatteredWilds;
  private readonly tempest: TempestReach;
  private readonly rift: RiftGate;
  private readonly region: RegionManager;
  private readonly enemyBolts: EnemyProjectiles;
  private regionTransition = 0; // >0 = fade em andamento
  private readonly telegraph: TelegraphSystem;
  private readonly particles: ParticleSystem;
  /** 10.6E — arco de corte / onda de impacto / clarão de tiro (geometria, não partícula). */
  private readonly slashFx: SlashFx;
  private readonly spellVfx: SpellVfx; // 10.9HI — efeitos próprios por magia
  private readonly audio: AudioManager;
  private readonly progress: GameProgress;
  private readonly autoTarget: AutoTarget;
  private readonly projectiles: ProjectileManager;
  private readonly equipment: Inventory;
  private readonly groundLoot: GroundLoot;
  private readonly chests: ChestManager;
  private fog: FogOfWar; // aponta para o fog da região ativa (RegionManager)
  private readonly access: Accessibility;
  private readonly tree = new VoidTreeState();
  private readonly regionMastery = new RegionMastery();
  private readonly surge = new RiftSurge();
  private breakdown: Record<string, { src: string; flat?: number; pct?: number }[]> = {};
  private combatPower = 0;
  private boss: Boss | null = null;
  private loopErrorLogged = false; // 10.5I: loga só o 1º erro de frame (evita spam)
  private eventBossTimer: number = GameConfig.eventBoss.firstDelay; // 10.4R: contagem até o próximo evento
  // 10.9AC — INVASÃO DO VAZIO (evento de 4h): estado transitório (o relógio persiste em progress).
  private worldEventActive = false;
  private worldEventTimeLeft = 0; // s restantes da janela do evento
  private worldEventCooldown = 0; // s até o próximo (derivado de progress.worldEventNextAt no boot)
  private eventStacks = 0; // buff temporário acumulado (dano+defesa) — some ao fim do evento
  private eventKillsTowardBoss = 0; // corrompidos mortos rumo a LIBERAR o próximo boss
  private eventBossTier = 0; // nº de vezes que o boss do evento renasceu (mais forte a cada uma)
  private eventMonsterTimer = 0; // cadência de reposição da horda
  private eventVariantIdx = 0; // índice da variante do boss (cicla → "boss diferente toda vez")
  private eventKillToastTimer = 0; // throttle do feedback de stacks
  /** 10.6P — timer do boss de área por região (decrementa só enquanto o player está naquela região). */
  private guardianArena: GuardianArena | null = null; // 10.9CC — arena fixa do boss de área
  private devDamageMult = 1; // 10.9CT — multiplicador de dano do painel DEV (1 = normal)
  private potionCd = 0; // 10.9CZ — recarga da poção de vida
  private potionBtn: HTMLButtonElement | null = null; // 10.9CZ
  private bossLeashTimer = 0; // 10.9CK — s que o player está longe da arena do boss
  private bossLeashWarned = false;
  private hiddenAt = 0; // 10.9CC — quando o app foi pro fundo (pra reiniciar após pausa longa)
  /** 10.6Q — cooldown dos bosses fixos (1h após kill para respawnar). */
  private fixedBossCooldowns: Record<string, number> = {};
  private readonly colossusGate: ColossusGate;
  private eliteTimer = 0;
  private surgeEliteTimer = 0;
  private stormBeyondShown = false;
  private lastRegion: string | null = null;
  /** 10.5M — grade de territórios da região ativa (gangues + mapa). */
  private readonly territories = new TerritoryManager();
  private lastTerritory: string | null = null;
  /** 10.5L — Contrato de Caça ativo (s restantes): multiplica Fragmentos. */
  private contractTimer = 0;
  // 10.9L19 — boosters da Loja de Diamantes agora vivem em `this.progress` (timestamp de expiração,
  // persistido no save — antes zerava ao fechar/reabrir o jogo, ver xpBoostEndAt/etc em GameProgress).
  private spellCds = [0, 0, 0]; // 10.9CX — recarga das 3 magias da classe
  private buffTimer = 0; // 10.9CX — duração restante do buff ativo (Grito/Foco/Barreira)
  private buffMods = { dmgMult: 0, dr: 0, moveMult: 0, atkSpeedMult: 0, critChance: 0 }; // 10.9CX
  private buffsHudTimer = 0; // 10.9EJ — atualiza a linha de bonus ativos 2x/s
  /** 10.9EQ — ROUBO DE VIDA: % do dano causado (ja com teto) e a "carteira" de cura por segundo. */
  private lifeSteal = 0;
  private lsBudget = 0;
  private lsLast = 0;
  private readonly meteors: { x: number; z: number; t: number; radius: number; dmgMult: number; spDmg: number; color: number; knockback: number; dt: string; crit: number }[] = []; // 10.9CX
  private readonly spellFx: { obj: THREE.Object3D; mat: THREE.Material; t: number; dur: number; kind: string; vy: number; groundY: number }[] = []; // 10.9DN — efeitos visuais das magias
  private readonly curses: { x: number; z: number; t: number; every: number; acc: number; radius: number; dmgMult: number; spDmg: number; color: number; dt: string; crit: number; follow?: boolean; slow?: number; drainPct?: number }[] = []; // 10.9CX · 10.9FC follow/slow/drain
  private readonly traps: { x: number; z: number; t: number; trigger: number; radius: number; dmgMult: number; spDmg: number; color: number; dt: string; crit: number; knockback: number; vfx?: { stop(): void } }[] = []; // 10.9FC · 10.9HI vfx
  /** 10.5N — tipo de arma escolhido na Bolsa: a corrente prende só armas desse tipo. */
  private chainType: string | null = null;
  /** 10.5V — clima (chuva/tempestade) e relógio do ciclo dia/noite. */
  private readonly weather: Weather;
  private dayTime: number = GameConfig.world.dayNight.startAt;
  /** 10.6B — segundos restantes de hit-stop (câmera lenta curtinha no impacto). */
  private hitStop = 0;
  /** 10.6N — tempo dentro de um território perigoso sem o anel certo. */
  private hazardTimer = 0;
  private readonly hazardHud: HTMLDivElement;
  private hazardHudHtml = ""; // 10.9L8 — só remonta o aviso quando o texto muda
  private hazardAction: (() => void) | null = null; // 10.9L8 — o que o toque no aviso faz
  /** 10.5U — tutorial contextual (uma dica por mecânica, na hora que ela acontece). */
  private readonly tutorial = new Tutorial((id) => this.showTip(id));
  private fullBannerTimer = 0;
  private minimapTimer = 0;
  private fogTimer = 0;
  // Fase 7 — Sanctuary 2.0 (zonas de combate)
  private lastZone: SanctuaryZone = "combat";
  private healFxTimer = 0;
  private safeTapToastTimer = 0;
  private pendingLevelUps = 0;
  private levelUpTimer: number | undefined;
  private readonly minimapEntities: MinimapEntity[] = [];
  private readonly comboTargets: Damageable[] = [];
  private readonly comboHooks: HookTarget[] = [];
  /** Loot/essência fora da safe zone não atravessam a barreira quando o Player está dentro. */
  private readonly lootPullBlocked = (x: number, z: number): boolean =>
    this.player.inSanctuary && !this.sanctuary.containsXZ(x, z);
  /** Alvo válido de combate (central): vivo, FORA da safe zone e ATACÁVEL pela arma atual
   *  (melee não mira criaturas voando — use Bow ou o Hook para derrubá-las). */
  /**
   * 10.9IG — Thiago: "parar de bater enquanto o monstro estiver do outro lado da parede".
   * O DANO já era bloqueado (performMelee/corrente/projétil checam `wallBetween`), mas a MIRA não:
   * o Caçador travava num bicho atrás do muro e ficava golpeando o vazio, sem tirar vida dele e
   * sem trocar pra quem estava do lado de cá. Agora quem está atrás de parede não é alvo válido.
   */
  private readonly isValidCombatTarget = (t: Damageable): boolean =>
    !t.isDead &&
    !this.sanctuary.containsXZ(t.position.x, t.position.z) &&
    !(this.player.meleeEquipped && (t as { flying?: boolean }).flying === true) &&
    !wallBetween(this.player.position.x, this.player.position.z, t.position.x, t.position.z);
  /** 10.9HQ — alvo ao alcance da ARMA (o AutoTarget troca pra quem está batível enquanto anda). */
  private readonly playerReach = (t: Damageable): boolean => this.player.inWeaponRange(t);
  private readonly targetRing: THREE.Mesh;

  private readonly hud: HUD;
  private readonly bossHud: BossHUD;
  private readonly enemyBars: EnemyHealthBars;
  private readonly equipBar: EquipmentBar;
  private readonly invScreen: InventoryScreen;
  private readonly shopScreen: ShopScreen;
  private readonly wardrobe: WardrobeScreen;
  private readonly minimap: Minimap;
  private readonly regionFade: HTMLDivElement;
  /** 10.9GT — CÂMERA LENTA: segundos restantes e fator (morte do Caçador, vitória sobre boss). */
  private slowMo = 0;
  private slowMoScale = 1;
  private readonly orientationOverlay: OrientationOverlay;
  /** 10.9GI — é um CELULAR? (ponteiro grosso + lado menor pequeno). Desktop/tablet = false → jogam em paisagem. */
  private static isPhone(): boolean {
    try {
      const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
      const small = Math.min(window.innerWidth, window.innerHeight) < 600;
      return coarse && small;
    } catch { return false; }
  }
  private readonly menuBar: MenuBar;
  private readonly characterScreen: CharacterScreen;
  private readonly voidTreeScreen: VoidTreeScreen;
  private readonly worldScreen: WorldScreen;
  /** 10.9DX — população fixa de cada território (pontos no mapa, sem modelo/IA). */
  private readonly terrPop = new TerritoryPopulation();
  private terrPopRegion = "";
  private readonly terrPopBlips: VirtualMob[] = [];
  private readonly terrRealCount = new Map<string, number>();
  private readonly materializedTerrs = new Set<string>();
  /** 10.9EO — RAID (boss mundial gerenciado pelo servidor). */
  private raid!: RaidController;
  private raidReturn: THREE.Vector3 | null = null;
  private hudStackEl: HTMLElement | null = null;
  /** 10.9ES — novidades da atualizacao (mostra 1x por sessao, ao comecar a jogar). */
  private updateNotes!: UpdateNotes;
  private updateNotesChecked = false;
  private depthIntro!: DepthIntro; // 10.9L20
  private readonly missionsScreen: MissionsScreen;
  private readonly masteryScreen: MasteryScreen;
  private readonly rankingScreen: RankingScreen;
  private profileSyncTimer = 0; // 10.9BA
  private readonly surgeScreen: SurgeScreen;
  private readonly mailSystem = new MailSystem();
  private readonly mailboxScreen: MailboxScreen;
  private readonly testPad: TestPad; // 10.7B — pad de teste de combate no Santuário
  private readonly trainingDummy: TrainingDummy; // 10.9BY — Boneco de Treino (skills offline)
  private readonly trainingScreen: TrainingScreen; // 10.9BY
  private skillDefCd = 0; // 10.9BY — cap de 1 evento de XP de Defesa por 0.5 s
  private trainHudTimer = 0; // 10.9BY — atualiza o indicador de treino 1×/s
  private readonly pvpPortal: PvpPortal; // 10.9 — portal da Arena PvP no Santuário
  // 10.9AJ — ZONA DOS BOSSES: portal no hub → grade de salas com boss + porta por nível
  private readonly bossZone: BossZone;
  private readonly bossPortal: ZonePortal; // hub → zona
  private readonly zoneExitPortal: ZonePortal; // zona → hub
  private inBossZone = false;
  private zoneActiveRoom: RoomView | null = null; // sala em luta (clamp do player)
  private zoneLockToast = 0;
  private zoneRefreshTimer = 0;
  private treeRebalanced = false; // 10.9AQ — árvore zerada no load (ritmo de pontos mudou) → avisar
  private zoneMoteTimer = 0; // 10.9AR — partículas de energia da fenda
  private pvpScreen!: PvpScreen; // 10.9 — lobby PvP
  private duelScreen!: DuelArena3D; // 10.9D/AH — arena de duelo 3D
  private teamArena!: TeamArena3D; // 10.9BC — arena 3v3
  private bossFuryRef: unknown = null; // 10.9BF — boss pro qual o aviso de FÚRIA já saiu
  private readonly vipScreen: VipScreen;
  private surgeOverlay!: HTMLDivElement;
  private surgeHud!: HTMLDivElement;
  private missionHud!: HTMLDivElement;
  private contractHud!: HTMLDivElement;
  /** 10.5O — missões com recompensa (3 ativas, sem timer). */
  private readonly missions = new Missions(() => this.xp.level);
  private pendingSurge: { region: string; tier: number } | null = null;
  private readonly mainMenu: MainMenu;
  private readonly classSelect: ClassSelectScreen; // 10.9T
  private readonly pauseMenu: PauseMenu;
  private readonly deathScreen: DeathScreen;
  private readonly settings: SettingsPanel;
  private readonly altarPanel: AltarPanel;
  private readonly debug: DebugOverlay;
  private readonly banner: Banner;
  /** 10.6C — canal exclusivo de drop (o Banner é único e engolia o aviso de item). */
  private readonly lootToast: LootToast;
  private readonly bossPrompt: HTMLDivElement;

  private state: GameState = "menu";
  private settingsReturn: GameState = "menu";
  private respawnTimer = 0;

  private last = performance.now();
  private fps = 0;
  private fpsAccum = 0;
  private fpsFrames = 0;
  // 10.8C/10.9F — governador de FPS escalonado (reduz gráficos em degraus se engasgar)
  private runTime = 0;
  private lowFpsTime = 0;

  constructor(gameRoot: HTMLElement, uiRoot: HTMLElement) {
    this.renderer = new Renderer(gameRoot);
    this.arena = new Arena(this.renderer.scene);
    this.weather = new Weather(this.renderer.scene); // 10.5V
    this.regions = new Regions(this.renderer.scene);
    this.barrier = new VoidBarrier(this.renderer.scene);
    this.sanctuary = new Sanctuary(this.renderer.scene);
    // 10.9BP — TODAS as posições do hub vêm de GameConfig.hub (layout em anel)
    this.testPad = new TestPad(this.renderer.scene, GameConfig.hub.testPad); // 10.7B — pad de teste (sul, perto da parede)
    this.testPad.setVisible(this.devPanelEnabled()); // 10.9FO — OFICIAL: pad de teste so aparece em modo dev
    this.trainingDummy = new TrainingDummy(this.renderer.scene, GameConfig.hub.training); // 10.9BY — Boneco de Treino (leste)
    this.pvpPortal = new PvpPortal(this.renderer.scene, GameConfig.hub.pvpPortal); // 10.9 — Arena PvP (sudoeste)
    // 10.9AJ — Zona dos Bosses: portal no hub (lado direito, espelho do PvP) + a zona em si + portal de volta
    const bz = GameConfig.bossZone;
    this.bossZone = new BossZone(this.renderer.scene);
    this.bossPortal = new ZonePortal(this.renderer.scene, bz.portalHub, i18n.t("bossZone.portal"), 0xffb020, i18n.t("bossZone.portalSub")); // 10.9GU
    this.zoneExitPortal = new ZonePortal(this.renderer.scene, bz.exitPortal, i18n.t("bossZone.exitPortal"), 0x2be0ff);
    this.zoneExitPortal.setVisible(false);
    this.cameraRig = new CameraRig(this.renderer.camera);
    this.input = new InputManager(this.renderer.renderer.domElement, uiRoot);

    this.telegraph = new TelegraphSystem(this.renderer.scene);
    this.particles = new ParticleSystem(this.renderer.scene);
    new ImpactFX(this.particles); // FX de impacto (hit/crit/kill) via eventos
    this.slashFx = new SlashFx(this.renderer.scene); // 10.6E: arco de corte, onda e clarão
    this.spellVfx = new SpellVfx(this.renderer.scene); // 10.9HI
    this.audio = new AudioManager();
    this.progress = new GameProgress();
    this.autoTarget = new AutoTarget();
    this.projectiles = new ProjectileManager(this.renderer.scene);
    this.equipment = new Inventory();
    this.groundLoot = new GroundLoot(this.renderer.scene);
    this.chests = new ChestManager(this.renderer.scene);
    this.access = new Accessibility();
    this.access.applyStickSide(); // 10.5Y: direcional na direita por padrão
    this.cameraRig.setUserZoom(this.access.camZoom); // 10.6C: abre o enquadramento padrão

    // Fase 8: ambiente da Região 2 + portal + projéteis de inimigos
    const wildsTheme = GameConfig.world.regions.find((r) => r.id === "shatteredWilds")!.theme;
    this.wilds = new ShatteredWilds(this.renderer.scene, wildsTheme);
    const tempestTheme = GameConfig.world.regions.find((r) => r.id === "tempestReach")!.theme;
    this.tempest = new TempestReach(this.renderer.scene, tempestTheme); // Fase 10: ambiente da Região 3
    this.rift = new RiftGate(this.renderer.scene, wildsTheme.accent, riftVariantFor(GameConfig.world.startRegion)); // 10.9BS: identidade por região
    this.terrGates = new TerritoryGates(this.renderer.scene, this.terrProgress); // 10.6L
    this.colossusGate = new ColossusGate(this.renderer.scene, GameConfig.colossus.gate.x, GameConfig.colossus.gate.z);
    this.colossusGate.setVisible(false); // só na Região 2
    this.enemyBolts = new EnemyProjectiles(this.renderer.scene);
    this.enemyBolts.setSanctuary(this.sanctuary);

    this.player = new Player(this.renderer.scene, this.renderer.camera, this.input, this.arena);
    this.pet = new Pet(this.renderer.scene); // 10.5C: companheiro (ativado se possuído)
    this.minions = new MinionManager(this.renderer.scene, 0x7fe0b0); // 10.9AD: servos do Necromante
    this.chain = new ChainWeapons(this.renderer.scene); // 10.5H
    this.player.setProjectiles(this.projectiles);
    this.chain.setProjectiles(this.projectiles); // 10.6D: arco preso na corrente atira flecha
    this.projectiles.setSanctuary(this.sanctuary); // barreira de projéteis
    this.hook = new HookSystem(this.renderer.scene, this.renderer.camera, this.input);
    this.hook.onThrow = () => this.player.playHookThrow(); // arremesso visual do Hook (§8)
    this.hook.setSanctuary(this.sanctuary); // hook nunca puxa p/ dentro da safe zone
    this.enemies = new EnemyManager(this.renderer.scene);
    this.enemies.setFx(this.telegraph, this.particles);
    this.enemies.setSanctuary(this.sanctuary);
    this.enemies.setBoltFn((o, dx, dz, sp, dmg, col) => this.enemyBolts.fire(o, dx, dz, sp, dmg, col));
    this.spawner = new EnemySpawner(this.arena, this.enemies);
    this.spawner.setSanctuary(this.sanctuary);
    this.spawner.setTerritories(this.territories); // 10.5M: gangue por território

    this.inventory = new EssenceInventory();
    this.essence = new EssenceManager(this.renderer.scene, this.inventory);
    this.altar = new VoidAltar(this.renderer.scene, this.inventory);

    // RegionManager: dono do estado de mundo (fog/descobertas por região + tema/ambiente)
    this.region = new RegionManager({
      renderer: this.renderer,
      arena: this.arena,
      forestBands: this.regions,
      wilds: this.wilds,
      tempest: this.tempest,
      altar: this.altar,
      gate: this.barrier,
      rift: this.rift,
      terrGates: this.terrGates, // 10.6L
      chests: this.chests,
      sanctuary: this.sanctuary,
      colossusGate: this.colossusGate,
    });
    this.region.activate(GameConfig.world.startRegion);
    this.spawner.setTable(this.region.enemyTable(), this.region.maxAlive()); // 10.9DV — teto/tabela da regiao desde o boot
    this.fog = this.region.activeFog();

    this.xp = new XPSystem();
    this.damageNumbers = new DamageNumbers(uiRoot, this.renderer.camera);

    // anel indicador do alvo atual (auto-target) — feedback discreto no chão
    this.targetRing = new THREE.Mesh(
      new THREE.RingGeometry(0.7, 0.85, 28),
      new THREE.MeshBasicMaterial({ color: 0xffd54a, transparent: true, opacity: 0.7, depthWrite: false, side: THREE.DoubleSide }),
    );
    this.targetRing.rotation.x = -Math.PI / 2;
    this.targetRing.visible = false;
    this.renderer.scene.add(this.targetRing);

    // 10.8 — storage local espelhado na nuvem (Supabase). O save local segue sendo a verdade;
    // o CloudBackedStorage só empurra o blob pra nuvem a cada gravação (tolerante a falha).
    // 10.9X — PERSONAGENS POR CLASSE: CharacterStorage guarda vários personagens num blob;
    // o SaveManager opera sempre no personagem ATIVO (transparente pro resto do código).
    this.charStore = new CharacterStorage(new CloudBackedStorage(new LocalStorageBackend(), GameConfig.save.key), GameConfig.save.key);
    this.save = new SaveManager(this.charStore, {
      xp: this.xp,
      inventory: this.inventory,
      altar: this.altar,
      audio: this.audio,
      progress: this.progress,
      equipment: this.equipment,
      region: this.region,
      access: this.access,
      tree: this.tree,
      regionMastery: this.regionMastery,
      mail: this.mailSystem, // 10.6R
    });

    this.hud = new HUD(uiRoot);
    uiRoot.querySelector<HTMLButtonElement>(".hud-set")?.addEventListener("click", () => { if (this.state === "playing") this.openSettings("playing"); }); // 10.9DQ — ⚙️ ao lado do level
    this.bossHud = new BossHUD(uiRoot);
    this.enemyBars = new EnemyHealthBars(uiRoot, this.renderer.camera);
    this.equipBar = new EquipmentBar(uiRoot, (slot) => this.openInventory(slot), (msg) => this.banner.show(msg, "info", 1800));
    this.invScreen = new InventoryScreen(uiRoot, {
      onEquip: (id) => this.doEquip(id),
      onUnequip: (slot) => this.doUnequip(slot),
      onUpgrade: (id, useElixir) => this.doUpgrade(id, useElixir),
      getElixirs: () => this.progress.elixirs,
      getCoins: () => this.progress.coins, // 10.9BI — a forja custa 🪙
      onConvert: (id) => this.doConvert(id),
      onSell: (ids) => this.doSell(ids), // 10.9M — vender vários por 🪙
      onSetAutoSell: (rarity) => this.setAutoSell(rarity), // 10.9M — auto-venda por raridade
      getAutoSell: () => this.progress.autoSellRarity, // 10.9M
      getChainedIds: () => this.chain.weapons.map((w) => w.instanceId), // 10.9L17
      onEquipBest: () => this.doEquipBest(), // 10.9HV — Thiago: "faz mais sentido um único botão que equipe as armas mais fortes" (era 1 botão por tipo)
      onShop: () => { this.closeInventory(); this.openShop(); }, // 10.5I: Loja pela Bolsa
      onWardrobe: () => { this.closeInventory(); this.openWardrobe(); },
      onAttributes: () => this.buildCharacterData(), // 10.5K: ❗ ATRIBUTOS completos dentro da Bolsa
      onClose: () => this.closeInventory(),
    });
    this.invScreen.setChainPreview((i) => this.chain.previewDamage(i)); // 10.9HK — "⛓ dano na corrente" no card
    this.shopScreen = new ShopScreen(uiRoot, {
      onClose: () => this.closeShop(),
      onBuy: (id) => { this.buyShopItem(id); cloudPriority(); }, // 10.9EP
    });
    this.wardrobe = new WardrobeScreen(uiRoot, {
      onClose: () => this.closeWardrobe(),
      onChoose: (id) => this.chooseOutfit(id),
      // 10.9HB — experimentar: veste a paleta na hora (sem salvar); null = volta pra roupa escolhida/classe
      onPreview: (id) => { const o = id ? GameConfig.outfits.find((x) => x.id === id) : null; if (o) this.player.applyOutfit(o.palette); else this.applyClassOutfit(); },
    });
    this.minimap = new Minimap(uiRoot, this.fog, () => this.openWorld()); // minimapa interativo (abre Mundo)
    this.orientationOverlay = new OrientationOverlay(uiRoot);
    // overlay de fade da troca de região (Rift Gate)
    this.regionFade = document.createElement("div");
    this.regionFade.className = "region-fade";
    uiRoot.appendChild(this.regionFade);
    this.menuBar = new MenuBar(uiRoot, {
      onCharacter: () => this.openCharacter(),
      onTree: () => this.openTree(),
      onInventory: () => this.openInventory(),
      onWorld: () => this.openWorld(),
      onShop: () => this.openShop(),
      onWardrobe: () => this.openWardrobe(),
      onMissions: () => this.openMissions(), // 10.5Y
      onMailbox: () => this.openMailbox(), // 10.6R
      onVip: () => this.openVip(), // 10.6R — botão dedicado VIP
      onTraining: () => this.openTraining(), // 10.9BY — indicador de treino abre a tela de Skills
      onSettings: () => { if (this.state === "playing") this.openSettings("playing"); }, // 10.9AD — ⚙️ no HUD
    });
    this.characterScreen = new CharacterScreen(uiRoot, () => this.closeOverlayToPlaying(), () => this.openTraining()); // 10.9BY: botão Skills
    // 10.9BY — tela de Skills + Treino (boneco do hub / painel do Herói)
    this.trainingScreen = new TrainingScreen(uiRoot, {
      onClose: () => this.closeTraining(),
      onStart: (skill, hours, payWith) => this.startTraining(skill, hours, payWith),
      onCollect: (double) => { void this.collectTraining(double); },
      onFinishNow: () => this.finishTrainingNow(),
    });
    this.missionsScreen = new MissionsScreen(uiRoot, () => this.closeMissions(), (id) => (id === "__daily" ? this.claimDaily() : id === "__daily_ad" ? void this.claimDailyDouble() : this.claimMission(id))); // 10.5Y / 10.6F / 10.6M / 10.9BE
    // 10.6R — Correio + VIP
    this.mailboxScreen = new MailboxScreen(uiRoot, {
      onClose: () => this.closeMailbox(),
      onClaim: (id) => this.claimMail(id),
      onClaimAll: () => this.claimAllMail(),
      onMarkAllRead: () => this.markAllMailRead(),
      onDeleteAllRead: () => this.deleteAllReadMail(),
      onRead: (id) => { this.mailSystem.read(id); this.mailboxScreen.refresh(); this.updateMailDot(); },
      onVip: () => { this.closeMailbox(); this.openVip(); },
    });
    this.vipScreen = new VipScreen(uiRoot, {
      onClose: () => this.closeVip(),
      onBuyPack: (packId) => { this.buyDiamondPack(packId); cloudPriority(); }, // 10.9EP
      onBuyItem: (itemId) => { this.buyDiamondItem(itemId); cloudPriority(); }, // 10.9EP
      onBuyAttr: (itemId) => { this.buyAttrItem(itemId); cloudPriority(); }, // 10.9EP/ET — compra: nuvem acompanha rapido
      onBuyPass: () => { this.buyVipPass(); cloudPriority(); }, // 10.9EP
      onAd: (p) => void this.adReward(p), // 10.9BE
    });
    this.voidTreeScreen = new VoidTreeScreen(uiRoot, {
      onUnlock: (id, times) => this.doUnlockNode(id, times), // 10.9EG — repassa a QUANTIDADE (o lote)
      onReset: () => this.doResetTree(),
      onClose: () => this.closeOverlayToPlaying(),
      getResetCost: () => this.treeResetCost(), // 10.9Y/K4 — 10💎 +1% por reset
    });
    this.worldScreen = new WorldScreen(uiRoot, {
      onTravel: (id) => { this.closeWorld(); this.switchRegion(id); },
      onSurge: (id) => this.openSurge(id),
      onMastery: (id) => this.openMastery(id),
      onClose: () => this.closeWorld(),
      onFlee: () => this.fleeBoss(), // 10.9CG
    });
    this.masteryScreen = new MasteryScreen(uiRoot, () => this.closeMastery());
    this.surgeScreen = new SurgeScreen(uiRoot, {
      onActivate: (id, tier) => this.activateSurge(id, tier),
      onDeactivate: (id) => this.deactivateSurge(id),
      onClose: () => this.closeSurge(),
    });
    // overlay de estado de Surge (borda energizada — §40)
    this.surgeOverlay = document.createElement("div");
    this.surgeOverlay.className = "surge-overlay";
    this.surgeOverlay.hidden = true;
    uiRoot.appendChild(this.surgeOverlay);
    // HUD do objetivo do Surge (progresso kills/elites — §54)
    this.surgeHud = document.createElement("div");
    this.surgeHud.className = "surge-hud";
    this.surgeHud.hidden = true;
    uiRoot.appendChild(this.surgeHud);
    // 10.5O — faixa da MISSÃO ativa (objetivo + progresso + prêmio)
    this.hazardHud = document.createElement("div");
    this.hazardHud.className = "hazard-hud";
    this.hazardHud.hidden = true;
    this.hazardHud.addEventListener("click", () => this.hazardAction?.()); // 10.9L8 — toque resolve (equipa/Loja)
    uiRoot.appendChild(this.hazardHud);
    this.missionHud = document.createElement("div");
    this.missionHud.className = "mission-hud";
    this.missionHud.hidden = true;
    // 10.6F — quando a missão está pronta a faixa recebe toque e coleta na hora
    this.missionHud.addEventListener("click", () => {
      const f = this.missions.focus;
      if (f && f.progress >= f.target) this.claimMission(f.id);
    });
    uiRoot.appendChild(this.missionHud);
    this.contractHud = document.createElement("div"); // 10.5T: Contrato de Caça ativo
    this.contractHud.className = "contract-hud";
    this.contractHud.hidden = true;
    uiRoot.appendChild(this.contractHud);
    // 10.9EJ — PILHA do canto esquerdo. Missao e Contrato tinham cada um um `top` FIXO (122 e 150)
    // e se sobrepunham quando os dois apareciam; os chips de bonus ativos iam cair em cima dos dois.
    // Agora os tres ficam numa coluna que se ajusta sozinha: o que estiver escondido nao ocupa lugar.
    const hudStack = document.createElement("div");
    hudStack.className = "hud-stack";
    // 10.9HK — a pilha RECOLHE num chip (no celular ela cobria o Caçador e a barra de vida dele).
    // Estado lembrado no aparelho; no celular começa recolhida.
    const stackToggle = document.createElement("button");
    stackToggle.className = "hud-stack-toggle";
    stackToggle.type = "button";
    let collapsed = false;
    try { const v = localStorage.getItem("vh_hudstack"); collapsed = v ? v === "1" : Game.isPhone(); } catch { collapsed = Game.isPhone(); }
    const applyStack = (): void => { hudStack.classList.toggle("collapsed", collapsed); stackToggle.textContent = collapsed ? "📋 ▸" : "📋 ▾"; stackToggle.title = collapsed ? i18n.t("hud.stackShow") : i18n.t("hud.stackHide"); };
    stackToggle.onclick = () => { collapsed = !collapsed; try { localStorage.setItem("vh_hudstack", collapsed ? "1" : "0"); } catch { /* ignora */ } applyStack(); };
    hudStack.appendChild(stackToggle);
    applyStack();
    hudStack.appendChild(this.missionHud);
    hudStack.appendChild(this.contractHud);
    const buffsEl = this.hud.buffsElement;
    if (buffsEl) hudStack.appendChild(buffsEl);
    uiRoot.appendChild(hudStack);
    this.hudStackEl = hudStack;
    this.updateNotes = new UpdateNotes(uiRoot); // 10.9ES
    this.depthIntro = new DepthIntro(uiRoot); // 10.9L20
    this.raid = new RaidController({
      scene: this.renderer.scene,
      camera: this.renderer.camera,
      player: this.player,
      telegraph: this.telegraph,
      particles: this.particles,
      uiRoot,
      hudStack: () => this.hudStackEl,
      impulse: (sv) => this.cameraRig.impulse(sv),
      banner: (t, k, ms) => this.banner.show(t, k, ms),
      canEnter: () => this.state === "playing" && !this.player.isDead,
      playerDamage: () => Threat.playerDamage,
      playerName: () => this.progress.charName || getName() || "Caçador",
      classId: () => this.progress.classId,
      level: () => this.xp.level,
      userId: () => cloudUserId(),
      attachBoss: (b) => { this.boss = b; this.bossHud.show(); this.cameraRig.setBossZoom(1.55); this.audio.playMusic("boss_theme"); }, // 10.9EO — o Tita tem ~10m: afasta pra caber
      detachBoss: () => this.clearBoss(),
      // 10.9GX — BUG: o fade ATRASAVA o teleporte (fadeThen) e a resposta do servidor chegava antes: o Tita
      // era anexado e o enterRaidArena atrasado o apagava ("entrei na sala e o boss nao apareceu").
      // Agora o teleporte e IMEDIATO (o RaidController conta com isso) e o fade e so cobertura visual.
      enterArena: (x, z) => { diag.log("raid", "entra"); this.fadeCover(); this.enterRaidArena(x, z); if (this.hudStackEl) this.hudStackEl.style.display = "none"; },
      leaveArena: () => { diag.log("raid", "sai"); this.fadeCover(); this.leaveRaidArena(); if (this.hudStackEl) this.hudStackEl.style.display = ""; },
      setInput: (on) => this.input.setEnabled(on),
      alreadyClaimed: (slot) => this.progress.raidClaimed.includes(slot),
      grantReward: (r) => this.grantRaidReward(r),
    });
    this.banner = new Banner(uiRoot);
    this.lootToast = new LootToast(uiRoot);
    this.debug = new DebugOverlay(uiRoot);
    new LevelUpToast(uiRoot);
    new EssenceToast(uiRoot);

    this.grimoire = new GrimoireScreen(uiRoot, { // 10.9FC
      onEquip: (slot, id) => this.equipSpell(slot, id),
      onClose: () => { this.grimoire.hide(); this.pauseMenu.show(); },
    });
    this.indexScreen = new IndexScreen(uiRoot, () => { this.indexScreen.hide(); this.pauseMenu.show(); }); // 10.9GA
    this.bossPrompt = document.createElement("div");
    this.bossPrompt.className = "altar-prompt";
    this.bossPrompt.hidden = true;
    uiRoot.appendChild(this.bossPrompt);
    // 10.9EX — botao "Abrir baú" flutuando EM CIMA do baú: toca e abre
    this.chestTag = document.createElement("button");
    this.chestTag.className = "chest-tag";
    this.chestTag.hidden = true;
    this.chestTag.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.tagAction?.(); // 10.9IA — baú ou teleporte (a ação é definida no updateContext)
    });
    uiRoot.appendChild(this.chestTag);

    // 10.9IF — tela de conta da primeira abertura (Google / ID do Void / convidado)
    this.accountGate = new AccountGate(uiRoot, { onDone: () => this.afterAccountGate() });

    this.mainMenu = new MainMenu(uiRoot, {
      onPlay: () => this.play(), // 10.9T: escolhe classe (1ª vez) antes de começar
      onCharacters: () => this.openCharacterSelect(), // 10.9X: criar/trocar de personagem
      onSettings: () => this.openSettings("menu"),
      onRanking: () => this.rankingScreen.open(),
      onSite: () => void openSite(), // 10.9L1 — site oficial numa aba por cima (não sai do jogo)
    });
    this.classSelect = new ClassSelectScreen(uiRoot, (id, name) => this.chooseClass(id, name), () => this.classSelect.hide(), (id) => this.deleteCharacter(id)); // 10.9T/X/Y/AF
    this.pauseMenu = new PauseMenu(uiRoot, {
      onResume: () => this.resume(),
      onSettings: () => this.openSettings("paused"),
      onRanking: () => this.rankingScreen.open(),
      onCharacters: () => this.pauseToCharacters(), // 10.9CY
      onGrimoire: () => this.openGrimoire(), // 10.9FC
      onIndex: () => this.openIndex(), // 10.9GA
      onHowTo: () => this.openHowTo(), // 10.9IH
      onSite: () => void openSite(), // 10.9L1
    });
    // 10.9IH — tutorial guiado (holofote) + manual "Como jogar"
    this.guide = new GuideTour(uiRoot, { onEnd: (fin) => this.onGuideEnd(fin) });
    this.banScreen = new BanScreen(uiRoot); // 10.9IT
    this.howTo = new HowToPlay(uiRoot, {
      onClose: () => this.pauseMenu.show(),
      onReplayTour: () => { this.resume(); this.startGuide(); },
    });
    this.deathScreen = new DeathScreen(uiRoot, () => void this.reviveByAd(), () => this.reviveByBless()); // 10.9BE / 10.9K4
    Ads.provider = new SimulatedAdProvider(uiRoot); // 10.9BE — fase de testes: vídeo simulado (trocar por AdMob no APK)
    this.rankingScreen = new RankingScreen(uiRoot, () => this.rankingScreen.hide(), () => this.myProfileStats());
    this.pvpScreen = new PvpScreen(
      uiRoot,
      () => this.closePvp(),
      () => ({ name: getName(), level: this.xp.level, power: this.combatPower, wins: this.progress.pvpWins, losses: this.progress.pvpLosses }),
      (opp) => this.startDuel(opp),
      (room) => this.startTeamMatch(room), // 10.9BC
    );
    this.duelScreen = new DuelArena3D(uiRoot, () => this.closeDuel(), (won) => this.onDuelResult(won), (note) => this.duelToLobby(note));
    this.teamArena = new TeamArena3D(uiRoot, () => this.closeDuel(), (won) => this.onDuelResult(won, true), () => this.duelToLobby());
    this.settings = new SettingsPanel(
      uiRoot,
      {
        onBack: () => this.closeSettings(),
        onUiScale: (k) => this.setUiScale(k),
        onReducedMotion: (v) => this.setReducedMotion(v),
        // 10.5Y — lado do direcional (destro = direcional na direita)
        onStickSide: (side) => {
          this.access.stickSide = side;
          this.access.applyStickSide();
          this.save.save();
        },
        // 10.6C — distância da câmera (o enquadramento padrão estava colado no Caçador)
        onCamZoom: (z) => {
          this.access.camZoom = clampCamZoom(z);
          this.cameraRig.setUserZoom(this.access.camZoom);
          this.save.save();
        },
        onQuality: (p) => this.setQuality(p),
      // 10.5W — backup do progresso em código (save é local; isso permite trocar de aparelho)
      onExportSave: () => this.save.exportCode(),
      onImportSave: (code) => {
        if (!this.save.importCode(code)) return false;
        window.setTimeout(() => window.location.reload(), 700); // recarrega já com o save novo
        return true;
      },
      },
      this.audio,
      this.access,
    );
    this.altarPanel = new AltarPanel(uiRoot, () => this.closeAltar(), () => { this.closeAltar(); this.openTree(); });

    events.on("player:died", () => this.onPlayerDied());
    events.on("app:saveNow", () => this.save.save()); // 10.9CH — antes de recarregar pra atualizar
    this.wireFeedback();
    this.wireProgressEvents();

    // responsividade centralizada: um único assinante do viewport
    viewport.onChange((s) => this.onViewport(s));
    this.onViewport(viewport.state);
    // mobile: pausa + MUTA ao perder foco (troca de app, tela bloqueada, chamada). 10.5G:
    // suspende o AudioContext (música/SFX paravam de tocar só o loop, mas o som seguia).
    // 10.9HH — 2º plano = o jogo PARA de verdade (Thiago: "ainda toca som e consome em segundo plano"):
    // loop cancelado (sem render/simulação/partículas), áudio suspenso E travado (nada religa),
    // save local + nuvem. Ao voltar: áudio destravado e loop religado (jogo segue pausado até Retomar).
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") this.sleep();
      else this.wake();
    });

    // carrega progresso salvo (se houver) antes de qualquer partida
    const saved = this.save.load();
    if (saved) this.save.restore(() => this.applyProgress(saved)); // não sobrescreve o save durante o load
    else this.barrier.setOpen(false);
    this.postLoadSetup();

    this.cameraRig.snapTo(this.player.position);
    this.hud.hide();
    this.equipBar.hide();
    this.minimap.hide();
    this.menuBar.hide();
    this.input.setEnabled(false);
    this.mainMenu.show();
  }

  start(): void {
    this.last = performance.now();
    if (import.meta.env.DEV) {
      const w = window as unknown as { __VH?: Game; __VHaudit?: () => unknown; __VHanim?: (s: string) => void; __VHattack?: (s?: string) => void };
      w.__VH = this;
      (w as unknown as { __VHmod?: unknown }).__VHmod = { createInstance, GameConfig, dealDamage }; // 10.9HL — DEV: medição de balance sem import() no console (import dinâmico faz o Vite recarregar a página)
      w.__VHaudit = () => Game.auditViewport();
      // controles DEV p/ tunar game feel do rig (§30): tocar locomoção / disparar ataque aditivo
      const rig = () => (this.player as unknown as { rigged?: { anim: { play: (s: string, o?: unknown) => void; playAdditiveOnce: (s: string, o?: unknown) => void } } }).rigged;
      w.__VHanim = (s) => rig()?.anim.play(s, { crossfade: 0.2 });
      w.__VHattack = (s) => rig()?.anim.playAdditiveOnce(s ?? "SwordAttack", {});
    }
    // 10.9CT — o PAINEL DEV vale no dev local E na versão publicada quando ativado por ?dev=1 na URL
    // (fica salvo em localStorage). Assim o Thiago valida no celular pela Cloudflare. Some com ?dev=0.
    if (this.devPanelEnabled()) this.buildDevPanel();
    this.buildPotionButton(); // 10.9CZ
    this.setupNative();
    this.installCrashTrap(); // 10.9IS
    this.rafId = requestAnimationFrame(this.loop);
  }

  /**
   * 10.9CC — Thiago: "deixei o jogo aberto num lugar, no dia seguinte estava pausado no mesmo lugar".
   * Horas pausado acumulam estado velho (evento, temporada, timers, RAF suspenso pelo sistema).
   * Passou de `longAbsenceMin` no fundo → salva e RECARREGA o app (volta no menu, limpo).
   */
  private checkLongAbsence(): void {
    if (!this.hiddenAt) return;
    const away = Date.now() - this.hiddenAt;
    this.hiddenAt = 0;
    if (away < GameConfig.save.longAbsenceMin * 60000) return;
    if (this.state === "duel" || this.state === "pvp") return; // no meio de um duelo: não derruba
    this.save.save();
    diag.log("app", "ausencia longa: recarrega"); // 10.9IT
    window.setTimeout(() => location.reload(), 150);
  }

  /** Integração nativa (Android/Capacitor §21): back button fecha menu/tela antes de sair;
   * pause do combate ao ir p/ background. No-op na web/sem plugin. */
  private setupNative(): void {
    import("@capacitor/app")
      .then(({ App }) => {
        App.addListener("backButton", () => {
          if (this.state === "menu") {
            void App.exitApp();
            return;
          }
          this.handleEscape(); // playing->pause · paused->resume · overlay->fecha
        });
        App.addListener("pause", () => this.sleep()); // 10.9HH — nativo: para tudo ao minimizar
        App.addListener("resume", () => this.wake());
      })
      .catch(() => {
        /* web ou plugin indisponível: sem back nativo */
      });
  }

  /**
   * Checagem DEV de regressão responsiva: overflow horizontal, elementos fora do viewport,
   * e alvos de toque abaixo do mínimo. Reduz regressão sem virar framework.
   */
  static auditViewport(): {
    w: number;
    h: number;
    horizontalOverflow: boolean;
    outOfBounds: string[];
    smallTouch: string[];
  } {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const critical = [".hud-topleft", ".hud-bottom", ".touch-buttons", ".tbtn", ".eq-slot", ".eq-bag", ".minimap", ".boss-hud", ".inv-panel", ".s-panel", ".world-panel", ".mastery-panel", ".surge-panel", ".surge-hud"];
    const outOfBounds: string[] = [];
    const smallTouch: string[] = [];
    for (const sel of critical) {
      document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
        if (el.hidden || el.offsetParent === null) return;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return;
        if (r.left < -1 || r.top < -1 || r.right > W + 1 || r.bottom > H + 1) outOfBounds.push(`${sel} [${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.right)},${Math.round(r.bottom)}]`);
        if ((sel === ".tbtn" || sel === ".eq-slot" || sel === ".eq-bag") && (r.width < 44 || r.height < 44)) smallTouch.push(`${sel} ${Math.round(r.width)}x${Math.round(r.height)}`);
      });
    }
    return { w: W, h: H, horizontalOverflow: document.documentElement.scrollWidth > W + 1, outOfBounds, smallTouch };
  }

  // ---- feedback de combate (partículas / impulso / áudio) + maestria + loot ----
  private wireFeedback(): void {
    const mx = GameConfig.mastery.xp;
    events.on("damage:dealt", (d) => {
      if (d.byPlayer && d.target === "enemy" && d.amount > 0 && this.lifeSteal > 0) this.applyLifeSteal(d.amount); // 10.9EQ
      const color = d.target === "player" ? 0xff5566 : 0x9d7bff;
      this.particles.burst(d.position.x, 1, d.position.z, color, { count: d.crit ? 10 : 6, speed: d.crit ? 6 : 4 });
      this.audio.playSfx(d.target === "player" ? "player_hit" : "hit");
      // 10.6B — PESO no golpe. Antes só o Boss sacudia a câmera; acerto normal não tinha
      // retorno físico nenhum. Agora todo acerto dá um tranco curto (crítico e dano no
      // Caçador batem mais forte) e o crítico ainda congela 1 quadro (hit-stop).
      // 10.9GS — o tranco no Caçador cresce com o % de vida perdido (arranhão ≈ leve, pancada ≈ forte)
      const hitPct = d.target === "player" ? d.amount / Math.max(1, this.player.health.max) : 0;
      this.cameraRig.impulse(d.target === "player" ? 0.1 + Math.min(0.22, hitPct * 0.9) : d.crit ? 0.13 : 0.06);
      if (d.crit || d.target === "player") this.hitStop = Math.max(this.hitStop, d.crit ? 0.055 : 0.04);
      // 10.9GR — clarão na tela proporcional ao golpe sofrido (25% da vida ≈ clarão nítido; arranhão ≈ nada)
      if (d.target === "player" && d.amount > 0) this.renderer.screenFlash(Math.min(0.22, (d.amount / Math.max(1, this.player.health.max)) * 0.9));
      // Mastery XP por dano validado (capado — evita farm artificial)
      if (d.target === "enemy") this.gainMastery(Math.min(mx.perHitCap, Math.max(mx.perHit, Math.round(d.amount / 12))), "hit");
      // 10.9BY — TOMAR dano treina Defesa (+3 se bloqueou), no máx. 1 evento por 0.5 s
      else if (d.target === "player" && this.skillDefCd <= 0 && d.amount > 0) {
        const so = GameConfig.skills.online;
        this.skillDefCd = so.defenseCooldown;
        this.gainSkillXp("defense", d.blocked ? so.perBlock : so.perHitTaken);
      }
    });
    // 10.9GS — golpe PESADO (machado/clava): poeira do chão + tranco curto, mesmo sem acertar
    events.on("fx:slash", (d) => {
      if (!d.heavy) return;
      const r = Math.max(1, d.range) * 0.7;
      this.particles.burst(d.x + Math.sin(d.angle) * r, 0.25, d.z + Math.cos(d.angle) * r, 0xb8ab8e, { count: 8, speed: 2.6, dur: 0.5 });
      this.cameraRig.impulse(0.05);
    });
    events.on("enemy:killed", (d) => {
      // 10.9AC — corrompido do evento: soma stack (dano+defesa TEMPORÁRIOS) e avança o gate do boss.
      if (d.eventMonster && this.worldEventActive) this.onEventMonsterKilled();
      // FX de morte (Void Energy Pop) agora vem do ImpactFX (linguagem coerente)
      // 10.7S — tranco na câmera na morte: elite/campeão sacode forte (peso/satisfação).
      this.cameraRig.impulse(d.elite ? 0.28 : 0.07);
      if (d.elite) this.hitStop = Math.max(this.hitStop, 0.09); // 10.9GS — abate de elite congela um instante
      this.gainMastery(mx.perKill, "kill");
      this.player.notifyKill(); // Void Dominance / Predator
      // 10.9L21 — pet ganha XP de abate; sobe de nível (mais alcance de coleta + dano do Caçador)
      if (this.progress.petOwned && this.progress.addPetXp(GameConfig.pet.xpPerKill)) {
        this.essence.reachMult = this.progress.petMagnetMult();
        this.recalcPlayer();
        this.banner.show(i18n.t("pet.levelUp", { n: String(this.progress.petLevel) }), "info", 1800);
      }
      // 1 ITEM por kill (ou nada = só dinheiro). Rift Surge (§38) melhora a RARIDADE do
      // sorteio (não a quantidade) — continua 1 item só.
      const surgeBoost = this.surge.anyActive && Math.random() < this.surge.lootMult(this.region.id) - 1;
      const drop = rollEnemyDrop(d.enemyId, surgeBoost || Vip.dropRarityBoost || this.progress.dropBoostSecondsLeft() > 0, this.classWeaponTypes(), this.xp.level); // 10.9CQ: arma pela CLASSE + cap por nível
      if (drop) this.collectItem(drop, "enemy"); // 10.4S: vai direto pra bolsa (duplicata → material)
      this.lastKillPos.copy(d.position); // 10.6G: onde o ganho flutuante vai nascer
      this.progress.totalKills++; // 10.8F — abates totais (ranking)
      this.areaGoal("kills"); // 10.6H
      if (!d.eventMonster && !this.inBossZone && !this.raid.active) this.guardianKill(); // 10.9CC — carrega o Guardião da região
      // 10.6L — conquista do TERRITÓRIO onde o monstro caiu (é o que abre a porta do muro)
      const tk = this.territories.at(d.position.x, d.position.z);
      if (tk) {
        // 10.6M — a missão do território tem 3 tipos de objetivo: caçar, campeão e baú
        const champDone = d.elite === true && !!tk.def.champion && this.terrProgress.addChampion(tk.def);
        const killDone = this.terrProgress.addKill(tk.def);
        if (champDone || killDone) {
          this.banner.show(i18n.t("terr.cleared", { name: i18n.t(tk.def.nameKey) }), "boss", 2600);
          this.audio.playSfx("level_up");
        }
      }
      this.terrGates.refresh();
      this.progress.territories = this.terrProgress.snapshot();
      this.applyAbsorb(d.enemyId, !!d.elite); // 10.4O: melhora permanente do boneco + cura
      this.addCoins(d.elite ? GameConfig.shop.coinsPerElite : GameConfig.shop.coinsPerKill); // 10.4Y: moeda
      this.missionProgress("kill"); // 10.5O
      // 10.9AZ — objetivo "campeões" da área conta só CAMPEÃO de verdade (elite de território com campeão);
      // qualquer elite contava e os alvos (9/14) passavam do nº de campeões da área.
      if (d.elite) { this.missionProgress("elite"); if (tk?.def.champion) this.areaGoal("champions"); this.tutorial.fire("elite"); }
      // Fase 9: Shard Wardens abatidos contam para o desbloqueio do Colossus
      if (d.enemyId === "shardWarden") {
        this.progress.recordWardenKill();
        this.save.save();
      }
      // Fase 10: Regional Mastery (ação válida — kills) + progresso de Rift Surge
      this.gainRegionMastery(d.elite ? GameConfig.regionMastery.xp.perElite : GameConfig.regionMastery.xp.perKill);
      if (this.surge.anyActive) {
        this.surge.recordKill(this.region.id, !!d.elite);
        this.updateSurgeHud();
        if (this.surge.checkComplete(this.region.id)) this.onSurgeComplete(this.region.id);
      }
    });
    events.on("boss:crystalBreak", () => {
      this.banner.show(i18n.t("boss.crystalBreak"), "boss", 900);
      this.audio.playSfx("crystal_break");
    });
    events.on("boss:defeatedColossus", () => this.onColossusDefeated());
    events.on("dash:used", () => this.audio.playSfx("dash"));
    events.on("sanctuary:projectileBlocked", (e) => {
      // impacto discreto na barreira (partículas baratas) + fundação de áudio
      this.particles.burst(e.position.x, 1.0, e.position.z, 0x8ea2ff, { count: 6, speed: 3.5, dur: 0.4 });
      this.audio.playSfx("sanctuary_block");
    });
    // 10.7C — BUG FIX: comprar no altar não estava aplicando os stats no Caçador.
    // O altar emitia o evento (pro save) mas o Game nunca ouvia pra chamar recalcPlayer.
    // Player gastava Essence, subia o nível no painel, mas os stats não mudavam.
    events.on("altar:upgraded", () => {
      this.recalcPlayer();
      this.audio.playSfx("level_up");
      // refresh do painel do altar se estiver aberto (mostra novo custo)
      if (this.state === "altar") this.altarPanel.open(this.altar, this.inventory);
    });
    events.on("level:up", () => {
      this.audio.playSfx("level_up");
      this.recalcPlayer();
      this.terrGates.setUnlockContext(this.xp.level, this.progress.depth); // 10.7V: porta pode abrir por nível
      void syncProfile(this.myProfileStats()); // 10.8F: mantém o ranking atualizado
      // 10.9AQ — soma só o ponto REALMENTE ganho neste nível (1 a cada levelsPerPoint níveis)
      const bossOk = this.progress.bossesDefeated.voidAlpha;
      this.pendingLevelUps += this.tree.earnedTotal(this.xp.level, bossOk) - this.tree.earnedTotal(this.xp.level - 1, bossOk);
      this.updateTreeBadge();
      this.tipLevelUp();
      this.mailSystem.sendLevelUp(this.xp.level);
      this.updateMailDot();
      this.evolveAll(); // 10.9K9 — barra cheia esperando nível: evolui agora
      // consolida múltiplos level-ups num único banner ("+N Void Points")
      if (this.levelUpTimer !== undefined) clearTimeout(this.levelUpTimer);
      this.levelUpTimer = window.setTimeout(() => {
        if (this.pendingLevelUps > 0) this.banner.show(`${i18n.t("levelUp.voidPoint")} +${this.pendingLevelUps}`, "info", 1800); // 10.9AQ: só quando ganhou ponto
        this.pendingLevelUps = 0;
        this.levelUpTimer = undefined;
      }, 140);
    });
  }

  /** Atualiza o selo de Void Points disponíveis no botão da Void Tree. */
  /** 10.5U — dica ao ganhar o primeiro ponto da árvore. */
  private tipLevelUp(): void {
    this.tutorial.fire("levelup");
  }

  private updateTreeBadge(): void {
    const avail = this.tree.available(this.xp.level, this.progress.bossesDefeated.voidAlpha);
    this.menuBar?.setTreeBadge(avail);
  }

  private currentWeaponType(): WeaponType | null {
    const w = this.equipment.equipped.weapon;
    if (!w) return null;
    const def = definitionById(w.definitionId);
    return def && isWeaponDef(def) ? def.weaponType : null;
  }

  /** Maestria por arma (10.x) + 10.9BY: o MESMO evento treina a SKILL da arma em uso
   * (golpe +1 · abate +6 · elite +30 · boss +150). */
  private gainMastery(xp: number, train: "hit" | "kill" | "elite" | "boss" = "hit"): void {
    const t = this.currentWeaponType();
    if (!t) return;
    const so = GameConfig.skills.online;
    const sk = skillForWeapon(t, this.progress.classId);
    if (sk) this.gainSkillXp(sk, train === "boss" ? so.perBoss : train === "elite" ? so.perElite : train === "kill" ? so.perKill : so.perHit);
    const before = this.equipment.masteryLevel(t);
    this.equipment.addMasteryXp(t, xp);
    if (this.equipment.masteryLevel(t) !== before) {
      this.recalcPlayer(); // novos bônus de maestria
      this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons); // 10.5N: sempre com a corrente (senão as armas presas somem da barra)
      this.invScreen.refresh(this.xp.level);
      this.save.save();
      this.banner.show(i18n.t("mastery.levelUp", { n: this.equipment.masteryLevel(t) }), "info", 1500);
    }
  }

  private wireProgressEvents(): void {
    events.on("elite:killed", () => this.onEliteKilled());
    events.on("boss:defeated", () => { this.onBossDefeated(); cloudPriority(); }); // 10.9EP
    events.on("level:up", () => cloudPriority()); // 10.9EP — subiu de nivel: a nuvem acompanha em ate 15s
    events.on("boss:defeatedEvent", () => {
      const b = this.boss as VoidAlpha & { _areaBossRegion?: string; _worldEvent?: boolean; _zoneRoom?: string };
      if (b?._zoneRoom) {
        this.onZoneBossDefeated(b._zoneRoom); // 10.9AJ — sala da Zona dos Bosses
      } else if (b?._worldEvent) {
        this.onWorldEventBossDefeated(); // 10.9AC — renasce mais forte + recompensa
      } else if (b?._areaBossRegion) {
        this.onAreaBossDefeated(b._areaBossRegion);
      } else {
        this.onEventBossDefeated();
      }
    }); // 10.4R + 10.6P + 10.9AC
    events.on("boss:interrupted", () => this.banner.show(i18n.t("boss.interrupt"), "danger", 1400));
    events.on("boss:phase", (p) => {
      if (p.phase >= 2) this.banner.show(i18n.t("boss.phaseRage"), "danger", 1800);
    });
  }

  private onEliteKilled(): void {
    this.gainMastery(GameConfig.mastery.xp.perElite, "elite");
    if (this.unlockSlot("head")) {
      /* banner já mostrado por unlockSlot */
    }
    if (this.progress.alphaGateOpen) {
      this.save.save();
      return;
    }
    const n = this.progress.recordEliteKill();
    this.banner.show(i18n.t("elite.progress", { n, total: GameConfig.gate.requiredElites }), "info", 1600);
    this.save.save();
    if (this.progress.canOpenGate()) {
      this.progress.openGate();
      this.barrier.setOpen(true);
      this.unlockSlot("chest"); // Alpha Den aberto -> slot CHEST liberado
      this.particles.burst(0, 2, GameConfig.gate.barrierZ, 0x9d7bff, { count: 30, speed: 10, dur: 1 });
      this.banner.show(i18n.t("region.pathOpened"), "info", 2600);
      this.save.save();
    }
  }

  private onBossDefeated(): void {
    this.bossVictoryFx(); // 10.9GT
    this.gainMastery(GameConfig.mastery.xp.perBoss, "boss");
    this.gainRegionMastery(GameConfig.regionMastery.xp.perBoss); // §31
    this.unlockSlot("accessory"); // derrotar o Alpha libera o slot ACCESSORY
    this.unlockSlot("gloves"); // Fase 10: e o slot GLOVES
    const firstTime = this.progress.recordBossDefeat();
    const r = firstTime ? GameConfig.boss.voidAlpha.rewards : GameConfig.boss.voidAlpha.refight;
    this.xp.gain(r.xp);
    this.addCoins(GameConfig.shop.coinsPerBoss + (firstTime ? GameConfig.bossCoins.firstKill : GameConfig.bossCoins.refight)); // 10.4Y/10.9BK: moeda (sem 💎)
    if (firstTime) this.mailSystem.sendBossMilestone("voidAlpha");
    this.missionProgress("boss"); // 10.5O
    this.inventory.add("power", r.essence.power);
    this.inventory.add("vitality", r.essence.vitality);
    this.inventory.add("ferocity", r.essence.ferocity);
    this.banner.show(i18n.t("boss.defeated"), "boss", 2600);
    const p = this.player.position;
    // 10.4P: arma EXCLUSIVA de boss (garantida na 1ª vitória; chance no refight)
    const bw = rollBossWeapon(firstTime ? "legendary" : "epic", this.classWeaponTypes(), this.xp.level);
    if (bw && (firstTime || Math.random() < 0.5)) this.collectItem(bw, "boss"); // 10.4S: auto-coleta
    // 10.9FX — boss tambem solta ROUPA (do maior tier do nivel: Eclipse T7 no Nv150)
    const ba = rollBossArmor(firstTime ? "legendary" : "epic", this.xp.level);
    if (ba && (firstTime || Math.random() < 0.45)) this.collectItem(ba, "boss");
    // Fase 8 liberava as Terras Fraturadas AQUI, só por matar o boss — 10.9L3: tirado (Thiago: "quando
    // pega lvl 12 pode viajar pro outro mapa, nem precisa do teleport"). Agora quem libera é concluir
    // TODAS as missões da área (areaGoal → unlockNextRegion), o mesmo que acende o Portal Rift.
    if (GameConfig.areaGoalBoss[this.region.id] === "voidAlpha") this.areaGoal("boss"); // 10.6H: o boss é UM dos objetivos, não a chave sozinha
    if (firstTime) {
      this.progress.grantAlphaSoul();
      window.setTimeout(() => this.banner.show(i18n.t("reward.alphaSoul"), "info", 2400), 900);
      this.spawnChestLoot("alpha", p.x, p.z); // Alpha Chest garantido na 1ª vitória
    } else if (Math.random() < 0.6) {
      const drop = rollEnemyDrop("corruptedBrute", false, this.classWeaponTypes(), this.xp.level); // 10.9CQ: arma pela CLASSE
      if (drop) this.collectItem(drop, "boss"); // 10.4S: auto-coleta
    }
    // 10.6Q: cooldown 1h + registro de kill diário (boss fixo)
    this.fixedBossCooldowns["voidAlpha"] = 3600;
    this.progress.recordFixedBossKill();
    this.audio.playSfx("boss_defeated");
    this.save.save();
  }

  /** Loot de baú: itens auto-coletados (10.4S) + essência + banner. */
  private spawnChestLoot(type: ChestType, _x: number, _z: number, guaranteed?: import("../items/EquipmentTypes").Rarity): void {
    const loot = rollChest(type, this.xp.level, this.classWeaponTypes(), guaranteed); // 10.6V cap por nível + 10.9CQ arma pela classe + 10.9K2 raridade garantida (baú premium)
    this.addCoins(GameConfig.shop.coinsPerChest); // 10.4Y: moeda
    this.missionProgress("chest"); // 10.5O
    loot.items.forEach((it) => this.collectItem(it, "chest")); // 10.4S: baú também vai direto pra bolsa
    if (loot.essence.amount > 0) {
      this.inventory.add(loot.essence.type, loot.essence.amount);
      events.emit("essence:collected", { type: loot.essence.type, amount: loot.essence.amount });
    }
    // 10.9O — baú PREMIUM: chance de largar uma ARMA DE BOSS (substitui a venda direta na loja de 💎)
    const bwCfg = GameConfig.loot.chestBossWeapon[type];
    if (bwCfg && Math.random() < bwCfg.chance) {
      const bw = rollBossWeapon(bwCfg.rarity, this.classWeaponTypes(), this.xp.level);
      if (bw) { this.collectItem(bw, "chest"); this.banner.show(i18n.t("chest.bossWeapon"), "boss", 2600); }
    }
    if (Math.random() < 0.45) { const n = 1 + (Math.random() < 0.35 ? 1 : 0); this.progress.addPotions(n); } // 10.9CZ — baú pode dar poções
    this.banner.show(i18n.t("chest.opened"), "info", 1500);
  }

  /** Desbloqueia um slot (uma vez) com banner. Retorna true se desbloqueou agora. */
  private unlockSlot(slot: EquipmentSlot): boolean {
    if (!this.equipment.unlockSlot(slot)) return false;
    this.banner.show(i18n.t(`slot.unlocked.${slot}`), "info", 2200);
    this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons); // 10.5N: sempre com a corrente (senão as armas presas somem da barra)
    this.save.save();
    return true;
  }

  /** Aplica desbloqueios de slot a partir do progresso salvo (saves existentes não repetem conteúdo). */
  private applySlotUnlocks(): void {
    if (this.progress.elitesDefeated > 0) this.equipment.unlockSlot("head");
    if (this.progress.alphaGateOpen) this.equipment.unlockSlot("chest");
    if (this.progress.bossesDefeated.voidAlpha) {
      this.equipment.unlockSlot("accessory");
      this.equipment.unlockSlot("gloves"); // Fase 10: luvas após Void Alpha
    }
    if (this.progress.bossesDefeated.aetherColossus) this.equipment.unlockSlot("boots"); // Fase 10: botas após Colossus
  }

  /** Garante ao menos uma arma inicial (novos jogos e saves v2 migrados sem inventário). */
  /**
   * 10.9FL — BRINDE DE COMPENSAÇÃO: 3 armas FORTES (legendárias), 1x por personagem. Foi um pedido do
   * Thiago depois do bug em que armas sumiam ao voltar (já corrigido em 10.9FK). Dá as melhores armas
   * dos tipos que a CLASSE usa (se a classe usa menos de 3 tipos, completa com a melhor do tipo).
   * Vai pra BOLSA (não auto-equipa); se faltar nível pra usar, o Caçador guarda até poder.
   */
  private grantGiftIfDue(): void {
    const token = GameConfig.save.giftToken ?? 0;
    if (token <= 0 || this.progress.giftClaimed >= token) return;
    // armas por TIPO, da mais FORTE pra mais fraca (pra dar 3 DISTINTAS quando der)
    const pool: Record<string, string[]> = {
      sword: ["galeBlade", "colossusEdge", "shardSaber", "voidBlade"],
      axe: ["tempestCleaver", "voidCleaver"],
      mace: ["stormMaul", "voidMace"],
      bow: ["arcLongbow", "aetherLongbow", "voidBow"],
      staff: ["aetherScepter", "voidStaff"],
    };
    const allowed = (this.classDef()?.allowedWeapons as readonly string[] | undefined) ?? ["sword", "axe", "mace"];
    const ids: string[] = [];
    // 1ª passada: a melhor de cada tipo permitido; 2ª+: vai descendo, sempre distinta
    for (let rank = 0; ids.length < 3 && rank < 4; rank++) {
      for (const t of allowed) { const id = pool[t]?.[rank]; if (id && !ids.includes(id) && ids.length < 3) ids.push(id); }
    }
    while (ids.length < 3 && ids.length > 0) ids.push(ids[0]); // classe de 1 tipo com pool pequeno: repete a melhor
    const lvl = Math.max(1, Math.min(GameConfig.equipment.upgradeMaxLevel, this.xp.level));
    const gifts = ids.slice(0, 3).map((id) => ({ defId: id, rarity: "legendary", level: lvl }));
    this.progress.giftClaimed = token;
    if (gifts.length > 0) {
      // 10.9FM — vai pelo CORREIO (com anexo de itens), o jogador coleta quando quiser
      this.mailSystem.sendItems("mail.gift.title", "mail.gift.body", gifts, { coins: 500, diamonds: 20 });
      this.updateMailDot();
      this.save.save();
    }
  }

  private ensureStarterWeapon(): void {
    if (this.equipment.equipped.weapon) return;
    if (this.equipment.bag.some((i) => i.slot === "weapon")) return;
    const wid = this.classDef()?.starterWeaponId ?? "voidBlade"; // 10.9T: arma inicial por classe
    const starter = createInstance(wid, "common");
    if (starter && this.equipment.add(starter)) this.equipment.equip(starter.instanceId);
  }

  /**
   * Recalcula TODOS os stats: BASE + FLAT (level/altar/equip) -> depois PERCENT (mastery/tree).
   * Fonte única (sem drift). Também monta o breakdown por atributo e o Combat Power.
   */
  /** 10.6H — objetivos que destrancam o portão da área + volta de Profundidade. */
  private readonly areaGoals = new AreaGoals(() => this.progress.depth);
  /** 10.6G — posição do último abate (origem do "+X ❤" flutuante). */
  private readonly lastKillPos = new THREE.Vector3();
  private absorbDirty = false;
  private absorbRecalcTimer = 0;

  /** Absorção por morte (10.4O): cura instantânea + melhora PERMANENTE (varia por monstro). */
  private applyAbsorb(enemyId: string, elite: boolean): void {
    const def = (GameConfig.enemies as Record<string, EnemyDef>)[enemyId];
    if (!def) return;
    const cfg = GameConfig.absorb;
    const kind = cfg.byShape[def.shape] ?? "hp";
    const mult = (elite ? cfg.eliteMult : 1) * Vip.absorbMult; // VIP (10.4Q): +% de absorção
    if (kind === "heal" || kind === "all") this.player.health.heal(cfg.healPctOfPlayerMax * this.player.health.max * mult);
    let hp = 0;
    let power = 0;
    if (kind === "hp" || kind === "all") hp = Math.max(1, Math.ceil(def.maxHp * cfg.hpFromMaxHp * mult));
    if (kind === "power" || kind === "all") power = Math.max(1, Math.ceil(def.damage * cfg.powerFromDamage * mult));
    if (hp || power) {
      this.progress.absorb(hp, power);
      if (!this.absorbDirty) { this.absorbDirty = true; this.absorbRecalcTimer = 0.5; } // recalc debounced
      // 10.6G — MOSTRA o ganho. A Absorção dava vida/poder permanentes a cada abate e nada
      // aparecia na tela: o jogador não fazia ideia de que matar bicho comum o deixava mais
      // forte para sempre. Agora sobe do corpo, como o dano.
      if (hp) this.damageNumbers.gain(`+${hp} ❤`, this.lastKillPos, "hp");
      if (power) this.damageNumbers.gain(`+${power} ⚔`, this.lastKillPos, "power");
    }
  }

  /** 10.9FO — OFICIAL: painel DEV só existe no dev local (build publicado nunca liga; sem backdoor ?dev=1). */
  private devPanelEnabled(): boolean {
    return import.meta.env.DEV;
  }

  /** 10.9CT — PAINEL DEV: testar o jogo no extremo. */
  private buildDevPanel(): void {
    const jump = (lvl: number): void => { this.xp.level = Math.max(this.xp.level, lvl); this.xp.xpForNext = GameConfig.progression.xpForLevel(this.xp.level); this.recalcPlayer(); this.updateTreeBadge(); this.save.save(); };
    const forgeMax = (): void => { const w = this.equipment.equipped.weapon; if (w) { w.level = GameConfig.equipment.upgradeMaxLevel; this.recalcPlayer(); this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons); this.save.save(); } };
    new DevPanel([
      { title: "Nível", buttons: [
        { label: "+10", fn: () => jump(this.xp.level + 10) },
        { label: "Lvl 50", fn: () => jump(50) },
        { label: "Lvl 100", fn: () => jump(100) },
      ] },
      { title: "Recursos", buttons: [
        { label: "+10k 🪙", fn: () => { this.addCoins(10000); } },
        { label: "+500 💎", fn: () => { this.progress.addDiamonds(500); this.save.save(); } },
        { label: "+30 pts árvore", fn: () => { this.tree.setBoughtPoints(this.tree.boughtPoints + 30); this.updateTreeBadge(); this.save.save(); } },
      ] },
      { title: "Arma", buttons: [
        { label: "Forja MÁX", fn: forgeMax },
      ] },
      { title: "Spawn", buttons: [
        { label: "1 de cada", fn: () => this.spawnTestArena() },
        { label: "Boss da área", fn: () => this.spawnAreaBoss(this.region.id) },
        { label: "👑 Rei do Vazio", fn: () => this.spawnVoidKing() }, // 10.9DR
      ] },
      { title: "Combate", buttons: [
        { label: () => "God: " + (this.player.invulnerable ? "ON" : "OFF"), fn: () => this.player.setDevInvuln(!this.player.invulnerable) },
        { label: () => "Dano ×" + this.devDamageMult, fn: () => { this.devDamageMult = this.devDamageMult >= 100 ? 1 : this.devDamageMult === 1 ? 10 : 100; this.recalcPlayer(); } },
        { label: "Curar", fn: () => this.player.health.heal(this.player.health.max) },
      ] },
      { title: "Reset", buttons: [
        { label: "RESETAR TUDO", danger: true, fn: () => { if (confirm("Apagar TODO o progresso e recomeçar o teste?")) { try { localStorage.clear(); } catch { /* ignora */ } location.reload(); } } },
      ] },
    ]);
  }

  /** 10.9CZ — botão flutuante da POÇÃO DE VIDA (🧪 + quantidade). Cria uma vez. */
  private buildPotionButton(): void {
    if (this.potionBtn) return;
    const b = document.createElement("button");
    b.id = "potion-btn"; b.type = "button"; b.hidden = true;
    b.innerHTML = "<span class=\"pb-ic\">🧪</span><span class=\"pb-n\"></span>";
    b.addEventListener("click", () => this.usePotion());
    // 10.9DF — a poção fica DENTRO do mesmo container das magias (.touch-buttons), com posição
    // ABSOLUTA e a MESMA fórmula das magias. Assim usam o mesmo sistema de coordenadas e o mesmo
    // --safe-bottom — nada de sobreposição por área segura (barra de gestos) que o `fixed` causava.
    const host = document.querySelector(".touch-buttons") ?? document.body;
    host.appendChild(b);
    const st = document.createElement("style");
    st.textContent = "#potion-btn{position:absolute;left:calc(18px + env(safe-area-inset-left));right:auto;bottom:calc(var(--safe-bottom) + 20px + var(--touch-size) * 1.4 + 12px + (var(--touch-size) * 0.95 + 10px) * 3);z-index:13;pointer-events:auto;width:calc(var(--touch-size) * 0.95);height:calc(var(--touch-size) * 0.95);border-radius:50%;border:2px solid #4be08a;background:radial-gradient(circle at 50% 35%,rgba(75,224,138,.35),rgba(12,40,26,.82));color:#eafff2;font:800 13px system-ui,sans-serif;box-shadow:0 6px 16px rgba(0,0,0,.5),0 0 16px rgba(75,224,138,.4);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0;cursor:pointer}#potion-btn .pb-ic{font-size:17px;line-height:1}#potion-btn .pb-n{font-size:10px;font-weight:900}#potion-btn:disabled{opacity:.4;filter:grayscale(.6)}#potion-btn:active{transform:scale(.94)}";
    document.head.appendChild(st);
    this.potionBtn = b;
  }
  /** 10.9CZ — usa uma poção: cura % da vida máx., com recarga; consome 1. */
  private usePotion(): void {
    if (this.state !== "playing") return;
    if (this.potionCd > 0) return;
    if (this.progress.potions <= 0) { this.banner.show(i18n.t("potion.none"), "info", 1600); return; }
    if (this.player.health.current >= this.player.health.max) { this.banner.show(i18n.t("potion.full"), "info", 1400); return; }
    this.progress.spendPotion();
    const heal = Math.round(this.player.health.max * GameConfig.potion.healFrac);
    this.player.health.heal(heal);
    this.potionCd = GameConfig.potion.cooldown;
    this.particles.burst(this.player.position.x, 1.0, this.player.position.z, 0x4be08a, { count: 20, speed: 5, dur: 0.8 });
    this.audio.playSfx("level_up");
    this.banner.show(i18n.t("potion.used", { n: String(heal) }), "info", 1400);
    this.refreshPotionButton();
    this.save.save();
  }
  /** 10.9CZ — atualiza contador/estado do botão da poção. */
  private refreshPotionButton(): void {
    if (!this.potionBtn) return;
    const show = this.state === "playing";
    this.potionBtn.hidden = !show;
    if (!show) return;
    const n = this.progress.potions;
    this.potionBtn.querySelector(".pb-n")!.textContent = String(n);
    this.potionBtn.disabled = n <= 0 || this.potionCd > 0;
  }

  private recalcPlayer(): void {
    const mods = emptyMods();
    // 10.9T — CLASSE: modifica os stats base (vida/dano/vel/crítico/armadura) por cima de tudo.
    const cls = this.classDef();
    if (cls) {
      mods.maxHpMult += cls.hpMult - 1;
      mods.damageMult += cls.damageMult - 1;
      mods.attackSpeedMult += cls.attackSpeedMult - 1;
      mods.moveSpeedMult += cls.moveSpeedMult - 1;
      mods.armor += cls.armorFlat;
      mods.critChance += cls.critChance;
      mods.critDamageMult += cls.critDamageMult;
      mods.blockChance += cls.blockChance; // 10.9EU — bloqueio nato (guerreiro)
      mods.lifeStealPct += cls.lifeSteal; // 10.9EW — roubo de vida nato (guerreiro)
    }
    // 10.9AC — INVASÃO DO VAZIO: buffs TEMPORÁRIOS (só durante o evento). Cada corrompido morto
    // soma dano + redução de dano; ao fim do evento eventStacks volta a 0 e este recalc limpa tudo.
    if (this.eventStacks > 0) {
      const eb = GameConfig.worldEvent.buff;
      mods.damageMult += this.eventStacks * eb.perKillDamage;
      mods.resilience += Math.min(eb.defenseCap, this.eventStacks * eb.perKillDefense);
    }
    const level = this.xp.level;
    const lu = GameConfig.progression.levelUp;

    // level (flat)
    const lvlDmg = (level - 1) * lu.baseDamage;
    const lvlHp = (level - 1) * lu.maxHp;
    mods.damage += lvlDmg;
    mods.maxHp += lvlHp;
    // 10.7O — ESCALA POR NÍVEL (%): dano e vida sobem por porcentagem a cada nível, além do flat.
    // É o que leva o dano a NÚMEROS GRANDES no endgame (junto com weapon level, forja, rarity).
    // +4%/nível de dano, +3%/nível de vida — combina multiplicativo com árvore/maestria.
    mods.damageMult += (level - 1) * 0.04;
    mods.maxHpMult += (level - 1) * 0.03;

    // absorção permanente (10.4O + 10.6U + 10.7A): SEM cap — cada compra sente na hora.
    // Balance vem das quantidades já reduzidas + Threat (monstros escalam até 250×).
    mods.maxHp += this.progress.absorbHp;
    mods.damage += this.progress.absorbPower;
    this.player.setFlatRegen(this.progress.absorbRegen);
    mods.damageMult += this.progress.petDamageMult(); // 10.9L21 — pet dá dano de verdade agora

    // altar (flat)
    const altar = { damage: 0, maxHp: 0, moveSpeed: 0, armor: 0, critChance: 0 };
    for (const upg of GameConfig.altar.upgrades) {
      const lvl = this.altar.levelOf(upg.key);
      if (lvl <= 0) continue;
      const amt = upg.amount * lvl;
      if (upg.stat === "baseDamage") altar.damage += amt;
      else if (upg.stat === "maxHp") altar.maxHp += amt;
      else if (upg.stat === "moveSpeed") altar.moveSpeed += amt;
      else if (upg.stat === "armor") altar.armor += amt;
      else if (upg.stat === "critChance") altar.critChance += amt;
    }
    mods.damage += altar.damage; mods.maxHp += altar.maxHp; mods.moveSpeed += altar.moveSpeed;
    mods.armor += altar.armor; mods.critChance += altar.critChance;
    if (this.buffTimer > 0) { // 10.9CX — buff de magia ativo (Grito/Foco/Barreira)
      mods.damageMult += this.buffMods.dmgMult; mods.resilience += this.buffMods.dr;
      mods.moveSpeedMult += this.buffMods.moveMult; mods.attackSpeedMult += this.buffMods.atkSpeedMult;
      mods.critChance += this.buffMods.critChance;
    }

    // equipment (+ mastery, já embutida no loadout)
    const lo = computeLoadout(this.equipment.equippedList(), (t) => this.equipment.masteryLevel(t));
    mods.maxHp += lo.mods.maxHp;
    mods.damage += lo.mods.damage;
    mods.armor += lo.mods.armor;
    mods.critChance += lo.mods.critChance;
    mods.critDamageMult += lo.mods.critDamageMult;
    mods.moveSpeed += lo.mods.moveSpeed;
    mods.moveSpeedMult += lo.mods.moveSpeedMult;
    mods.attackSpeedMult += lo.mods.attackSpeedMult;
    mods.attackRange += lo.mods.attackRange;
    mods.damageMult += lo.mods.damageMult;
    // 10.9CP — afixos de TIPO DE DANO do equipamento agora ENTRAM no player, roteados por classe
    // (mago/necro: viram arcano; guerreiro: mantém cortante/contundente; arqueiro: perfurante).
    setDisplayClass(this.progress.classId); // rótulos das telas de item seguem a classe
    for (const [nat, amt] of [["slashing", lo.mods.dmgSlashPct], ["piercing", lo.mods.dmgPiercePct], ["blunt", lo.mods.dmgBluntPct]] as const) {
      if (!amt) continue;
      const t = routeDamageType(nat, this.progress.classId);
      if (t === "slashing") mods.dmgSlashPct += amt;
      else if (t === "piercing") mods.dmgPiercePct += amt;
      else if (t === "blunt") mods.dmgBluntPct += amt;
      else mods.dmgArcanePct += amt;
    }

    // 10.9P — Weapon Level (cópias) APOSENTADO: o poder da arma agora vem do NÍVEL DE FORJA
    // (inst.level 1→250), aplicado em Stats.weaponBaseDamage via upgradeDamagePerLevel.
    const wInst = this.equipment.equipped.weapon;

    // 10.9BY — SKILLS (treino): a skill da ARMA em uso dá +0.8% dano/nível (máx +80%) e +2% de
    // velocidade de ataque a cada 10 níveis; DEFESA dá -0.4% de dano recebido/nível (máx 40%,
    // entra como resilience — o mesmo canal da árvore/evento).
    const sk = { damagePct: 0, attackSpeedPct: 0, resiliencePct: 0 };
    const skId = skillForWeapon(this.currentWeaponType(), this.progress.classId);
    if (skId) {
      const wb = weaponSkillBonus(this.progress.skills[skId].level);
      sk.damagePct = wb.damagePct;
      sk.attackSpeedPct = wb.attackSpeedPct;
    }
    sk.resiliencePct = defenseSkillBonus(this.progress.skills.defense.level);
    mods.damageMult += sk.damagePct;
    mods.attackSpeedMult += sk.attackSpeedPct;
    mods.resilience += sk.resiliencePct;

    // Void Tree (percent + condicionais + flags)
    const te = this.tree.effects();
    mods.damageMult += te.damagePct;
    mods.critChance += te.critChance;
    mods.critDamageMult += te.critDamagePct;
    mods.attackSpeedMult += te.attackSpeedPct;
    // 10.5L: alcance da árvore (Agilidade) entra direto na arma efetiva — o computeLoadout
    // já consumiu mods.attackRange do equipamento, então somar lá seria ignorado.
    mods.attackRange += te.attackRangeFlat;
    if (lo.weapon) lo.weapon.attackRange += te.attackRangeFlat;
    mods.maxHpMult += te.maxHpPct;
    mods.armor += te.armorFlat;
    mods.moveSpeedMult += te.moveSpeedPct;
    mods.hookRangeMult += te.hookRangePct;
    mods.hookCdReduc += te.hookCdReducPct;
    mods.sprintDurationMult += te.sprintDurationPct;
    mods.sprintCdReduc += te.sprintCdReducPct;
    mods.vsEliteMult += te.vsElitePct;
    mods.executeMult += te.executePct;
    mods.resilience += te.resiliencePct;
    // 10.5K — por tipo de dano / defesa por tipo / bloqueio / regeneração (só a árvore por enquanto)
    // 10.9CO — os 3 nós de "tipo de dano" do Poder se ADAPTAM à classe: mago/necro causam ARCANO
    // (cajado), guerreiro cortante/contundente, arqueiro perfurante. Antes o mago via "perfurante"
    // inútil. O que sobra (slots sem tipo próprio da classe) vira dano bruto.
    const pr = this.powerTypeMods();
    mods.dmgSlashPct += pr.slash;
    mods.dmgPiercePct += pr.pierce;
    mods.dmgBluntPct += pr.blunt;
    mods.dmgArcanePct += pr.arcane;
    mods.damageMult += pr.generic; // 10.9CO — dano bruto dos slots sem tipo próprio da classe
    mods.defSlashPct += te.defSlashPct;
    mods.defPiercePct += te.defPiercePct;
    mods.defBluntPct += te.defBluntPct;
    mods.blockChance += te.blockChance;
    mods.regenPct += te.regenPct;
    mods.lifeStealPct += te.lifeStealPct; // 10.9EQ
    this.lifeSteal = Math.min(GameConfig.combat.lifeStealCap, mods.lifeStealPct); // 10.9EQ
    setFallbackName(this.progress.charName); // 10.9FG — sem nome de perfil, usa o do personagem
    this.player.setClassDamageReduction(cls ? cls.damageReduction : 0); // 10.9EW — Pele de Aco (fora da Ameaca)

    const traits = new Set<string>(lo.traits);
    for (const f of te.flags) traits.add(f);
    if (cls && cls.cleaveTargets >= 3) traits.add("cleave3"); // 10.9EU — golpe em area do guerreiro

    // 10.5P — a CORRENTE herda os atributos do Caçador (menos a arma da mão, que é trocada
    // pela arma presa em cada slot). Sem isso a mão batia 200 e as presas 19.
    const mainBase = wInst ? weaponBaseDamage(wInst) : 0;
    this.chain.setPower(Math.max(0, GameConfig.player.baseDamage + mods.damage - mainBase), 1 + mods.damageMult, 1 + mods.attackSpeedMult);

    if (this.devDamageMult !== 1) mods.damageMult += this.devDamageMult - 1; // 10.9CT — DEV: dano ×N
    // 10.9EU — BUG ANTIGO: o computeLoadout aplicava na arma so a velocidade de ataque do EQUIPAMENTO.
    // Classe (arqueiro +15%, mago -10%), Arvore do Void (inclusive o no infinito de 50 ranks), treino e
    // buff de magia (Foco do Cacador +30%) eram somados em `mods` DEPOIS e nunca chegavam na arma.
    // Medido: 1,15 golpe/s sem nada, com o buff de +30% e com +87% da arvore. Mesma coisa que ja tinham
    // feito com o alcance da arvore (acima), agora pra velocidade: reaplica com TODOS os bonus + teto.
    if (lo.weapon && lo.baseAps > 0) {
      lo.weapon.attacksPerSecond = Math.min(GameConfig.combat.maxAttacksPerSecond, lo.baseAps * (1 + mods.attackSpeedMult));
    }
    this.player.recalculate(mods, lo.weapon, traits);
    // 10.6E — a arma na mão brilha na cor da RARIDADE (comum = veio nativo da arma).
    this.player.setWeaponRarity(wInst ? RARITY_COLOR[wInst.rarity] : null, wInst ? 1 + rarityIndex(wInst.rarity) * 0.2 : 1, wInst ? ((definitionById(wInst.definitionId) as { tier?: number } | undefined)?.tier ?? 1) : 1, wInst ? wInst.definitionId.replace(/[A-Z].*$/, "") : ""); // 10.9GX/GZ: tier + família → skin
    this.player.setEquipmentVisual(this.equipment.equippedList()); // armadura visível no Hunter
    this.hook.setModifiers(mods.hookRangeMult, mods.hookCdReduc);
    // 10.5B — Ameaça Crescente: inimigos escalam com o poder do player (mas ficam atrás)
    // 10.5T — a corrente conta no poder do player (metade do somado: elas batem em 1 alvo por vez)
    // 10.9CR — a Ameaça agora usa o dano EFETIVO por golpe (base×multiplicador + crítico + bônus do
    // tipo da arma), não só o dano plano. Antes, builds de multiplicador (classe/árvore/crít) tinham
    // DPS muito acima do que a Ameaça via → monstros morriam fácil demais. `combatStats.baseDamage`
    // já embute (base+flat)×(1+damageMult); somo crítico médio e o bônus do tipo de dano da classe.
    const csx = this.player.combatStats;
    const critF = 1 + Math.min(0.95, Math.max(0, csx.critChance)) * Math.max(0, csx.critDamageMult - 1);
    const ownType = 1 + Math.max(0, pr.slash + pr.pierce + pr.blunt + pr.arcane); // bônus do dano da classe
    const effDamage = (csx.baseDamage + this.chain.powerSum * 0.5) * critF * ownType;
    // 10.9EU — a Ameaca NAO cobra a DEFESA da classe (so do guerreiro, a unica com vida acima do normal).
    // Antes: +35% de vida -> monstros batiam 28% mais forte nele -> todas as classes morriam com 2
    // golpes (51% da vida por golpe) e o tanque nao era tanque. Tira a parte da classe da vida (o
    // multiplicador e SOMADO com arvore/equipamento, entao a conta e exata, nao um /1.35) e a
    // armadura fixa da classe da vida efetiva que a Ameaca enxerga.
    const clsHpBonus = cls && cls.hpMult > 1 ? cls.hpMult - 1 : 0;
    let threatHp = clsHpBonus > 0 ? this.player.health.max * (1 + mods.maxHpMult - clsHpBonus) / (1 + mods.maxHpMult) : this.player.health.max;
    const threatArmor = Math.max(0, this.player.combatStats.armor - (clsHpBonus > 0 ? cls!.armorFlat : 0));
    // 10.9FI — Thiago (opcao A): a Ameaca NAO enxerga o que foi COMPRADO (vida/dano/regen do VIP e
    // da loja de moedas). Antes, comprar atributo so inflava os monstros na mesma medida e nao
    // adiantava; agora quem compra fica de fato mais forte. So a parte comprada sai da conta —
    // progressao normal (nivel, arvore, equipamento) continua sendo acompanhada pela Ameaca.
    // 10.9GO — BUG (auditoria): absorbHp/absorbPower somam TAMBEM a absorcao dos ABATES; descontar o
    // campo inteiro fazia cada abate "sumir" da Ameaca -> com centenas de kills os monstros voltavam
    // a 1x ("monstros fracos"). Agora desconta SO o que foi comprado (boughtStats separa).
    const bs = this.boughtStats();
    // 10.9GP — Thiago: "monstros escalam, mas não a ponto de desanimar". A absorção dos ABATES só
    // entra na Ameaça com peso `absorbWeight` (0.5 = metade): antes cada abate subia os monstros
    // 1:1 (medido: 2000 abates -> mesmo % de vida por golpe e MAIS golpes pra matar). O comprado
    // continua 100% fora (opção A); nível/árvore/equipamento seguem acompanhados 1:1.
    const aw = Math.min(1, Math.max(0, GameConfig.threat.absorbWeight));
    const killHp = Math.max(0, this.progress.absorbHp - bs.boughtHp) * (1 - aw); // parte dos abates que a Ameaça NÃO vê
    const killDmg = Math.max(0, this.progress.absorbPower - bs.boughtDmg) * (1 - aw);
    // 10.9HM — EQUIPAMENTO pesa gearWeight (0.4): 60% da vida/armadura das peças e do dano base da arma
    // ficam FORA da Ameaça — forjar e subir de tier passa a render contra monstro comum.
    // (só a parte que vem de FORJA + RARIDADE fica de fora: o valor base do tier continua 1:1 —
    //  senão a régua inteira do jogo afrouxa; medido: contar toda a peça deixava t1 em 5% pra todos)
    const gw = Math.min(1, Math.max(0, GameConfig.threat.gearWeight));
    let gearHp = 0, gearArmor = 0;
    for (const it of this.equipment.equippedList()) {
      if (!it || it.slot === "weapon") continue;
      const full = armorBaseOf(it);
      const def = definitionById(it.definitionId) as { baseHp?: number; baseArmor?: number } | undefined;
      gearHp += Math.max(0, full.hp - (def?.baseHp ?? 0));
      gearArmor += Math.max(0, full.armor - (def?.baseArmor ?? 0));
    }
    const wDefT = wInst ? definitionById(wInst.definitionId) : null;
    const wDefBase = wDefT && isWeaponDef(wDefT) ? (wDefT as WeaponDefinition).baseDamage : 0;
    const gearHpOut = gearHp * (1 - gw);
    const gearDmgOut = Math.max(0, mainBase - wDefBase) * (1 - gw);
    const boughtHp = (bs.boughtHp + killHp + gearHpOut) * (1 + mods.maxHpMult - clsHpBonus); // vida comprada com o % (a parte da classe ja saiu acima)
    threatHp = Math.max(1, threatHp - boughtHp);
    const boughtDmg = (bs.boughtDmg + killDmg + gearDmgOut) * (1 + mods.damageMult) * critF * ownType; // dano comprado, mesmos multiplicadores do effDamage
    const threatDamage = Math.max(1, effDamage - boughtDmg);
    const threatArmorW = Math.max(0, threatArmor - gearArmor * (1 - gw)); // 10.9HM
    Threat.update(threatHp, threatDamage, {
      // 10.9AO — sustain/mitigação entram na ameaça: vida/regen/dano comprados no VIP e armadura de
      // tier alto deixam o player forte — os monstros acompanham em vez de virar cosquinha.
      // 10.9EQ — roubo de vida e SUSTAIN: entra na vida efetiva como regen equivalente (dano x ~2,5
      // golpes/s x %, no maximo o teto de cura/s). Sem isso quem monta roubo de vida ficava imortal.
      // 10.9FI — regen COMPRADA (absorbRegen) tambem sai da conta (opcao A); so a regen da arvore fica
      regen: mods.regenPct * this.player.health.max
        // 10.9EW — o roubo de vida NATO da classe nao entra (mesma regra da defesa de classe)
        + Math.min(Math.max(0, this.lifeSteal - (cls ? cls.lifeSteal : 0)) * effDamage * 2.5, this.player.health.max * GameConfig.combat.maxLifeStealPerSecPct),
      armor: threatArmorW, // 10.9EU · 10.9HM (parte do equipamento fora)
      resilience: mods.resilience,
    });
    // 10.6H — PROFUNDIDADE: cada volta do mundo deixa todo monstro mais forte, para sempre.
    const ds = this.depthScale();
    Threat.hpMult *= ds.hp;
    Threat.dmgMult *= ds.dmg;

    this.buildBreakdown(lvlDmg, lvlHp, altar, lo.mods, te, sk);
    this.combatPower = this.computeCombatPower();
  }

  /** Breakdown por atributo (fonte -> valor). Mastery entra dentro de "Equipment". */
  private buildBreakdown(
    lvlDmg: number,
    lvlHp: number,
    altar: { damage: number; maxHp: number; moveSpeed: number; armor: number; critChance: number },
    eq: import("../items/EquipmentTypes").StatMods,
    te: import("../progression/VoidTree").TreeEffects,
    sk: { damagePct: number; attackSpeedPct: number; resiliencePct: number }, // 10.9BY
  ): void {
    const cfg = GameConfig.player;
    const flat = (src: string, v: number): { src: string; flat: number } => ({ src, flat: v });
    const pct = (src: string, v: number): { src: string; pct: number } => ({ src, pct: v });
    this.breakdown = {
      damage: [flat("base", cfg.baseDamage), flat("level", lvlDmg), flat("altar", altar.damage), flat("equipment", eq.damage), pct("equipment", eq.damageMult), pct("voidTree", te.damagePct), pct("skills", sk.damagePct)],
      maxHp: [flat("base", cfg.maxHp), flat("level", lvlHp), flat("altar", altar.maxHp), flat("equipment", eq.maxHp), pct("voidTree", te.maxHpPct)],
      armor: [flat("base", cfg.armor), flat("altar", altar.armor), flat("equipment", eq.armor), flat("voidTree", te.armorFlat)],
      critChance: [flat("base", cfg.critChance), flat("altar", altar.critChance), flat("equipment", eq.critChance), flat("voidTree", te.critChance)],
      critDamage: [flat("base", cfg.critDamageMult), flat("equipment", eq.critDamageMult), flat("voidTree", te.critDamagePct)],
      attackSpeed: [pct("equipment", eq.attackSpeedMult), pct("voidTree", te.attackSpeedPct), pct("skills", sk.attackSpeedPct)],
      moveSpeed: [flat("base", cfg.moveSpeed), flat("altar", altar.moveSpeed), pct("equipment", eq.moveSpeedMult), pct("voidTree", te.moveSpeedPct)],
      // 10.5K — só a Árvore do Void alimenta estes (por enquanto)
      dmgSlash: [pct("voidTree", this.powerTypeMods().slash)], // 10.9CO — roteado por classe
      dmgPierce: [pct("voidTree", this.powerTypeMods().pierce)],
      dmgBlunt: [pct("voidTree", this.powerTypeMods().blunt)],
      defSlash: [pct("voidTree", te.defSlashPct)],
      defPierce: [pct("voidTree", te.defPiercePct)],
      defBlunt: [pct("voidTree", te.defBluntPct)],
      block: [pct("voidTree", te.blockChance)],
      resilience: [pct("voidTree", te.resiliencePct), pct("skills", sk.resiliencePct)],
      vsElite: [pct("voidTree", te.vsElitePct)],
      execute: [pct("voidTree", te.executePct)],
      regen: [pct("voidTree", te.regenPct)],
    };
  }

  /** Combat Power: número de RESUMO (não influencia dano real). */
  private computeCombatPower(): number {
    const cp = GameConfig.combatPower;
    const cs = this.player.combatStats;
    const w = this.player.weaponInfo;
    return Math.round(
      cs.baseDamage * cp.damage +
        this.player.health.max * cp.maxHp +
        cs.armor * cp.armor +
        cs.critChance * cp.critChance +
        (cs.critDamageMult - 1) * cp.critDamage +
        w.attacksPerSecond * cp.attackSpeed +
        this.player.speed * cp.moveSpeed,
    );
  }

  /** Setup pós-load/reset: arma inicial, desbloqueios, recálculo, vida cheia, baús, UI, acessibilidade. */
  private postLoadSetup(): void {
    this.ensureStarterWeapon();
    this.applySlotUnlocks();
    // 10.9AN — peça EQUIPADA acima do nível (tier/raridade) volta pra bolsa — a regra vale pra todos
    const kicked = this.equipment.unequipIneligible(this.xp.level);
    if (kicked > 0) this.banner.show(i18n.t("inventory.unequippedLevel", { n: kicked }), "info", 2600);
    if (this.treeRebalanced) { this.treeRebalanced = false; this.banner.show(i18n.t("voidTree.rebalanced"), "boss", 4200); } // 10.9AQ
    for (const i of [...this.equipment.bag, ...this.equipment.equippedList().filter(Boolean)]) if (i) this.progress.discoverItem(i.definitionId); // 10.9GA — Índice: marca o que ja tem
    this.sweepOffClassWeapons(); // 10.9K1 — arma de outra classe que já estava na bolsa vira 🪙
    this.grantGiftIfDue(); // 10.9FL — brinde de compensação (3 armas fortes), 1x por personagem
    this.migrateToEvolution(); // 10.9K9 — cópias da regra antiga viram barra; reembolso da Fusão Automática
    this.evolveAll(); // 10.9K9 — barra que já estava cheia
    this.recalcPlayer();
    this.player.health.reset();
    this.chests.refresh((id) => this.equipment.isChestOpen(id));
    // 10.6N — baú já aberto conta na missão do território (senão ela fica impossível)
    this.terrProgress.seedOpenedChests(this.region.id, (id) => this.equipment.isChestOpen(id));
    this.terrGates.setUnlockContext(this.xp.level, this.progress.depth); // 10.7V: portas com trava de nível

    this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons); // 10.5N: sempre com a corrente (senão as armas presas somem da barra)
    this.updateTreeBadge();
    this.applyAccessibility();
    // 10.6R: correio — welcome + daily login
    this.mailSystem.sendWelcome();
    this.checkDailyLoginNow();
    this.updateMailDot();
    // 10.9K0 — a sincronização da temporada manda só o que ESTE personagem ganhou desde a última
    // vez; a marca mora no save do personagem ativo (lida na hora, então trocar de personagem já
    // troca de marca). Precisa estar ligada antes da 1ª sincronização, senão o delta sai sempre 0.
    setSeasonMarkIO({
      get: () => this.progress.seasonMark,
      set: (m) => { this.progress.seasonMark = { ...m }; },
    });
    // 10.9BA — prêmio da temporada anterior (claim única no servidor → correio)
    void import("../net/CloudSeason").then(async (m) => {
      await m.syncSeason(this.myProfileStats(), true);
      const prizes = await m.claimSeasonPrizes();
      if (prizes.length === 0) return;
      for (const p of prizes) this.mailSystem.sendSeasonPrize(p.season, p.category, p.rank, p.diamonds);
      this.banner.show(i18n.t("mail.newMail"), "info", 2000);
      this.updateMailDot();
      this.save.save();
    });
    this.initWorldEvent(); // 10.9AC: deriva o relógio de 4h do progresso (não mexe em evento ativo)
    this.hud.setCharName(this.progress.charName); // 10.9AF: nome do personagem no HUD
    // 10.9BY — treino offline: anel do boneco aceso + aviso se já terminou enquanto o jogo estava fechado
    this.trainingDummy.setTraining(!!this.progress.training);
    this.updateTrainingHud();
    if (this.progress.training && Date.now() >= this.progress.training.endsAt) {
      window.setTimeout(() => this.banner.show(i18n.t("training.finished"), "info", 3000), 2600);
    }
  }

  // ---- 10.9BY: SKILLS + TREINO ----
  private skillName(id: SkillId): string { return i18n.t(`skills.${id}`); }

  /** Soma XP numa skill; level-ups em cadeia → banner + som + recalc. */
  private gainSkillXp(id: SkillId, amount: number): void {
    if (amount <= 0) return;
    const before = this.progress.skills[id].level;
    const ups = addSkillXp(this.progress.skills, id, amount);
    if (ups > 0) {
      const lvl = this.progress.skills[id].level;
      this.banner.show(i18n.t("skills.levelUp", { icon: SKILL_ICON[id], name: this.skillName(id), n: lvl }), "info", 1800);
      this.audio.playSfx("level_up");
      if (lvl !== before) this.recalcPlayer();
      this.save.save();
    }
  }

  private openTraining(): void {
    if (this.state !== "playing" && this.state !== "character") return;
    if (this.state === "character") this.characterScreen.hide();
    this.state = "training";
    this.input.setEnabled(false);
    this.bossPrompt.hidden = true;
    const wt = this.currentWeaponType();
    this.trainingScreen.open(() => this.buildTrainingView(), skillForWeapon(wt, this.progress.classId));
  }
  private closeTraining(): void {
    if (this.state !== "training") return;
    this.trainingScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
    if (this.shouldOfferGuide()) this.queueGuide(); // 10.9IH
  }

  /** 10.9DO — ícone da skill: SVG real da arma do jogo (renderiza igual em todo aparelho, sem
   *  emoji quebrado). Skills sem arma (invocar/defesa) mantêm o emoji. */
  private skillIcon(id: SkillId): string {
    const wt: Record<string, string> = { sword: "sword", axe: "axe", mace: "mace", ranged: "bow", magic: "staff" };
    if (wt[id]) return weaponTypeSvg(wt[id], "#cdd6ff");
    const C = "#cdd6ff";
    if (id === "defense") return `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block"><path d="M12 3 L19 6 V11 C19 16 15.5 19 12 21 C8.5 19 5 16 5 11 V6 Z" fill="none" stroke="${C}" stroke-width="2" stroke-linejoin="round"/><path d="M9 12 l2 2 l4 -4.5" fill="none" stroke="${C}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    if (id === "summon") return `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block"><path d="M6 11 a6 6 0 0 1 12 0 v7 l-2 -1.6 l-2 1.6 l-2 -1.6 l-2 1.6 l-2 -1.6 z" fill="${C}"/><circle cx="9.6" cy="11" r="1.3" fill="#12122a"/><circle cx="14.4" cy="11" r="1.3" fill="#12122a"/></svg>`;
    return SKILL_ICON[id];
  }
  private buildTrainingView(): TrainingView {
    const cur = skillForWeapon(this.currentWeaponType(), this.progress.classId);
    const tcfg = GameConfig.skills.training;
    const t = this.progress.training;
    const now = Date.now();
    return {
      skills: SKILL_IDS.map((id) => {
        const s = this.progress.skills[id];
        const max = s.level >= GameConfig.skills.maxLevel;
        const bonus = id === "defense"
          ? i18n.t("skills.bonusDefense", { n: Math.round(defenseSkillBonus(s.level) * 1000) / 10 })
          : (() => { const b = weaponSkillBonus(s.level); return i18n.t("skills.bonusWeapon", { dmg: Math.round(b.damagePct * 1000) / 10, spd: Math.round(b.attackSpeedPct * 100) }); })();
        return {
          id, icon: this.skillIcon(id), name: this.skillName(id), level: s.level, xp: s.xp, xpNext: max ? 0 : skillXpFor(s.level),
          bonus, costPerHour: trainCostPerHour(s.level), ratePerMin: trainRatePerMin(s.level), current: id === cur,
        };
      }),
      training: t ? {
        skill: t.skill, name: this.skillName(t.skill), icon: this.skillIcon(t.skill), startedAt: t.startedAt, endsAt: t.endsAt, ratePerMin: t.ratePerMin,
        pendingXp: trainPendingXp(t, now), finishCost: trainFinishCost(t, now), done: now >= t.endsAt,
      } : null,
      coins: this.progress.coins,
      diamonds: this.progress.diamonds,
      hours: Vip.active ? [...tcfg.hours, tcfg.vipHours] : [...tcfg.hours],
      adDouble: Ads.available("trainDouble") ? (Vip.active ? "vip" : "ad") : null,
    };
  }

  /** Contrata treino offline: 🪙 = (20 + nível×4)/h × horas · ou 💎 = ceil(🪙/100). */
  private startTraining(skill: SkillId, hours: number, payWith: "coins" | "diamonds"): void {
    if (this.progress.training) { this.banner.show(i18n.t("training.busy"), "info", 1600); return; }
    const tcfg = GameConfig.skills.training;
    const allowed = Vip.active ? [...tcfg.hours, tcfg.vipHours] : tcfg.hours;
    if (!allowed.includes(hours)) return;
    const level = this.progress.skills[skill].level;
    if (level >= GameConfig.skills.maxLevel) return;
    const coins = trainCostPerHour(level) * hours;
    if (payWith === "coins") {
      if (!this.progress.spendCoins(coins)) { this.banner.show(i18n.t("shop.noCoins"), "info", 1600); return; }
    } else if (!this.progress.spendDiamonds(trainDiamondCost(coins))) { this.banner.show(i18n.t("vip.notEnough"), "info", 1600); return; }
    const now = Date.now();
    this.progress.training = { skill, startedAt: now, endsAt: now + hours * 3600_000, ratePerMin: trainRatePerMin(level) };
    this.trainingDummy.setTraining(true);
    this.banner.show(i18n.t("training.started", { name: this.skillName(skill), h: hours }), "info", 2000);
    this.audio.playSfx("chest_open");
    this.save.save();
    this.updateTrainingHud();
    this.trainingScreen.refresh();
  }

  /** Coleta o XP rendido (parcial ou total). `double` = 📺 anúncio (VIP: direto), 1/dia. */
  private async collectTraining(double: boolean): Promise<void> {
    const t = this.progress.training;
    if (!t) return;
    const now = Date.now();
    let xp = trainPendingXp(t, now);
    const done = now >= t.endsAt;
    if (xp <= 0 && !done) return;
    if (double && xp > 0) {
      const ok = await Ads.show("trainDouble");
      if (!ok) { this.banner.show(i18n.t("ads.notRewarded"), "info", 1600); this.trainingScreen.refresh(); return; }
      if (this.progress.training !== t) return; // estado mudou enquanto o vídeo rodava
      xp *= 2;
    }
    const mins = Math.floor((Math.min(now, t.endsAt) - t.startedAt) / 60000);
    if (done) this.progress.training = null;
    else t.startedAt += mins * 60000; // coleta parcial: o que já rendeu sai da conta (sobra do minuto fica)
    if (xp > 0) {
      this.gainSkillXp(t.skill, xp);
      this.banner.show(i18n.t("training.collected", { n: xp, name: this.skillName(t.skill) }), "boss", 2200);
      this.audio.playSfx("chest_open");
    }
    this.trainingDummy.setTraining(!!this.progress.training);
    this.save.save();
    this.updateTrainingHud();
    this.trainingScreen.refresh();
  }

  /** "Terminar agora": 💎 = ceil(minutos restantes / 30); depois coleta tudo. */
  private finishTrainingNow(): void {
    const t = this.progress.training;
    if (!t) return;
    const cost = trainFinishCost(t);
    if (cost > 0 && !this.progress.spendDiamonds(cost)) { this.banner.show(i18n.t("vip.notEnough"), "info", 1600); return; }
    // encerra AGORA creditando todo o rendimento ainda não coletado
    const totalLeftXp = Math.floor((t.endsAt - t.startedAt) / 60000) * t.ratePerMin;
    this.progress.training = null;
    if (totalLeftXp > 0) {
      this.gainSkillXp(t.skill, totalLeftXp);
      this.banner.show(i18n.t("training.collected", { n: totalLeftXp, name: this.skillName(t.skill) }), "boss", 2200);
      this.audio.playSfx("chest_open");
    }
    this.trainingDummy.setTraining(false);
    this.save.save();
    this.updateTrainingHud();
    this.trainingScreen.refresh();
  }

  /** Indicador no menu: "🎯 Espada · 3h 12min" (ou "pronto!"). */
  private updateTrainingHud(): void {
    const t = this.progress.training;
    if (!t) { this.trainingDummy.setLabel("🎯 Treino"); return; } // 10.9DQ — tempo no TOTEM
    const left = t.endsAt - Date.now();
    const name = this.skillName(t.skill);
    this.trainingDummy.setLabel(left <= 0 ? "🎯 " + i18n.t("training.hudDone", { name }) : `🎯 ${name} · ${fmtDuration(left)}`);
  }

  // ---- responsividade / acessibilidade ----
  /** Único ponto que reage a mudança de viewport: canvas, câmera e minimapa. */
  private onViewport(s: ViewportState): void {
    this.renderer.resize(s.width, s.height, s.dpr);
    this.cameraRig.setAspect(s.aspect);
    this.minimap.setViewport(s);
  }

  private applyAccessibility(): void {
    document.documentElement.setAttribute("data-reduced-motion", String(this.access.reducedMotion));
    viewport.setUserScale(this.access.uiScaleMult);
    this.cameraRig.setReducedMotion(this.access.reducedMotion);
  }
  private setUiScale(k: UiScaleKey): void {
    this.access.uiScale = k;
    this.applyAccessibility();
    this.save.save();
  }
  private setReducedMotion(v: boolean): void {
    this.access.reducedMotion = v;
    this.applyAccessibility();
    this.save.save();
  }
  /** 10.8F — stats atuais do jogador pro perfil público / ranking. */
  private myProfileStats(): ProfileStats {
    return {
      depth: this.progress.depth,
      level: this.xp.level,
      power: this.combatPower,
      kills: this.progress.totalKills,
      pvpWins: this.progress.pvpWins,
      classId: this.progress.classId || undefined, // 10.9L28 — ranking por personagem
      charName: this.progress.charName || undefined,
    };
  }

  /** 10.4X: troca o preset de qualidade gráfica em runtime (modo leve p/ celular fraco).
   *  10.9HY — o preset agora também manda na CARGA DE CENA, então reaplica o teto de inimigos.
   *  Escolher aqui é MANUAL: `quality.setPreset` desliga o modo automático e o save guarda isso. */
  private setQuality(p: QualityPreset): void {
    quality.setPreset(p);
    // 10.9HZ — escolha MANUAL zera os degraus do governador: sem isso, depois de uma queda de fps
    // o jogador escolhia "Alto" e a imagem continuava em resolução 1.0, sem sombra e sem brilho
    // pelo resto da sessão, sem explicação nenhuma.
    this.renderer.resetDegrade();
    this.renderer.applyQuality();
    this.spawner.refreshQuality();
    this.save.save();
  }

  // ---- progressão / save ----
  /** Carrega um save nos sistemas. O recálculo de stats acontece em postLoadSetup(). */
  private applyProgress(save: SaveData): void {
    this.xp.setState(save.level, save.xp);
    this.altar.applyLoadedLevels(save.altarLevels);
    this.inventory.load(save.essence);
    this.equipment.load(save.inventory);
    i18n.setLocale(save.settings.language as Locale);
    this.audio.load(save.settings.audio);
    this.access.load(save.settings.access);
    this.cameraRig.setUserZoom(this.access.camZoom); // 10.6C: distância de câmera salva
    // 10.4X/10.9HY — qualidade gráfica: escolha MANUAL manda. Em modo automático mantém o preset
    // que a detecção escolheu no boot (main.ts), MAS respeita o PISO lembrado: se numa sessão
    // anterior o governador de fps já teve que baixar neste aparelho, começa direto lá embaixo
    // em vez de fazer o jogador passar de novo pelos ~12 s de travada até o governador agir.
    if (!save.settings.graphicsAuto) {
      quality.setPreset(save.settings.graphics);
    } else {
      const rank = { low: 0, medium: 1, high: 2 } as const;
      if (rank[save.settings.graphics] < rank[quality.name]) {
        quality.setPreset(save.settings.graphics);
        quality.auto = true; // continua automático (o piso veio do governador, não do jogador)
      }
    }
    this.renderer.applyQuality();
    this.progress.load(save.progress);
    Vip.setTier(this.progress.vipTier);
    this.progress.onVipPoints = () => this.checkVipTier(); // 10.9BG
    Vip.regionProvider = () => this.region.id; // 10.9BO — XP bônus da área VIP da região atual
    this.checkVipTier(); // save antigo pode já ter pontos pra um tier maior
    Vip.setPassExpiresAt(this.progress.passExpiresAt);
    this.applyClassOutfit(); // 10.9X: roupa da classe (ou custom escolhida na Guarda-roupa)
    this.applyPet(); // 10.5C: ativa o pet se possuído
    this.equipment.extraCapacity = this.progress.bagSlots + Vip.bagBonus; // 10.9HA — mochila comprada + 10.9J7 — bônus de tier VIP (grátis, só conveniência)
    // v6: estado de mundo (regiões/fog/descobertas). Semeia a Forgotten Forest do save legado.
    const seedDisc = {
      sanctuary: save.progress.discoveries.sanctuary,
      altar: save.progress.discoveries.altar,
      alphaDen: save.progress.discoveries.alphaDen,
    };
    this.region.load(
      { unlockedRegions: save.unlockedRegions, currentRegion: save.currentRegion, fogByRegion: save.fogByRegion, fogFromExtent: save.fogFromExtent, discoveriesByRegion: save.discoveriesByRegion },
      save.fog,
      seedDisc,
    );
    this.region.activate(this.region.id);
    // 10.9DV — BUG: carregar o save ativava a regiao mas NUNCA passava a tabela/teto dela ao
    // spawner, que ficava com o default de 22 vivos (a Area 1 pede 110). Com teto 22, a
    // guarnicao da base (16) comia quase tudo e o resto do mapa ficava VAZIO.
    this.spawner.setTable(this.region.enemyTable(), this.region.maxAlive());
    this.fog = this.region.activeFog();
    this.minimap.setFog(this.fog);
    this.missions.load(this.progress.missions); // 10.5O: missões salvas (ou 3 novas)
    this.areaGoals.load(this.progress.areaGoals); // 10.6H
    this.recountTerritoryGoal(); // 10.9L6 — conta inflada do vai-e-vem → número real
    this.enforceRegionGates(); // 10.9L3 — região só abre com a área anterior concluída
    this.terrProgress.load(this.progress.territories); // 10.6L
    this.daily.load(this.progress.daily); // 10.6M
    this.tutorial.load(this.progress.tips); // 10.5U
    this.mailSystem.load(save.mail); // 10.6R: correio
    Ads.attach(this.progress.ads, () => { this.progress.ads = Ads.counters; this.save.save(); }); // 10.9BE
    this.tree.setBoughtPoints(this.progress.voidPointsBought); // 10.5L: pontos comprados na Loja
    // 10.9AQ — se o ritmo de pontos mudou e a árvore gastou mais do que ganha, ela zera (devolve
    // os pontos) e avisamos no postLoadSetup.
    if (this.tree.load(save.voidTree, save.level, save.progress.bossesDefeated.voidAlpha)) this.treeRebalanced = true;
    this.regionMastery.load(save.regionMastery);
    this.barrier.setOpen(this.progress.alphaGateOpen);
    // Rift da Região 1 acende após o Apex Fang
    this.refreshGate(); // 10.6H: portão só abre com os objetivos da área cumpridos
  }

  /** Limpa todo o mundo transitório (inimigos, boss, essências, FX, hook, loot). */
  private clearWorld(): void {
    this.traps.length = 0; this.curses.length = 0; this.meteors.length = 0; this.spellVfx.clear(); // 10.9GO — magias de area nao vazam pra outra area/respawn
    this.clearBoss();
    this.enemies.clear();
    this.minions.clear(); // 10.9AD — some com os servos do Necromante
    this.essence.clear();
    this.telegraph.clear();
    this.particles.clear();
    this.projectiles.clear();
    this.enemyBolts.clear();
    this.groundLoot.clear();
    this.hook.reset();
    this.autoTarget.clear();
    this.enemyBars.clear();
    this.targetRing.visible = false;
    this.eliteTimer = 0;
    this.lastRegion = null;
  }

  private clearBoss(): void {
    if (this.boss) {
      this.boss.dispose(this.renderer.scene);
      this.boss = null;
    }
    this.zoneActiveRoom = null; // 10.9AJ — solta o clamp da sala
    this.bossHud.hide();
    this.bossPrompt.hidden = true;
    this.input.touch.setHookPulse(false);
    this.telegraph.clear(); // sem shard/wave/lane fantasma ao encerrar o Boss
    this.cameraRig.setBossZoom(1); // restaura o zoom normal ao sair do Boss
    this.cameraRig.setBossFocus(null);
  }

  // ---- 10.4R: Boss de Evento (periódico, loot exclusivo) ----
  /** Conta o tempo até o próximo evento e o dispara quando as condições batem. */
  private updateEventBoss(dt: number): void {
    const cfg = GameConfig.eventBoss;
    if (!cfg.enabled) return;
    if (cfg.requireAlpha && !this.progress.bossesDefeated.voidAlpha) return;
    if (this.player.inSanctuary) return; // não surge dentro da base
    this.eventBossTimer -= dt;
    if (this.eventBossTimer <= 0) {
      this.eventBossTimer = cfg.interval;
      this.spawnEventBoss();
    }
  }

  /** Invoca o boss de evento perto do player (reusa a IA do Void Alpha, buffada). */
  private spawnEventBoss(): void {
    if (this.boss) return;
    const cfg = GameConfig.eventBoss;
    const bt = this.bossThreat(); // 10.9AP — acompanha a ameaça
    this.enemies.clear();
    this.hook.reset();
    this.autoTarget.clear();
    this.telegraph.clear();
    // nasce ~6m à frente do player (a arena do boss acompanha esse ponto)
    const p = this.player.position;
    const sx = p.x + Math.sin(this.player.facingAngle) * 6;
    const sz = p.z + Math.cos(this.player.facingAngle) * 6;
    this.boss = new VoidAlpha(
      this.renderer.scene,
      {
        player: this.player,
        telegraph: this.telegraph,
        particles: this.particles,
        summon: (count, center) => this.summonAdds(count, center),
        impulse: (s) => this.cameraRig.impulse(s),
      },
      { event: true, hpMult: cfg.hpMult * bt.hp, dmgMult: cfg.dmgMult * bt.dmg, spawnAt: { x: sx, z: sz } },
    );
    this.bossHud.show();
    this.bossPrompt.hidden = true;
    this.banner.show(i18n.t("event.bossAwakens"), "boss", 2800);
    this.audio.playMusic("boss_theme");
  }

  /** Recompensa do boss de evento: arma EXCLUSIVA garantida + baú premium + essência/XP.
   * NÃO mexe em progressão (sem unlock de região/alma) — é conteúdo repetível. */
  private onEventBossDefeated(): void {
    this.bossVictoryFx(); // 10.9GT
    const cfg = GameConfig.eventBoss;
    this.gainMastery(GameConfig.mastery.xp.perBoss, "boss");
    this.gainRegionMastery(GameConfig.regionMastery.xp.perBoss);
    this.xp.gain(cfg.xp);
    this.addCoins(GameConfig.shop.coinsPerEventBoss); // 10.4Y: moeda
    this.missionProgress("boss"); // 10.5O
    this.inventory.add("power", cfg.essence.power);
    this.inventory.add("vitality", cfg.essence.vitality);
    this.inventory.add("ferocity", cfg.essence.ferocity);
    const p = this.player.position;
    // arma EXCLUSIVA de boss garantida (fora do pool normal, §10.4P)
    const bw = rollBossWeapon(cfg.weaponRarity, this.classWeaponTypes(), this.xp.level);
    if (bw) this.collectItem(bw, "event"); // 10.4S: auto-coleta
    this.spawnChestLoot("void", p.x, p.z + 2); // baú premium do evento
    this.banner.show(i18n.t("event.bossDefeated"), "boss", 2600);
    this.audio.playSfx("boss_defeated");
    this.save.save();
  }

  // ==================== 10.9AC — INVASÃO DO VAZIO (evento a cada 4h) ====================
  /** Deriva o cooldown do relógio PERSISTIDO (chamado no boot, depois de carregar o progresso). */
  private initWorldEvent(): void {
    if (this.worldEventActive) return; // não reinicializa por cima de um evento em andamento
    const cfg = GameConfig.worldEvent;
    const now = Date.now();
    if (!this.progress.worldEventNextAt || this.progress.worldEventNextAt <= 0) {
      this.progress.worldEventNextAt = now + cfg.firstDelay * 1000; // conta nova → 1º evento em breve
    }
    this.worldEventCooldown = Math.max(0, (this.progress.worldEventNextAt - now) / 1000);
    this.worldEventActive = false;
  }

  private countEventMonsters(): number {
    let n = 0;
    for (const e of this.enemies.enemies) if (!e.isDead && e.eventMonster) n++;
    return n;
  }

  /** Loop do evento: roda SEMPRE (conta a janela mesmo em luta de boss) — mas a horda de
   *  corrompidos só é reposta na fase de ONDA (sem boss). Fases alternam: onda → boss → onda. */
  private updateWorldEvent(dt: number): void {
    const cfg = GameConfig.worldEvent;
    if (!cfg.enabled) return;
    if (cfg.requireAlpha && !this.progress.bossesDefeated.voidAlpha) return;

    if (this.worldEventActive) {
      this.worldEventTimeLeft -= dt;
      this.eventKillToastTimer = Math.max(0, this.eventKillToastTimer - dt);
      if (this.worldEventTimeLeft <= 0) { this.endWorldEvent(); return; }
      if (this.boss) return; // fase de boss: a horda espera
      // fase de onda: repõe corrompidos e checa o gate do boss
      this.eventMonsterTimer -= dt;
      if (this.eventMonsterTimer <= 0 && this.countEventMonsters() < cfg.monster.maxAlive && !this.player.inSanctuary) {
        this.eventMonsterTimer = cfg.monster.spawnInterval;
        this.spawner.spawnEventMonster(this.player.position, cfg.monster.hpMult, cfg.monster.dmgMult, cfg.monster.ringMin, cfg.monster.ringMax);
      }
      const need = cfg.boss.unlockKills + this.eventBossTier * cfg.boss.unlockKillsPerTier;
      if (this.eventKillsTowardBoss >= need) this.spawnWorldEventBoss();
      return;
    }

    // fora do evento: conta o cooldown SÓ no campo (não "gasta" evento com o player parado na base,
    // nem começa uma invasão dentro da Zona dos Bosses)
    if (this.player.inSanctuary || this.inBossZone || this.raid.active) return; // 10.9EO
    this.worldEventCooldown -= dt;
    if (this.worldEventCooldown <= 0) this.startWorldEvent();
  }

  private startWorldEvent(): void {
    if (this.boss) { this.worldEventCooldown = 20; return; } // não começa em cima de outro boss
    const cfg = GameConfig.worldEvent;
    this.worldEventActive = true;
    this.worldEventTimeLeft = cfg.duration;
    this.eventStacks = 0;
    this.eventKillsTowardBoss = 0;
    this.eventBossTier = 0;
    this.eventMonsterTimer = 0;
    this.enemies.clear(); // o campo vira INVASÃO (limpa a fauna normal)
    this.recalcPlayer(); // estado limpo (stacks=0)
    this.banner.show(i18n.t("worldEvent.start"), "boss", 3200);
    this.audio.playMusic("boss_theme");
    this.save.save();
  }

  private onEventMonsterKilled(): void {
    const cfg = GameConfig.worldEvent;
    this.eventKillsTowardBoss++;
    if (this.eventStacks < cfg.buff.maxStacks) {
      this.eventStacks++;
      this.recalcPlayer(); // aplica +dano/+defesa TEMPORÁRIOS na hora
    }
    if (this.eventKillToastTimer <= 0 && !this.boss) {
      const need = cfg.boss.unlockKills + this.eventBossTier * cfg.boss.unlockKillsPerTier;
      const left = Math.max(0, need - this.eventKillsTowardBoss);
      this.banner.show(left > 0 ? i18n.t("worldEvent.progress", { left }) : i18n.t("worldEvent.bossReady"), "info", 1000);
      this.eventKillToastTimer = 0.9;
    }
  }

  private spawnWorldEventBoss(): void {
    if (this.boss) return;
    const cfg = GameConfig.worldEvent;
    const tier = this.eventBossTier;
    const variant = cfg.boss.variants[this.eventVariantIdx % cfg.boss.variants.length];
    this.eventVariantIdx++;
    this.eventKillsTowardBoss = 0;
    this.enemies.clear();
    this.hook.reset();
    this.autoTarget.clear();
    this.telegraph.clear();
    const p = this.player.position;
    const sx = p.x + Math.sin(this.player.facingAngle) * 6;
    const sz = p.z + Math.cos(this.player.facingAngle) * 6;
    const bt = this.bossThreat(); // 10.9AP — o boss da Invasão também acompanha a ameaça
    const hpMult = cfg.boss.baseHpMult * variant.hp * (1 + tier * cfg.boss.hpPerTier) * bt.hp;
    const dmgMult = cfg.boss.baseDmgMult * variant.dmg * (1 + tier * cfg.boss.dmgPerTier) * bt.dmg;
    // 10.9AG — BOSS DIFERENTE DE VERDADE a cada aparição: alterna Rift Seer (conjurador) e Apex
    // Fang (melee). Cada um tem IA/telegraphs próprios — não é só um reskin do mesmo esqueleto.
    if (this.eventVariantIdx % 2 === 0) {
      this.boss = new RiftSeer(
        this.renderer.scene,
        { player: this.player, telegraph: this.telegraph, particles: this.particles, impulse: (s) => this.cameraRig.impulse(s) },
        { event: true, hpMult, dmgMult, spawnAt: { x: sx, z: sz }, tint: variant.tint, titleKey: `worldEvent.boss.${variant.key}`, form: variant.seerForm }, // 10.9AT
      );
    } else {
      this.boss = new VoidAlpha(
        this.renderer.scene,
        {
          player: this.player,
          telegraph: this.telegraph,
          particles: this.particles,
          summon: (count, center) => this.summonAdds(count, center),
          impulse: (s) => this.cameraRig.impulse(s),
        },
        { event: true, hpMult, dmgMult, spawnAt: { x: sx, z: sz }, tint: variant.tint, titleKey: `worldEvent.boss.${variant.key}`, form: variant.alphaForm }, // 10.9AT
      );
    }
    (this.boss as { _worldEvent?: boolean })._worldEvent = true;
    this.bossHud.show();
    this.bossPrompt.hidden = true;
    this.banner.show(i18n.t("worldEvent.bossAwakens", { name: i18n.t(`worldEvent.boss.${variant.key}`), tier: tier + 1 }), "boss", 2800);
    this.audio.playMusic("boss_theme");
  }

  private onWorldEventBossDefeated(): void {
    this.bossVictoryFx(); // 10.9GT
    const cfg = GameConfig.worldEvent;
    const tier = this.eventBossTier;
    this.xp.gain(cfg.reward.xpPerBoss);
    this.addCoins(GameConfig.bossCoins.eventBoss + tier * GameConfig.bossCoins.eventPerTier); // 10.9BK: 🪙 no lugar do 💎
    this.inventory.add("power", cfg.reward.essence.power);
    this.inventory.add("vitality", cfg.reward.essence.vitality);
    this.inventory.add("ferocity", cfg.reward.essence.ferocity);
    const bw = rollBossWeapon(cfg.reward.weaponRarity, this.classWeaponTypes(), this.xp.level);
    if (bw) this.collectItem(bw, "event");
    const p = this.player.position;
    this.spawnChestLoot("void", p.x, p.z + 2);
    this.missionProgress("boss");
    this.eventBossTier = Math.min(cfg.boss.maxTier, tier + 1); // RENASCE mais forte (exige nova onda)
    this.banner.show(i18n.t("worldEvent.bossDown", { tier: tier + 1 }), "boss", 2600);
    this.audio.playSfx("boss_defeated");
    this.save.save();
    // NÃO respawna já: precisa matar corrompidos de novo pra LIBERAR o próximo (mais forte)
  }

  private endWorldEvent(): void {
    const cfg = GameConfig.worldEvent;
    this.worldEventActive = false;
    this.worldEventTimeLeft = 0;
    this.eventStacks = 0;
    this.eventKillsTowardBoss = 0;
    this.eventBossTier = 0;
    if (this.boss && (this.boss as VoidAlpha & { _worldEvent?: boolean })._worldEvent) this.clearBoss();
    for (const e of this.enemies.enemies) if (e.eventMonster) e.removeRequested = true; // some com a horda
    this.recalcPlayer(); // REMOVE os buffs temporários (voltam ao normal)
    const now = Date.now();
    this.progress.worldEventNextAt = now + cfg.interval * 1000; // próximo daqui a 4h
    this.worldEventCooldown = cfg.interval;
    this.banner.show(i18n.t("worldEvent.end"), "info", 3000);
    this.audio.stopMusic();
    this.save.save();
  }

  // ==================== 10.9AJ — ZONA DOS BOSSES (Fenda dos Titãs) ====================
  /** Portal do hub: teleporta pra zona (limpa a fauna, mostra salas/portal de volta). */
  private enterBossZone(): void {
    const bz = GameConfig.bossZone;
    if (!bz.enabled || this.inBossZone) return;
    this.renderer.setGradeOverride(VOID_GRADE); // 10.9GR — cara do Void
    this.enemies.clear();
    this.hook.reset();
    this.autoTarget.clear();
    this.telegraph.clear();
    this.inBossZone = true;
    this.zoneActiveRoom = null;
    this.bossZone.setVisible(true);
    this.zoneExitPortal.setVisible(true);
    // 10.9AM — a zona fica FORA do mapa: troca o limite do mundo pelo quadrado da zona ANTES de
    // teleportar (senão o clamp do mapa puxaria o player de volta no mesmo frame).
    this.arena.setClampOverride({ cx: bz.center.x, cz: bz.center.z, half: bz.bounds.half });
    this.player.teleportTo(bz.arrive.x, bz.arrive.z);
    this.cameraRig.snapTo(this.player.position);
    this.refreshBossZone();
    this.banner.show(i18n.t("bossZone.welcome"), "boss", 3200);
    this.audio.playSfx("level_up");
  }

  /** Sai da zona. `toHub` = teleporta pro Santuário (portal de volta); false = quem chamou já move o player. */
  private leaveBossZone(toHub: boolean): void {
    this.renderer.setGradeOverride(null); // 10.9GR
    if (!this.inBossZone) return;
    if (this.boss && (this.boss as { _zoneRoom?: string })._zoneRoom) this.clearBoss(); // sair = desistir (sem cooldown)
    this.inBossZone = false;
    this.zoneActiveRoom = null;
    this.bossZone.resetLocks(); // 10.9CN — sair/morrer abre todas as portas
    this.arena.setClampOverride(null); // 10.9AM — volta o limite do mundo ao mapa
    this.bossZone.setVisible(false);
    this.zoneExitPortal.setVisible(false);
    if (toHub) {
      const def = this.region.current();
      this.player.teleportTo(def.spawn.x, def.spawn.z);
      this.cameraRig.snapTo(this.player.position);
      this.spawner.prewarm(this.player.position);
      this.banner.show(i18n.t("bossZone.left"), "info", 1800);
    }
  }

  private zoneRoomHere(): RoomView | null {
    return this.bossZone.roomAt(this.player.position.x, this.player.position.z);
  }

  /** 10.9HU — salas de prestígio (r11+) também travam por FRAÇÃO da Árvore do Void fechada. */
  private zoneTreeOk(def: { treePct?: number }): boolean {
    const pct = def.treePct;
    if (!pct) return true;
    const total = VoidTreeState.totalMaxPoints(this.progress.classId);
    return total > 0 && this.tree.spent() >= total * pct;
  }

  private zoneCanChallenge(room: RoomView): boolean {
    if (this.boss || this.xp.level < room.def.level || !this.zoneTreeOk(room.def)) return false;
    return (this.progress.bossZoneNextAt[room.def.id] ?? 0) <= Date.now();
  }

  private refreshBossZone(): void {
    // 10.9HW — a Fenda também marca a trava da ÁRVORE no rótulo/piso/porta da sala (antes só o
    // nível; uma sala de prestígio com nível ok mas árvore incompleta aparecia "👑 liberada" e não
    // deixava desafiar). Passa a checagem da árvore por sala.
    this.bossZone.refresh(this.xp.level, this.progress.bossZoneNextAt, Date.now(), (def) => !this.zoneTreeOk(def));
  }

  /** Loop da zona: PORTAS (empurra de sala trancada), etiquetas periódicas e clamp durante a luta. */
  private updateBossZone(dt: number): void {
    if (!this.inBossZone) return;
    // 10.9CU — os adds que o boss invoca (summonAdds) sobravam vagando depois que ele morria.
    // 10.9HX — mas agora a Fenda TEM monstros ambientes rondando os corredores (Thiago: "seria bom
    // ter uns monstros rodando por aqui tbm"): sem boss, varre só o que NÃO é ambiente.
    if (!this.boss) {
      for (const e of this.enemies.enemies) if (!e.zoneAmbient && !e.isDead) e.removeRequested = true;
      this.spawnZoneAmbient(dt);
    }
    this.zoneLockToast = Math.max(0, this.zoneLockToast - dt);
    if (!this.zoneActiveRoom) {
      const blocked = this.bossZone.blockLocked(this.player.position, GameConfig.player.radius, this.xp.level);
      if (blocked && this.zoneLockToast <= 0) {
        this.banner.show(i18n.t("bossZone.locked", { n: blocked.def.level }), "danger", 1400);
        this.zoneLockToast = 2.2;
      }
    }
    // 10.9CN — a Fenda é feita de arenas SELADAS: paredes sólidas ao redor de cada sala; você entra
    // pelo vão da porta e, ao desafiar, o portão fecha o vão (fica preso até vencer ou cair).
    this.bossZone.resolveWalls(this.player.position, GameConfig.player.radius);
    this.zoneRefreshTimer -= dt;
    if (this.zoneRefreshTimer <= 0) { this.zoneRefreshTimer = 5; this.refreshBossZone(); }
    // 10.9AR — motes de energia da fenda ao redor do player (1 burst pequeno a cada 0.35 s — barato)
    this.zoneMoteTimer -= dt;
    if (this.zoneMoteTimer <= 0) {
      this.zoneMoteTimer = 0.35;
      const p = this.player.position;
      const a = Math.random() * Math.PI * 2, r = 4 + Math.random() * 10;
      this.particles.burst(p.x + Math.cos(a) * r, 0.5 + Math.random() * 4, p.z + Math.sin(a) * r, Math.random() < 0.5 ? 0x8f6bff : 0x2be0ff, { count: 3, speed: 1.2, dur: 1.1 });
    }
  }

  /** 10.9HX — MONSTROS AMBIENTES da Fenda: uma população pequena rondando os CORREDORES (nunca
   *  dentro de uma sala, pra não atrapalhar a luta). Teto baixo: a Fenda também roda em celular
   *  fraco. Eles levam `zoneAmbient`, então a varredura de adds do boss não os apaga. */
  /** 10.9HY — memoriza a lista de magias (era refeita a cada quadro). */
  private spellsCacheKey = "";
  private spellsCache: ReturnType<Game["spellsOf"]> = [];
  private zoneAmbientTimer = 0;
  private spawnZoneAmbient(dt: number): void {
    const cfg = GameConfig.bossZone.ambient;
    this.zoneAmbientTimer -= dt;
    if (this.zoneAmbientTimer > 0) return;
    this.zoneAmbientTimer = cfg.interval;
    let alive = 0;
    for (const e of this.enemies.enemies) if (e.zoneAmbient && !e.isDead) alive++;
    if (alive >= cfg.maxAlive) return;
    const def = GameConfig.enemies[cfg.ids[Math.floor(Math.random() * cfg.ids.length)] as keyof typeof GameConfig.enemies] as EnemyDef | undefined;
    if (!def) return;
    const b = GameConfig.bossZone.bounds.half - 3;
    const p = this.player.position;
    for (let tries = 0; tries < 12; tries++) {
      const x = GameConfig.bossZone.center.x + (Math.random() * 2 - 1) * b;
      const z = GameConfig.bossZone.center.z + (Math.random() * 2 - 1) * b;
      if (Math.hypot(x - p.x, z - p.z) < cfg.minFromPlayer) continue; // não nasce no colo do player
      // corredor = fora de QUALQUER sala (com folga pras paredes)
      if (this.bossZone.rooms.some((r) => Math.abs(x - r.cx) < r.half + 2.5 && Math.abs(z - r.cz) < r.half + 2.5)) continue;
      this.spawner.spawnZoneAmbient(def, new THREE.Vector3(x, 0, z));
      return;
    }
  }

  /** Tocou "Desafiar": nasce o boss da sala (Apex Fang ou Rift Seer) com os buffs e a arena da sala. */
  private challengeZoneRoom(room: RoomView): void {
    if (!this.zoneCanChallenge(room)) return;
    const bz = GameConfig.bossZone;
    const d = room.def;
    this.enemies.clear();
    this.hook.reset();
    this.autoTarget.clear();
    this.telegraph.clear();
    const bt = this.bossThreat(); // 10.9AP — salas da zona também acompanham a ameaça
    const common = { event: true, hpMult: d.hpMult * bt.hp, dmgMult: d.dmgMult * bt.dmg, spawnAt: { x: room.cx, z: room.cz }, tint: d.tint, titleKey: d.nameKey, arenaRadius: bz.arenaRadius, form: d.form }; // 10.9AT: morfologia da sala
    if (d.archetype === "seer") {
      this.boss = new RiftSeer(this.renderer.scene, { player: this.player, telegraph: this.telegraph, particles: this.particles, impulse: (s) => this.cameraRig.impulse(s) }, common);
    } else {
      this.boss = new VoidAlpha(
        this.renderer.scene,
        { player: this.player, telegraph: this.telegraph, particles: this.particles, summon: (count, center) => this.summonAdds(count, center), impulse: (s) => this.cameraRig.impulse(s) },
        common,
      );
    }
    (this.boss as { _zoneRoom?: string })._zoneRoom = d.id;
    this.zoneActiveRoom = room;
    this.bossZone.setLocked(room, true); // 10.9CN — a porta se tranca: só sai vencendo ou morrendo
    this.bossHud.show();
    this.bossPrompt.hidden = true;
    this.banner.show(i18n.t("bossZone.sealed"), "danger", 2600);
    this.banner.show(i18n.t("bossZone.awakens", { name: i18n.t(d.nameKey) }), "boss", 2600);
    this.audio.playMusic("boss_theme");
  }

  /** Boss da sala caiu: XP (escala com o nível da sala), moedas, essência, baú normal e a chance
   * MINÚSCULA da arma exclusiva. Sala entra em cooldown (farm sem spam). */
  private onZoneBossDefeated(roomId: string): void {
    this.bossVictoryFx(); // 10.9GT
    const bz = GameConfig.bossZone;
    const room = this.bossZone.rooms.find((r) => r.def.id === roomId);
    if (!room) return;
    const d = room.def;
    this.zoneActiveRoom = null;
    this.bossZone.setLocked(room, false); // 10.9CN — venceu: a porta se abre
    this.xp.gain(bz.reward.xpBase + d.level * bz.reward.xpPerLevel);
    this.addCoins(bz.reward.coins);
    this.inventory.add("power", bz.reward.essence.power);
    this.inventory.add("vitality", bz.reward.essence.vitality);
    this.inventory.add("ferocity", bz.reward.essence.ferocity);
    this.gainMastery(GameConfig.mastery.xp.perBoss, "boss");
    this.missionProgress("boss");
    this.spawnChestLoot("alpha", room.cx, room.cz + 2); // loot normal (sem chance alta de arma de boss)
    if (Math.random() < (d.dropChance ?? bz.weaponDropChance)) { // 10.9HU — salas de prestígio (r11+) têm chance própria
      const ba = rollBossArmor(bz.weaponRarity, this.xp.level); if (ba && Math.random() < 0.5) this.collectItem(ba, "boss"); // 10.9FX — roupa de boss
      const bw = rollBossWeapon(bz.weaponRarity, this.classWeaponTypes(), this.xp.level);
      if (bw) { this.collectItem(bw, "boss"); this.banner.show(i18n.t("bossZone.weaponDrop", { item: itemName(bw) }), "boss", 3600); }
    }
    // 10.9BV — Thiago: cada sala 1x POR DIA — renasce à meia-noite local (não 15h corridas)
    const midnight = new Date(); midnight.setHours(24, 0, 0, 0);
    this.progress.bossZoneNextAt[d.id] = midnight.getTime();
    const t = fmtDuration(midnight.getTime() - Date.now());
    this.banner.show(i18n.t("bossZone.defeated", { name: i18n.t(d.nameKey), t }), "boss", 3000);
    this.audio.playSfx("boss_defeated");
    this.refreshBossZone();
    this.save.save();
  }

  // ---- 10.6P: Boss de Área (Guardião) — um por região, cada 1h, fica mais forte ----
  /** 10.9CC — abates necessários pro próximo Guardião da região (sobe a cada um derrotado). */
  private guardianNeed(regionId: string): number {
    const cfg = GameConfig.areaBoss;
    return Math.min(cfg.killsCap, cfg.killsToAwaken + this.progress.areaBossKillCount(regionId) * cfg.killsPerAwaken);
  }
  private guardianReady(regionId: string): boolean {
    return GameConfig.areaBoss.enabled && (this.progress.guardianKills[regionId] ?? 0) >= this.guardianNeed(regionId);
  }
  /** Constrói a arena fixa da região ativa (no centro do território configurado). */
  private buildGuardianArena(): void {
    this.guardianArena?.dispose();
    this.guardianArena = null;
    const cfg = GameConfig.areaBoss;
    if (!cfg.enabled) return;
    const t = this.territories.byId(cfg.arenaTerritory[this.region.id] ?? "");
    if (!t) return;
    this.guardianArena = new GuardianArena(this.renderer.scene, t.cx, t.cz, cfg.arenaRadius);
    this.refreshGuardianArena();
  }
  private refreshGuardianArena(): void {
    if (!this.guardianArena) return;
    const rid = this.region.id;
    const n = this.progress.guardianKills[rid] ?? 0, need = this.guardianNeed(rid);
    const ready = this.guardianReady(rid);
    this.guardianArena.setProgress(ready ? i18n.t("guardian.ready") : i18n.t("guardian.label", { n, need }), ready, ready ? "#ffb347" : "#c9b6ff");
  }
  /** Abate comum na região carrega o Guardião; ao encher, avisa (o boss espera na arena). */
  private guardianKill(): void {
    const rid = this.region.id;
    if (!GameConfig.areaBoss.enabled || !this.guardianArena) return;
    if (this.guardianReady(rid)) return; // já desperto: espera o player ir lá
    const need = this.guardianNeed(rid);
    const n = Math.min(need, (this.progress.guardianKills[rid] ?? 0) + 1);
    this.progress.guardianKills[rid] = n;
    if (n >= need) {
      this.banner.show(i18n.t("guardian.awakened"), "boss", 3400);
      this.audio.playSfx("boss_theme");
      this.cameraRig.impulse(0.2);
    } else if (n % 20 === 0) {
      this.banner.show(i18n.t("guardian.progress", { n, need }), "info", 1600);
    }
    this.refreshGuardianArena();
  }

  /** 10.9DR — invoca o REI DO VAZIO (boss humanoide) perto do player. Usa todo o wiring de boss. */
  private spawnVoidKing(): void {
    if (this.boss) return;
    const bt = this.bossThreat();
    this.enemies.clear(); this.hook.reset(); this.autoTarget.clear(); this.telegraph.clear();
    const p = this.player.position;
    const sx = p.x + Math.sin(this.player.facingAngle) * 7, sz = p.z + Math.cos(this.player.facingAngle) * 7;
    this.boss = new VoidKing(
      this.renderer.scene,
      { player: this.player, telegraph: this.telegraph, particles: this.particles, summon: (count, center) => this.summonAdds(count, center), impulse: (s) => this.cameraRig.impulse(s) },
      { event: true, hpMult: bt.hp, dmgMult: bt.dmg, spawnAt: { x: sx, z: sz }, titleKey: "boss.voidKing" },
    );
    this.bossHud.show();
    this.banner.show(i18n.t("bossZone.awakens", { name: i18n.t("boss.voidKing") }), "boss", 2600);
    this.audio.playMusic("boss_theme");
  }

  private spawnAreaBoss(regionId: string): void {
    if (this.boss) return;
    const cfg = GameConfig.areaBoss;
    const kills = this.progress.areaBossKillCount(regionId);
    const scale = Math.min(cfg.maxScale, Math.pow(1 + cfg.scalePerKill, kills));
    const bt = this.bossThreat(); // 10.9AP — acompanha a ameaça
    this.enemies.clear();
    this.hook.reset();
    this.autoTarget.clear();
    this.telegraph.clear();
    // 10.9CC — nasce no CENTRO da arena (o player já está dentro dela), preso ao raio dela
    const arena = this.guardianArena;
    const p = this.player.position;
    const sx = arena ? arena.cx : p.x + Math.sin(this.player.facingAngle) * 6;
    const sz = arena ? arena.cz : p.z + Math.cos(this.player.facingAngle) * 6;
    const bs = this.bossScale(GameConfig.world.regions.find((r) => r.id === regionId)?.recommendedLevel ?? 1);
    this.boss = new VoidAlpha(
      this.renderer.scene,
      {
        player: this.player,
        telegraph: this.telegraph,
        particles: this.particles,
        summon: (count, center) => this.summonAdds(count, center),
        impulse: (s) => this.cameraRig.impulse(s),
      },
      {
        event: true,
        hpMult: cfg.baseHpMult * scale * bs.hp * bt.hp,
        dmgMult: cfg.baseDmgMult * scale * bs.dmg * bt.dmg,
        spawnAt: { x: sx, z: sz },
        arenaRadius: arena ? arena.radius : undefined, // 10.9CC
      },
    );
    (this.boss as VoidAlpha & { _areaBossRegion?: string })._areaBossRegion = regionId;
    this.bossHud.show();
    this.bossPrompt.hidden = true;
    this.banner.show(i18n.t("areaBoss.awakens"), "boss", 2800);
    this.audio.playMusic("boss_theme");
  }

  private onAreaBossDefeated(regionId: string): void {
    this.bossVictoryFx(); // 10.9GT
    const cfg = GameConfig.areaBoss;
    const kills = this.progress.recordAreaBossKill(regionId);
    this.progress.guardianKills[regionId] = 0; // 10.9CC — recarrega do zero (e o próximo pede mais)
    this.refreshGuardianArena();
    this.gainMastery(GameConfig.mastery.xp.perBoss, "boss");
    this.gainRegionMastery(GameConfig.regionMastery.xp.perBoss);
    this.xp.gain(cfg.xp);
    this.addCoins(GameConfig.shop.coinsPerEventBoss + GameConfig.bossCoins.areaBoss); // 10.9BK: 🪙 no lugar do 💎
    this.missionProgress("boss");
    // 10.9L3 — área sem boss principal (Tempestade): o Guardião é o boss dela
    if (regionId === this.region.id && GameConfig.areaGoalBoss[regionId] === "guardian") this.areaGoal("boss");
    this.inventory.add("power", cfg.essence.power);
    this.inventory.add("vitality", cfg.essence.vitality);
    this.inventory.add("ferocity", cfg.essence.ferocity);
    const bw = rollBossWeapon(cfg.weaponRarity, this.classWeaponTypes(), this.xp.level);
    if (bw) this.collectItem(bw, "boss");
    if (kills % 3 === 0) this.spawnChestLoot("void", this.player.position.x, this.player.position.z + 2);
    this.banner.show(i18n.t("areaBoss.defeated"), "boss", 2600);
    this.audio.playSfx("boss_defeated");
    this.save.save();
  }

  // ---- transições de estado ----
  /** 10.9X — "Jogar": se já tem personagem ativo, começa; senão abre a tela de Personagens. */
  private play(): void {
    // 10.9IT — conta BANIDA pelo painel nao entra no mundo. Tolerante: sem rede, segue normal.
    void fetchBan().then((b) => {
      if (b) { diag.log("conta", "banida"); this.mainMenu.hide(); this.banScreen.show(b); return; }
      this.playInner();
    });
  }

  private playInner(): void {
    const active = this.charStore.activeClassId();
    if (active && this.charStore.hasCharacter(active)) { this.startRun(); return; }
    // 10.9IF — 1ª abertura (ainda sem personagem): pergunta a CONTA antes de criar o Caçador.
    // Assim o progresso já nasce ligado a uma conta, em vez de o jogador descobrir isso horas
    // depois. `shouldAsk` é assíncrono (consulta a sessão), então o fluxo segue no callback.
    void AccountGate.shouldAsk().then((ask) => {
      if (ask) { this.mainMenu.hide(); this.accountGate.show(); }
      else this.openCharacterSelect();
    });
  }

  /** 10.9IF — escolheu conta (ou "convidado"): segue pro fluxo normal de criar personagem. */
  private afterAccountGate(): void {
    this.openCharacterSelect();
  }
  /** 10.9CY — do jogo, volta pro menu de PERSONAGENS (salva antes; some com o HUD). */
  private pauseToCharacters(): void {
    this.save.save();
    this.pauseMenu.hide();
    this.hud.hide(); this.equipBar.hide(); this.minimap.hide(); this.menuBar.hide();
    this.input.setEnabled(false);
    this.state = "menu";
    this.openCharacterSelect();
  }

  /** 10.9X — tela de Personagens (criar/escolher/trocar). Fecha só se já há um ativo. */
  private openCharacterSelect(): void {
    const active = this.charStore.activeClassId();
    const canClose = !!(active && this.charStore.hasCharacter(active));
    if (this.state === "menu") this.mainMenu.hide(); // 10.9CC — os botões do menu não ficam por baixo da tela
    this.classSelect.open(this.charStore.summaries(), canClose);
  }
  /** 10.9X — escolheu uma classe: TROCA pro personagem existente ou CRIA um novo (do zero). */
  private chooseClass(id: string, name?: string): void {
    if (!GameConfig.classes.some((c) => c.id === id)) return;
    this.classSelect.hide();
    // 10.9IW — trocar de personagem reabre a checagem de concessoes do painel: uma concessao
    // alvejada numa classe especifica (ex.: "remover item") so e resgatada quando ESSA classe
    // fica ativa, e sem isto `grantsChecked` ficava travado em true da primeira checagem da sessao.
    this.grantsChecked = false;
    if (this.charStore.hasCharacter(id)) {
      this.charStore.setActive(id);
      this.switchToActiveCharacter();
    } else {
      this.charStore.setActive(id);
      this.createFreshCharacter(id, name);
    }
    this.startRun();
  }
  /** Carrega o personagem ATIVO (troca de classe): reusa o applyProgress (caminho testado). */
  private switchToActiveCharacter(): void {
    const saved = this.save.load();
    this.clearWorld();
    if (saved) this.save.restore(() => this.applyProgress(saved));
    this.postLoadSetup();
    this.applyClassOutfit();
    this.recalcPlayer();
  }
  /** Cria um personagem NOVO (do zero) da classe ativa, preservando as configurações. */
  private createFreshCharacter(id: string, name?: string): void {
    const fresh = this.save.freshData();
    this.clearWorld();
    this.save.restore(() => this.applyProgress(fresh)); // zera TODOS os sistemas (level/árvore/itens/região)
    this.progress.classId = id;
    this.progress.charName = (name ?? "").trim(); // 10.9AF — nome escolhido na criação
    this.postLoadSetup(); // arma inicial da classe (ensureStarterWeapon lê a classe) + welcome
    this.applyClassOutfit();
    this.recalcPlayer();
    this.save.save(); // grava o novo personagem no slot da classe ativa
  }
  /** 10.9Y — exclui um personagem (build errada / abrir espaço). Reabre a tela atualizada. */
  private deleteCharacter(id: string): void {
    this.charStore.deleteCharacter(id);
    void deleteProfileChar(id); // 10.9L28 — some do ranking também
    this.openCharacterSelect();
  }
  /** Def da classe atual (null se ainda não escolheu). */
  /** 10.9CQ — tipos de arma que a CLASSE pode usar (para o loot só largar arma útil ao jogador). */
  private classWeaponTypes(): string[] | undefined {
    const a = this.classDef()?.allowedWeapons;
    return a ? [...a] : undefined;
  }
  private classDef(): typeof GameConfig.classes[number] | null {
    return GameConfig.classes.find((c) => c.id === this.progress.classId) ?? null;
  }
  /** 10.9Z — a classe permite este TIPO de arma? (sem classe = tudo liberado). */
  private classAllowsType(weaponType: string): boolean {
    const cls = this.classDef();
    if (!cls) return true;
    return (cls.allowedWeapons as readonly string[]).includes(weaponType);
  }
  /** 10.9Z — a classe pode usar esta arma? Armadura sempre pode. */
  private classAllowsWeapon(inst: EquipmentInstance): boolean {
    if (inst.slot !== "weapon") return true;
    const def = definitionById(inst.definitionId);
    if (!def || !isWeaponDef(def)) return true;
    return this.classAllowsType((def as WeaponDefinition).weaponType);
  }
  /** 10.9X — roupa por CLASSE (aparência própria). Se o jogador escolheu roupa custom na
   * Guarda-roupa (!= padrão "void"), respeita a escolha; senão usa a roupa da classe. */
  private applyClassOutfit(): void {
    const custom = this.progress.outfit && this.progress.outfit !== "void" ? this.progress.outfit : null;
    const classOutfit = this.progress.classId === "warrior" ? "ember" : this.progress.classId === "archer" ? "tempest" : this.progress.classId === "mage" ? "astral" : "void";
    const id = custom ?? classOutfit;
    const o = GameConfig.outfits.find((x) => x.id === id) ?? GameConfig.outfits[0];
    this.player.applyOutfit(o.palette);
    // 10.9BT — reator nas costas + luz de borda na cor da classe
    const clsColor = this.classDef()?.color;
    this.player.setClassColor(clsColor ? parseInt(clsColor.replace("#", ""), 16) : null);
    this.input.touch.setSpellIcon(this.progress.classId, clsColor ?? "#b48bff"); // 10.9CF — ícone da magia por classe
    const _spells = this.spellsOf(); // 10.9CX — ícones/visibilidade das magias 2 e 3
    for (let i = 1; i < 3; i++) { const s2 = _spells[i]; this.input.touch.setSpellSlotVisible(i, !!s2); if (s2) this.input.touch.setSpellSlotIcon(i, SPELL_EMOJI[s2.kind] ?? "✨", hexColor(s2.color)); }
    // 10.9FC — slot 0 trocado no Grimorio: icone da magia escolhida (senao fica o icone da classe)
    const s0 = _spells[0];
    if (s0 && s0.id && s0.id !== "base0") this.input.touch.setSpellSlotIcon(0, SPELL_EMOJI[s0.kind] ?? "✨", hexColor(s0.color)); // 10.9GO — so a base0 usa o icone da classe
  }

  /** 10.9W — quantas armas a CORRENTE pode prender = slots de arma DESBLOQUEADOS (além da mão),
   * por NÍVEL, + o slot VIP (10.9J7 tirou; 10.9L0 VOLTOU por decisão do Thiago — vantagem de dano
   * do VIP/Passe). Começa em 0 (só a mão) e libera por progressão (ver equipment.weaponSlots). */
  private chainSlotLimit(): number {
    let n = 0;
    const slots = GameConfig.equipment.weaponSlots;
    for (let i = 1; i < slots.length; i++) {
      const s = slots[i];
      if (s.unlock === "start") n++;
      else if (s.unlock === "level" && this.xp.level >= s.level) n++;
      else if (s.unlock === "vip" && Vip.weaponSlotUnlocked) n++; // 10.9L0 — slot VIP voltou
    }
    return n;
  }

  /**
   * 10.9CM — os servos do necromante têm vida e servem de ISCA: cada inimigo a até `soulAggro` m
   * de um servo vivo troca o alvo do player para esse servo (o servo tanqueia). Sem servos por
   * perto, o alvo continua sendo o player. Retorna undefined quando não há servo (caminho normal).
   */
  private enemyTargetFor(): ((e: import("../enemies/Enemy").Enemy) => import("../combat/Damageable").Damageable) | undefined {
    const souls = this.minions.souls();
    if (souls.length === 0 || this.player.inSanctuary) return undefined;
    const r2 = GameConfig.enemyCombat.soulAggro * GameConfig.enemyCombat.soulAggro;
    return (e) => {
      let best: import("../combat/Damageable").Damageable | null = null;
      let bd = r2;
      for (const s of souls) {
        if (s.isDead) continue;
        const dx = s.position.x - e.position.x, dz = s.position.z - e.position.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < bd) { bd = d2; best = s; }
      }
      return best ?? this.player;
    };
  }

  /** 10.9U — MAGIA DA CLASSE (AoE): slam/nova em volta do Caçador; volley num cone à frente. */
  /** 10.9CX — lista das 3 magias da classe (slot 0 = principal, 1 e 2 = novas). */
  private spellsOf(): (SpellLike | null)[] {
    const book = this.spellBook();
    if (book.length === 0) return [null, null, null];
    // 10.9FC — GRIMORIO: os 3 slots vem da escolha salva (so vale magia liberada no nivel atual)
    const pick = this.progress.spellLoadout[this.progress.classId] ?? [];
    const out: (SpellLike | null)[] = [];
    for (let i = 0; i < 3; i++) {
      const s = book.find((b) => b.id === pick[i] && this.xp.level >= (b.unlockLevel ?? 0));
      out.push(s ?? book[i] ?? null);
    }
    return out;
  }

  /** 10.9FC — TODAS as magias da classe: as 3 de base (ids base0..2) + as novas do Grimorio. */
  private spellBook(): SpellLike[] {
    const c = this.classDef() as unknown as { spell?: SpellLike; spell2?: SpellLike; spell3?: SpellLike; extraSpells?: SpellLike[] } | null;
    if (!c) return [];
    const base = [c.spell, c.spell2, c.spell3].map((s, i): SpellLike | null => (s ? { ...s, id: `base${i}`, unlockLevel: 0 } : null)).filter((s): s is SpellLike => !!s);
    return [...base, ...(c.extraSpells ?? [])];
  }

  /** 10.9FC — abre o Grimorio (pelo menu de pausa). */
  private openIndex(): void { this.pauseMenu.hide(); this.indexScreen.open(this.progress.discoveredItems); } // 10.9GA

  /** 10.9IH — manual completo, aberto pela pausa. */
  private openHowTo(): void { this.pauseMenu.hide(); this.howTo.show(); }

  /** 10.9IH — marca no `progress.tips` que o tour ja foi oferecido (feito OU pulado). */
  private static readonly GUIDE_KEY = "guide";

  /**
   * O tour so aparece sozinho pra quem esta comecando DE VERDADE. Um save antigo tem a lista de
   * dicas cheia mas nao tem a marca `guide` — sem o teste de nivel/abates, todo veterano levaria
   * um tutorial na cara ao abrir a atualizacao. Quem quiser rever abre pelo "Como jogar".
   */
  private shouldOfferGuide(): boolean {
    if (this.progress.tips.includes(Game.GUIDE_KEY)) return false;
    return this.xp.level <= 3 && this.progress.totalKills < 20;
  }

  /**
   * Espera a tela ficar LIVRE pra comecar o tour. Duas razoes, as duas reais no primeiro jogo:
   * o painel "Novidades" abre sozinho (e fica em cima, num z-index maior que o do tour), e
   * enquanto ele esta aberto o toque fica desligado — os controles tem tamanho zero, entao o
   * holofote apontaria pro nada. Tambem da tempo do layout medir os botoes.
   *
   * 10.9IJ — a espera era de 25 tentativas (15 s) e ISSO NAO BASTA: medido num save zerado de
   * verdade, o jogador leva mais que isso so lendo o painel de Novidades, e o tutorial
   * simplesmente nunca aparecia — justo na abertura em que ele mais importa. 200 tentativas
   * (2 min) cobrem a leitura com folga; passou disso, ele se oferece na proxima sessao.
   */
  private queueGuide(tentativas = 200): void {
    window.setTimeout(() => {
      if (!this.shouldOfferGuide()) return;
      const ocupado = this.state !== "playing" || document.querySelector(".overlay:not([hidden])") !== null;
      if (ocupado) {
        if (tentativas > 0) this.queueGuide(tentativas - 1);
        return;
      }
      this.startGuide();
    }, 600);
  }

  private startGuide(): void {
    if (this.guide.active) return;
    this.guide.start(this.guideSteps());
  }

  private onGuideEnd(finished: boolean): void {
    // Marca pelo MESMO caminho do tutorial contextual: `showTip` reescreve `progress.tips` a
    // partir do snapshot do Tutorial, entao gravar so no progresso seria apagado no primeiro tip.
    this.tutorial.mark(Game.GUIDE_KEY);
    this.progress.tips = this.tutorial.snapshot();
    this.save.save();
    if (finished) this.banner.show(i18n.t("guide.done"), "info", 4200);
  }

  /** Pausar ou morrer no meio do tour encerra ele (nao fica cartao boiando sobre o menu). */
  private endGuideIfRunning(): void {
    if (!this.guide?.active) return;
    this.guide.stop();
    this.onGuideEnd(false);
  }

  private readonly guidePos = new THREE.Vector3();

  /**
   * Os passos. Curtos de proposito: o manual ("Como jogar") e que tem o resto. Cada alvo e
   * resolvido na HORA e so vira holofote se estiver VISIVEL — no desktop os controles de toque
   * tem tamanho zero, entao esses passos viram cartao centralizado em vez de apontar pro nada.
   */
  private guideSteps(): TourStep[] {
    const alvo = (sel: string) => (): Element | null => {
      const e = document.querySelector<HTMLElement>(sel);
      return e && e.getBoundingClientRect().width > 0 ? e : null;
    };
    const existe = (sel: string) => (): boolean =>
      (document.querySelector<HTMLElement>(sel)?.getBoundingClientRect().width ?? 0) > 0;
    return [
      { id: "welcome", title: "guide.welcome.t", text: "guide.welcome.b" },
      {
        id: "move",
        title: "guide.move.t",
        text: "guide.move.b",
        target: alvo(".stick-base"),
        onEnter: () => this.guidePos.copy(this.player.position),
        until: () => this.player.position.distanceTo(this.guidePos) > 4,
      },
      { id: "auto", title: "guide.auto.t", text: "guide.auto.b" },
      { id: "life", title: "guide.life.t", text: "guide.life.b", target: alvo(".php") },
      { id: "base", title: "guide.base.t", text: "guide.base.b" },
      { id: "sprint", title: "guide.sprint.t", text: "guide.sprint.b", target: alvo(".tbtn.sprint"), when: existe(".tbtn.sprint") },
      { id: "hook", title: "guide.hook.t", text: "guide.hook.b", target: alvo(".tbtn.hook"), when: existe(".tbtn.hook") },
      { id: "potion", title: "guide.potion.t", text: "guide.potion.b", target: alvo("#potion-btn"), when: existe("#potion-btn") },
      { id: "gear", title: "guide.gear.t", text: "guide.gear.b", target: alvo(".equip-bar") },
      { id: "status", title: "guide.status.t", text: "guide.status.b", target: alvo(".hud-topleft") },
      {
        id: "mission",
        title: "guide.mission.t",
        text: "guide.mission.b",
        target: alvo(".mission-hud"),
        // A pilha comeca RECOLHIDA no celular: sem abrir, o holofote apontaria pra um chip vazio.
        // Abre pelo proprio botao pra nao dessincronizar o estado lembrado no aparelho.
        onEnter: () => {
          const st = this.hudStackEl;
          if (st?.classList.contains("collapsed")) st.querySelector<HTMLButtonElement>(".hud-stack-toggle")?.click();
        },
      },
      { id: "map", title: "guide.map.t", text: "guide.map.b", target: alvo(".minimap") },
      { id: "menu", title: "guide.menu.t", text: "guide.menu.b", target: alvo(".menu-bar") },
      // 10.9J8 — AUDITORIA: o tour nunca mencionava o Correio (onde mora o bônus diário) — quem só
      // segue o tour podia terminar os primeiros minutos sem saber que precisa voltar amanhã.
      { id: "mail", title: "guide.mail.t", text: "guide.mail.b", target: alvo('.mbtn[data-a="mail"]') },
      { id: "more", title: "guide.more.t", text: "guide.more.b", target: alvo(".hud-set") },
    ];
  }
  private openGrimoire(): void {
    this.pauseMenu.hide();
    this.grimoire.open(this.spellBook(), this.spellsOf().map((s) => s?.id ?? ""), this.xp.level, SPELL_EMOJI);
  }
  private equipSpell(slot: number, id: string): void {
    const cur = this.spellsOf().map((s) => s?.id ?? "");
    const other = cur.indexOf(id);
    if (other >= 0 && other !== slot) cur[other] = cur[slot]; // ja estava noutro slot: troca de lugar
    cur[slot] = id;
    this.progress.spellLoadout[this.progress.classId] = cur;
    this.spellCds[slot] = 0; // 10.9GO — so o slot trocado (antes zerava as 3 recargas)
    this.applyClassOutfit(); // icones dos botoes
    this.save.save();
    this.grimoire.open(this.spellBook(), this.spellsOf().map((s) => s?.id ?? ""), this.xp.level, SPELL_EMOJI);
  }

  /** 10.9U/CX — lança a magia do slot (0/1/2). Cada tipo (kind) tem seu efeito. */
  /**
   * 10.9IZ — MIRA DAS MAGIAS DIRECIONAIS. Thiago (Mago): "precisa virar do lado pra poder bater".
   *
   * Meteoro e Nevasca calculavam o ponto de impacto a partir de `player.facingAngle` — a rotação
   * VISUAL do boneco. Só que `facingAngle` só aponta pro alvo durante a janelinha de 0,25s do
   * golpe do ataque básico (`updateFacing`); o resto do tempo ele segue a ÚLTIMA DIREÇÃO DE
   * MOVIMENTO. Um mago parado, recuando ou circulando o inimigo — o jeito normal de jogar
   * "vidro-canhão" à distância — conjurava pra qualquer lado que o corpo estivesse virado por
   * último, não pro monstro. Dava a impressão de "ataque fraco": não é o número, é o tiro errando.
   *
   * O Arqueiro já tinha ganho esse conserto pra Chuva de Flechas (10.9DZ/10.9ED: mira o alvo
   * travado, senão o mais próximo dentro do alcance, e só cai "à frente" se não houver ninguém).
   * Isto generaliza o MESMO padrão pra qualquer magia que precise de uma direção, e falta aplicar
   * nas outras duas famílias que ainda usavam facing cru: Meteoro/Nevasca (ponto de impacto) e
   * Onda de Choque/Flecha Perfurante/Explosão de Ossos (linha reta) — via `spellAimDir` abaixo.
   */
  private spellAimPoint(p: THREE.Vector3, fallbackDirX: number, fallbackDirZ: number, searchRadius: number, reach: number): { x: number; z: number } {
    let tgt: { position: THREE.Vector3; isDead: boolean } | null = this.autoTarget.current;
    if (!tgt || tgt.isDead) {
      tgt = null;
      let best = searchRadius * searchRadius;
      for (const e of this.enemies.enemies) {
        if (e.isDead) continue;
        const dx = e.position.x - p.x, dz = e.position.z - p.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < best) { best = d2; tgt = e; }
      }
      if (this.boss && !this.boss.isDead) {
        const dx = this.boss.position.x - p.x, dz = this.boss.position.z - p.z;
        if (dx * dx + dz * dz < best) tgt = this.boss;
      }
    }
    if (tgt) {
      const tx = tgt.position.x - p.x, tz = tgt.position.z - p.z;
      const d = Math.hypot(tx, tz);
      if (d > 0.001) {
        const r = Math.min(d, reach);
        return { x: p.x + (tx / d) * r, z: p.z + (tz / d) * r };
      }
    }
    return { x: p.x + fallbackDirX * reach, z: p.z + fallbackDirZ * reach }; // ninguém por perto: cai à frente, como antes
  }

  /** Mesma ideia que `spellAimPoint`, mas devolve uma DIREÇÃO (pra magias em linha, tipo a Onda). */
  private spellAimDir(p: THREE.Vector3, fallbackDirX: number, fallbackDirZ: number, searchRadius: number): { dirX: number; dirZ: number } {
    const pt = this.spellAimPoint(p, fallbackDirX, fallbackDirZ, searchRadius, searchRadius);
    const dx = pt.x - p.x, dz = pt.z - p.z;
    const d = Math.hypot(dx, dz);
    return d > 0.001 ? { dirX: dx / d, dirZ: dz / d } : { dirX: fallbackDirX, dirZ: fallbackDirZ };
  }

  private castSpell(slot = 0): void {
    const sp = this.spellsOf()[slot];
    if (!sp || this.spellCds[slot] > 0) return;
    const te = this.tree.effects(); // nós da CLASSE modificam TODAS as magias
    this.spellCds[slot] = sp.cooldown * Math.max(0.4, 1 - te.spellCdReducPct);
    const spDmg = 1 + te.spellDamagePct;
    const spRadius = sp.radius * (1 + te.spellRadiusPct);
    const p = this.player.position;
    const cs = this.player.combatStats;
    const critB = te.spellCritBonus;
    if (te.spellHealOnCast > 0) this.player.health.heal(this.player.health.max * te.spellHealOnCast);

    // NECROMANTE: invocação
    const summon = (sp as { summon?: MinionConfig }).summon;
    if (sp.kind === "summon" && summon) {
      const mstats = { ...cs, baseDamage: Math.round(cs.baseDamage * summon.dmgMult * spDmg), critChance: cs.critChance + critB };
      const cfg: MinionConfig = { ...summon, lifetime: summon.lifetime * (1 + te.summonLifePct), attacksPerSecond: summon.attacksPerSecond * (1 + te.summonSpeedPct), maxAlive: summon.maxAlive + Math.round(te.summonCount) * 2, hp: Math.round(summon.hp * (1 + te.summonHpPct) * (1 + this.xp.level * 0.04)) };
      const n = this.minions.summon(summon.count + Math.round(te.summonCount), p, mstats, cfg);
      this.telegraph.circle(p.x, p.z, 2.2, 0.5, sp.color);
      this.particles.burst(p.x, 0.7, p.z, sp.color, { count: 30, speed: 9, dur: 0.7 });
      this.cameraRig.impulse(0.14); this.audio.playSfx("spell_summon"); // 10.9HK
      this.banner.show(i18n.t("spell.summonCast", { n }), "info", 1200);
      return;
    }

    // BUFF: Grito de Guerra / Foco / Barreira (efeito no player, com duração)
    if (sp.kind === "buff" && sp.buff) {
      const b = sp.buff;
      this.buffTimer = b.seconds;
      this.buffMods = { dmgMult: b.dmgMult ?? 0, dr: b.dr ?? 0, moveMult: b.moveMult ?? 0, atkSpeedMult: b.atkSpeedMult ?? 0, critChance: b.critChance ?? 0 };
      if (b.healFrac) this.player.health.heal(this.player.health.max * b.healFrac);
      this.recalcPlayer();
      this.telegraph.circle(p.x, p.z, 2.6, 0.6, sp.color);
      this.fxPillar(p.x, p.z, sp.color); // 10.9DN
      this.spellVfx.halo(() => this.player.position, sp.color, b.seconds); // 10.9HI — aro enquanto o buff dura
      this.particles.burst(p.x, 1.0, p.z, sp.color, { count: 30, speed: 6, dur: 0.9 });
      this.cameraRig.impulse(0.12); this.audio.playSfx("spell_buff"); // 10.9HK
      this.banner.show(i18n.t("spell.buffOn", { name: i18n.t(sp.nameKey) }), "info", 1400);
      return;
    }

    const stats = { ...cs, baseDamage: Math.round(cs.baseDamage * sp.dmgMult * spDmg), critChance: cs.critChance + critB };
    const dirX = Math.sin(this.player.facingAngle), dirZ = Math.cos(this.player.facingAngle);

    // DASH: investida/salto — avança e causa dano no caminho, com i-frames curtos
    if (sp.kind === "dash") {
      // 10.9FA — BUG "o teleporte passa pelas paredes": antes pulava direto pro ponto final. Agora
      // anda em passos curtos com as MESMAS colisões do andar e para no primeiro muro/porta/pedra.
      const range = this.dashReach(p.x, p.z, dirX, dirZ, sp.dashRange ?? 7);
      this.player.grantInvuln(0.35);
      const steps = 6;
      const hit = new Set<unknown>();
      for (let i = 1; i <= steps; i++) {
        const hx = p.x + dirX * (range * i / steps), hz = p.z + dirZ * (range * i / steps);
        for (const e of this.enemies.enemies) {
          if (e.isDead || hit.has(e)) continue;
          const dx = e.position.x - hx, dz = e.position.z - hz;
          if (dx * dx + dz * dz <= spRadius * spRadius) { hit.add(e); dealDamage(e, stats, { targetKind: "enemy", byPlayer: true, damageType: sp.damageType, knockbackFrom: p, knockbackForce: sp.knockback }); }
        }
      }
      _spellTo.copy(this._dashEnd);
      this.spellVfx.streak(p.x, p.z, _spellTo.x, _spellTo.z, sp.color); // 10.9HI — rastro da investida
      this.player.position.x = _spellTo.x; this.player.position.z = _spellTo.z;
      this.cameraRig.snapTo(this.player.position);
      this.telegraph.circle(_spellTo.x, _spellTo.z, spRadius, 0.4, sp.color);
      this.particles.burst(_spellTo.x, 0.8, _spellTo.z, sp.color, { count: 22, speed: 10, dur: 0.5 });
      this.cameraRig.impulse(0.16); this.audio.playSfx("hit");
      return;
    }

    // METEORO: telegrafa um ponto à frente e cai depois de meteorDelay
    // 10.9IZ — mira o alvo travado/mais próximo dentro do alcance; só cai "à frente" sem ninguém.
    if (sp.kind === "meteor") {
      const reach = sp.meteorRange ?? 8;
      const { x: mx, z: mz } = this.spellAimPoint(p, dirX, dirZ, reach, reach);
      this.meteors.push({ x: mx, z: mz, t: sp.meteorDelay ?? 0.9, radius: spRadius, dmgMult: sp.dmgMult, spDmg, color: sp.color, knockback: sp.knockback, dt: sp.damageType, crit: critB });
      this.spellVfx.meteor(mx, mz, sp.meteorDelay ?? 0.9, sp.color); // 10.9HI — a bola de fogo cai de verdade
      this.audio.playSfx("spell_meteor"); // 10.9HK
      return;
    }

    // PRAGA: cria uma zona de dano no tempo no lugar do player
    if ((sp.kind === "curse" || sp.kind === "whirl") && sp.field) {
      // 10.9FC — Nevasca cai a frente (ahead); Redemoinho SEGUE o Cacador (follow)
      // 10.9IZ — "a frente" agora mira o alvo travado/mais próximo (ex.: Nevasca do Mago); sem
      // `ahead` (ex.: Praga do Necromante, que já nasce embaixo do jogador) nada muda.
      const ahead = sp.ahead ?? 0;
      const { x: fx, z: fz } = ahead > 0 ? this.spellAimPoint(p, dirX, dirZ, ahead, ahead) : { x: p.x, z: p.z };
      this.curses.push({ x: fx, z: fz, t: sp.field.seconds, every: sp.field.tickEvery, acc: 0, radius: spRadius, dmgMult: sp.dmgMult, spDmg, color: sp.color, dt: sp.damageType, crit: critB, follow: sp.kind === "whirl", slow: sp.slow, drainPct: sp.drainPct });
      if (sp.kind === "whirl") this.player.grantInvuln(0.3);
      // 10.9HI — zona com círculo rúnico girando; Redemoinho ainda ganha lâminas orbitando o Caçador
      const followP = sp.kind === "whirl" ? () => this.player.position : undefined;
      this.spellVfx.rune(fx, fz, spRadius, sp.color, sp.field.seconds, followP);
      if (sp.kind === "whirl") this.spellVfx.blades(() => this.player.position, spRadius * 0.75, sp.color, sp.field.seconds);
      this.telegraph.circle(fx, fz, spRadius, 0.5, sp.color);
      this.audio.playSfx(sp.kind === "whirl" ? "spell_whirl" : "spell_curse"); // 10.9HK
      return;
    }

    // 10.9FC — ONDA/LINHA: Onda de Choque, Flecha Perfurante, Explosao de Ossos (acerta tudo na linha)
    // 10.9IZ — mesma correção de mira do Meteoro: a linha sai na direção do alvo travado/mais
    // próximo dentro do alcance dela, em vez de sair sempre na direção que o corpo está virado.
    // Afeta Guerreiro (Onda de Choque), Arqueiro (Flecha Perfurante) e Necromante (Explosão de
    // Ossos) — a mesma causa-raiz do bug do Mago, só que numa linha reta em vez de um ponto.
    if (sp.kind === "wave") {
      const len = sp.waveLength ?? 12, hw = spRadius;
      const { dirX: wx, dirZ: wz } = this.spellAimDir(p, dirX, dirZ, len);
      const inLine = (x: number, z: number): boolean => {
        const rx = x - p.x, rz = z - p.z;
        const along = rx * wx + rz * wz;
        if (along < -0.5 || along > len) return false;
        return Math.abs(rx * wz - rz * wx) <= hw;
      };
      for (const e of this.enemies.enemies) { if (!e.isDead && inLine(e.position.x, e.position.z)) dealDamage(e, stats, { targetKind: "enemy", byPlayer: true, damageType: sp.damageType, knockbackFrom: p, knockbackForce: sp.knockback }); }
      if (this.boss && !this.boss.isDead && inLine(this.boss.position.x, this.boss.position.z)) dealDamage(this.boss, stats, { targetKind: "enemy", byPlayer: true, damageType: sp.damageType, knockbackFrom: p, knockbackForce: sp.knockback });
      this.spellVfx.wavefront(p.x, p.z, wx, wz, len, hw, sp.color); // 10.9HI
      for (let d = 1.5; d <= len; d += 1.5) {
        this.particles.burst(p.x + wx * d, 0.7, p.z + wz * d, sp.color, { count: 6, speed: 4, dur: 0.45 });
        if (d % 3 < 1.5) this.telegraph.circle(p.x + wx * d, p.z + wz * d, hw, 0.35, sp.color);
      }
      this.cameraRig.impulse(0.16); this.audio.playSfx("spell_wave"); // 10.9HK
      return;
    }
    // 10.9FC — ARMADILHA: fica no chao e explode quando um monstro pisa perto
    if (sp.kind === "trap" && sp.trap) {
      this.traps.push({ x: p.x, z: p.z, t: sp.trap.seconds, trigger: sp.trap.trigger, radius: spRadius, dmgMult: sp.dmgMult, spDmg, color: sp.color, dt: sp.damageType, crit: critB, knockback: sp.knockback, vfx: this.spellVfx.rune(p.x, p.z, sp.trap.trigger, sp.color, sp.trap.seconds) }); // 10.9HI
      this.telegraph.circle(p.x, p.z, sp.trap.trigger, 0.6, sp.color);
      this.audio.playSfx("spell_trap"); // 10.9HK
      return;
    }
    // 10.9FC — CORRENTE DE RAIOS: acerta o mais perto e SALTA pros vizinhos
    if (sp.kind === "chain" && sp.chain) {
      const hitSet = new Set<unknown>();
      let fromX = p.x, fromZ = p.z, range = spRadius;
      for (let j = 0; j <= sp.chain.jumps; j++) {
        let best: { position: THREE.Vector3; isDead: boolean } | null = null, bd = range * range;
        for (const e of this.enemies.enemies) {
          if (e.isDead || hitSet.has(e)) continue;
          const dx = e.position.x - fromX, dz = e.position.z - fromZ, d2 = dx * dx + dz * dz;
          if (d2 < bd) { bd = d2; best = e; }
        }
        if (this.boss && !this.boss.isDead && !hitSet.has(this.boss)) {
          const dx = this.boss.position.x - fromX, dz = this.boss.position.z - fromZ;
          if (dx * dx + dz * dz < bd) best = this.boss;
        }
        if (!best) break;
        hitSet.add(best);
        dealDamage(best as never, stats, { targetKind: "enemy", byPlayer: true, damageType: sp.damageType, knockbackFrom: p, knockbackForce: sp.knockback });
        const tx = best.position.x, tz = best.position.z;
        this.spellVfx.bolt(fromX, fromZ, tx, tz, sp.color); // 10.9HI — raio serrilhado entre os alvos
        this.particles.burst(tx, 1.1, tz, sp.color, { count: 8, speed: 4, dur: 0.3 });
        fromX = tx; fromZ = tz; range = sp.chain.jumpRange;
      }
      if (hitSet.size === 0) { this.spellCds[slot] = 0.5; this.banner.show(i18n.t("spell.noTarget"), "info", 900); return; }
      this.cameraRig.impulse(0.12); this.audio.playSfx("spell_bolt"); // 10.9HK
      return;
    }

    // slam / nova / volley: AoE (círculo) ou cone à frente
    // ARQUEIRO — CHUVA DE FLECHAS: flechas caem do céu numa área À FRENTE (10.9DN)
    if (sp.kind === "volley") {
      // 10.9DZ — MIRA NO ALVO. Antes a chuva caía sempre 6.5m à frente do rosto do Caçador, com
      // raio cortado a 45%: o arqueiro luta LONGE, então a maioria das saraivadas caía no chão
      // antes do monstro. Agora ela cai em cima do alvo travado (até `radius` metros), e só usa
      // o ponto à frente quando não há alvo nenhum. O raio também deixou de ser cortado.
      // 10.9ED — o alvo travado NAO basta: o raio de aquisicao do auto-alvo e de so 9m (larga em
      // 11.5m), e o arqueiro atira de mais longe — na hora do toque `current` costuma estar NULO,
      // e a magia caia no ponto cego a frente. Agora, sem alvo travado, ela procura o monstro mais
      // PROXIMO dentro do alcance dela. So cai "a frente" quando nao ha absolutamente ninguem.
      let tgt: { position: THREE.Vector3; isDead: boolean } | null = this.autoTarget.current;
      if (!tgt || tgt.isDead) {
        tgt = null;
        let best = spRadius * spRadius;
        for (const e of this.enemies.enemies) {
          if (e.isDead) continue;
          const dx = e.position.x - p.x, dz = e.position.z - p.z;
          const d2 = dx * dx + dz * dz;
          if (d2 < best) { best = d2; tgt = e; }
        }
        if (this.boss && !this.boss.isDead) {
          const dx = this.boss.position.x - p.x, dz = this.boss.position.z - p.z;
          if (dx * dx + dz * dz < best) tgt = this.boss;
        }
      }
      let cx = p.x + dirX * 6.5, cz = p.z + dirZ * 6.5;
      if (tgt && !tgt.isDead) {
        const tx = tgt.position.x - p.x, tz = tgt.position.z - p.z;
        const d = Math.hypot(tx, tz);
        const reach = Math.min(d, spRadius); // não teleporta a magia pra fora do alcance dela
        if (d > 0.001) { cx = p.x + (tx / d) * reach; cz = p.z + (tz / d) * reach; }
      }
      const rr = Math.max(5, spRadius * 0.55), rr2 = rr * rr;
      _spellTo.set(cx, 0, cz);
      for (const e of this.enemies.enemies) { if (e.isDead) continue; const dx = e.position.x - cx, dz = e.position.z - cz; if (dx * dx + dz * dz <= rr2) dealDamage(e, stats, { targetKind: "enemy", byPlayer: true, damageType: sp.damageType, knockbackFrom: _spellTo, knockbackForce: sp.knockback }); }
      if (this.boss && !this.boss.isDead) { const dx = this.boss.position.x - cx, dz = this.boss.position.z - cz; if (dx * dx + dz * dz <= rr2) dealDamage(this.boss, stats, { targetKind: "enemy", byPlayer: true, damageType: sp.damageType, knockbackFrom: _spellTo, knockbackForce: sp.knockback }); }
      this.telegraph.circle(cx, cz, rr, 0.5, sp.color);
      this.fxArrowsFromSky(cx, cz, rr, sp.color, 24); // 10.9EM — mais flechas (le como CHUVA)
      this.cameraRig.impulse(0.12); this.audio.playSfx("spell_volley"); // 10.9HK
      return;
    }
    // NOVA / GOLPE / EXPLOSÃO — AoE em volta + ONDA DE CHOQUE
    const r2 = spRadius * spRadius;
    for (const e of this.enemies.enemies) {
      if (e.isDead) continue;
      const dx = e.position.x - p.x, dz = e.position.z - p.z;
      if (dx * dx + dz * dz > r2) continue;
      dealDamage(e, stats, { targetKind: "enemy", byPlayer: true, damageType: sp.damageType, knockbackFrom: p, knockbackForce: sp.knockback });
    }
    if (this.boss && !this.boss.isDead) { const dx = this.boss.position.x - p.x, dz = this.boss.position.z - p.z; if (dx * dx + dz * dz <= r2) dealDamage(this.boss, stats, { targetKind: "enemy", byPlayer: true, damageType: sp.damageType, knockbackFrom: p, knockbackForce: sp.knockback }); }
    this.telegraph.circle(p.x, p.z, spRadius, 0.45, sp.color);
    this.fxShockwave(p.x, p.z, sp.color, spRadius);
    if (sp.kind === "slam") this.spellVfx.scorch(p.x, p.z, spRadius * 0.55); // 10.9HI — o chão racha
    this.particles.burst(p.x, 0.8, p.z, sp.color, { count: 34, speed: 12, dur: 0.6 });
    this.cameraRig.impulse(0.18); this.audio.playSfx("spell_impact"); // 10.9HK
  }

  /** 10.9DN — FLECHAS DO CÉU (arqueiro): várias flechas caem sobre a área, com impacto. */
  private fxArrowsFromSky(cx: number, cz: number, radius: number, color: number, n: number): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, r = Math.random() * radius;
      const x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r;
      // 10.9EM — FLECHA DE VERDADE. Antes era um cone de 7cm de raio: da camera do jogo era
      // INVISIVEL, e o que sobrava na tela era so o anel no chao — igual a Praga do necromante.
      // Agora: haste + ponta + penas + rastro de luz, ~2m, material sem luz (brilha no gramado).
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1, depthWrite: false });
      const arrow = new THREE.Group();
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.5, 5), mat); shaft.position.y = 0.95; arrow.add(shaft);
      const head = new THREE.Mesh(new THREE.ConeGeometry(0.17, 0.42, 5), mat); head.rotation.x = Math.PI; head.position.y = 0.0; arrow.add(head);
      for (const s of [-1, 1]) {
        const fl = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.28, 0.02), mat);
        fl.position.set(s * 0.1, 1.62, 0); fl.rotation.z = s * 0.35; arrow.add(fl);
      }
      const trailMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35, depthWrite: false, blending: THREE.AdditiveBlending });
      const trail = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.09, 2.6, 5), trailMat); trail.position.y = 3.0; arrow.add(trail);
      arrow.rotation.y = Math.random() * Math.PI; // as penas nao ficam todas viradas pro mesmo lado
      arrow.rotation.z = (Math.random() - 0.5) * 0.25; // leve inclinacao: chuva, nao grade
      arrow.position.set(x, 10 + Math.random() * 4, z);
      this.renderer.scene.add(arrow);
      this.spellFx.push({ obj: arrow, mat, t: -i * 0.03, dur: 0.5, kind: "arrow", vy: 30, groundY: 0.05 });
    }
  }
  /** 10.9DN — ONDA DE CHOQUE: anel que cresce e some (nova/golpe/explosão). */
  private fxShockwave(cx: number, cz: number, color: number, maxR: number): void {
    const geo = new THREE.RingGeometry(0.9, 1.25, 40);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85, side: THREE.DoubleSide });
    const m = new THREE.Mesh(geo, mat);
    m.rotation.x = -Math.PI / 2;
    m.position.set(cx, 0.12, cz);
    (m as unknown as { _maxR: number })._maxR = Math.max(2, maxR);
    this.renderer.scene.add(m);
    this.spellFx.push({ obj: m, mat, t: 0, dur: 0.45, kind: "ring", vy: 0, groundY: 0 });
  }
  /** 10.9DN — PILAR DE LUZ: coluna que sobe e some (buffs). */
  private fxPillar(cx: number, cz: number, color: number): void {
    const geo = new THREE.CylinderGeometry(0.9, 1.2, 6, 16, 1, true);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthWrite: false });
    const m = new THREE.Mesh(geo, mat);
    m.position.set(cx, 3, cz);
    this.renderer.scene.add(m);
    this.spellFx.push({ obj: m, mat, t: 0, dur: 0.7, kind: "pillar", vy: 0, groundY: 0 });
  }
  /** 10.9DN — atualiza/limpa os efeitos visuais das magias. */
  private updateSpellFx(dt: number): void {
    for (let i = this.spellFx.length - 1; i >= 0; i--) {
      const f = this.spellFx[i];
      f.t += dt;
      if (f.t < 0) continue; // flecha em atraso (queda escalonada)
      const m = f.obj as THREE.Mesh;
      if (f.kind === "arrow") {
        m.position.y -= f.vy * dt;
        if (m.position.y <= f.groundY) {
          m.position.y = f.groundY - 0.35; // 10.9EM — CRAVA no chao (a ponta afunda)
          this.particles.burst(m.position.x, 0.3, m.position.z, (f.mat as THREE.MeshBasicMaterial).color.getHex(), { count: 7, speed: 4.5, dur: 0.4 });
          const trail = m.children[m.children.length - 1];
          if (trail) trail.visible = false; // o rastro de luz so existe no ar
          f.kind = "stuck"; f.t = 0; f.dur = 0.7;
        }
      } else if (f.kind === "stuck") {
        // 10.9EM — flecha cravada fica um instante e desbota (e o que "assina" a Chuva de Flechas)
        const k = f.t / f.dur;
        (f.mat as THREE.MeshBasicMaterial).opacity = k < 0.6 ? 1 : 1 - (k - 0.6) / 0.4;
        if (f.t >= f.dur) { this.disposeFx(i); continue; }
      } else if (f.kind === "ring") {
        const k = f.t / f.dur;
        const maxR = (m as unknown as { _maxR: number })._maxR;
        const sc = 1 + k * (maxR - 1);
        m.scale.set(sc, sc, sc);
        (f.mat as THREE.MeshBasicMaterial).opacity = 0.85 * (1 - k);
        if (f.t >= f.dur) { this.disposeFx(i); continue; }
      } else if (f.kind === "pillar") {
        const k = f.t / f.dur;
        m.position.y = 3 + k * 3;
        m.rotation.y += dt * 4;
        (f.mat as THREE.MeshBasicMaterial).opacity = 0.5 * (1 - k);
        if (f.t >= f.dur) { this.disposeFx(i); continue; }
      }
    }
  }
  private disposeFx(i: number): void {
    const f = this.spellFx[i];
    this.renderer.scene.remove(f.obj);
    // 10.9EM — a flecha e um GRUPO (haste/ponta/penas/rastro): libera tudo, nao so a raiz
    f.obj.traverse((o) => {
      const mesh = o as THREE.Mesh;
      mesh.geometry?.dispose();
      const mm = mesh.material as THREE.Material | undefined;
      if (mm && mm !== f.mat) mm.dispose();
    });
    f.mat.dispose();
    this.spellFx.splice(i, 1);
  }

  /** 10.9CX — buff temporário: conta o tempo e recalcula os stats quando acaba. */
  private updateBuff(dt: number): void {
    if (this.buffTimer <= 0) return;
    this.buffTimer -= dt;
    if (this.buffTimer <= 0) { this.buffTimer = 0; this.buffMods = { dmgMult: 0, dr: 0, moveMult: 0, atkSpeedMult: 0, critChance: 0 }; this.recalcPlayer(); }
  }

  /** 10.9CX — meteoros pendentes: telegrafa e detona ao fim do atraso. */
  private updateMeteors(dt: number): void {
    for (let i = this.meteors.length - 1; i >= 0; i--) {
      const m = this.meteors[i];
      m.t -= dt;
      this.telegraph.circle(m.x, m.z, m.radius, 0.25, m.color);
      if (m.t > 0) continue;
      const cs = this.player.combatStats;
      const stats = { ...cs, baseDamage: Math.round(cs.baseDamage * m.dmgMult * m.spDmg), critChance: cs.critChance + m.crit };
      const r2 = m.radius * m.radius;
      _spellTo.set(m.x, 0, m.z);
      for (const e of this.enemies.enemies) { if (e.isDead) continue; const dx = e.position.x - m.x, dz = e.position.z - m.z; if (dx * dx + dz * dz <= r2) dealDamage(e, stats, { targetKind: "enemy", byPlayer: true, damageType: m.dt as never, knockbackFrom: _spellTo, knockbackForce: m.knockback }); }
      if (this.boss && !this.boss.isDead) { const dx = this.boss.position.x - m.x, dz = this.boss.position.z - m.z; if (dx * dx + dz * dz <= r2) dealDamage(this.boss, stats, { targetKind: "enemy", byPlayer: true, damageType: m.dt as never, knockbackFrom: _spellTo, knockbackForce: m.knockback }); }
      this.particles.burst(m.x, 0.9, m.z, m.color, { count: 40, speed: 14, dur: 0.7 });
      this.fxShockwave(m.x, m.z, m.color, m.radius * 1.4); // 10.9DN
      this.spellVfx.scorch(m.x, m.z, m.radius * 0.7); // 10.9HI
      this.cameraRig.impulse(0.22); this.audio.playSfx("spell_impact"); // 10.9HK
      this.meteors.splice(i, 1);
    }
  }

  /** 10.9FC — armadilhas do arqueiro: explodem quando um monstro chega perto (ou somem no tempo). */
  private updateTraps(dt: number): void {
    for (let i = this.traps.length - 1; i >= 0; i--) {
      const t = this.traps[i];
      t.t -= dt;
      this.telegraph.circle(t.x, t.z, t.trigger, 0.12, t.color);
      const tr2 = t.trigger * t.trigger;
      let fire = false;
      for (const e of this.enemies.enemies) { if (e.isDead) continue; const dx = e.position.x - t.x, dz = e.position.z - t.z; if (dx * dx + dz * dz <= tr2) { fire = true; break; } }
      if (!fire && this.boss && !this.boss.isDead) { const dx = this.boss.position.x - t.x, dz = this.boss.position.z - t.z; fire = dx * dx + dz * dz <= tr2; }
      if (fire) {
        const cs = this.player.combatStats;
        const stats = { ...cs, baseDamage: Math.round(cs.baseDamage * t.dmgMult * t.spDmg), critChance: cs.critChance + t.crit };
        const r2 = t.radius * t.radius;
        _spellTo.set(t.x, 0, t.z);
        for (const e of this.enemies.enemies) { if (e.isDead) continue; const dx = e.position.x - t.x, dz = e.position.z - t.z; if (dx * dx + dz * dz <= r2) dealDamage(e, stats, { targetKind: "enemy", byPlayer: true, damageType: t.dt as never, knockbackFrom: _spellTo, knockbackForce: t.knockback }); }
        if (this.boss && !this.boss.isDead) { const dx = this.boss.position.x - t.x, dz = this.boss.position.z - t.z; if (dx * dx + dz * dz <= r2) dealDamage(this.boss, stats, { targetKind: "enemy", byPlayer: true, damageType: t.dt as never, knockbackFrom: _spellTo, knockbackForce: t.knockback }); }
        this.particles.burst(t.x, 0.9, t.z, t.color, { count: 40, speed: 13, dur: 0.6 });
        this.fxShockwave(t.x, t.z, t.color, t.radius * 1.3);
        t.vfx?.stop(); this.spellVfx.scorch(t.x, t.z, t.radius * 0.5); // 10.9HI
        this.cameraRig.impulse(0.2); this.audio.playSfx("spell_impact"); // 10.9HK
        this.traps.splice(i, 1);
      } else if (t.t <= 0) { t.vfx?.stop(); this.traps.splice(i, 1); }
    }
  }

  /** 10.9CX — zonas de praga: dano periódico enquanto duram. */
  private updateCurses(dt: number): void {
    for (let i = this.curses.length - 1; i >= 0; i--) {
      const f = this.curses[i];
      f.t -= dt; f.acc += dt;
      if (f.follow) { f.x = this.player.position.x; f.z = this.player.position.z; } // 10.9FC — Redemoinho
      this.telegraph.circle(f.x, f.z, f.radius, 0.18, f.color);
      if (f.acc >= f.every) {
        f.acc = 0;
        const cs = this.player.combatStats;
        const stats = { ...cs, baseDamage: Math.round(cs.baseDamage * f.dmgMult * f.spDmg), critChance: cs.critChance + f.crit };
        const r2 = f.radius * f.radius;
        let hits = 0;
        for (const e of this.enemies.enemies) { if (e.isDead) continue; const dx = e.position.x - f.x, dz = e.position.z - f.z; if (dx * dx + dz * dz <= r2) { dealDamage(e, stats, { targetKind: "enemy", byPlayer: true, damageType: f.dt as never }); hits++; if (f.slow) e.applySlow(f.slow, f.every + 0.2); } }
        // 10.9FC — Drenar Vida: cura por monstro atingido (ate 5 por pulso)
        if (f.drainPct && hits > 0) this.player.health.heal(this.player.health.max * f.drainPct * Math.min(5, hits));
        if (this.boss && !this.boss.isDead) { const dx = this.boss.position.x - f.x, dz = this.boss.position.z - f.z; if (dx * dx + dz * dz <= r2) dealDamage(this.boss, stats, { targetKind: "enemy", byPlayer: true, damageType: f.dt as never }); }
        this.particles.burst(f.x + (Math.random() - 0.5) * f.radius, 0.5, f.z + (Math.random() - 0.5) * f.radius, f.color, { count: 8, speed: 3, dur: 0.8 });
      }
      if (f.t <= 0) this.curses.splice(i, 1);
    }
  }

  private startRun(): void {
    this.audio.resume(); // 10.4V: destrava o áudio no gesto do usuário (autoplay mobile)
    // NÃO zera progressão (é permanente/salva). Reinicia só o mundo transitório.
    if (this.raid?.active) this.raid.leave(); // 10.9EO
    if (this.inBossZone) this.leaveBossZone(false); // 10.9AJ — trocar de região/run sai da zona
    this.clearWorld();
    // aplica ambiente/tabela da região ativa
    const def = this.region.activate(this.region.id);
    this.audio.playRegion(this.region.id); // 10.5D: música da região
    this.refreshGate(); // 10.6H: portão só abre com os objetivos da área cumpridos
    this.spawner.setTable(this.region.enemyTable(), this.region.maxAlive());
    this.territories.setRegion(def); // 10.5M: grade de territórios da região
    this.buildGuardianArena(); // 10.9CC: arena fixa do Guardião da região
    this.weather.setRegion(def.id); // 10.5V
    this.fog = this.region.activeFog();
    this.minimap.setFog(this.fog);
    // começa na Sanctuary da região, com vida cheia
    this.player.respawnAt(def.spawn.x, def.spawn.z, 1);
    this.spawner.prewarm(this.player.position);
    this.cameraRig.snapTo(this.player.position);
    this.mainMenu.hide();
    this.pauseMenu.hide();
    this.settings.hide();
    this.deathScreen.hide();
    this.altarPanel.close();
    this.altarPanel.setPromptVisible(false);
    this.hud.show();
    this.equipBar.show();
    this.chain.setFromBag(this.equipment.bag, this.equipment.equipped.weapon, this.chainType, this.chainSlotLimit(), this.classDef()?.allowedWeapons, (i) => this.equipment.canEquipLevel(i, this.xp.level)); // 10.5H: prende as armas da bolsa
    this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
    this.minimap.show();
    this.menuBar.show();
    this.updateTreeBadge();
    this.missions.refill(); // 10.5O: garante 3 missões ativas
    this.updateMissionHud();
    this.tutorial.fire("move"); // 10.5U: primeira dica ao entrar no mundo
    this.fog.reveal(this.player.position.x, this.player.position.z);
    this.checkDiscoveries();
    this.input.setEnabled(true);
    this.state = "playing";
  }

  /** Troca de região (Rift Gate): fade rápido, swap de ambiente/tabela/fog, chega na Sanctuary. */
  private switchRegion(toId: string): void {
    if (this.regionTransition > 0 || this.state !== "playing") return;
    // 10.5T — porta de NÍVEL: dava para viajar cedo demais e só apanhar do outro lado.
    const target = this.region.defById(toId);
    // 10.6I — LIMITE DURO de nível por área. Era recommendedLevel-2 (uma sugestão);
    // agora é requisito: a área seguinte não é uma opção precoce, é o próximo degrau.
    const minLevel = Math.max(1, target.recommendedLevel);
    if (this.xp.level < minLevel) {
      this.banner.show(i18n.t("world.tooLow", { n: target.recommendedLevel }), "danger", 2200);
      return;
    }
    this.regionTransition = 1;
    this.regionFade.classList.add("active");
    this.audio.playSfx("rift_enter");
    const total = GameConfig.world.rift.transitionMs;
    window.setTimeout(() => {
      // sair de uma região encerra o Rift Surge dela (volta ao normal)
      this.surge.deactivate();
      this.spawner.setSurge(1, 1);
      this.region.unlock(toId);
      const def = this.region.activate(toId);
      diag.log("regiao", toId); // 10.9IT
      this.audio.playRegion(this.region.id); // 10.5D: troca a música ao mudar de região
      this.clearWorld();
      this.refreshGate(); // 10.6H: portão só abre com os objetivos da área cumpridos
      this.spawner.setTable(this.region.enemyTable(), this.region.maxAlive());
      this.territories.setRegion(def); // 10.5M: grade de territórios da nova região
      this.weather.setRegion(def.id); // 10.5V
      this.fog = this.region.activeFog();
      this.minimap.setFog(this.fog);
      this.player.teleportTo(def.spawn.x, def.spawn.z);
      // Surge pendente (ativado via tela de viagem p/ outra região): aplica ao chegar
      if (this.pendingSurge && this.pendingSurge.region === toId) {
        const t = this.pendingSurge.tier;
        this.pendingSurge = null;
        this.surge.activate(toId, t);
        this.spawner.setSurge(this.surge.hpMult(toId), this.surge.dmgMult(toId));
      }
      this.updateSurgeVisual();
      this.spawner.prewarm(this.player.position);
      this.cameraRig.snapTo(this.player.position);
      this.fog.reveal(this.player.position.x, this.player.position.z);
      this.checkDiscoveries();
      const banner = this.surge.anyActive ? i18n.t("surge.activated", { n: this.surge.activeTier(toId) }) : i18n.t("region.entering", { name: i18n.t(def.nameKey) });
      this.banner.show(banner, this.surge.anyActive ? "boss" : "info", 1800);
      this.save.save();
    }, total / 2);
    window.setTimeout(() => {
      this.regionFade.classList.remove("active");
      this.regionTransition = 0;
    }, total);
  }

  // ---- 10.5Y: MISSÕES (tela própria) ----
  /** 10.9 — abre o lobby PvP (portal do Santuário). */
  private openPvp(): void {
    if (this.state !== "playing") return;
    this.state = "pvp";
    this.input.setEnabled(false);
    this.bossPrompt.hidden = true;
    void syncProfile(this.myProfileStats()); // garante seu perfil/nome atualizado no lobby
    this.pvpScreen.open();
  }
  private closePvp(): void {
    if (this.state !== "pvp") return;
    this.pvpScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
  }

  /** 10.9D — matchmaking achou par: fecha o lobby e entra na arena de duelo. */
  private startDuel(opp: LobbyPlayer): void {
    const myId = selfId();
    if (!myId) return;
    this.pvpScreen.hide(8000); // 10.9FF — fica 8 s no lobby (invisivel) marcado "vs rival", pro rival entrar junto
    this.state = "duel";
    // 10.9AW — leva a arma equipada (visual) e a cor da classe pro avatar do duelo
    const wInst = this.equipment.equipped.weapon;
    const wDef = wInst ? definitionById(wInst.definitionId) : null;
    const visualId = wDef && isWeaponDef(wDef) ? (wDef as WeaponDefinition).visualId : undefined;
    const clsColor = this.classDef()?.color;
    const energy = clsColor ? parseInt(clsColor.replace("#", ""), 16) : undefined;
    this.duelScreen.open(myId, opp, { name: getName(), power: this.combatPower, visualId, energy, stats: this.pvpStats(), weaponTier: (wDef as { tier?: number } | null)?.tier, weaponFamily: wInst ? wInst.definitionId.replace(/[A-Z].*$/, "") : undefined }, selfQ()); // 10.9FF/FG/HG
  }
  /**
   * 10.9FG — atributos REAIS do Cacador pro duelo (vida, dano, alcance, velocidade, defesas e as 3 magias).
   * 10.9L11 — CORREÇÃO: mandava só `player.damageReduction` (resiliência da Árvore), sem
   * `player.classDamageReduction` (a Pele de Aço do Guerreiro, 35%) — o duelo/3v3 vinham ignorando
   * a defesa nata da classe por inteiro. Guerreiro tomava dano como se não tivesse Pele de Aço nenhuma.
   * 10.9L11 — Thiago: "melhorar os magos no pvp e melhorar os danos deles". Medido ANTES desta
   * correção: Guerreiro só mitigava por armadura+bloqueio (~13% em média); Mago não mitiga NADA
   * (única classe sem armadura/bloqueio/DR) e tem 1,8× menos vida (hpMult 0,75 vs 1,35) — vida
   * EFETIVA do Mago era ~3,2× menor que a do Guerreiro num duelo. Corrigindo o bug sozinho pioraria
   * isso (Guerreiro passa a mitigar ~44%), então o Mago ganha aqui uma compensação SÓ DE PVP (não
   * mexe no PvE, já calibrado várias vezes: 10.9CC/10.9IZ): +20% de dano (afeta ataque básico E
   * toda magia, que usam este `dmg`) e +20 pontos de redução (uma "barreira arcana" ativa só no
   * duelo/3v3) — vida efetiva do Mago sobe pra ~0,94, contra ~2,4 do Guerreiro: melhora real sem
   * virar a mesa.
   * 10.9L12 — Thiago: "faça o mesmo nele também" — Necromante tem a MESMA fragilidade estrutural
   * (0 armadura/bloqueio/DR, cajado igual ao Mago; hpMult 0,78, quase idêntico ao 0,75 do Mago).
   * Mesma compensação, mesmos números.
   */
  private pvpStats(): import("../ui/DuelArena3D").PvpStats {
    const cs = this.player.combatStats;
    const w = this.player.weaponInfo;
    const isSquishyCaster = this.progress.classId === "mage" || this.progress.classId === "necromancer";
    const dmgMult = isSquishyCaster ? 1.2 : 1;
    const dr = Math.min(0.8, this.player.damageReduction + this.player.classDamageReduction + (isSquishyCaster ? 0.2 : 0));
    return {
      maxHp: this.player.health.max,
      dmg: cs.baseDamage * dmgMult,
      aps: w.attacksPerSecond,
      range: w.attackRange,
      armor: cs.armor,
      dr,
      block: this.player.blockChance,
      crit: cs.critChance,
      critMult: cs.critDamageMult,
      lifeSteal: this.lifeSteal,
      spells: this.spellsOf().filter((s): s is SpellLike => !!s).map((s) => ({
        kind: s.kind, name: i18n.t(s.nameKey), emoji: SPELL_EMOJI[s.kind] ?? "✨", color: s.color, cooldown: s.cooldown, dmgMult: s.dmgMult, radius: s.radius,
        buff: s.buff, dashRange: s.dashRange, waveLength: s.waveLength, field: s.field, meteorDelay: s.meteorDelay, meteorRange: s.meteorRange,
        trap: s.trap, ahead: s.ahead, drainPct: s.drainPct,
      })),
    };
  }

  /** 10.9BC — sala 3v3 fechou (6 na fila): entra na arena de times. */
  private startTeamMatch(room: TeamRoom): void {
    const myId = selfId();
    if (!myId) return;
    this.pvpScreen.hide(9000); // 10.9FH — fica 9 s no lobby (invisivel) pros 6 fecharem a mesma sala
    this.state = "duel";
    const wInst = this.equipment.equipped.weapon;
    const wDef = wInst ? definitionById(wInst.definitionId) : null;
    const visualId = wDef && isWeaponDef(wDef) ? (wDef as WeaponDefinition).visualId : undefined;
    const clsColor = this.classDef()?.color;
    const energy = clsColor ? parseInt(clsColor.replace("#", ""), 16) : undefined;
    this.teamArena.open(myId, room, { name: getName(), power: this.combatPower, visualId, energy, stats: this.pvpStats(), weaponTier: (wDef as { tier?: number } | null)?.tier, weaponFamily: wInst ? wInst.definitionId.replace(/[A-Z].*$/, "") : undefined }); // 10.9FH/HG
  }
  private closeDuel(): void {
    if (this.state !== "duel") return;
    this.duelScreen.hide();
    this.teamArena.hide();
    this.input.setEnabled(true);
    this.state = "playing";
  }
  /** 10.9F — do duelo de volta pro lobby (revanche). O duelScreen já se escondeu. */
  private duelToLobby(note?: string): void {
    this.state = "pvp";
    this.pvpScreen.open(note);
  }
  private onDuelResult(won: boolean, team = false): void {
    if (won) {
      this.progress.pvpWins++;
      const coins = team ? 150 : 100;
      // 10.9K2 — AUDITORIA: 💎 do PvP não tinha limite (duas contas combinadas farmavam diamante
      // infinito uma na outra). Teto diário; a 🪙 continua sem teto (não compra nada fora do jogo).
      const dia = this.progress.takeDailyCapped("pvpDia", team ? 2 : 1, GameConfig.diamonds.pvpDailyCap); // 10.9BC — 3x3 paga um pouco mais
      this.progress.addCoins(coins); // recompensa por vitória
      if (dia > 0) this.progress.addDiamonds(dia);
      this.save.save();
      void syncProfile(this.myProfileStats());
      this.banner.show(`🏆 ${team ? "Vitória do time" : "Vitória no duelo"}! +${coins}🪙${dia > 0 ? ` +${dia}💎` : ""}`, "boss", 2800);
    } else {
      this.progress.pvpLosses++; // 10.9H — registro W/L
      this.save.save();
      void syncProfile(this.myProfileStats());
      this.banner.show("Duelo perdido. Fortaleça seu build e volte!", "info", 2400);
    }
  }

  private openMissions(): void {
    if (this.state !== "playing") return;
    this.state = "missions";
    this.input.setEnabled(false);
    this.altarPanel.setPromptVisible(false);
    this.bossPrompt.hidden = true;
    this.missionsScreen.open(() => this.buildMissionsData());
  }
  private closeMissions(): void {
    if (this.state !== "missions") return;
    this.missionsScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
  }
  private buildMissionsData(): MissionsData {
    let total = 0;
    for (const m of this.missions.list) total += this.missions.timesDone(m.id);
    const done = this.progress.missions?.done ?? {};
    total = Object.values(done).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
    return {
      rows: this.missions.list.map((m) => ({
        id: m.id,
        name: i18n.t(`mission.${m.id}`),
        progress: m.progress,
        target: m.target,
        coins: m.coins,
        xp: m.xp,
        shards: m.shards,
        done: this.missions.timesDone(m.id),
        ready: m.progress >= m.target, // 10.6F
      })),
      daily: (() => {
        const d = this.daily.current();
        return { name: i18n.t(`mission.${d.t.id}`), progress: d.progress, target: d.target, coins: d.coins, xp: d.xp, shards: d.shards, ready: d.ready, claimed: d.claimed, done: this.daily.timesDone,
          adDouble: Ads.available("dailyDouble") ? (Vip.active ? "vip" as const : "ad" as const) : undefined }; // 10.9BE
      })(),
      totalDone: total,
      contractSeconds: this.contractTimer,
    };
  }

  // ---- Character / Void Tree ----
  private openCharacter(): void {
    if (this.state !== "playing") return;
    this.state = "character";
    this.input.setEnabled(false);
    this.bossPrompt.hidden = true;
    this.characterScreen.open(this.buildCharacterData());
  }
  private openTree(): void {
    if (this.state !== "playing") return;
    this.state = "tree";
    this.input.setEnabled(false);
    this.bossPrompt.hidden = true;
    this.voidTreeScreen.open(() => this.buildTreeView());
  }
  private closeOverlayToPlaying(): void {
    if (this.state !== "character" && this.state !== "tree") return;
    this.characterScreen.hide();
    this.voidTreeScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
  }

  // ---- World / Regions travel (Fase 10) ----
  private openWorld(): void {
    if (this.state !== "playing") return;
    if (this.raid.active) return; // 10.9EO — na raid nao se viaja (sai pela vitoria/derrota)
    this.state = "world";
    this.input.setEnabled(false);
    this.bossPrompt.hidden = true;
    this.tutorial.fire("map"); // 10.5U
    this.worldScreen.open(() => this.buildWorldRows(), () => this.buildWorldMap());
    // 10.9L20 — Thiago: "a cada volta sair aparecendo no mapa e explicando o que vai ser liberado".
    // Mostra 1x por Profundidade nova, na primeira vez que o Mapa abre depois dela.
    if (this.progress.depth > this.progress.depthIntroSeen) {
      const d = this.progress.depth;
      this.depthIntro.open(d, GameConfig.voidTree.pointsPerDepth, () => {
        this.progress.depthIntroSeen = d;
        this.save.save();
      });
    }
  }
  /** 10.9CG — FUGIR DA LUTA: desiste do boss atual (sem recompensa) e volta ao Santuário da região. */
  private fleeBoss(): void {
    this.closeWorld();
    if (!this.boss) return;
    if (this.inBossZone && this.zoneActiveRoom) { // 10.9CN — na Fenda a porta está trancada: sem fuga
      this.banner.show(i18n.t("bossZone.noFlee"), "danger", 2400);
      return;
    }
    if (this.inBossZone) { this.leaveBossZone(false); } // sala da Fenda: sair = desistir (sem cooldown)
    else {
      this.clearBoss();
      this.player.respawnAt(this.sanctuary.center.x, this.sanctuary.center.z, Math.max(0.3, this.player.health.fraction));
      this.cameraRig.snapTo(this.player.position);
    }
    this.banner.show(i18n.t("world.fled"), "info", 2200);
    this.save.save();
  }
  private closeWorld(): void {
    if (this.state !== "world") return;
    this.worldScreen.close();
    this.input.setEnabled(true);
    this.state = "playing";
  }
  /** Uma região está "concluída" (boss derrotado)? R3 não tem boss nesta fase (§50). */
  private regionCompleted(id: string): boolean {
    if (id === "forgottenForest") return this.progress.bossesDefeated.voidAlpha;
    if (id === "shatteredWilds") return this.progress.bossesDefeated.aetherColossus;
    return false;
  }
  /** 10.9L4 — onde está o boss que conta no objetivo "boss" desta área (pro mapa apontar). */
  private mapBossTarget(): WorldMapData["bossTarget"] {
    const kind = GameConfig.areaGoalBoss[this.region.id];
    if (kind === "voidAlpha") {
      const den = GameConfig.boss.voidAlpha.arena.center;
      // 10.9L5 — o Covil fica atrás da barreira até derrotar N elites: o mapa diz isso em vez de mandar pro boss
      const lock = this.progress.alphaGateOpen ? undefined
        : i18n.t("map.boss.lockAlpha", { have: Math.min(this.progress.elitesDefeated, GameConfig.gate.requiredElites), need: GameConfig.gate.requiredElites });
      return { x: den.x, z: den.z, ready: !lock, name: i18n.t("area.goal.boss.voidAlpha"), lock };
    }
    if (kind === "aetherColossus") {
      const g = GameConfig.colossus.gate;
      const u = GameConfig.colossus.unlock;
      const lock = this.colossusUnlocked() || this.progress.bossesDefeated.aetherColossus ? undefined
        : i18n.t("map.boss.lockColossus", { have: Math.min(this.progress.wardensDefeated, u.wardens), need: u.wardens });
      return { x: g.x, z: g.z, ready: !lock, name: i18n.t("area.goal.boss.aetherColossus"), lock };
    }
    if (kind === "guardian" && this.guardianArena) {
      const rid = this.region.id;
      const ready = this.guardianReady(rid);
      return { x: this.guardianArena.cx, z: this.guardianArena.cz, ready, name: i18n.t("area.goal.boss.guardian"), have: this.progress.guardianKills[rid] ?? 0, need: this.guardianNeed(rid) };
    }
    return undefined;
  }
  /** Linhas da tela de viagem: nome/tier/conclusão/maestria/surge por região. */
  /** 10.5M — MAPA da região: os territórios (com gangue), a barreira da base e os POIs. */
  private buildWorldMap(): WorldMapData {
    const cur = this.territories.at(this.player.position.x, this.player.position.z);
    const disc = this.region.activeDiscoveries();
    const pois: MapPoi[] = [{ x: this.sanctuary.center.x, z: this.sanctuary.center.z, kind: "sanctuary" }];
    if (this.guardianArena && this.guardianReady(this.region.id)) pois.push({ x: this.guardianArena.cx, z: this.guardianArena.cz, kind: "boss" }); // 10.9CC
    // 10.5Q — monstros vivos aparecem no mapa (o mapa "respira" junto com o mundo)
    for (const e of this.enemies.enemies) {
      if (e.isDead) continue;
      pois.push({ x: e.position.x, z: e.position.z, kind: e.elite ? "elite" : "enemy" });
    }
    // 10.9DX — e a população fixa dos territórios que você já descobriu, mesmo longe daqui.
    // (o teto de 45 saiu: com a população do mapa inteiro ele cortava áreas inteiras do desenho)
    for (const m of this.virtualBlips()) pois.push({ x: m.x, z: m.z, kind: m.champion ? "elite" : "enemy" });
    if (this.region.id === "forgottenForest") {
      pois.push({ x: GameConfig.altar.position.x, z: GameConfig.altar.position.z, kind: "altar" });
      const den = GameConfig.boss.voidAlpha.arena.center;
      pois.push({ x: den.x, z: den.z, kind: "boss" });
    }
    const rc = this.region.current().rift;
    if (rc) pois.push({ x: rc.x, z: rc.z, kind: "rift" });
    for (const c of GameConfig.chests.world) {
      if ((c as { region?: string }).region !== this.region.id) continue;
      if (!this.equipment.isChestOpen(c.id)) pois.push({ x: c.x, z: c.z, kind: "chest" });
    }
    return {
      bossActive: !!this.boss && !(this.inBossZone && !!this.zoneActiveRoom), // 10.9CG/CN — na Fenda selada não dá pra fugir
      regionName: i18n.t(this.region.current().nameKey),
      playerLevel: this.xp.level, // 10.9HP
      goals: this.areaGoals.list(this.region.id), // 10.9HP — objetivos da área (abrem o Portal Rift)
      goalBoss: GameConfig.areaGoalBoss[this.region.id], // 10.9L3 — qual boss conta nesta área
      // 10.9L4 — a PRÓXIMA região na ordem do mundo. Antes vinha do destino do Rift, que nas Terras e
      // na Tempestade aponta pra TRÁS (Floresta/Terras) — a faixa dizia "abre o Portal → Floresta".
      nextRegion: (() => { const nid = this.nextRegionId(this.region.id); const nr = nid ? this.region.defById(nid) : undefined; return nr ? { name: i18n.t(nr.nameKey), level: nr.recommendedLevel } : undefined; })(),
      nextDepth: this.nextRegionId(this.region.id) ? undefined : this.progress.depth + 1, // última área: fecha a volta
      bossTarget: this.mapBossTarget(), // 10.9L4 — onde está o boss que conta nesta área
      half: this.arena.halfSize, // 10.6H: cada área tem o seu tamanho
      territories: this.territories.all.map((t) => ({ ground: this.mapGround(t.biome.ground), tasks: this.terrProgress.tasks(t.def), cleared: this.terrProgress.cleared(t.def), open: this.terrProgress.isOpen(t.def, this.territories.all.map((x) => x.def), this.xp.level, this.progress.depth), needLevel: this.terrProgress.gateLevel(t.def, this.progress.depth), // 10.9HL/HP — piso + missão/estado
       
        id: t.def.id,
        name: i18n.t(t.def.nameKey),
        cx: t.cx,
        cz: t.cz,
        halfW: t.halfW,
        halfH: t.halfH,
        tier: t.def.tier,
        current: cur?.def.id === t.def.id,
        gang: t.def.gang.map((g) => i18n.t(`enemy.${g.id}`)),
        champion: t.def.champion ? i18n.t(`enemy.${t.def.champion}`) : undefined,
        discovered: cur?.def.id === t.def.id || disc[`terr:${t.def.id}`] === true, // 10.5Q
        visited: this.areaGoals.hasVisited(this.region.id, t.def.id), // 10.9L6 — conta pra "explorar" nesta volta
        col: t.def.col, // 10.7I — trilhas
        row: t.def.row,
      })),
      player: { x: this.player.position.x, z: this.player.position.z, facing: this.player.facingAngle },
      sanctuary: { x: this.sanctuary.center.x, z: this.sanctuary.center.z, r: this.region.current().sanctuary.radius },
      pois,
      gates: this.inBossZone || this.raid.active ? [] : this.terrGates.mapGates(), // 10.9EL — portas no Mapa do Mundo
      divides: this.inBossZone || this.raid.active ? [] : this.terrGates.mapDivides(), // 10.9HN — rios/lava/gelo/abismo
    };
  }

  private buildWorldRows(): WorldRow[] {
    return GameConfig.world.regions.map((def): WorldRow => {
      const completed = this.regionCompleted(def.id);
      return {
        id: def.id,
        name: i18n.t(def.nameKey),
        tier: def.tier,
        unlocked: this.region.isUnlocked(def.id),
        current: def.id === this.region.id,
        completed,
        recommendedLevel: def.recommendedLevel,
        levelOk: this.xp.level >= def.recommendedLevel, // 10.9FE
        goals: this.areaGoals.list(def.id), // 10.9HP — pra aba dizer o que falta na área anterior
        masteryLevel: this.regionMastery.level(def.id),
        masteryMax: GameConfig.regionMastery.maxLevel,
        surgeMaxTier: this.regionMastery.surgeMaxTier(def.id, completed),
        surgeActiveTier: this.surge.activeTier(def.id),
      };
    });
  }

  // ---- Regional Mastery (Fase 10) ----
  private openMastery(regionId: string): void {
    if (this.state !== "world" && this.state !== "playing") return;
    this.worldScreen.close();
    this.state = "mastery";
    this.input.setEnabled(false);
    this.bossPrompt.hidden = true;
    this.masteryScreen.open(() => this.buildMasteryView(regionId));
  }
  private closeMastery(): void {
    if (this.state !== "mastery") return;
    this.masteryScreen.close();
    this.input.setEnabled(true);
    this.state = "playing";
  }
  private buildMasteryView(regionId: string): MasteryView {
    const def = this.region.defById(regionId);
    const lv = this.regionMastery.level(regionId);
    const rewards: MasteryRewardRow[] = GameConfig.regionMastery.rewards.map((r) => ({
      level: r.level,
      label: i18n.t(`mastery.reward.${r.kind}`),
      earned: lv >= r.level,
      isNext: lv < r.level && !GameConfig.regionMastery.rewards.some((o) => o.level > lv && o.level < r.level),
    }));
    return {
      regionName: i18n.t(def.nameKey),
      level: lv,
      max: GameConfig.regionMastery.maxLevel,
      xpInto: this.regionMastery.xpInto(regionId),
      xpNext: this.regionMastery.xpForNext(regionId),
      rewards,
    };
  }

  /** Concede XP de Regional Mastery na região ATIVA (ação válida; nunca AFK — §56). */
  private gainRegionMastery(amount: number): void {
    const region = this.region.id;
    const mult = this.surge.masteryMult(region);
    const reached = this.regionMastery.gain(region, amount * mult);
    for (const level of reached) this.grantMasteryReward(region, level);
    if (reached.length) {
      this.worldScreen.refresh();
      this.save.save();
    }
  }
  /** Recompensa de nível de maestria (play-to-progress; §41/§44). */
  private grantMasteryReward(region: string, level: number): void {
    const def = GameConfig.regionMastery.rewards.find((r) => r.level === level);
    const rname = i18n.t(this.region.defById(region).nameKey);
    this.banner.show(i18n.t("regionMastery.levelUp", { region: rname, n: level }), "info", 2000);
    if (!def) return;
    const p = this.player.position;
    switch (def.kind) {
      case "essence":
        for (const [k, v] of Object.entries({ power: 6, vitality: 5, ferocity: 4 })) this.inventory.add(k as EssenceType, v);
        break;
      case "chest":
        this.spawnChestLoot(region === "forgottenForest" ? "void" : region === "shatteredWilds" ? "shard" : "skyruin", p.x, p.z + 3);
        break;
      case "surge2":
      case "surge3":
        window.setTimeout(() => this.banner.show(i18n.t("mastery.reward." + def.kind), "boss", 2200), 900);
        break;
      case "mastered":
        window.setTimeout(() => this.banner.show(i18n.t("mastery.masteredBanner", { region: rname }), "boss", 3000), 900);
        break;
      default:
        break;
    }
  }

  // ---- Rift Surge (Fase 10) ----
  private openSurge(regionId: string): void {
    if (this.state !== "world" && this.state !== "playing") return;
    this.worldScreen.close();
    this.state = "surge";
    this.input.setEnabled(false);
    this.bossPrompt.hidden = true;
    this.surgeScreen.open(regionId, () => this.buildSurgeView(regionId));
  }
  private closeSurge(): void {
    if (this.state !== "surge") return;
    this.surgeScreen.close();
    this.input.setEnabled(true);
    this.state = "playing";
  }
  private buildSurgeView(regionId: string): SurgeView {
    const def = this.region.defById(regionId);
    const maxTier = this.regionMastery.surgeMaxTier(regionId, this.regionCompleted(regionId));
    const active = this.surge.activeTier(regionId);
    const options: SurgeOption[] = GameConfig.riftSurge.tiers.map((t): SurgeOption => ({
      tier: t.tier,
      unlocked: t.tier <= maxTier,
      active: active === t.tier,
      descKey: `surge.desc${t.tier}`,
      objective: i18n.t("surge.objective", { kills: t.objKills, elites: t.objElites }),
    }));
    return { regionName: i18n.t(def.nameKey), activeTier: active, options };
  }
  private activateSurge(regionId: string, tier: number): void {
    const maxTier = this.regionMastery.surgeMaxTier(regionId, this.regionCompleted(regionId));
    if (tier < 1 || tier > maxTier) return;
    this.closeSurge();
    if (regionId === this.region.id) {
      this.applySurgeNow(regionId, tier);
    } else {
      this.pendingSurge = { region: regionId, tier }; // ativa ao chegar na região
      this.switchRegion(regionId);
    }
  }
  private deactivateSurge(regionId: string): void {
    this.closeSurge();
    if (this.surge.activeTier(regionId) <= 0) return;
    this.surge.deactivate();
    this.spawner.setSurge(1, 1);
    this.updateSurgeVisual();
    this.enemies.clear();
    this.autoTarget.clear();
    this.hook.reset();
    this.spawner.prewarm(this.player.position);
    this.banner.show(i18n.t("surge.ended"), "info", 1600);
  }
  private applySurgeNow(region: string, tier: number): void {
    this.surge.activate(region, tier);
    this.spawner.setSurge(this.surge.hpMult(region), this.surge.dmgMult(region));
    this.enemies.clear();
    this.autoTarget.clear();
    this.hook.reset();
    this.spawner.prewarm(this.player.position);
    this.updateSurgeVisual();
    this.audio.playSfx("rift_enter");
    this.banner.show(i18n.t("surge.activated", { n: tier }), "boss", 2400);
    window.setTimeout(() => {
      const o = this.surge.objective(region);
      if (o) this.banner.show(i18n.t("surge.objective", { kills: o.needKills, elites: o.needElites }), "info", 2200);
    }, 1200);
  }
  private updateSurgeVisual(): void {
    this.surgeOverlay.hidden = !this.surge.anyActive;
    this.updateSurgeHud();
  }
  private updateSurgeHud(): void {
    const o = this.surge.anyActive ? this.surge.objective(this.region.id) : null;
    if (!o) {
      this.surgeHud.hidden = true;
      return;
    }
    this.surgeHud.hidden = false;
    const tier = this.surge.activeTier(this.region.id);
    const done = this.surge.isComplete;
    this.surgeHud.innerHTML = done
      ? `<b>${i18n.t("surge.tierName", { n: tier })}</b> — ${i18n.t("surge.doneShort")}`
      : `<b>${i18n.t("surge.tierName", { n: tier })}</b> · ${i18n.t("surge.hud", { k: o.kills, nk: o.needKills, e: o.elites, ne: o.needElites })}`;
  }
  private onSurgeComplete(region: string): void {
    const tier = this.surge.activeTier(region);
    this.gainRegionMastery(GameConfig.regionMastery.xp.perBoss); // bônus grande de maestria
    const em = this.surge.essenceMult(region);
    for (const [k, v] of Object.entries({ power: 8 * tier, vitality: 6 * tier, ferocity: 5 * tier })) {
      this.inventory.add(k as EssenceType, Math.round((v as number) * em));
    }
    const p = this.player.position;
    this.spawnChestLoot(region === "forgottenForest" ? "void" : region === "shatteredWilds" ? "shard" : "tempest", p.x, p.z + 3); // Surge Chest (§53)
    const weq = this.equipment.equipped.weapon;
    if (weq) this.equipment.addShards(weq.definitionId, 5 * (1 + tier)); // 10.9P: Surge dá SHARDS (alimenta a forja)
    this.banner.show(i18n.t("surge.complete"), "boss", 2600);
    this.audio.playSfx("boss_defeated");
    this.updateSurgeHud();
    this.save.save();
  }
  /**
   * 10.9EG — compra VARIOS ranks de uma vez. Com nos de rank 50 (a Maestria de Classe e os nos
   * infinitos dos ramos comuns), subir de 1 em 1 era dezenas de toques pra cada no. Aplica
   * quantos couberem e so entao recalcula/salva UMA vez.
   */
  private doUnlockNode(id: string, times = 1): void {
    let n = 0;
    while (n < times && this.tree.unlock(id, this.xp.level, this.progress.bossesDefeated.voidAlpha)) n++;
    if (n > 0) {
      this.recalcPlayer();
      this.updateTreeBadge();
      this.voidTreeScreen.refresh();
      this.save.save();
    }
  }
  /** 10.9Y — custo do PRÓXIMO reset da Árvore Void em 💎.
   *  10.9K4 — Thiago: "10 diamantes, depois sobe 1% a cada reset" (era 1º grátis e depois dobrando
   *  50→100→…→800 — caro demais pra quem quer testar builds). */
  private treeResetCost(): number {
    const n = Math.max(0, this.progress.treeResets ?? 0);
    const c = GameConfig.progression.treeReset;
    return Math.max(1, Math.round(c.base * Math.pow(1 + c.growth, n)));
  }
  private doResetTree(): void {
    const cost = this.treeResetCost();
    if (cost > 0 && !this.progress.spendDiamonds(cost)) {
      this.banner.show(i18n.t("vip.notEnough"), "info", 1800); // sem 💎 suficiente
      return;
    }
    this.progress.treeResets = (this.progress.treeResets ?? 0) + 1;
    this.tree.reset();
    this.recalcPlayer();
    this.updateTreeBadge();
    this.voidTreeScreen.refresh();
    this.save.save();
    this.banner.show(cost > 0 ? i18n.t("voidTree.resetPaid", { cost: String(cost) }) : i18n.t("voidTree.resetDone"), "info", 1800);
  }

  /** Monta os dados da Character Screen (valores efetivos + breakdown + Combat Power). */
  private buildCharacterData(): CharacterData {
    const cs = this.player.combatStats;
    const w = this.player.weaponInfo;
    const wInst = this.equipment.equipped.weapon;
    const wt = this.currentWeaponType();
    const te = this.tree.effects();
    // DPS da mão (dano × golpes/s × esperança de crítico) + DPS da corrente (×dmgMult)
    const critExp = 1 + cs.critChance * (cs.critDamageMult - 1);
    const dps = Math.round(cs.baseDamage * w.attacksPerSecond * critExp);
    const chainInsts = this.chain.weapons;
    const chainDps = this.chain.totalDps; // 10.5P: já inclui os atributos do Caçador
    return {
      level: this.xp.level,
      combatPower: this.combatPower,
      voidAvailable: this.tree.available(this.xp.level, this.progress.bossesDefeated.voidAlpha),
      treeNodes: this.tree.totalNodesUnlocked,
      weapon: wInst ? { name: itemName(wInst), rarity: rarityName(wInst), icon: itemIcon(wInst), typeLabel: i18n.t(`weapon.${w.weaponType}`) } : null,
      masteryLabel: wt ? `${i18n.t(`weapon.${wt}`)} ${this.equipment.masteryLevel(wt)}` : "—",
      altarTotal: this.altarTotalLevels(),
      stats: {
        damage: cs.baseDamage,
        maxHp: this.player.health.max,
        armor: cs.armor,
        moveSpeed: this.player.moveSpeedBase,
        attackSpeed: w.attacksPerSecond,
        critChance: cs.critChance,
        critDamage: cs.critDamageMult,
        attackRange: w.attackRange,
        hookRange: this.hook.effectiveRange,
        hookCooldown: this.hook.effectiveCooldown,
        sprintDuration: this.player.sprintDurationValue,
        sprintCooldown: this.player.sprintCooldownValue,
        // 10.5K — atributos completos (tela de atributos da Bolsa)
        dps,
        regen: this.player.regenPerSecond,
        lifeSteal: this.lifeSteal, // 10.9EQ
        ...this.boughtStats(), // 10.9EV — quanto veio de COMPRA
        dmgSlash: this.powerTypeMods().slash, // 10.9CO — roteado por classe
        dmgPierce: this.powerTypeMods().pierce,
        dmgBlunt: this.powerTypeMods().blunt,
        defSlash: te.defSlashPct,
        defPierce: te.defPiercePct,
        defBlunt: te.defBluntPct,
        block: Math.min(GameConfig.combat.blockChanceCap, te.blockChance),
        resilience: te.resiliencePct + defenseSkillBonus(this.progress.skills.defense.level), // 10.9BY
        vsElite: te.vsElitePct,
        execute: te.executePct,
        chainCount: chainInsts.length,
        chainDps,
        chainReach: GameConfig.chain.reach,
      },
      breakdown: this.breakdown,
      missions: this.missions.list.map((m) => ({ name: i18n.t(`mission.${m.id}`), progress: m.progress, target: m.target, coins: m.coins, xp: m.xp })),
      // 10.9BY — resumo: skill da arma em uso + Defesa
      skillsLabel: (() => {
        const sk = skillForWeapon(wt, this.progress.classId);
        const parts = sk ? [`${SKILL_ICON[sk]} ${this.progress.skills[sk].level}`] : [];
        parts.push(`${SKILL_ICON.defense} ${this.progress.skills.defense.level}`);
        return parts.join(" · ");
      })(),
    };
  }

  private altarTotalLevels(): number {
    let s = 0;
    for (const upg of GameConfig.altar.upgrades) s += this.altar.levelOf(upg.key);
    return s;
  }

  /** Monta os dados da Void Tree (estado + descrições localizadas). */
  private buildTreeView(): TreeViewData {
    const avail = this.tree.available(this.xp.level, this.progress.bossesDefeated.voidAlpha);
    const boss = this.progress.bossesDefeated.voidAlpha;
    const cls = this.progress.classId;
    const nodes: TreeNodeView[] = VOID_NODES.filter((n) => !n.classId || n.classId === cls).map((n) => { // 10.9CL
      const rank = this.tree.rankOf(n.id);
      const reqMet = this.tree.requirementsMet(n);
      let state: NodeState;
      if (rank >= n.maxRank) state = "max";
      else if (rank > 0) state = "unlocked";
      else if (reqMet) state = "available";
      else state = "locked";
      const missing = nodeRequires(n).filter((r) => this.tree.rankOf(r) < 1).map((r) => i18n.t(`node.${r}`));
      const slot = POW_TYPE_SLOT[n.id]; // 10.9CO — 3 nós de tipo de dano se adaptam à classe
      const meta = slot !== undefined ? ROUTE_META[this.powerDmgRoute()[slot]] : null;
      return {
        id: n.id,
        branch: n.branch,
        tier: n.tier,
        parent: n.parent,
        name: meta ? i18n.t(meta.name) : i18n.t(n.nameKey),
        rank,
        maxRank: n.maxRank,
        cost: n.cost,
        state,
        desc: this.nodeDesc(n.id, rank),
        nextDesc: this.nodeDesc(n.id, rank + 1),
        maxDesc: this.nodeDesc(n.id, n.maxRank), // 10.9EH — onde o no CHEGA se voce encher
        requires: missing,
        icon: meta ? meta.icon : n.icon,
      };
    });
    void avail;
    void boss;
    return { available: avail, nodes };
  }

  /** Descrição de um nó no rank informado (gerada dos efeitos ou descKey especial). */
  /** 10.9CO — para cada um dos 3 nós de tipo de dano do Poder, qual dano a CLASSE realmente causa. */
  private powerDmgRoute(): PowerRoute[] {
    switch (this.progress.classId) {
      case "warrior": return ["slashing", "blunt", "damage"]; // espada/machado + clava
      case "archer": return ["piercing", "damage", "damage"]; // arco
      case "mage": return ["arcane", "damage", "damage"]; // cajado
      case "necromancer": return ["arcane", "damage", "damage"]; // cajado
      default: return ["slashing", "piercing", "blunt"];
    }
  }
  /** Distribui os 3 slots de tipo de dano do Poder no dano REAL da classe (o resto = dano bruto). */
  private powerTypeMods(): { slash: number; pierce: number; blunt: number; arcane: number; generic: number } {
    const te = this.tree.effects();
    const route = this.powerDmgRoute();
    const slotV = [te.dmgSlashPct, te.dmgPiercePct, te.dmgBluntPct];
    const out = { slash: 0, pierce: 0, blunt: 0, arcane: 0, generic: 0 };
    for (let i = 0; i < 3; i++) {
      const v = slotV[i];
      switch (route[i]) {
        case "slashing": out.slash += v; break;
        case "piercing": out.pierce += v; break;
        case "blunt": out.blunt += v; break;
        case "arcane": out.arcane += v; break;
        default: out.generic += v; break;
      }
    }
    return out;
  }

  private nodeDesc(id: string, rank: number): string {
    const n = VOID_NODES.find((x) => x.id === id);
    if (!n) return "";
    if (n.descKey) return i18n.t(n.descKey);
    const r = Math.min(rank, n.maxRank);
    const slot = POW_TYPE_SLOT[id]; // 10.9CO — 3 nós de tipo: rótulo conforme a classe
    if (slot !== undefined) {
      const meta = ROUTE_META[this.powerDmgRoute()[slot]];
      const total = (n.effects[0]?.perRank ?? 0) * r;
      return `+${Math.round(total * 100)}% ${i18n.t(meta.eff)}`;
    }
    return n.effects
      .map((e) => {
        const total = e.perRank * r;
        const label = i18n.t(`treeeffect.${e.key}`);
        const flat = FLAT_EFFECTS.has(e.key);
        // regen é fração/s pequena: 1 casa decimal (0.5%/s) em vez de arredondar p/ 0
        const val = e.key === "regenPct"
          ? `${(total * 100).toFixed(1)}%/s`
          : e.key === "attackRangeFlat"
            ? `${total.toFixed(2)}m`
            : flat
              ? String(Math.round(total))
              : `${Math.round(total * 100)}%`;
        return `+${val} ${label}`;
      })
      .join(", ");
  }

  /** Revela POIs próximos pela primeira vez -> toast + save (por região). */
  private checkDiscoveries(): void {
    const p = this.player.position;
    const near = (x: number, z: number, r: number): boolean => Math.hypot(p.x - x, p.z - z) <= r;
    const found: { key: string; nameKey: string }[] = [];
    if (this.sanctuary.contains(p)) found.push({ key: "sanctuary", nameKey: "map.sanctuary" });
    const rc = this.region.current().rift;
    if (rc && near(rc.x, rc.z, GameConfig.world.rift.interactRadius + 2)) found.push({ key: "rift", nameKey: "map.rift" });
    if (this.region.id === "forgottenForest") {
      const den = GameConfig.boss.voidAlpha.arena.center;
      if (near(GameConfig.altar.position.x, GameConfig.altar.position.z, GameConfig.altar.interactRadius + 2)) found.push({ key: "altar", nameKey: "map.altar" });
      if (this.region.subAreaAt(p.z).id === "alphaDen" || near(den.x, den.z, GameConfig.boss.voidAlpha.arena.radius)) found.push({ key: "alphaDen", nameKey: "map.alphaDen" });
    }
    for (const f of found) {
      if (this.region.discover(f.key)) {
        this.banner.show(`${i18n.t("map.discovered")}: ${i18n.t(f.nameKey)}`, "info", 2200);
        this.gainRegionMastery(GameConfig.regionMastery.xp.perDiscovery); // §31
        this.save.save();
      }
    }
  }

  // ---- Inventário / Equipamento ----
  private openInventory(slot?: EquipmentSlot): void {
    if (this.state !== "playing") return;
    this.state = "inventory";
    this.input.setEnabled(false);
    this.altarPanel.setPromptVisible(false);
    this.bossPrompt.hidden = true;
    this.invScreen.open(this.equipment, slot, this.xp.level, this.classDef()?.color); // 10.9FE/FS
  }

  private closeInventory(): void {
    if (this.state !== "inventory") return;
    this.invScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
  }

  // ---- 10.4Y: Loja ----
  /** `focusId` (10.9L8) — abre já no item (ex.: o anel que o aviso de perigo pediu). */
  private openShop(focusId?: string): void {
    if (this.state !== "playing") return;
    this.state = "shop";
    this.input.setEnabled(false);
    this.altarPanel.setPromptVisible(false);
    this.bossPrompt.hidden = true;
    this.shopScreen.open(this.progress, focusId);
  }
  private closeShop(): void {
    if (this.state !== "shop") return;
    this.shopScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
  }

  // ---------------------------- 10.6R: CORREIO ----------------------------
  private openMailbox(): void {
    if (this.state !== "playing") return;
    this.state = "mailbox";
    this.input.setEnabled(false);
    this.mailboxScreen.open(() => ({
      messages: this.mailSystem.messages,
      diamonds: this.progress.diamonds,
      coins: this.progress.coins,
    }));
  }
  private closeMailbox(): void {
    if (this.state !== "mailbox") return;
    this.mailboxScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
    this.updateMailDot();
  }
  /** 10.9FM — transforma os ANEXOS do correio em itens de verdade na bolsa. */
  private grantMailItems(items: { defId: string; rarity: string; level: number; vip?: boolean }[]): void {
    let n = 0;
    for (const it of items) {
      const inst = createInstance(it.defId, it.rarity as import("../items/EquipmentTypes").Rarity, Math.max(1, Math.floor(it.level)));
      if (inst) {
        // 10.9J4 — item de TESTE do admin (ex.: arma de tier de fim de jogo pra provar num
        // personagem de nível baixo): mesmo mecanismo do item VIP/comprado, ignora o nível
        // mínimo pra equipar/entrar na corrente (Inventory.canEquipLevel).
        if (it.vip) inst.vip = true;
        this.progress.discoverItem(inst.definitionId); // 10.9GM Índice
        if (this.sellIfOffClass(inst)) continue; // 10.9K1 — arma de outra classe vira 🪙
        this.equipment.bag.push(inst); n++; // brinde/prêmio: entra na bolsa mesmo cheia
      }
    }
    if (n > 0) {
      this.chain.setFromBag(this.equipment.bag, this.equipment.equipped.weapon, this.chainType, this.chainSlotLimit(), this.classDef()?.allowedWeapons, (i) => this.equipment.canEquipLevel(i, this.xp.level)); // 10.9GM — arma nova entra na corrente
      this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
      this.banner.show(i18n.t("gift.weapons", { n: String(n) }), "boss", 3200);
    }
  }
  /** 10.9K2 — bônus diário (e baú semanal). Antes só no boot: quem deixava o jogo aberto (ou em
   *  2º plano) virando a meia-noite não recebia o dia — e podia até perder a sequência. Agora
   *  também no wake() e a cada 90 s de jogo. Barato: só compara a data. */
  private checkDailyLoginNow(): void {
    if (!this.mailSystem.checkDailyLogin(Vip.dailyDiamonds)) return;
    this.banner.show(i18n.t("mail.newMail"), "info", 2000);
    this.updateMailDot();
    this.save.save();
  }
  private claimMail(id: string): void {
    const r = this.mailSystem.claim(id);
    if (!r) return;
    if (r.coins) this.addCoins(r.coins);
    if (r.diamonds) this.progress.addDiamonds(r.diamonds);
    if (r.xp) this.xp.gain(r.xp);
    if (r.elixirs) this.progress.addElixirs(r.elixirs); // 10.9K2 — baú semanal
    if (r.items?.length) this.grantMailItems(r.items); // 10.9FM
    events.emit("mail:claimed", { id, diamonds: r.diamonds ?? 0, coins: r.coins ?? 0 });
    this.mailboxScreen.refresh();
    this.updateMailDot();
    this.save.save();
  }
  private claimAllMail(): void {
    const r = this.mailSystem.claimAll();
    if (r.coins) this.addCoins(r.coins);
    if (r.diamonds) this.progress.addDiamonds(r.diamonds);
    if (r.xp) this.xp.gain(r.xp);
    if (r.elixirs) this.progress.addElixirs(r.elixirs); // 10.9K2 — baú semanal
    if (r.items?.length) this.grantMailItems(r.items); // 10.9FM
    this.mailboxScreen.refresh();
    this.updateMailDot();
    this.save.save();
  }
  private markAllMailRead(): void {
    const n = this.mailSystem.markAllRead();
    if (n > 0) { this.mailboxScreen.refresh(); this.updateMailDot(); this.save.save(); }
  }
  private deleteAllReadMail(): void {
    const n = this.mailSystem.deleteAllRead();
    if (n > 0) { this.mailboxScreen.refresh(); this.updateMailDot(); this.save.save(); }
  }
  private updateMailDot(): void {
    this.menuBar.setMailDot(this.mailSystem.unclaimedCount > 0 || this.mailSystem.unreadCount > 0);
  }

  // ---------------------------- 10.6S: LOJA VIP ----------------------------
  private openVip(): void {
    if (this.state !== "playing" && this.state !== "mailbox") return;
    if (this.state === "mailbox") this.mailboxScreen.hide();
    this.state = "vip";
    this.input.setEnabled(false);
    this.vipScreen.open(this.progress);
  }
  private closeVip(): void {
    if (this.state !== "vip") return;
    this.vipScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
  }
  /** 10.9BG — o tier VIP DERIVA dos pontos (💎 gastos/comprados). Chamado a cada ponto ganho e no load. */
  private checkVipTier(): void {
    const want = this.progress.vipTierFromPoints();
    if (want <= this.progress.vipTier) return;
    const tiers = GameConfig.vipTiers;
    for (let t = this.progress.vipTier + 1; t <= want; t++) {
      const cfg = tiers[t - 1];
      for (const areaId of cfg.areas) this.progress.unlockVipArea(areaId);
      events.emit("vip:unlocked", { areaId: cfg.areas[cfg.areas.length - 1] ?? "" });
    }
    this.progress.vipTier = want;
    Vip.setTier(want);
    this.equipment.extraCapacity = this.progress.bagSlots + Vip.bagBonus; // 10.9J7 — bônus de bolsa do novo tier
    this.banner.show(i18n.t("vip.tierUp", { tier: i18n.t(tiers[want - 1].nameKey) }), "boss", 2800);
    this.vipScreen.refresh(this.progress);
    this.chain.setFromBag(this.equipment.bag, this.equipment.equipped.weapon, this.chainType, this.chainSlotLimit(), this.classDef()?.allowedWeapons, (i) => this.equipment.canEquipLevel(i, this.xp.level)); // 10.9L0 — virou VIP: slot extra na hora
    this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
    this.recalcPlayer();
    this.audio.playSfx("level_up");
    this.save.save();
  }
  /** 10.9BE — recompensas de anúncio da loja: 💎 grátis e booster de XP. */
  private async adReward(p: "freeDiamonds" | "xpBoost"): Promise<void> {
    const ok = await Ads.show(p);
    if (!ok) { this.banner.show(i18n.t("ads.notRewarded"), "info", 1600); return; }
    if (p === "freeDiamonds") {
      this.progress.addDiamonds(GameConfig.ads.freeDiamonds);
      this.banner.show(`+${GameConfig.ads.freeDiamonds} 💎`, "boss", 1800);
    } else {
      this.progress.extendXpBoost(GameConfig.ads.xpBoostSeconds, 1.5);
      this.xp.boostMult = this.progress.xpBoostMult;
      this.banner.show("⚡ +50% XP por 15 min", "boss", 1800);
    }
    this.audio.playSfx("level_up");
    this.vipScreen.refresh(this.progress);
    this.save.save();
  }

  private buyDiamondPack(packId: string): void {
    const pack = GameConfig.diamondPacks.find(p => p.id === packId);
    if (!pack) return;
    if (!GameConfig.billing.enabled) { this.banner.show(i18n.t("vip.soon"), "info", 1800); return; } // 10.9FB
    // 10.6W — MODO TESTE: concede diamantes SEM COBRAR de verdade. UI mostra que é teste.
    // Quando plugar o Google Play Billing, essa função vira o callback de "compra confirmada".
    const tier = this.progress.vipTier;
    const bonus = pack.vipBonusPct[tier] ?? 0;
    // 10.9J8 — 1ª compra de qualquer pacote vem com bônus (uma vez só por conta): reduz o medo do
    // primeiro gasto, o gatilho de conversão mais comum em jogo free-to-play. Não muda preço nem
    // poder — só a quantidade dessa vez.
    // 10.9L10 — Thiago: "12 mil diamantes não pode nunca dar o dobro, no máximo 30%". Dobrar o
    // maior pacote (12.000 → 24.000 numa tacada só) inflava demais. `firstBonusPct` no pacote
    // deixa isso configurável; sem ele, continua dobro (100%) como sempre foi pros menores.
    const isFirst = !this.progress.firstPackBought;
    const firstPct = (pack as { firstBonusPct?: number }).firstBonusPct ?? 100;
    const total = Math.round(pack.diamonds * (1 + bonus / 100) * (isFirst ? 1 + firstPct / 100 : 1));
    if (isFirst) this.progress.firstPackBought = true;
    this.progress.addDiamonds(total);
    // 10.9K2 — AUDITORIA DE JUSTIÇA: aqui somava pontos VIP na COMPRA, e spendDiamonds somava de
    // novo no GASTO — 💎 comprado contava 2×, o grátis 1× (um pacote Lenda na 1ª compra chegava ao
    // Mestre gastando só 1/4). Agora ponto VIP vem só do gasto, pra todo 💎, pago ou grátis.
    const firstKey = firstPct >= 100 ? "vip.testPurchaseFirst" : "vip.testPurchaseFirstPct";
    this.banner.show(i18n.t(isFirst ? firstKey : "vip.testPurchase", { amount: String(total), pct: String(firstPct) }), "boss", 2600);
    this.vipScreen.refresh(this.progress);
    this.audio.playSfx("level_up");
    this.save.save();
  }

  /** 10.6T — Loja de Diamantes: gastar 💎 em baús, boosters, consumíveis, skins. */
  private buyDiamondItem(itemId: string): void {
    const item = GameConfig.diamondShop.items.find(x => x.id === itemId);
    if (!item) return;
    const tier = this.progress.vipTier;
    const disc = GameConfig.diamondShop.vipDiscountPct[tier] ?? 0;
    let cost = Math.max(1, Math.round(item.cost * (1 - disc / 100)));
    if (item.kind === "bag") { // 10.9HA — preço sobe por compra; no teto não vende
      const info = bagUpgradeInfo(this.progress.bagSlots, item.cost);
      if (info.full) { this.banner.show(i18n.t("dshop.bagMax", { max: String(info.max) }), "info", 1800); return; }
      cost = Math.max(1, Math.round(info.price * (1 - disc / 100)));
    }
    if (item.kind === "outfit" && item.outfitId && this.progress.ownsOutfit(item.outfitId)) {
      this.banner.show(i18n.t("dshop.alreadyOwned"), "info", 1600); return;
    }
    if (!this.progress.spendDiamonds(cost)) {
      this.banner.show(i18n.t("vip.notEnough"), "info", 1800); return;
    }
    switch (item.kind) {
      case "bag": { // 10.9HA
        const c = GameConfig.equipment.bagUpgrade;
        this.progress.bagSlots = Math.min(c.max - GameConfig.equipment.capacity, this.progress.bagSlots + c.step);
        this.equipment.extraCapacity = this.progress.bagSlots + Vip.bagBonus; // 10.9J7
        this.banner.show(i18n.t("dshop.bagBought", { n: String(this.equipment.capacity), max: String(c.max) }), "info", 2200);
        this.audio.playSfx("chest_open");
        break;
      }
      case "chest": {
        // Baú premium: reusa o rollChest com tipo bom conforme rarityMin.
        // 10.9K2 — AUDITORIA: o Épico (180💎, rarityMin 3) caía no baú "void" — lendário 0%, PIOR que o
        // Raro de 50💎 — e o Mítico (2200💎) era idêntico ao Lendário (700💎); o rarityMin nunca era
        // usado. Agora a escada sobe de verdade: raro/épico → "alpha" (2 itens), lendário/mítico →
        // "colossus" (3 itens + chance de arma de boss), e o 1º item sai com PELO MENOS a raridade do
        // nome do baú (teto por nível continua valendo).
        const min = item.rarityMin ?? 2;
        const t: ChestType = min >= 4 ? "colossus" : "alpha";
        const guaranteed = RARITIES[Math.max(0, Math.min(RARITIES.length - 1, min))];
        this.spawnChestLoot(t, this.player.position.x, this.player.position.z, guaranteed);
        break;
      }
      // 10.9K2 — case "coins" removido junto com o coinPack (💎 → 🪙 → atributo permanente)
      case "shards":
        // shards vão para a arma equipada (se houver); senão banner de aviso
        if (this.equipment.equipped.weapon) {
          this.equipment.addShards(this.equipment.equipped.weapon.definitionId, item.amount ?? 0);
          this.banner.show(`+${item.amount} ✨`, "info", 1600);
        } else {
          this.progress.addDiamonds(cost); // devolve o custo
          this.banner.show(i18n.t("dshop.noWeapon"), "info", 1800); return;
        }
        break;
      case "booster":
        // 10.9L19 — comprar de novo com o booster ainda ativo SOMA tempo (empilha), não substitui
        // (Thiago: "quando compra exp essas coisas no jogo não tá somando").
        if (item.boosterType === "xp") {
          this.progress.addXpBoost(item.duration ?? 1800, item.mult ?? 2);
          this.xp.boostMult = this.progress.xpBoostMult;
        } else if (item.boosterType === "coin") {
          this.progress.addCoinBoost(item.duration ?? 1800, item.mult ?? 2);
        } else if (item.boosterType === "luck") {
          // 10.9I — SORTE: enquanto ativo, todo drop rola raridade melhorada (2 rolls, o melhor)
          this.progress.addDropBoost(item.duration ?? 1800);
        } else if (item.boosterType === "combo") {
          // 10.9I — COMBO: XP e moedas dobrados juntos, por mais tempo (melhor custo-benefício)
          const m = item.mult ?? 2, d = item.duration ?? 3600;
          this.progress.addXpBoost(d, m); this.xp.boostMult = m;
          this.progress.addCoinBoost(d, m);
        }
        this.banner.show(i18n.t(item.nameKey) + " ✓", "info", 1800);
        break;
      case "elixir":
        // 10.9O — elixires de proteção da forja
        this.progress.addElixirs(item.amount ?? 1);
        this.banner.show(i18n.t("dshop.elixirGot", { n: String(item.amount ?? 1) }), "info", 1800);
        break;
      case "bless":
        // 10.9AD — bênçãos: protegem contra a perda de item ao morrer
        this.progress.addBless(item.amount ?? 1);
        this.banner.show(i18n.t("dshop.blessGot", { n: String(item.amount ?? 1) }), "info", 1800);
        break;
      case "outfit":
        if (item.outfitId) {
          this.progress.addOutfit(item.outfitId);
          this.banner.show(i18n.t("dshop.outfitUnlocked"), "info", 2000);
        }
        break;
    }
    this.vipScreen.refresh(this.progress);
    this.audio.playSfx("level_up");
    this.save.save();
  }

  /** 10.6U — Loja de Atributos: comprar melhorias PERMANENTES.
   * 10.9J7 — era 💎 com desconto de tier VIP: dinheiro real comprando atributo de combate direto,
   * mais barato ainda pra quem tinha VIP. Agora custa 🪙 Fragmentos (moeda que se ganha jogando) e
   * SEM desconto de tier — o desconto do diamondShop continua valendo só pra compras de 💎 mesmo. */
  private buyAttrItem(itemId: string): void {
    const item = GameConfig.attrShop.items.find(x => x.id === itemId);
    if (!item) return;
    if (!this.progress.spendCoins(item.cost)) {
      this.banner.show(i18n.t("shop.noCoins"), "info", 1800); return;
    }
    this.progress.recordShopBuy(`attr:${item.id}`); // 10.9EV — conta a compra (mostra "comprado Nx")
    if (item.kind === "hp") this.progress.absorbHp += item.amount;
    else if (item.kind === "damage") this.progress.absorbPower += item.amount;
    else if (item.kind === "regen") this.progress.absorbRegen += item.amount;
    this.recalcPlayer();
    this.banner.show(`+${item.amount} ${item.kind === "hp" ? "❤️" : item.kind === "damage" ? "⚔️" : "💚"}`, "boss", 2200);
    this.vipScreen.refresh(this.progress);
    this.audio.playSfx("level_up");
    this.save.save();
  }

  /** 10.9AE — Passe VIP: comprado com DINHEIRO REAL (modo teste) + BÔNUS de 💎. 30 dias de bônus
   * multiplicativos exclusivos. Quando plugar o billing, esta função vira o callback de "compra ok". */
  private buyVipPass(): void {
    const p = GameConfig.vipPass;
    if (!GameConfig.billing.enabled) { this.banner.show(i18n.t("vip.soon"), "info", 1800); return; } // 10.9FB
    const now = Date.now();
    const base = Math.max(now, this.progress.passExpiresAt);
    this.progress.passExpiresAt = base + p.durationDays * 24 * 60 * 60 * 1000;
    Vip.setPassExpiresAt(this.progress.passExpiresAt);
    // bônus de diamantes concedido junto com o passe
    if (p.diamondBonus > 0) this.progress.addDiamonds(p.diamondBonus); // 10.9K2 — ponto VIP só no gasto (ver buyDiamondPack)
    // 10.9K3 — roupa exclusiva do passe: fica pra sempre (renovar não dá outra)
    const newOutfit = !!p.outfitId && !this.progress.ownsOutfit(p.outfitId);
    if (newOutfit) this.progress.addOutfit(p.outfitId);
    const msg = i18n.t("vip.passActivatedBonus", { days: String(p.durationDays), diamonds: String(p.diamondBonus) });
    this.banner.show(newOutfit ? `${msg} ${i18n.t("vip.passOutfitGot", { name: i18n.t(`outfit.${p.outfitId}.name`) })}` : msg, "boss", 3400);
    this.vipScreen.refresh(this.progress);
    this.chain.setFromBag(this.equipment.bag, this.equipment.equipped.weapon, this.chainType, this.chainSlotLimit(), this.classDef()?.allowedWeapons, (i) => this.equipment.canEquipLevel(i, this.xp.level)); // 10.9L0 — o Passe libera o slot de arma VIP
    this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
    this.audio.playSfx("level_up");
    this.save.save();
  }

  // ---------------------------- 10.5O: MISSÕES ----------------------------
  /** Avança as missões do tipo informado e paga o que for concluído. */
  private missionProgress(kind: "kill" | "elite" | "chest" | "boss" | "territory", amount: number | string = 1): void {
    // 10.6M — a DIÁRIA aproveita os mesmos eventos (não é um contador paralelo que
    // desalinha): o que conta para a missão do dia conta aqui também.
    if (this.daily.advance(kind as DailyKind, kind === "territory" ? 1 : Number(amount) || 1)) {
      this.banner.show(i18n.t("daily.ready"), "boss", 2600);
      this.audio.playSfx("level_up");
    }
    this.progress.daily = this.daily.snapshot();
    const done = kind === "territory" ? this.missions.visitTerritory(String(amount)) : this.missions.advance(kind, Number(amount) || 1);
    if (done.length === 0) {
      this.updateMissionHud();
      return;
    }
    // 10.6F — a missão NÃO paga sozinha mais: ela fica pronta esperando o toque em Coletar.
    // Antes o prêmio caía no mesmo frame em que a missão era trocada e passava batido.
    this.audio.playSfx("level_up");
    this.banner.show(`${i18n.t("mission.ready")}: ${i18n.t(`mission.${done[0].id}`)}`, "info", 2000);
    this.progress.missions = this.missions.snapshot();
    this.updateMissionHud();
    this.missionsScreen.refresh();
    this.save.save();
  }

  /**
   * 10.6H — conta um evento nos OBJETIVOS DA ÁREA (o que destranca o portão) e reavalia o
   * portão na hora. Diferente das missões rotativas: aqui não tem prêmio, tem PASSAGEM.
   */
  private areaGoal(kind: "territories" | "champions" | "kills" | "chests" | "boss", amount = 1): void {
    const rid = this.region.id;
    const justDone = this.areaGoals.advance(rid, kind, amount);
    this.progress.areaGoals = this.areaGoals.snapshot();
    this.refreshGate();
    if (justDone) {
      this.banner.show(i18n.t("area.complete"), "boss", 3000);
      this.audio.playSfx("level_up");
      this.unlockNextRegion(rid, true); // 10.9L3 — só AQUI a próxima região libera (antes: matar o boss bastava)
      // 10.9AX — XP de conclusão da área (escala com a Profundidade) — fecha o buraco até o nível da próxima área
      const axp = Math.round((GameConfig.areaCompleteXp[rid] ?? 0) * (1 + Math.max(0, this.progress.depth - 1) * GameConfig.depth.rewardPerDepth));
      if (axp > 0) { this.xp.gain(axp); window.setTimeout(() => this.banner.show(`✦ +${axp} XP`, "boss", 2400), 1200); }
      this.onAreaCompleted(rid);
    }
  }

  /**
   * 10.6H — PORTÃO TRANCADO. Só acende quando TODOS os objetivos da área estão cumpridos.
   * Antes bastava derrotar o boss, e dava para atravessar o jogo ignorando o mapa inteiro.
   */
  private refreshGate(): void {
    const open = this.areaGoals.isComplete(this.region.id);
    this.rift.setActive(open);
  }

  /** 10.9L3 — região seguinte na ordem do mundo (Floresta → Terras → Tempestade). */
  private nextRegionId(rid: string): string | null {
    const regs = GameConfig.world.regions;
    const i = regs.findIndex((r) => r.id === rid);
    return i >= 0 && i + 1 < regs.length ? regs[i + 1].id : null;
  }
  /** 10.9L3 — área concluída: libera a próxima região (a aba do Mapa passa a viajar pra ela). */
  private unlockNextRegion(rid: string, announce: boolean): void {
    const next = this.nextRegionId(rid);
    if (!next || this.region.isUnlocked(next)) return;
    this.region.unlock(next);
    if (!announce) return;
    const def = this.region.defById(next);
    window.setTimeout(() => this.banner.show(i18n.t("region.unlockedNext", { name: i18n.t(def.nameKey), n: def.recommendedLevel }), "boss", 3600), 2400);
  }
  /**
   * 10.9L3 — ao carregar: a região N só fica liberada se a área N-1 já foi CONCLUÍDA alguma vez (em
   * qualquer Profundidade). Quem ganhou a liberação pelo atalho antigo (matou o boss e nem pisou lá)
   * perde; quem já JOGOU na região (tem mapa revelado/descobertas) mantém — não prende ninguém no
   * meio do caminho. A região atual nunca tranca.
   */
  /**
   * 10.9L6 — "explorar territórios" contava CADA travessia de divisa (ir e voltar fechava a missão).
   * Save antigo: a missão vira o número REAL de territórios visitados (os `terr:` revelados no mapa
   * da região). Área já fechada nesta volta não é tocada (ninguém perde Portal nem região).
   * Profundidade ≥2 (ninguém lá hoje): as descobertas são de voltas anteriores e não dizem o que foi
   * visitado nesta → mantém o número e começa a lista do zero.
   */
  private recountTerritoryGoal(): void {
    if (!this.areaGoals.needsTerritoryRecount) return;
    const depth = Math.max(1, this.progress.depth);
    for (const r of GameConfig.world.regions) {
      const disc = this.region.discoveriesOf(r.id);
      const ids = GameConfig.territories.list.filter((t) => t.regionId === r.id && disc[`terr:${t.id}`] === true).map((t) => t.id);
      const fechada = this.areaGoals.isComplete(r.id) || this.areaGoals.timesCleared(r.id) >= depth;
      if (depth > 1) this.areaGoals.seedTerritories(r.id, fechada ? ids : [], false);
      else this.areaGoals.seedTerritories(r.id, ids, !fechada);
    }
    this.areaGoals.finishTerritoryRecount();
    this.progress.areaGoals = this.areaGoals.snapshot();
  }

  private enforceRegionGates(): void {
    const regs = GameConfig.world.regions;
    for (let i = 1; i < regs.length; i++) {
      const prev = regs[i - 1].id, id = regs[i].id;
      if (this.areaGoals.timesCleared(prev) > 0 || this.areaGoals.isComplete(prev)) this.region.unlock(id);
      else if (this.region.isUnlocked(id) && this.region.id !== id && !this.region.visited(id)) this.region.lock(id);
    }
  }

  /**
   * 10.6H — fechou a ÚLTIMA área: o mundo não acaba, ele afunda. Profundidade +1 zera os
   * objetivos de todas as áreas e RELOUQUEIA o mundo inteiro (territórios/baús), que volta mais
   * forte, pagando mais e dando pontos da Árvore do Void. É o laço de conteúdo sem fim.
   * 10.9HW — NÃO teleporta o Caçador (ele fica onde está, na última área; volta pelo Mapa do Mundo).
   */
  private onAreaCompleted(regionId: string): void {
    const last = GameConfig.world.regions[GameConfig.world.regions.length - 1];
    if (regionId !== last.id) return;
    const d = Math.min(GameConfig.depth.maxDepth, this.progress.depth + 1);
    if (d === this.progress.depth) return;
    this.progress.depth = d;
    // 10.9HS — RELOUQUEIA O MAPA: sem isto, gateLevel(tier, depth) nunca chegava a valer — uma vez
    // conquistado, o território ficava aberto pra sempre (cleared() nunca mais false), então o
    // nível pedido por Profundidade nunca era checado de novo. Agora toda volta pede reconquista
    // (abates/campeão/baú de novo), com o nível-gate maior de cada tier valendo desta vez.
    this.terrProgress.resetMissions();
    this.equipment.resetChests();
    this.chests.refresh((id) => this.equipment.isChestOpen(id));
    this.progress.territories = this.terrProgress.snapshot();
    this.terrGates.setUnlockContext(this.xp.level, d); // 10.7V: portas pedem +nível na nova volta
    this.areaGoals.resetForNewDepth();
    this.progress.areaGoals = this.areaGoals.snapshot();
    // 10.9HT — cada volta de Profundidade também paga pontos da Árvore do Void: sem isso, fechar
    // a árvore inteira (~867-871 pontos) só de nível levaria ao Nv~1735 (~1.690h). Reaproveita o
    // cano `voidPointsBought` (existia mas nada o incrementava — "comprar na Loja" nunca foi ligado).
    this.progress.voidPointsBought += GameConfig.voidTree.pointsPerDepth;
    this.tree.setBoughtPoints(this.progress.voidPointsBought);
    this.updateTreeBadge();
    this.banner.show(i18n.t("depth.new", { n: d }), "boss", 3600);
    // 10.9HW — avisa que a volta pagou pontos da Árvore (senão os pontos "aparecem do nada")
    this.banner.show(i18n.t("depth.treePoints", { n: GameConfig.voidTree.pointsPerDepth }), "info", 3000);
    // 10.6R: diamantes + mail a cada 5 profundidades
    // 10.9K4 — AUDITORIA: pagava 2× (10💎 direto AQUI + 10💎 no correio). Agora só o correio, que
    // já é de-uma-vez por profundidade (Mail.once) e usa o mesmo valor do GameConfig.
    if (d % 5 === 0) {
      this.mailSystem.sendDepthMilestone(d);
      this.updateMailDot();
    }
    this.save.save();
  }

  /** Multiplicadores da volta atual (Profundidade) aplicados aos monstros. */
  private depthScale(): { hp: number; dmg: number } {
    const c = GameConfig.depth;
    const over = Math.max(0, this.progress.depth - 1);
    return { hp: Math.min(c.maxHpMult, 1 + over * c.hpPerDepth), dmg: Math.min(c.maxDmgMult, 1 + over * c.dmgPerDepth) }; // 10.9BM: com teto
  }

  /**
   * 10.9IY — REMOVIDO o botão "Reiniciar missões" das Configurações. Thiago: "remover o botão de
   * reiniciar missões mantendo os itens, não tem lógica mais isso". Fazia sentido em dev, testando
   * sozinho pra recomeçar o mapa sem perder nível/equipamento (10.6N) — mas exposto a QUALQUER
   * jogador oficial, num botão de Configurações ao lado de idioma e som, era um risco: apagava
   * abates/campeões/baús do território sem benefício nenhum pra quem já está de verdade jogando.
   * A funcao de PROFUNDIDADE que faz a mesma coisa internamente (relouqueia o mapa a cada volta,
   * em `onAreaCompleted`) continua intocada — só o atalho manual do jogador foi embora.
   */

  /**
   * 10.6N — PERIGO AMBIENTAL. Territórios de gelo, fogo, vendaval e tempestade drenam vida
   * de quem entra sem o anel correspondente equipado. Tem alguns segundos de carência (dá
   * para atravessar correndo) e um aviso claro na tela, porque morrer sem entender por quê
   * é o pior jeito de ensinar uma regra.
   */
  private updateHazard(dt: number): void {
    const t = this.territories.at(this.player.position.x, this.player.position.z);
    const hz = t?.biome.hazard;
    const acc = this.equipment.equipped.accessory;
    const def = acc ? definitionById(acc.definitionId) : null;
    const protectedBy = def && !isWeaponDef(def) ? (def as { protects?: string }).protects : undefined;
    if (!hz || this.player.inSanctuary || protectedBy === hz) {
      this.hazardTimer = 0;
      this.hazardAction = null;
      if (this.hazardHud.hidden === false) this.hazardHud.hidden = true;
      return;
    }
    const c = GameConfig.hazard;
    this.hazardTimer += dt;
    this.hazardHud.hidden = false;
    const left = Math.max(0, c.graceSeconds - this.hazardTimer);
    // 10.9L8 — o aviso dizia só "sem o anel certo": agora diz QUAL anel (o nome é trocado de propósito:
    // Brasa protege do frio, Geada do calor) e ONDE ele está. Tocar resolve: equipa na hora se está na
    // Bolsa, ou abre a Loja já no anel. Só remonta o HTML quando o texto muda (roda todo frame).
    const a = this.hazardAdvice(hz);
    const cls = `hazard-hud hz-${hz}${left > 0 ? " grace" : " hurt"}${a.action ? " tap" : ""}`;
    const head = left > 0
      ? i18n.t("hazard.warn", { what: i18n.t(`hazard.${hz}`), n: Math.ceil(left) })
      : i18n.t("hazard.hurt", { what: i18n.t(`hazard.${hz}`) });
    const html = `<span class="hz-h">${head}</span><span class="hz-a">${a.text}</span>`;
    if (this.hazardHud.className !== cls) this.hazardHud.className = cls;
    if (this.hazardHudHtml !== html) { this.hazardHud.innerHTML = html; this.hazardHudHtml = html; }
    this.hazardAction = a.action;
    if (left <= 0) this.player.health.damage(this.player.health.max * c.dpsFraction * dt);
  }

  /** 10.9L8 — o que resolve o perigo `hz`: nome do anel, se já está na Bolsa, e o que o toque faz. */
  private hazardAdvice(hz: string): { text: string; action: (() => void) | null } {
    const ringDef = GameConfig.equipment.armor.find((d) => (d as { protects?: string }).protects === hz);
    if (!ringDef) return { text: "", action: null };
    const shopIt = GameConfig.shop.items.find((s) => s.id === ringDef.id);
    const ring = `${shopIt?.icon ?? "💍"} ${i18n.t(ringDef.nameKey)}`;
    if (!this.equipment.isSlotUnlocked("accessory")) return { text: i18n.t("hazard.needSlot", { ring, how: i18n.t("area.goal.boss.voidAlpha") }), action: null };
    const owned = this.equipment.bag.find((it) => it.definitionId === ringDef.id && this.equipment.canEquipLevel(it, this.xp.level));
    if (owned) {
      return {
        text: i18n.t("hazard.equipIt", { ring }),
        action: () => {
          if (this.equipment.equip(owned.instanceId, this.xp.level)) {
            this.afterInventoryChange();
            this.banner.show(i18n.t("inventory.autoEquipped", { name: i18n.t(ringDef.nameKey) }), "info", 1600);
          }
        },
      };
    }
    const price = shopIt ? shopItemPrice(shopIt.id, this.progress.shopCount(shopIt.id)) : 0;
    return {
      text: i18n.t("hazard.buyIt", { ring, n: price }),
      action: shopIt ? () => this.openShop(shopIt.id) : null,
    };
  }

  /** 10.9BE — diária em DOBRO via anúncio (VIP: direto). Só coleta se a recompensa foi liberada. */
  private async claimDailyDouble(): Promise<void> {
    if (!this.daily.current().ready || this.daily.current().claimed) return;
    const ok = await Ads.show("dailyDouble");
    if (!ok) { this.banner.show(i18n.t("ads.notRewarded"), "info", 1600); return; }
    this.claimDaily(2);
  }

  /** 10.6M — coleta o prêmio da missão DIÁRIA. */
  private claimDaily(mult = 1): void {
    const paid = this.daily.claim();
    if (!paid) return;
    const coins = paid.coins * mult, xp = paid.xp * mult, dia = GameConfig.diamonds.perDailyMission * mult;
    this.progress.addCoins(coins);
    this.progress.addDiamonds(dia); // 10.6R
    this.xp.gain(xp);
    const w = this.equipment.equipped.weapon;
    if (paid.shards > 0 && w) this.equipment.addShards(w.definitionId, paid.shards * mult);
    this.banner.show(`${i18n.t("daily.done")}${mult > 1 ? " ×2" : ""} — +${coins} 🪙  +${xp} XP  +${dia} 💎`, "boss", 2800);
    this.audio.playSfx("chest_open");
    this.particles.burst(this.player.position.x, 1.2, this.player.position.z, 0x8fe8ff, { count: 20, speed: 6, dur: 0.7 });
    this.progress.daily = this.daily.snapshot();
    this.missionsScreen.refresh();
    this.save.save();
  }

  /** 10.6F — o jogador coletou a recompensa de uma missão pronta. */
  private claimMission(id: string): void {
    const m = this.missions.claim(id);
    if (!m) return;
    this.payMission(m);
    this.progress.missions = this.missions.snapshot();
    this.updateMissionHud();
    this.missionsScreen.refresh();
    this.save.save();
  }

  /** Paga a recompensa da missão (Fragmentos + XP + shards) com aviso claro. */
  private payMission(m: MissionState): void {
    this.progress.addCoins(m.coins); // prêmio cheio (não depende do Contrato de Caça)
    this.xp.gain(m.xp);
    const w = this.equipment.equipped.weapon;
    if (m.shards > 0 && w) this.equipment.addShards(w.definitionId, m.shards);
    const parts = [`+${m.coins} 🪙`, `+${m.xp} XP`];
    if (m.shards > 0 && w) parts.push(`+${m.shards} 🔷`);
    this.banner.show(`${i18n.t("mission.done")}: ${i18n.t(`mission.${m.id}`)} — ${parts.join("  ")}`, "boss", 2600);
    this.audio.playSfx("chest_open");
    this.particles.burst(this.player.position.x, 1.2, this.player.position.z, 0xffd54a, { count: 18, speed: 6, dur: 0.7 });
  }

  /** Faixa da missão ativa no HUD (+ chip do Contrato de Caça e bússola do objetivo). */
  private updateMissionHud(): void {
    const m = this.missions.focus;
    if (!m || this.state !== "playing") {
      this.missionHud.hidden = true;
      this.contractHud.hidden = true;
      return;
    }
    this.missionHud.hidden = false;
    // 10.5Y — bolinha no botão 📜 quando falta pouco (ou quando tem prêmio pra coletar)
    this.menuBar?.setMissionDot(this.missions.hasReady || this.missions.list.some((x) => x.target > 0 && x.progress / x.target >= 0.8));
    // 10.6F — missão pronta: a faixa vira BOTÃO de coletar (o prêmio não cai mais sozinho)
    const ready = m.progress >= m.target;
    this.missionHud.classList.toggle("ready", ready);
    this.missionHud.style.pointerEvents = ready ? "auto" : "none";
    if (ready) {
      this.missionHud.innerHTML =
        `<span class="mh-ic">🎁</span><b>${i18n.t(`mission.${m.id}`)}</b> ` +
        `<span class="mh-claim">${i18n.t("mission.claim")}</span>`;
      return;
    }
    const dir = this.missionCompass(m.kind);
    this.missionHud.innerHTML =
      `<span class="mh-ic">📜</span><b>${i18n.t(`mission.${m.id}`)}</b> ` +
      `<span class="mh-prog">${m.progress}/${m.target}</span> <span class="mh-rw">+${m.coins}🪙</span>` +
      (dir ? `<span class="mh-dir" style="transform:rotate(${dir.deg}deg)">➤</span><span class="mh-dist">${dir.dist}m</span>` : "");
    // 10.9AS — DIÁRIA visível na tela (linha abaixo da missão)
    const d = this.daily.current();
    if (d && !d.claimed) {
      this.missionHud.innerHTML += `<div class="mh-daily">☀️ <b>${i18n.t(`mission.${d.t.id}`)}</b> <span class="mh-prog">${Math.min(d.progress, d.target)}/${d.target}</span>${d.ready ? " ✅" : ""}</div>`;
    }
    // 10.5T — Contrato de Caça: mostra que está ativo e quanto falta
    if (this.contractTimer > 0) {
      const s = Math.ceil(this.contractTimer);
      this.contractHud.hidden = false;
      this.contractHud.innerHTML = `📜 <b>+${Math.round((GameConfig.shop.contract.coinMult - 1) * 100)}%</b> 🪙 · ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    } else {
      this.contractHud.hidden = true;
    }
  }

  /** 10.5T — BÚSSOLA da missão: ângulo/distância até o objetivo mais próximo (quando existe). */
  private missionCompass(kind: string): { deg: number; dist: number } | null {
    const p = this.player.position;
    let tx: number | null = null;
    let tz = 0;
    if (kind === "chest") {
      let best = Infinity;
      for (const c of GameConfig.chests.world) {
        if ((c as { region?: string }).region !== this.region.id) continue;
        if (this.equipment.isChestOpen(c.id)) continue;
        const d = (c.x - p.x) ** 2 + (c.z - p.z) ** 2;
        if (d < best) { best = d; tx = c.x; tz = c.z; }
      }
    } else if (kind === "territory") {
      const disc = this.region.activeDiscoveries();
      let best = Infinity;
      for (const t of this.territories.all) {
        if (disc[`terr:${t.def.id}`] === true) continue;
        const d = (t.cx - p.x) ** 2 + (t.cz - p.z) ** 2;
        if (d < best) { best = d; tx = t.cx; tz = t.cz; }
      }
    } else if (kind === "boss" && this.region.id === "forgottenForest") {
      const a = GameConfig.boss.voidAlpha.arena.center;
      tx = a.x;
      tz = a.z;
    } else if (kind === "elite") {
      let best = Infinity;
      for (const t of this.territories.all) {
        if (!t.def.champion) continue;
        const d = (t.cx - p.x) ** 2 + (t.cz - p.z) ** 2;
        if (d < best) { best = d; tx = t.cx; tz = t.cz; }
      }
    }
    if (tx === null) return null;
    const dx = tx - p.x;
    const dz = tz - p.z;
    const dist = Math.round(Math.hypot(dx, dz));
    if (dist < 4) return null;
    // 0° = seta apontando para cima na tela = para o NORTE do mundo (z negativo)
    const deg = Math.round((Math.atan2(dx, -dz) * 180) / Math.PI);
    return { deg, dist };
  }

  /** 10.5U — mostra a dica do tutorial e guarda que ela já apareceu. */
  private showTip(id: TipId): void {
    this.banner.show(`💡 ${i18n.t(`tip.${id}`)}`, "info", 4000);
    this.progress.tips = this.tutorial.snapshot();
    this.save.save();
  }

  /** 10.5L — ganho de Fragmentos passando pelo Contrato de Caça (bônus temporário). */
  private addCoins(base: number): void {
    const boost = this.progress.coinBoostSecondsLeft() > 0 ? this.progress.coinBoostMult : 1;
    const mult = Math.min(GameConfig.progression.coinMultCap, (this.contractTimer > 0 ? GameConfig.shop.contract.coinMult : 1) * Vip.coinMult * boost); // 10.9K2 — teto igual ao do XP
    this.progress.addCoins(Math.round(base * mult));
    if (this.progress.coins >= 80) this.tutorial.fire("shop"); // 10.5U
  }

  private buyShopItem(id: string): void {
    const it = GameConfig.shop.items.find((i) => i.id === id);
    if (!it) return;
    // valida pré-requisitos ANTES de cobrar (shards precisa de arma equipada)
    if ((it.kind === "shards" || it.kind === "forge") && !this.equipment.equipped.weapon) {
      this.banner.show(i18n.t("shop.needWeapon"), "danger", 1500);
      return;
    }
    if (it.kind === "pet" && this.progress.petOwned) {
      this.banner.show(i18n.t("shop.alreadyOwned"), "info", 1400);
      return;
    }
    // "Chamado de Elite" só faz sentido fora da base e sem boss em campo
    if (it.kind === "hunt" && (this.player.inSanctuary || this.boss)) {
      this.banner.show(i18n.t("shop.huntFail"), "danger", 1500);
      return;
    }
    const price = shopItemPrice(id, this.progress.shopCount(id));
    if (!this.progress.spendCoins(price)) {
      this.banner.show(i18n.t("shop.noCoins"), "danger", 1400);
      return;
    }
    switch (it.kind) {
      case "heal":
        this.player.health.heal(this.player.health.max);
        break;
      case "hp":
        this.progress.absorb(it.amount, 0);
        this.recalcPlayer();
        break;
      case "power":
        this.progress.absorb(0, it.amount);
        this.recalcPlayer();
        break;
      case "shards": {
        const w = this.equipment.equipped.weapon;
        if (w) this.equipment.addShards(w.definitionId, it.amount);
        break;
      }
      case "potion": // 10.9CZ — poções de vida
        this.progress.addPotions(it.amount);
        this.banner.show(i18n.t("potion.got", { n: String(it.amount) }), "info", 1600);
        break;
      case "pet":
        this.progress.petOwned = true;
        this.applyPet();
        break;
      // 10.6N — ANEL DE PROTEÇÃO: cai direto na bolsa como acessório (o id da loja é o id
      // da definição). Comprável para o jogador nunca ficar travado por sorte de drop.
      case "ring": {
        const inst = createInstance(id, "epic"); // 10.9GJ — anel de protecao vem com atributos (epico = 3 rolls)
        if (inst) {
          this.collectItem(inst);
          this.banner.show(i18n.t("shop.ringBought", { name: i18n.t(`armor.${id}`) }), "info", 2000);
        }
        break;
      }
      // 10.9DL — Ponto do Void NÃO se compra: só sobe de nível. (case removido)
      case "contract":
        this.contractTimer = GameConfig.shop.contract.duration;
        break;
      case "hunt":
        if (!this.spawner.spawnHuntElite(this.player.position, this.region.id)) {
          this.banner.show(i18n.t("shop.huntFail"), "danger", 1500);
        }
        break;
      case "forge": {
        // 10.9P — Fragmentos viram SHARDS da arma equipada (material p/ forjar)
        const wf = this.equipment.equipped.weapon;
        if (wf) this.equipment.addShards(wf.definitionId, it.amount);
        break;
      }
    }
    this.progress.recordShopBuy(id);
    this.audio.playSfx("chest_open");
    this.banner.show(i18n.t("shop.bought", { name: i18n.t(`shop.item.${id}.name`) }), "info", 1500);
    this.shopScreen.refresh(this.progress);
    this.save.save();
  }

  // ---- 10.4Z: Guarda-roupa (roupas/skins) ----
  private openWardrobe(): void {
    if (this.state !== "playing") return;
    this.state = "wardrobe";
    this.input.setEnabled(false);
    this.altarPanel.setPromptVisible(false);
    this.bossPrompt.hidden = true;
    this.wardrobe.open(this.progress);
  }
  private closeWardrobe(): void {
    if (this.state !== "wardrobe") return;
    this.wardrobe.hide();
    this.input.setEnabled(true);
    this.state = "playing";
  }
  /** Possui a roupa → equipa; senão tenta comprar (gasta Fragmentos) e equipa. */
  private chooseOutfit(id: string): void {
    const o = GameConfig.outfits.find((x) => x.id === id);
    if (!o) return;
    if (!this.progress.ownsOutfit(id)) {
      // 10.9J6 — roupa EXCLUSIVA (hoje só "gold"): não compra por Fragmentos aqui, mesmo tendo
      // price:0 no catálogo — ela só entra na posse pela Loja VIP (buyDiamondItem). Sem isto,
      // qualquer jogador conseguia de graça o que a Loja vende por 500💎.
      if ((o as { exclusive?: boolean }).exclusive) {
        this.banner.show(i18n.t((o as { lockKey?: string }).lockKey ?? "wardrobe.exclusive"), "info", 2200);
        return;
      }
      if (!this.progress.spendCoins(o.price)) {
        this.banner.show(i18n.t("shop.noCoins"), "danger", 1400);
        return;
      }
      this.progress.addOutfit(id);
      this.audio.playSfx("chest_open");
    }
    this.progress.selectOutfit(id);
    this.applyClassOutfit(); // 10.9X: custom escolhida OU roupa da classe (se voltar pro padrão)
    this.banner.show(i18n.t("wardrobe.wearing", { name: i18n.t(`outfit.${id}.name`) }), "info", 1400);
    this.wardrobe.refresh(this.progress);
    this.save.save();
  }
  /** 10.5C — ativa/desativa o pet e seu efeito (alcance de coleta de essência, sobe com o nível). */
  private applyPet(): void {
    const on = this.progress.petOwned;
    this.pet.setVisible(on);
    this.essence.reachMult = on ? this.progress.petMagnetMult() : 1;
  }

  private doEquip(id: string): void {
    // 10.6V — checa nível mínimo por raridade antes de equipar
    const inst = this.equipment.bag.find(i => i.instanceId === id);
    if (inst && !this.classAllowsWeapon(inst)) { // 10.9Z — não equipa arma de outra classe
      this.banner.show(i18n.t("class.wrongWeapon"), "info", 1900);
      return;
    }
    if (inst && !this.equipment.canEquipLevel(inst, this.xp.level)) {
      const need = this.equipment.minLevelToEquip(inst); // 10.9AN — raridade E tier da armadura
      this.banner.show(i18n.t("inventory.needLevel", { n: need }), "info", 1800);
      return;
    }
    if (this.equipment.equip(id, this.xp.level)) this.afterInventoryChange();
  }

  /** 10.9HV — equipa a MELHOR arma (maior DPS) entre TODOS os tipos que a classe usa (era um botão
   * por tipo — Thiago: "faz mais sentido um único botão que equipe as armas mais fortes"; pra
   * arqueiro/mago/necro já era só 1 tipo mesmo, então o comportamento deles não muda). A mão fica
   * com a melhor de todas; a corrente (`chainType = null`) enche com as próximas melhores de
   * QUALQUER tipo permitido — não só do mesmo tipo da mão. */
  private doEquipBest(): void {
    const inv = this.equipment;
    const lvl = this.xp.level;
    const types = this.classDef()?.allowedWeapons as readonly string[] | undefined;
    const isAllowed = (it: EquipmentInstance): boolean => {
      const d = definitionById(it.definitionId);
      if (!d || !isWeaponDef(d)) return false;
      return !types || types.includes((d as WeaponDefinition).weaponType);
    };
    const cands = inv.bag.filter(it => isAllowed(it) && inv.canEquipLevel(it, lvl));
    const cur = inv.equipped.weapon;
    const curAllowed = cur ? isAllowed(cur) : false;
    this.chainType = null; // corrente livre: pega as próximas melhores de qualquer tipo permitido
    if (cands.length > 0) {
      let best = cands[0];
      for (const w of cands) if (weaponDps(w) > weaponDps(best)) best = w;
      const alreadyBest = curAllowed && cur && weaponDps(cur) >= weaponDps(best);
      if (!alreadyBest) inv.equip(best.instanceId, lvl);
    }
    // 10.9L7 — Thiago: "da mesma forma que equipa as armas mais fortes, equipa cada item também".
    // Cada peça de armadura vai pra MAIS FORTE da bolsa (mesma conta da seta ▲: armadura base, empate
    // na vida), respeitando nível/tier e slot liberado. O anel que protege do perigo do território
    // atual não sai por um que não protege (e, num território com perigo, o anel certo tem prioridade).
    let pecas = 0;
    for (const slot of ["head", "chest", "gloves", "boots", "accessory"] as const) {
      if (!inv.isSlotUnlocked(slot)) continue;
      let pool = inv.bag.filter((it) => it.slot === slot && inv.canEquipLevel(it, lvl));
      const now = inv.equipped[slot];
      const hz = slot === "accessory" ? this.currentHazard() : undefined;
      if (hz && [...pool, ...(now ? [now] : [])].some((it) => this.ringProtects(it) === hz)) {
        pool = pool.filter((it) => this.ringProtects(it) === hz);
        if (now && this.ringProtects(now) !== hz && pool.length) { // sem proteção equipada: põe a melhor que protege
          let bestRing = pool[0];
          for (const it of pool) if (this.itemBeats(it, bestRing)) bestRing = it;
          if (inv.equip(bestRing.instanceId, lvl)) pecas++;
          continue;
        }
      }
      if (pool.length === 0) continue;
      let best = pool[0];
      for (const it of pool) if (this.itemBeats(it, best)) best = it;
      if (now && (!this.itemBeats(best, now) || this.keepsHazardRing(now, best))) continue;
      if (inv.equip(best.instanceId, lvl)) pecas++;
    }
    const inHand = this.equipment.equipped.weapon;
    if (cands.length === 0 && !curAllowed && pecas === 0) {
      this.banner.show(i18n.t("inventory.bestNone"), "info", 1500);
      return;
    }
    this.afterInventoryChange();
    this.invScreen.refresh(lvl);
    const total = (inHand && isAllowed(inHand) ? 1 : 0) + this.chain.weapons.length;
    const armor = pecas > 0 ? i18n.t("inventory.bestArmorSwapped", { n: pecas }) : i18n.t("inventory.bestArmorKept");
    this.banner.show(i18n.t("inventory.bestEquippedAll", { n: total, armor }), "info", 2200);
  }

  /** 10.9L7 — `a` é mais forte que `b` no MESMO slot? Arma = DPS; armadura = armadura base
   *  (empate: vida). É a mesma conta da seta ▲/▼ da Bolsa — o botão faz o que a seta mostra. */
  private itemBeats(a: EquipmentInstance, b: EquipmentInstance): boolean {
    const da = definitionById(a.definitionId);
    if (da && isWeaponDef(da)) return weaponDps(a) > weaponDps(b);
    const pa = armorBaseOf(a), pb = armorBaseOf(b);
    return pa.armor > pb.armor || (pa.armor === pb.armor && pa.hp > pb.hp);
  }
  /** 10.9L7 — perigo do território onde o Caçador está (fogo, gelo…), se houver. */
  private currentHazard(): string | undefined {
    if (this.player.inSanctuary) return undefined;
    return this.territories.at(this.player.position.x, this.player.position.z)?.biome.hazard ?? undefined;
  }
  private ringProtects(it: EquipmentInstance): string | undefined {
    const d = definitionById(it.definitionId);
    return d && !isWeaponDef(d) ? (d as { protects?: string }).protects : undefined;
  }
  /** 10.9L7 — trocar `cur` por `cand` tiraria a proteção do perigo de AGORA? (aí não troca) */
  private keepsHazardRing(cur: EquipmentInstance, cand: EquipmentInstance): boolean {
    if (cur.slot !== "accessory") return false;
    const hz = this.currentHazard();
    return !!hz && this.ringProtects(cur) === hz && this.ringProtects(cand) !== hz;
  }
  private doUnequip(slot: EquipmentSlot): void {
    if (this.equipment.unequip(slot)) this.afterInventoryChange();
    else this.banner.show(i18n.t("inventory.full"), "danger", 1400);
  }
  /**
   * 10.9P — FORJA com risco de quebra. `useElixir` protege da queda.
   * 10.9BH — Thiago: "não dá mensagem de sucesso/falha e não gasta o elixir". Causas: (1) o banner
   * fica ATRÁS do overlay do inventário → agora devolve o resultado e a tela da forja mostra um
   * toast próprio; (2) o elixir só era consumido quando SALVAVA (quebra rara no início) → agora é
   * consumido em TODA forja protegida com risco > 0 (é um consumível, como o jogador espera).
   */
  private doUpgrade(id: string, useElixir = false): { text: string; kind: "ok" | "bad" | "info" } | void {
    const inst = this.equipment.findAnywhere(id);
    if (!inst) return;
    const bc = this.equipment.breakChance(inst);
    const protect = useElixir && this.progress.elixirs > 0 && bc > 0;
    // 10.9BI — a forja custa FRAGMENTOS além dos shards (confere antes; cobra só se a forja rodar)
    const coins = this.equipment.upgradeCoins(inst);
    if (Number.isFinite(coins) && this.progress.coins < coins) return { text: i18n.t("forge.noCoins", { n: String(coins) }), kind: "info" };
    if (!this.equipment.canUpgrade(inst) && inst.level < GameConfig.equipment.upgradeMaxLevel) return { text: i18n.t("forge.noShards"), kind: "info" };
    const res = this.equipment.forge(id, protect);
    if (res.result === "nomat") return { text: i18n.t("forge.noShards"), kind: "info" };
    if (res.result === "max") return { text: i18n.t("equipment.maxLevel"), kind: "info" };
    if (res.result === "none") return;
    this.progress.spendCoins(coins);
    if (protect) this.progress.spendElixir(); // consumível: gasta na tentativa protegida
    let out: { text: string; kind: "ok" | "bad" | "info" };
    switch (res.result) {
      case "up":
        out = { text: i18n.t("forge.up", { n: String(inst.level) }) + (protect ? " · 🧪−1" : ""), kind: "ok" };
        this.audio.playSfx("level_up");
        break;
      case "saved":
        out = { text: i18n.t("forge.saved", { n: String(inst.level) }), kind: "info" };
        this.audio.playSfx("chest_open");
        break;
      default:
        out = { text: i18n.t("forge.broke", { n: String(inst.level) }), kind: "bad" };
        this.cameraRig.impulse(0.2);
        break;
    }
    this.afterInventoryChange();
    return out;
  }
  private doConvert(id: string): void {
    const n = this.equipment.convertToShards(id);
    if (n > 0) this.afterInventoryChange();
  }
  /** 10.9K9 — troca da fusão (10.9K5) pela evolução, 1x por personagem:
   *  (1) as cópias IGUAIS que a regra antiga guardava na bolsa (até 5) viram barra + fragmentos;
   *  (2) quem comprou a "Fusão Automática" (150💎) recebe os 💎 de volta pelo Correio. */
  private migrateToEvolution(): void {
    let mudou = false;
    if (!this.progress.evolveMigrated) {
      this.progress.evolveMigrated = true;
      mudou = true;
      const n = this.equipment.absorbDuplicateWeapons();
      if (n > 0) this.banner.show(i18n.t("evolve.migrated", { n }), "info", 4200);
    }
    if (this.progress.autoFuseOwned) {
      this.progress.autoFuseOwned = false;
      mudou = true;
      this.mailSystem.send("mail.refund.title", "mail.refund.body", { diamonds: 150 });
      this.updateMailDot();
    }
    if (mudou) this.save.save();
  }

  /** 10.9K9 — EVOLUÇÃO: toda arma com a barra cheia (e nível pra raridade nova) sobe de raridade, em
   *  cascata (a nova raridade pode já ter barra cheia também). Automática pra todo mundo. */
  private evolveAll(): void {
    let n = 0;
    for (let guard = 0; guard < 50; guard++) {
      const pool = [...this.equipment.bag, ...(this.equipment.equipped.weapon ? [this.equipment.equipped.weapon] : [])];
      const g = pool.find((i) => i.slot === "weapon" && this.equipment.evolveInfo(i.definitionId, i.rarity, this.xp.level).ready);
      if (!g) break;
      const out = this.equipment.evolve(g.definitionId, g.rarity, this.xp.level);
      if (!out) break;
      n++;
      this.banner.show(i18n.t("evolve.done", { name: itemName(out), rarity: rarityName(out) }), "boss", 3000);
      reportDrop(out.definitionId, out.rarity, "fusion", this.progress.classId || null); // 10.9K8 — feed do site ("evolução")
    }
    if (n > 0) {
      this.audio.playSfx("level_up");
      this.recalcPlayer();
      this.chain.setFromBag(this.equipment.bag, this.equipment.equipped.weapon, this.chainType, this.chainSlotLimit(), this.classDef()?.allowedWeapons, (i) => this.equipment.canEquipLevel(i, this.xp.level));
      this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
      this.invScreen.refresh(this.xp.level);
      this.save.save();
    }
  }
  /** 10.9M — vende vários itens da bolsa por moedas. */
  private doSell(ids: readonly string[]): void {
    // 10.9L17 — trava de segurança: arma presa na corrente nunca é vendida, mesmo se o id
    // chegar aqui de algum jeito (a tela já esconde essas linhas do painel de venda).
    const chained = new Set(this.chain.weapons.map((w) => w.instanceId));
    ids = ids.filter((id) => !chained.has(id));
    if (ids.length === 0) return;
    const coins = this.equipment.sell(ids);
    if (coins <= 0) return;
    this.progress.addCoins(coins);
    this.banner.show(i18n.t("sell.sold", { n: String(ids.length), coins: String(coins) }), "info", 1800);
    this.afterInventoryChange();
  }
  /** 10.9M — define a raridade-teto da auto-venda (drops até ela viram 🪙 na hora). */
  private setAutoSell(rarity: string): void {
    this.progress.autoSellRarity = rarity;
    this.save.save();
  }
  /** 10.9M — se a auto-venda cobre esta raridade e o item ficou na bolsa (não equipou), vende. */
  private autoSellIfEnabled(inst: EquipmentInstance): void {
    const th = this.progress.autoSellRarity;
    if (!th || th === "off") return;
    if (rarityIndex(inst.rarity) > rarityIndex(th as EquipmentInstance["rarity"])) return; // acima do teto: mantém
    if (!this.equipment.bag.some((i) => i.instanceId === inst.instanceId)) return; // já equipado/saiu da bolsa
    const coins = this.equipment.sell([inst.instanceId]);
    if (coins > 0) { this.progress.addCoins(coins); this.lootToast.note(i18n.t("sell.autoSold", { coins: String(coins) })); }
  }
  /** Após qualquer mudança de inventário: recalcula, atualiza UI e salva. */
  private afterInventoryChange(): void {
    this.recalcPlayer();
    this.chain.setFromBag(this.equipment.bag, this.equipment.equipped.weapon, this.chainType, this.chainSlotLimit(), this.classDef()?.allowedWeapons, (i) => this.equipment.canEquipLevel(i, this.xp.level)); // 10.5H: refaz a corrente
    this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
    this.invScreen.refresh(this.xp.level);
    this.save.save();
  }

  private pause(): void {
    if (this.state !== "playing") return;
    this.endGuideIfRunning(); // 10.9IH
    this.save.save(); // checkpoint ao pausar (robustez de progresso)
    this.state = "paused";
    this.input.setEnabled(false);
    this.altarPanel.setPromptVisible(false);
    this.pauseMenu.show();
  }

  private resume(): void {
    if (this.state !== "paused") return;
    this.state = "playing";
    this.pauseMenu.hide();
    this.input.setEnabled(true);
  }

  private openSettings(from: GameState): void {
    this.settingsReturn = from;
    this.mainMenu.hide();
    this.pauseMenu.hide();
    // 10.9AD — aberto direto do HUD (botão ⚙️ na barra): pausa o jogo e esconde o HUD.
    if (from === "playing") {
      this.hud.hide();
      this.equipBar.hide();
      this.minimap.hide();
      this.menuBar.hide();
      this.input.setEnabled(false);
    }
    this.settings.show();
    this.state = "settings";
  }

  private closeSettings(): void {
    this.settings.hide();
    if (this.settingsReturn === "menu") {
      this.mainMenu.show();
      this.state = "menu";
    } else if (this.settingsReturn === "playing") {
      // 10.9AD — volta direto pro jogo (não passa pelo menu de pausa)
      this.hud.show();
      this.equipBar.show();
      this.minimap.show();
      this.menuBar.show();
      this.input.setEnabled(true);
      this.state = "playing";
    } else {
      this.pauseMenu.show();
      this.state = "paused";
    }
  }

  private openAltar(): void {
    this.state = "altar";
    this.input.setEnabled(false);
    this.altarPanel.open(this.altar, this.inventory);
  }

  private closeAltar(): void {
    if (this.state !== "altar") return;
    this.altarPanel.close();
    this.input.setEnabled(true);
    this.state = "playing";
  }

  private onPlayerDied(): void {
    // 10.9EO — na RAID morrer nao custa nada: volta em segundos na borda da arena
    if (this.raid.active) { this.hook.reset(); this.raid.onLocalDeath(); return; }
    this.state = "dead";
    this.renderer.screenFlash(0.35); // 10.9GT — clarão + câmera lenta na morte
    this.slowMotion(0.3, 1.1);
    this.tutorial.fire("death"); // 10.5U
    this.endGuideIfRunning(); // 10.9IH
    this.input.setEnabled(false);
    this.hook.reset();
    // 10.9BF — o boss NÃO é limpo ao morrer: reviver por anúncio continua a luta de onde parou
    // (o boss fica congelado enquanto a tela de morte está aberta). Só limpa ao renascer na base.
    this.altarPanel.setPromptVisible(false);
    // 10.9BE — a penalidade (Bless/item) só é aplicada ao RENASCER na base: reviver por anúncio não perde nada
    this.deathPenaltyPending = true;
    this.deathPos.copy(this.player.position);
    this.respawnTimer = GameConfig.respawn.delay;
    this.deathScreen.setCountdown(this.respawnTimer);
    this.deathScreen.setRevive(Ads.available("revive") ? Ads.left("revive") : 0, Vip.active);
    this.deathScreen.setBless(this.progress.bless); // 10.9K4
    this.deathScreen.show();
  }

  private deathPenaltyPending = false;
  private readonly deathPos = new THREE.Vector3();
  private reviving = false;

  /** 10.9BE — REVIVER AQUI (anúncio recompensado; VIP sem vídeo): volta no lugar com 50% de vida, sem penalidade. */
  private async reviveByAd(): Promise<void> {
    if (this.state !== "dead" || this.reviving) return;
    this.reviving = true;
    const ok = await Ads.show("revive");
    this.reviving = false;
    if (this.state !== "dead") return; // o timer renasceu na base enquanto o vídeo rodava
    if (!ok) { this.banner.show(i18n.t("ads.notRewarded"), "info", 1600); return; }
    this.reviveHere();
  }

  /** 10.9K4 — AUDITORIA: a Bênção só protegia a bolsa ao renascer na BASE — mas o "reviver aqui"
   *  por anúncio (ilimitado, decisão do Thiago) já evita a perda de item sem custar nada, então a
   *  Bênção (40💎) quase não tinha uso. Agora ela também é "reviver aqui SEM anúncio": o mesmo que
   *  o anúncio dá de graça, só que sem vídeo (como o VIP) — conveniência, não poder a mais. */
  private reviveByBless(): void {
    if (this.state !== "dead" || this.reviving) return;
    if (!this.progress.consumeBless()) return;
    this.reviveHere();
    this.banner.show(i18n.t("bless.revived", { n: this.progress.bless }), "boss", 2400);
  }

  /** Volta no lugar da morte com 50% de vida, sem penalidade (anúncio ou Bênção). */
  private reviveHere(): void {
    this.deathPenaltyPending = false;
    this.player.respawnAt(this.deathPos.x, this.deathPos.z, 0.5);
    this.cameraRig.snapTo(this.player.position);
    this.deathScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
    this.banner.show(i18n.t("ads.revived"), "boss", 2400);
    this.audio.playSfx("level_up");
    this.particles.burst(this.player.position.x, 1.2, this.player.position.z, 0x8affc0, { count: 26, speed: 7, dur: 0.8 });
    this.save.save();
  }

  private deathPenaltyMsg: { text: string; kind: "boss" | "danger" } | null = null;

  /** 10.9AD — penalidade de morte: com BÊNÇÃO, não perde nada (consome 1); sem ela, perde um
   * item ALEATÓRIO da BOLSA (nunca equipado). A mensagem é mostrada ao renascer (visível no jogo). */
  private applyDeathPenalty(): void {
    if (this.progress.consumeBless()) {
      this.deathPenaltyMsg = { text: i18n.t("bless.saved", { n: this.progress.bless }), kind: "boss" };
      this.save.save();
      return;
    }
    // 10.9J0 — as armas da CORRENTE (flutuam presas, atacam sozinhas) ficam de fora do sorteio: pro
    // jogador elas são equipamento em uso, mesmo sendo, pros dados, itens da bolsa.
    const presas = new Set(this.chain.weapons.map((w) => w.instanceId));
    const lost = this.equipment.loseRandom(presas);
    if (lost) {
      this.deathPenaltyMsg = { text: i18n.t("bless.lost", { item: itemName(lost) }), kind: "danger" };
      this.save.save();
    } else {
      this.deathPenaltyMsg = null; // bolsa vazia: nada a perder
    }
  }

  private respawn(): void {
    if (this.reviving) { this.respawnTimer = 1; return; } // 10.9BE — vídeo rodando: espera decidir
    this.fadeThen(() => this.respawnNow()); // 10.9GT — escurece, renasce no meio, reabre
  }
  private respawnNow(): void {
    if (this.deathPenaltyPending) { this.deathPenaltyPending = false; this.applyDeathPenalty(); } // 10.9AD/BE
    this.clearBoss(); // morrer no boss e renascer na base reseta o desafio (re-desafiar depois)
    // reaparece na Void Sanctuary com 30% do MaxHP; a base regenera o resto
    if (this.inBossZone) this.leaveBossZone(false); // 10.9AJ — morreu na zona: volta pro hub
    this.clearWorld();
    this.player.respawnAt(this.sanctuary.center.x, this.sanctuary.center.z, GameConfig.respawn.hpFraction);
    this.cameraRig.snapTo(this.player.position);
    this.spawner.prewarm(this.player.position);
    this.deathScreen.hide();
    this.input.setEnabled(true);
    this.state = "playing";
    // 10.9AD — mostra o resultado da penalidade de morte já no jogo (Bless salvou / item perdido)
    if (this.deathPenaltyMsg) {
      this.banner.show(this.deathPenaltyMsg.text, this.deathPenaltyMsg.kind, 3000);
      this.deathPenaltyMsg = null;
    }
    this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons); // reflete item perdido
  }

  private handleEscape(): void {
    if (this.state === "playing") this.pause();
    else if (this.state === "paused") this.resume();
    else if (this.state === "settings") this.closeSettings();
    else if (this.state === "altar") this.closeAltar();
    else if (this.state === "inventory") this.closeInventory();
    else if (this.state === "shop") this.closeShop();
    else if (this.state === "wardrobe") this.closeWardrobe();
    else if (this.state === "character" || this.state === "tree") this.closeOverlayToPlaying();
    else if (this.state === "world") this.closeWorld();
    else if (this.state === "mastery") this.closeMastery();
    else if (this.state === "training") this.closeTraining(); // 10.9BY
    else if (this.state === "surge") this.closeSurge();
    else if (this.state === "pvp") this.closePvp();
    else if (this.state === "duel") this.closeDuel();
  }

  // ---- 10.6Q: bosses fixos spawnam ao entrar na arena (cooldown 1h, máx 6/dia) ----
  /** 10.6Y — boss NÃO spawna mais só por chegar perto. Ver `bossReadyToChallenge` +
   * updateContext (o player precisa TOCAR pra desafiar, como no altar). */
  private checkAutoBossSpawn(): void {
    // agora só zera o flag pra release dos cooldowns
  }

  /** Retorna a chave do boss (voidAlpha/aetherColossus) pronto pra desafiar, ou null. */
  private bossReadyToChallenge(): "voidAlpha" | "aetherColossus" | null {
    if (this.boss || this.player.inSanctuary) return null;
    if (!this.progress.canSpawnFixedBoss()) return null;
    const pos = this.player.position;
    if (
      this.region.id === "forgottenForest" &&
      this.progress.alphaGateOpen &&
      (this.fixedBossCooldowns["voidAlpha"] ?? 0) <= 0
    ) {
      const den = GameConfig.boss.voidAlpha.arena;
      if (Math.hypot(pos.x - den.center.x, pos.z - den.center.z) <= den.radius + 2) return "voidAlpha";
    }
    if (
      this.region.id === "shatteredWilds" &&
      (this.colossusUnlocked() || this.progress.bossesDefeated.aetherColossus) &&
      (this.fixedBossCooldowns["aetherColossus"] ?? 0) <= 0 &&
      this.colossusGate.isPlayerNear(pos, GameConfig.colossus.gate.interactRadius + 1)
    ) return "aetherColossus";
    return null;
  }

  private updateFixedBossCooldowns(dt: number): void {
    for (const key of Object.keys(this.fixedBossCooldowns)) {
      if (this.fixedBossCooldowns[key] > 0) this.fixedBossCooldowns[key] -= dt;
    }
  }

  /** 10.7B — Test Arena: teleporta player pra clareira de teste e spawna 1 de cada inimigo. */
  private spawnTestArena(): void {
    // teleporta o Caçador pra uma clareira longe da safe zone
    const target = { x: 20, z: 25 };
    this.player.respawnAt(target.x, target.z, 1);
    this.hook.reset();
    this.autoTarget.clear();
    this.enemies.clear(); // limpa o campo pra o teste ser isolado
    const n = this.spawner.spawnTestArena(this.player.position);
    this.banner.show(i18n.t("test.spawned", { n: String(n) }), "boss", 3200);
    this.audio.playSfx("level_up");
  }

  // ---- Boss ----
  private spawnBoss(): void {
    if (this.boss) return;
    this.enemies.clear(); // arena limpa: os adds virão do Howl
    this.hook.reset(); // evita hook preso num inimigo recém-removido
    this.autoTarget.clear();
    this.telegraph.clear();
    const bs = this.bossScale(GameConfig.world.regions[0].recommendedLevel);
    const bt = this.bossThreat(); // 10.9AP
    this.boss = new VoidAlpha(this.renderer.scene, {
      player: this.player,
      telegraph: this.telegraph,
      particles: this.particles,
      summon: (count, center) => this.summonAdds(count, center),
      impulse: (s) => this.cameraRig.impulse(s),
    }, { hpMult: bs.hp * bt.hp, dmgMult: bs.dmg * bt.dmg }); // 10.5Z nível + 10.9AP ameaça
    this.bossHud.show();
    this.bossPrompt.hidden = true;
    this.banner.show(i18n.t("boss.awakens"), "boss", 2600);
    this.audio.playMusic("boss_theme");
  }

  /** 10.5Z — escala de boss pelo nível acima do recomendado da região. */
  private bossScale(recommended: number): { hp: number; dmg: number } {
    const c = GameConfig.boss.levelScale;
    // 10.9AY — o "nível esperado" do boss da área é o nível de SAÍDA dela (portão da próxima área),
    // não o de entrada: o Alfa contava Nv1 como base e um Nv8 (normal ao chegar nele) já levava
    // +63% de vida/+42% de dano POR CIMA da ameaça. Só escala quem está acima do plano de etapas.
    const regs = GameConfig.world.regions;
    const idx = regs.findIndex((r) => r.recommendedLevel === recommended);
    const expected = idx >= 0 && idx + 1 < regs.length ? regs[idx + 1].recommendedLevel : recommended + 10;
    const over = Math.max(0, this.xp.level - Math.max(recommended, expected));
    return {
      hp: Math.min(c.maxHp, 1 + over * c.hpPerLevel),
      dmg: Math.min(c.maxDmg, 1 + over * c.dmgPerLevel),
    };
  }

  /**
   * 10.9DU — TETO DE GOLPES DO BOSS. A vida do boss soma a Ameaça Crescente (×0.7), a escala
   * de nível da área (até ×3.2) e o tier da sala — com o player batendo 3.2K isso chegava a
   * 1.200.000 (≈375 golpes). Limita a vida a `bossHitsCap` golpes do teu dano efetivo,
   * mantendo a FRAÇÃO de vida atual (as fases/fúria continuam certas) e nunca abaixo do
   * valor base. Roda todo frame: se o teu dano subir no meio da luta, o teto acompanha.
   */
  private capBossHp(): void {
    const b = this.boss;
    if (!b || b instanceof RaidTitan) return; // 10.9EO — a vida do Tita e do SERVIDOR
    const cap = Math.max(GameConfig.threat.bossHpFloor, Math.round(Threat.playerDamage * GameConfig.threat.bossHitsCap));
    if (cap <= 0 || b.health.max <= cap) return;
    const frac = b.health.max > 0 ? b.health.current / b.health.max : 1;
    b.health.max = cap;
    b.health.current = Math.max(1, Math.round(cap * frac));
  }

  /** 10.9AP — bosses ACOMPANHAM a Ameaça Crescente (poder do player: dano, vida efetiva com regen/
   * armadura/resiliência, compras de VIP, tier, profundidade) — como os monstros. Multiplica os
   * multiplicadores próprios de cada spawn (nível, tier de sala, variante, kills de área). */
  private bossThreat(): { hp: number; dmg: number } {
    const c = GameConfig.threat;
    return {
      hp: 1 + (Threat.hpMult - 1) * c.bossHpFollow,
      dmg: 1 + (Threat.dmgMult - 1) * c.bossDmgFollow,
    };
  }

  private summonAdds(count: number, center: THREE.Vector3): void {
    const room = GameConfig.boss.voidAlpha.maxAdds - this.enemies.aliveCount;
    const n = Math.max(0, Math.min(count, room));
    for (let i = 0; i < n; i++) this.spawner.spawnAddNear(center);
  }

  // ---- Fase 9: Aether Colossus ----
  /** Mantém o player dentro da arena do Colossus (parede circular suave). */
  /** 10.9AK — mantém o player dentro do círculo de arena de QUALQUER boss (Apex Fang/Rift Seer,
   * de história, evento, área ou sala da zona). Sem isso o boss parava na borda e o player matava de fora. */
  /**
   * 10.9CK — COLEIRA: o player anda livre pelo mapa durante a luta (o boss fica na arena dele).
   * Longe da borda por `leash.seconds` → o boss perde o alvo, a luta reseta sem punição e o
   * desafio volta a ficar disponível (Guardião continua desperto; sala da Fenda sem cooldown).
   */
  private updateBossLeash(dt: number): void {
    const b = this.boss;
    if (!b) return;
    if (this.inBossZone || this.raid.active) return; // 10.9CN — a Fenda usa PAREDES (arena selada), não coleira · 10.9EO raid
    let cx: number, cz: number, r: number;
    if (b instanceof AetherColossus) {
      const a = GameConfig.boss.aetherColossus.arena;
      cx = a.center.x; cz = a.center.z; r = a.radius;
    } else if (b instanceof VoidAlpha || b instanceof RiftSeer || b instanceof VoidKing) {
      const a = b.arenaInfo;
      cx = a.x; cz = a.z; r = a.r;
    } else return;
    const c = GameConfig.boss.leash;
    const d = Math.hypot(this.player.position.x - cx, this.player.position.z - cz);
    if (d <= r + c.margin) { this.bossLeashTimer = 0; this.bossLeashWarned = false; return; }
    if (!this.bossLeashWarned) {
      this.bossLeashWarned = true;
      this.banner.show(i18n.t("boss.leashWarn", { s: c.seconds }), "danger", 2200);
    }
    this.bossLeashTimer += dt;
    if (this.bossLeashTimer < c.seconds) return;
    this.bossLeashTimer = 0;
    this.bossLeashWarned = false;
    this.clearBoss();
    this.zoneActiveRoom = null;
    this.banner.show(i18n.t("boss.leashReset"), "info", 2800);
  }

  private colossusUnlocked(): boolean {
    const u = GameConfig.colossus.unlock;
    if (this.progress.wardensDefeated < u.wardens) return false;
    const disc = this.region.activeDiscoveries();
    return u.discoveries.every((k) => disc[k] === true);
  }

  private spawnColossus(): void {
    if (this.boss) return;
    this.enemies.clear();
    this.hook.reset();
    this.autoTarget.clear();
    this.telegraph.clear();
    // teleporta o player para a boca da arena (o clamp mantém dentro durante a luta)
    const ac = GameConfig.boss.aetherColossus.arena;
    this.player.teleportTo(ac.center.x, ac.center.z + ac.radius - 2);
    this.cameraRig.setBossZoom(1.4); // afasta p/ ver o Colossus inteiro + telegraphs
    this.cameraRig.snapTo(this.player.position);
    const bt = this.bossThreat(); // 10.9AP — o Colossus também acompanha a ameaça
    this.boss = new AetherColossus(this.renderer.scene, {
      player: this.player,
      telegraph: this.telegraph,
      particles: this.particles,
      impulse: (s) => this.cameraRig.impulse(s),
    }, { hpMult: bt.hp, dmgMult: bt.dmg });
    this.bossHud.show();
    this.bossPrompt.hidden = true;
    this.banner.show(i18n.t("boss.awakensColossus"), "boss", 2600);
    this.audio.playMusic("boss_theme");
  }

  private onColossusDefeated(): void {
    this.gainMastery(GameConfig.mastery.xp.perBoss, "boss");
    this.gainRegionMastery(GameConfig.regionMastery.xp.perBoss); // §31
    const first = this.progress.recordColossusDefeat();
    this.unlockSlot("boots"); // Fase 10: derrotar o Colossus libera o slot BOOTS
    const bc = GameConfig.boss.aetherColossus;
    const r = first ? bc.rewards : bc.refight;
    this.xp.gain(r.xp);
    this.addCoins(GameConfig.shop.coinsPerBoss + (first ? GameConfig.bossCoins.firstKill : GameConfig.bossCoins.refight)); // 10.4Y/10.9BK: moeda (sem 💎)
    if (first) this.mailSystem.sendBossMilestone("aetherColossus");
    this.missionProgress("boss"); // 10.5O
    // 10.9L3 — o Colosso é o boss das Terras: conta o objetivo "boss" (antes não contava → a área nunca fechava)
    if (GameConfig.areaGoalBoss[this.region.id] === "aetherColossus") this.areaGoal("boss");
    for (const [k, v] of Object.entries(r.essence)) this.inventory.add(k as EssenceType, v as number);
    this.banner.show(i18n.t("region.shatteredWildsCleared"), "boss", 2800);
    const p = this.player.position;
    if (first) {
      this.progress.grantAetherCore();
      // 10.9L3 — o Colosso NÃO libera mais a Região 3 sozinho: é uma das missões das Terras (areaGoal)
      window.setTimeout(() => this.banner.show(i18n.t("reward.aetherCore"), "info", 2400), 1000);
      this.spawnChestLoot("colossus", p.x, p.z + 4); // Colossus Chest garantido
      // 10.4P: arma EXCLUSIVA de boss GARANTIDA na 1ª vitória (assinatura do Colossus)
      const it = rollBossWeapon("legendary", this.classWeaponTypes(), this.xp.level); // 10.9CV — arma de boss da classe
      if (it) this.collectItem(it, "boss"); // 10.4S: auto-coleta
    } else {
      if (Math.random() < 0.5) this.spawnChestLoot("colossus", p.x, p.z + 4);
      if (Math.random() < 0.3) {
        const it = rollBossWeapon("rare", this.classWeaponTypes(), this.xp.level); // 10.9CV
        if (it) this.collectItem(it, "boss"); // 10.4S: auto-coleta
      }
    }
    // 10.6Q: cooldown 1h + registro de kill diário (boss fixo)
    this.fixedBossCooldowns["aetherColossus"] = 3600;
    this.progress.recordFixedBossKill();
    this.audio.playSfx("boss_defeated");
    this.save.save();
  }

  /** Alvos que o player pode atingir (inclui o Boss quando ativo). */
  /** Feedback de travessia entre zonas (banner discreto + fundação de áudio). */
  private onZoneChanged(prev: SanctuaryZone, next: SanctuaryZone): void {
    if (next === "sanctuary") {
      this.banner.show(i18n.t("sanctuary.safeZone"), "info", 1100);
      this.audio.playSfx("sanctuary_enter");
      events.emit("sanctuary:enter", undefined);
    } else if (next === "combat" && prev !== "combat") {
      // saiu totalmente do buffer: combate liberado
      this.banner.show(i18n.t("sanctuary.combatZone"), "info", 900);
      this.audio.playSfx("sanctuary_exit");
      events.emit("sanctuary:exit", undefined);
    }
    // "buffer" (transição intermediária) não dispara banner — evita poluição na borda.
  }

  /** Partículas leves de cura subindo até o Player durante qualquer regeneração. */
  private updateHealFx(dt: number, interval = GameConfig.sanctuary.healFxInterval): void {
    if (this.player.health.current >= this.player.health.max) return;
    this.healFxTimer -= dt;
    if (this.healFxTimer > 0) return;
    this.healFxTimer = interval;
    const p = this.player.position;
    this.particles.burst(p.x, 0.2, p.z, 0x8ea2ff, { count: 4, speed: 2.2, dur: 0.7 });
    events.emit("sanctuary:heal", { position: p.clone() });
    // 10.5I: sem som a cada tick de regen (a cada 0,3 s ficava chato) — só as partículas
  }

  private targetsForPlayer(): readonly Damageable[] {
    if (!this.boss) return this.enemies.targets();
    this.comboTargets.length = 0;
    for (const e of this.enemies.enemies) this.comboTargets.push(e);
    this.comboTargets.push(this.boss);
    return this.comboTargets;
  }

  /** Alvos fisgáveis pelo Hook (inclui o Boss quando ativo). */
  private hookTargets(): readonly HookTarget[] {
    if (!this.boss) return this.enemies.enemies;
    this.comboHooks.length = 0;
    for (const e of this.enemies.enemies) this.comboHooks.push(e);
    this.comboHooks.push(this.boss);
    return this.comboHooks;
  }

  private updateRegionAndGate(dt: number): void {
    // 10.5M — TERRITÓRIO atual (banner ao entrar num pedaço novo do mapa: nome + gangue)
    const terr = this.territories.at(this.player.position.x, this.player.position.z);
    const tid = terr?.def.id ?? null;
    if (tid !== this.lastTerritory) {
      if (this.lastTerritory !== null && terr) {
        const gangName = i18n.t(`enemy.${terr.def.gang[0]?.id ?? "voidling"}`);
        this.banner.show(`${i18n.t(terr.def.nameKey)} · ${i18n.t("terr.gang", { name: gangName })}`, "info", 1900);
      }
      this.lastTerritory = tid;
      if (tid) {
        this.missionProgress("territory", tid); // 10.5O: missão de explorar
        if (this.region.discover(`terr:${tid}`)) this.save.save(); // 10.5Q: revela o pedaço no mapa
        this.tutorial.fire("territory"); // 10.5U
        // 10.6H — 10.9L6: só a 1ª entrada em cada território conta (antes cada travessia somava +1)
        if (this.areaGoals.visitTerritory(this.region.id, tid)) this.areaGoal("territories");
      }
    }
    // sub-área da região ativa (usada por descobertas/HUD)
    const sub = this.region.subAreaAt(this.player.position.z);
    if (sub.id !== this.lastRegion) this.lastRegion = sub.id;

    if (this.region.id === "forgottenForest") {
      // 10.9HS — era `!alphaGateOpen`: uma vez aberto o portão do Alfa (pra sempre, inclusive em
      // voltas de Profundidade futuras), esse elite parava de nascer PARA SEMPRE. Thiago: "elites
      // com mais frequência" — agora ele continua rondando o Grove mesmo depois do Alfa.
      if (!this.boss) {
        this.eliteTimer -= dt;
        const aliveElites = this.enemies.enemies.filter((e) => e.elite && !e.isDead).length;
        if (this.eliteTimer <= 0 && aliveElites < GameConfig.gate.eliteMaxAlive && this.player.position.z < 6) {
          this.spawner.spawnElite();
          this.eliteTimer = 2.0;
        }
      }
    } else if (this.region.id === "shatteredWilds") {
      // Região 2: Shard Warden ronda os Warden Grounds (raro; máx 1)
      const we = GameConfig.world.wildsElite;
      this.eliteTimer -= dt;
      const aliveWardens = this.enemies.enemies.filter((e) => e.elite && !e.isDead).length;
      if (this.eliteTimer <= 0 && aliveWardens < we.maxAlive && this.player.position.z < -30) {
        this.spawner.spawnWildsElite();
        this.eliteTimer = we.interval;
      }
    } else if (this.region.id === "tempestReach") {
      // §50: teaser do boss futuro ao alcançar o fundo da Tempest Crown (uma vez por sessão)
      if (!this.stormBeyondShown && this.player.position.z < -54) {
        this.stormBeyondShown = true;
        this.banner.show(i18n.t("region.stormBeyond"), "boss", 2600);
      }
      // Região 3: Tempest Warden ronda Thunderstone Ridge / Tempest Crown (raro; máx 1)
      const te = GameConfig.world.tempestElite;
      this.eliteTimer -= dt;
      const aliveWardens = this.enemies.enemies.filter((e) => e.elite && !e.isDead).length;
      if (this.eliteTimer <= 0 && aliveWardens < te.maxAlive && this.player.position.z < -34) {
        this.spawner.spawnTempestElite();
        this.eliteTimer = te.interval;
      }
    }

    // Rift Surge (§36): mais elites durante o Surge, em qualquer região (frequência ~ tier)
    if (this.surge.anyActive && !this.player.inSanctuary) {
      this.surgeEliteTimer -= dt;
      const aliveElites = this.enemies.enemies.filter((e) => e.elite && !e.isDead).length;
      if (this.surgeEliteTimer <= 0 && aliveElites < 2) {
        this.spawnRegionElite();
        this.surgeEliteTimer = 7 - this.surge.eliteChance(this.region.id) * 8; // tier alto = mais frequente
      }
    }
  }

  /** Spawna o Elite da região ATIVA (usado pelo Rift Surge). */
  private spawnRegionElite(): void {
    if (this.region.id === "shatteredWilds") this.spawner.spawnWildsElite();
    else if (this.region.id === "tempestReach") this.spawner.spawnTempestElite();
    else this.spawner.spawnElite();
  }

  /**
   * 10.9IB — O QUE FALTA pro Portal Rift abrir, com NOMES. Thiago: "informa que está trancado,
   * falta derrotar 3 campeões — preciso informar e mostrar QUAL campeão é". Antes só dizia a
   * quantidade e o tipo da meta ("falta 3 derrotar campeões"), sem dizer onde procurar.
   * Para campeões, lista a CRIATURA e os TERRITÓRIOS onde o campeão ainda não caiu.
   */
  private riftLockedLabel(): string {
    const left = this.areaGoals.remaining(this.region.id);
    if (left.length === 0) return i18n.t("rift.inactive");
    // Prioriza a meta de CAMPEÕES quando ela está pendente: "abater monstros" ou "explorar
    // territórios" se resolvem só jogando, mas "derrotar campeões" não diz QUEM nem ONDE — que é
    // exatamente a queixa. As demais caem no texto curto de sempre.
    const champ = left.find((x) => x.kind === "champions");
    const g = champ ?? left[0];
    const n = Math.max(0, g.target - g.progress);
    if (g.kind === "champions") {
      const pend = this.territories.all.filter((t) =>
        t.def.champion && this.terrProgress.tasks(t.def).some((k) => k.kind === "champion" && k.progress < k.target));
      if (pend.length > 0) {
        const nomes = pend.slice(0, 2).map((t) => i18n.t(t.def.nameKey)).join(", ");
        const resto = pend.length > 2 ? ` +${pend.length - 2}` : "";
        // se todos os pendentes são a MESMA criatura, dá pra nomeá-la (é o caso das 3 áreas)
        const ids = new Set(pend.map((t) => t.def.champion));
        const quem = ids.size === 1 ? i18n.t(`enemy.${pend[0].def.champion}`) : i18n.t("area.goal.champions");
        return i18n.t("rift.lockedChampions", { n: String(n), quem, onde: nomes + resto });
      }
    }
    const rotulo = g.kind === "boss" ? i18n.t(`area.goal.boss.${GameConfig.areaGoalBoss[this.region.id] ?? "guardian"}`) : i18n.t(`area.goal.${g.kind}`); // 10.9L3 — diz QUAL boss
    return i18n.t("area.locked", { n: `${n} ${rotulo}` });
  }

  /** Interação contextual UNIFICADA (uma ação por toque): baú > altar > desafiar boss. */
  private updateContext(): void {
    const pos = this.player.position;
    let chestNear: { id: string; type: ChestType; pos: THREE.Vector3 } | null = null; // 10.9EX
    let text: string | null = null;
    let action: (() => void) | null = null;
    // 10.9IA — TELEPORTE com plaquinha em cima (igual ao baú): nome do destino + toque pra entrar,
    // sem depender do botão "USAR" do centro. `tag` = onde desenhar; `text` continua valendo pros
    // casos INFORMATIVOS (portal trancado, porta sem nível), que seguem no aviso do centro.
    let tag: { label: string; pos: THREE.Vector3; y: number } | null = null;

    // 10.5D — o ALTAR fica DENTRO da base (safe zone), então precisa funcionar mesmo
    // in-sanctuary (antes só ativava pelo lado de fora, parecendo "bugado").
    // 10.9AJ — ZONA DOS BOSSES: portal do hub / portal de volta / desafiar o boss da sala
    const zroom = this.inBossZone && !this.boss ? this.zoneRoomHere() : null;
    if (!this.boss && !this.inBossZone && !this.raid.active && this.bossPortal.isPlayerNear(pos)) {
      tag = { label: `👑 ${i18n.t("bossZone.enter")}`, pos: this.bossPortal.position, y: PORTAL_TAG_Y };
      action = () => this.fadeThen(() => this.enterBossZone()); // 10.9GT
    } else if (this.inBossZone && !this.boss && this.zoneExitPortal.isPlayerNear(pos)) {
      tag = { label: `🏠 ${i18n.t("bossZone.exit")}`, pos: this.zoneExitPortal.position, y: PORTAL_TAG_Y };
      action = () => this.fadeThen(() => this.leaveBossZone(true)); // 10.9GT
    } else if (zroom && this.zoneCanChallenge(zroom)) {
      text = i18n.t("bossZone.challenge", { name: i18n.t(zroom.def.nameKey) });
      action = () => this.challengeZoneRoom(zroom);
    } else if (zroom && this.xp.level >= zroom.def.level && !this.zoneTreeOk(zroom.def)) {
      // 10.9HU — porta física já abriu (nível ok), mas falta fechar a árvore: diz exatamente
      // quanto falta (mesma régua da porta de território — 10.9HP).
      const total = VoidTreeState.totalMaxPoints(this.progress.classId);
      const need = Math.ceil(total * (zroom.def.treePct ?? 0));
      text = i18n.t("bossZone.lockedTree", { name: i18n.t(zroom.def.nameKey), have: this.tree.spent(), need });
    } else if (!this.boss && this.pvpPortal.isPlayerNear(pos)) {
      // 10.9 — PORTAL PvP: fica no Santuário (vale em qualquer região). Abre o lobby.
      tag = { label: i18n.t("pvp.interact"), pos: this.pvpPortal.position, y: PORTAL_TAG_Y };
      action = () => this.openPvp();
    } else if (!this.boss && this.region.id === "forgottenForest" && this.altar.isPlayerNear(pos)) {
      text = i18n.t("altar.interact");
      action = () => this.openAltar();
    } else if (!this.boss && this.region.id === "forgottenForest" && this.trainingDummy.isPlayerNear(pos)) {
      // 10.9BY — Boneco de Treino: skills + treino offline
      text = i18n.t("training.interact");
      action = () => this.openTraining();
    } else if (!this.boss && this.devPanelEnabled() && this.region.id === "forgottenForest" && this.testPad.isPlayerNear(pos)) { // 10.9FO — so dev
      // 10.7B — pad de teste: spawna 1 de cada monstro
      text = i18n.t("test.interact");
      action = () => this.spawnTestArena();
    } else if (!this.boss && this.guardianArena && this.guardianReady(this.region.id) && this.guardianArena.isPlayerInside(pos)) {
      // 10.9CC — Guardião desperto: o player entra na arena e ACEITA (não nasce só de chegar)
      text = i18n.t("guardian.challenge");
      action = () => this.spawnAreaBoss(this.region.id);
    } else if (!this.boss && this.bossReadyToChallenge() === "voidAlpha") {
      // 10.6Y — DESAFIAR BOSS: player precisa TOCAR (não nasce só chegando perto)
      text = i18n.t("boss.challenge");
      action = () => this.spawnBoss();
    } else if (!this.boss && this.bossReadyToChallenge() === "aetherColossus") {
      text = i18n.t("boss.challenge");
      action = () => this.spawnColossus();
    } else if (!this.boss && !this.player.inSanctuary) {
      // Demais interagíveis são EXTERNOS: só valem fora da safe zone (a barreira separa).
      const chest = this.chests.nearest(pos);
      const rc = this.region.current().rift;
      if (chest) {
        // 10.9EX — o baú ganha o botão "Abrir baú" em cima dele (sem o aviso do centro)
        chestNear = chest;
        action = () => this.openChest(chest.id, chest.type);
      } else if (this.terrGates.lockedNeighbour(pos.x, pos.z, 8)) {
        // 10.6M/10.7V — porta trancada: diz a MISSÃO ou o NÍVEL que falta daquele território
        const nd = this.terrGates.lockedNeighbour(pos.x, pos.z, 8)!;
        // 10.9HP — BUG: a porta mostrava a missão do território TRANCADO (do outro lado, onde você nem
        // entra). A regra real: a porta abre quando o território ONDE VOCÊ ESTÁ tem a missão cumprida
        // E você tem o nível do próximo. Agora a mensagem diz exatamente isso.
        const here = this.territories.at(pos.x, pos.z)?.def ?? null;
        const hereTask = here && !here.home ? this.terrProgress.nextTask(here) : (here ? this.terrProgress.nextTask(here) : null);
        const needLvl = this.terrProgress.gateLevel(nd, this.progress.depth);
        if (hereTask && here) {
          text = i18n.t("terr.lockedHere", {
            name: i18n.t(nd.nameKey),
            what: i18n.t(`terr.task.${hereTask.kind}`, { n: hereTask.target - hereTask.progress }),
            here: i18n.t(here.nameKey),
          });
        } else if (this.xp.level < needLvl) {
          text = i18n.t("terr.lockedLevel", { name: i18n.t(nd.nameKey), n: needLvl });
        } else {
          text = i18n.t("terr.lockedChain", { name: i18n.t(nd.nameKey) });
        }
      } else if (rc && this.rift.isPlayerNear(pos, GameConfig.world.rift.interactRadius)) {
        // Rift Gate: abre a tela de VIAGEM (todas as regiões desbloqueadas). Inativo = dica.
        // 10.6H/I — o portão diz POR QUE está trancado (e o que falta), em vez de só
        // "inativo". Sem isso o jogador ficava batendo no portal sem entender.
        // 10.9IA/IB — o Rift agora tem plaquinha SEMPRE (Thiago: "esse portal ficar igual os
        // outros"), inclusive trancado: aí ela diz o que falta (com nomes) e tocar abre o Mapa
        // do Mundo, que lista as metas da área inteira.
        if (this.rift.isActive) {
          tag = { label: i18n.t("rift.open"), pos: this.rift.group.position, y: PORTAL_TAG_Y };
          action = () => this.openWorld();
        } else {
          tag = { label: this.riftLockedLabel(), pos: this.rift.group.position, y: PORTAL_TAG_Y };
          action = () => this.openWorld();
        }
      } else if (this.region.id === "shatteredWilds" && this.colossusGate.isPlayerNear(pos, GameConfig.colossus.gate.interactRadius)) {
        // Colossus Gate: mostra dica de bloqueio se trancado
        if (!this.colossusUnlocked() && !this.progress.bossesDefeated.aetherColossus) {
          text = i18n.t("colossus.locked");
        }
      }
    }

    // 10.9EX/10.9IA — baú e teleportes usam a PLAQUINHA em cima do objeto; o resto (avisos de
    // trancado, desafiar boss, altar…) segue no aviso + botão do centro.
    if (chestNear) {
      const c = chestNear;
      this.showWorldTag(`🗝️ ${i18n.t("chest.openTag")}`, c.pos, 2.2, () => this.openChest(c.id, c.type));
    } else if (tag && action) {
      const act = action;
      this.showWorldTag(tag.label, tag.pos, tag.y, act);
    } else {
      this.hideWorldTag();
    }
    this.bossPrompt.hidden = text === null;
    if (text) this.bossPrompt.textContent = text;
    this.input.touch.setInteractVisible(text !== null);
    if (text && action && this.input.interactPressed()) action();
  }

  private readonly _dashEnd = new THREE.Vector3();
  private readonly _dashPrev = new THREE.Vector3();
  /** 10.9FA — até onde a investida chega sem atravessar nada (ponto final em _dashEnd). */
  private dashReach(x: number, z: number, dx: number, dz: number, range: number): number {
    const r = GameConfig.player.radius;
    const step = 0.3;
    const q = this._dashEnd.set(x, 0, z);
    const prev = this._dashPrev;
    let went = 0;
    while (went < range) {
      const s = Math.min(step, range - went);
      prev.copy(q);
      const wx = q.x + dx * s, wz = q.z + dz * s;
      q.x = wx; q.z = wz;
      if (this.inBossZone) this.bossZone.resolveWalls(q, r);
      else if (!this.raid.active) {
        this.barrier.block(q, r);
        this.terrGates.block(q, r);
        this.arena.resolveObstacles(q, r);
      }
      this.arena.clamp(q, r);
      // empurrado pra fora do caminho = bateu em algo: para ANTES do obstáculo
      if ((q.x - wx) ** 2 + (q.z - wz) ** 2 > 0.01) { q.copy(prev); break; }
      went += s;
    }
    return went;
  }

  private grimoire!: GrimoireScreen; // 10.9FC
  private indexScreen!: IndexScreen; // 10.9GA
  private guide!: GuideTour; // 10.9IH
  private howTo!: HowToPlay; // 10.9IH
  private banScreen!: BanScreen; // 10.9IT
  private grantsChecked = false; // 10.9IT
  // 10.9J3 — Thiago testou dar/remover/forjar item e "não funcionou": o jogo só reconferia a fila
  // 1x por sessão (grantsChecked), reaberto só ao trocar de personagem ou voltar do 2º plano — com
  // o jogo aberto o tempo todo em primeiro plano, uma concessão criada NO MEIO da sessão nunca era
  // buscada de novo. Este contador reabre a checagem periodicamente mesmo sem nada disso acontecer.
  private grantsPoll = 45; // s
  private diagState: GameState = "menu"; // 10.9IT
  private accountGate!: AccountGate; // 10.9IF
  private chestTag!: HTMLButtonElement;
  /** 10.9IA — ação da plaquinha atual (baú OU teleporte). */
  private tagAction: (() => void) | null = null;
  private readonly _tagV = new THREE.Vector3();
  /**
   * 10.9EX/10.9IA — PLAQUINHA NO MUNDO: um botão flutuando EM CIMA do objeto, que se toca pra
   * agir — no lugar do botão "USAR" do centro da tela. Nasceu pro baú (10.9EX) e agora vale
   * também pros TELEPORTES (Thiago: "os teleportes pode aparecer igual o bau, o nome, entrar
   * clicando ao invés do botão"): o nome do destino fica em cima do portal e você entra tocando.
   */
  private showWorldTag(label: string, pos: THREE.Vector3, yOff: number, action: () => void): void {
    const v = this._tagV.set(pos.x, pos.y + yOff, pos.z).project(this.renderer.camera);
    if (v.z > 1) { this.hideWorldTag(); return; } // atrás da câmera
    this.tagAction = action;
    if (this.chestTag.textContent !== label) this.chestTag.textContent = label;
    this.chestTag.hidden = false; // precisa estar visível pra medir (offsetWidth/Height)
    // 10.9IB — PRENDE NA TELA. A plaquinha é ancorada com translate(-50%,-100%), então cresce pra
    // CIMA e pros LADOS: perto do portal (ou em tela baixa) ela saía pela borda. Agora o ponto é
    // limitado pelo tamanho real dela — vale pra qualquer tela, que é o caso do celular pequeno.
    const w = window.innerWidth, h = window.innerHeight;
    const halfW = this.chestTag.offsetWidth / 2, tagH = this.chestTag.offsetHeight;
    const x = Math.min(w - halfW - 6, Math.max(halfW + 6, ((v.x + 1) / 2) * w));
    const y = Math.min(h - 6, Math.max(tagH + 6, ((1 - v.y) / 2) * h));
    this.chestTag.style.left = `${x}px`;
    this.chestTag.style.top = `${y}px`;
  }

  private hideWorldTag(): void {
    this.tagAction = null;
    this.chestTag.hidden = true;
  }

  private openChest(id: string, type: ChestType): void {
    this.hideWorldTag();
    if (this.equipment.isChestOpen(id)) return;
    this.equipment.markChestOpen(id);
    this.chests.open(id);
    this.audio.playSfx("chest_open");
    const p = this.player.position;
    this.spawnChestLoot(type, p.x, p.z);
    this.gainRegionMastery(GameConfig.regionMastery.xp.perChest); // §31
    this.areaGoal("chests"); // 10.6H
    // 10.6M — baú também conta na missão do território onde ele está
    const tc = this.territories.at(this.player.position.x, this.player.position.z);
    if (tc && this.terrProgress.addChest(tc.def)) {
      this.banner.show(i18n.t("terr.cleared", { name: i18n.t(tc.def.nameKey) }), "boss", 2600);
    }
    this.terrGates.refresh();
    this.progress.territories = this.terrProgress.snapshot();
    this.save.save();
  }

  /** 10.9GT — LENDÁRIO/MÍTICO no mundo: feixe de luz + anel onde o item nasceu (abate recente ou o Caçador). */
  private lootWorldFx(inst: EquipmentInstance): void {
    const ri = rarityIndex(inst.rarity);
    if (ri < 3) return; // 10.9HK — épico também ganha feixe (menor); lendário/mítico com som
    const p = this.player.position;
    const near = this.lastKillPos.distanceTo(p) < 14 ? this.lastKillPos : p;
    this.slashFx.lootBeam(near.x, near.z, RARITY_COLOR[inst.rarity], inst.rarity === "mythic" ? 1.4 : ri >= 4 ? 1 : 0.6);
    if (ri >= 4) this.audio.playSfx("boss_defeated");
  }

  /** Tenta coletar um item do chão; false se a bolsa estiver cheia (item permanece). */
  private tryPickup(inst: EquipmentInstance): boolean {
    if (this.equipment.add(inst)) {
      this.lootToast.drop(inst); // 10.6C: cartão de drop (raro+ vira cartão grande)
      this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons); // 10.5N: sempre com a corrente (senão as armas presas somem da barra)
      this.save.save();
      return true;
    }
    return false;
  }

  /**
   * AUTO-COLETA (10.4S): loot vai DIRETO pra bolsa. Duplicata (arma OU armadura) não enche
   * a bolsa — vira SHARDS que alimentam a FORJA (10.9P). A bolsa guarda só o MELHOR de cada def.
   */
  /** 10.6Y — auto-equipa o item recém-coletado se for um UPGRADE (e o player tem nível). */
  private tryAutoEquip(inst: EquipmentInstance): void {
    if (!this.classAllowsWeapon(inst)) return; // 10.9Z — NÃO auto-equipa arma de outra classe (bug do Thiago)
    if (!this.equipment.canEquipLevel(inst, this.xp.level)) return;
    if (!this.equipment.isSlotUnlocked(inst.slot)) return;
    const current = this.equipment.equipped[inst.slot];
    // 10.9L7 — armadura agora compara IGUAL à arma: pela força real (a mesma conta da seta ▲ da Bolsa).
    // Antes era só a raridade — uma peça Rara forjada L10 perdia pra uma Épica L1 mais fraca.
    // 10.9L8 — no meio do perigo, o anel que PROTEGE dele entra mesmo sendo mais fraco que o atual
    // (quem compra o anel pelo aviso quer ele no dedo na hora)
    const hz = inst.slot === "accessory" ? this.currentHazard() : undefined;
    const salva = !!current && !!hz && this.ringProtects(inst) === hz && this.ringProtects(current) !== hz;
    const isUpgrade = !current || salva || (this.itemBeats(inst, current) && !this.keepsHazardRing(current, inst));
    if (!isUpgrade) return;
    if (this.equipment.equip(inst.instanceId, this.xp.level)) {
      this.banner.show(i18n.t("inventory.autoEquipped", { name: definitionById(inst.definitionId) ? i18n.t(definitionById(inst.definitionId)!.nameKey) : "?" }), "info", 1600);
      this.afterInventoryChange();
    }
  }

  /**
   * 10.9K1 — arma que a classe NÃO usa vira 🪙 na hora (mesmo preço da tela Vender). Os drops
   * aleatórios já sorteiam só armas da classe (classWeaponTypes), então isto é rede de segurança
   * pro que chega por fora: correio/admin, saves antigos. Antes ficava morta na bolsa pra sempre
   * (não equipa, não entra na corrente). Devolve true se vendeu. Sem classe escolhida não vende.
   */
  private sellIfOffClass(inst: EquipmentInstance): boolean {
    if (this.classAllowsWeapon(inst)) return false;
    const coins = this.equipment.sellValue(inst);
    this.progress.addCoins(coins); // preço fixo: sem booster/VIP (igual doSell)
    const def = definitionById(inst.definitionId);
    this.lootToast.note(i18n.t("loot.offClassSold", { name: def ? i18n.t(def.nameKey) : "?", n: coins }));
    return true;
  }

  /** 10.9K1 — vende as armas de OUTRA classe que já estavam na bolsa (saves de antes da venda
   *  automática, ou que chegaram pelo correio antes dela). Depois da 1ª vez não acha nada. */
  private sweepOffClassWeapons(): void {
    const off = this.equipment.bag.filter((it) => !this.classAllowsWeapon(it));
    if (off.length === 0) return;
    const coins = this.equipment.sell(off.map((it) => it.instanceId));
    this.progress.addCoins(coins);
    this.banner.show(i18n.t("loot.offClassSwept", { n: off.length, coins }), "info", 3600);
    this.save.save();
  }

  /** `source` — de onde veio (10.9K8): se for lendário/mítico, vai pro feed "drops ao vivo" do site.
   *  Sem source (loja, correio) não entra no feed. */
  private collectItem(inst: EquipmentInstance, source?: DropSource): void {
    this.progress.discoverItem(inst.definitionId); // 10.9GA — Índice (conta mesmo vendendo)
    // 10.9K1 — antes do collect: ele transformaria duplicata/bolsa cheia em shards daquela arma,
    // que também não servem pra classe.
    if (this.sellIfOffClass(inst)) { this.save.save(); return; }
    if (source) reportDrop(inst.definitionId, inst.rarity, source, this.progress.classId || null); // 10.9K8
    const res = this.equipment.collect(inst);
    if (res.kind === "copy") {
      // 10.9K9 — cópia de arma que já tem: entrou na barra de evolução (+ fragmentos da forja)
      const need = GameConfig.equipment.evolution.copies;
      this.lootToast.note(i18n.t("evolve.copy", { name: itemName(inst), bar: Math.min(res.bar, need), need, n: res.shards }));
      this.evolveAll();
    } else if (res.kind === "added") {
      this.lootToast.drop(inst); // 10.6C
      this.lootWorldFx(inst); // 10.9GT
      // 10.6Y — AUTO-EQUIP: se o item novo é UPGRADE (mais DPS/HP) do slot dele
      // E o player pode equipar (nível mínimo por raridade), troca automaticamente.
      this.tryAutoEquip(inst);
      this.autoSellIfEnabled(inst); // 10.9M — auto-venda de raridade baixa (se ligada e não equipou)
    } else if (res.kind === "shards") {
      // 10.9P — duplicata (arma ou armadura) → shards que alimentam a forja
      const def = definitionById(res.defId);
      this.lootToast.note(i18n.t("loot.toShards", { name: def ? i18n.t(def.nameKey) : "?", n: res.amount }));
    }
    this.chain.setFromBag(this.equipment.bag, this.equipment.equipped.weapon, this.chainType, this.chainSlotLimit(), this.classDef()?.allowedWeapons, (i) => this.equipment.canEquipLevel(i, this.xp.level)); // 10.5I: arma nova entra na corrente
    if (this.chain.weapons.length > 0) this.tutorial.fire("chain"); // 10.5U
    this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
    this.save.save();
  }

  /** Avança a simulação um passo. Separado do loop p/ permitir testes determinísticos. */
  private update(dt: number): void {
    if (this.state !== "playing" && this.chestTag && !this.chestTag.hidden) this.hideWorldTag(); // 10.9EX
    // 10.9BA — abates da temporada sobem a cada ~90 s de jogo (o perfil só subia no level-up)
    this.profileSyncTimer += dt;
    if (this.profileSyncTimer > 90) { this.profileSyncTimer = 0; void syncProfile(this.myProfileStats()); this.checkDailyLoginNow(); } // 10.9K2 — login que vira meia-noite com o jogo aberto
    if (this.state === "playing") {
      // zona de combate (safe zone / buffer / combate) — histerese anti-cheese.
      const zone = this.player.updateZone(this.sanctuary);
      if (zone !== this.lastZone) {
        this.onZoneChanged(this.lastZone, zone);
        if (zone === "combat") this.tutorial.fire("leaveBase"); // 10.5U
      }
      this.lastZone = zone;
      const combatEnabled = this.player.combatEnabled;

      // auto-target: só com combate liberado; alvo válido = vivo e fora da safe zone.
      const candidates = this.targetsForPlayer();
      const target = combatEnabled
        ? this.autoTarget.update(dt, this.player.position, candidates, this.isValidCombatTarget, this.playerReach) // 10.9HQ
        : (this.autoTarget.clear(), null);

      // 10.6H — a câmera acompanha o alvo (senão o arco mata o que você nem vê)
      if (!this.boss) this.cameraRig.setAimFocus(target ? target.position : null);
      this.player.update(dt, target, candidates);
      // 10.9L21 — o pet mira na essência solta mais perto (dentro do raio) e voa até ela.
      const petOrbTarget = this.progress.petOwned ? this.essence.nearestOrbPos(this.player.position, GameConfig.pet.chaseRadius) : null;
      this.pet.update(dt, this.player.position, this.player.facingAngle, petOrbTarget); // 10.5C
      // 10.9AD — servos do Necromante caçam e batem. 10.9CC — inclusive no BOSS (a lista de inimigos não o contém).
      // 10.9L13 — passa o boss À PARTE: ele usa coleira maior (o Titã da Raid é grande e o Necromante
      // luta de longe, ver o comentário em Minions.ts).
      this.minions.update(dt, this.boss ? [...this.enemies.enemies, this.boss] : this.enemies.enemies, this.player.position, this.boss);
      this.chain.update(dt, this.player.position, this.player.facingAngle, this.player.combatStats, candidates, combatEnabled); // 10.5H/J
      // 10.5S — o Arena é o chão de TODAS as regiões (cada uma com o seu bioma e colisão).
      this.arena.update(dt, this.player.position.x, this.player.position.z); // ambiente vivo (vento, nuvens, bichos) — 10.9BR: culling de chunks pela posição
      // 10.5V — dia/noite + clima (a chuva segue o player; a tempestade clareia a cena)
      this.dayTime = (this.dayTime + dt / GameConfig.world.dayNight.period) % 1;
      const flash = this.weather.update(dt, this.player.position.x, this.player.position.z);
      const dayFactor = 0.5 - 0.5 * Math.cos(this.dayTime * Math.PI * 2);
      this.renderer.setDayFactor(dayFactor, flash);
      this.arena.dayLight = dayFactor; // 10.9BS: vagalumes da Floresta acendem à noite
      this.renderer.followShadow(this.player.position.x, this.player.position.z); // 10.5J: sombra segue o player
      if (!this.inBossZone && !this.raid.active) this.barrier.block(this.player.position, GameConfig.player.radius); // 10.9AM: a barreira é do mapa, não da zona
      // 10.9AJ — na Zona dos Bosses as salas mandam (portas por nível + círculo de luta); muros de
      // território e pedras do bioma de baixo NÃO bloqueiam lá (a grade é desenhada por cima).
      if (!this.inBossZone && !this.raid.active) this.terrGates.block(this.player.position, GameConfig.player.radius); // 10.6L: porta trancada
      this.terrGates.update(dt); // 10.6X: pulsação dos painéis/cristais
      this.updateDivideAmbient(dt); // 10.9HO: brasas/motes/borrifos nas divisas perto do Caçador
      this.updateHazard(dt); // 10.6N: gelo/fogo/vendaval/tempestade exigem o anel certo
      if (!this.inBossZone && !this.raid.active) this.arena.resolveObstacles(this.player.position, GameConfig.player.radius); // 10.5J: não atravessa pedra/árvore
      this.updateBossZone(dt); // 10.9AJ
      this.updateTerrPopulation(dt); // 10.9DX — população fixa de cada território
      this.raid.update(dt); // 10.9EO — raid: convite, rede, Tita, sala
      if (!this.updateNotesChecked) this.checkUpdateNotes(); // 10.9ES
      else if (!this.grantsChecked) this.checkAdminGrants(); // 10.9IT — primeira checagem da janela
      else if (!this.raid.active && (this.grantsPoll -= dt) <= 0) { this.grantsPoll = 45; this.checkAdminGrants(); } // 10.9J3 — reconfere a cada ~45s mesmo com o jogo aberto o tempo todo (sem depender de trocar de personagem ou minimizar)
      if (this.contractTimer > 0) this.contractTimer = Math.max(0, this.contractTimer - dt); // 10.5L
      // 10.9L19 — boosters vivem em `this.progress` como timestamp de expiração (persistem no save);
      // aqui só sincroniza o multiplicador de XP em uso (drena sozinho pelo relógio, sem precisar de dt).
      this.xp.boostMult = this.progress.xpBoostSecondsLeft() > 0 ? this.progress.xpBoostMult : 1;
      for (let i = 0; i < 3; i++) if (this.spellCds[i] > 0) this.spellCds[i] = Math.max(0, this.spellCds[i] - dt); // 10.9CX — recargas
      if (combatEnabled) {
        if (this.input.spellPressed() && this.spellCds[0] <= 0) this.castSpell(0);
        if (this.input.spell2Pressed() && this.spellCds[1] <= 0) this.castSpell(1);
        if (this.input.spell3Pressed() && this.spellCds[2] <= 0) this.castSpell(2);
      }
      this.updateBuff(dt); this.updateMeteors(dt); this.updateCurses(dt); this.updateTraps(dt); this.updateSpellFx(dt); this.spellVfx.update(dt); // 10.9FC traps · // 10.9CX/DN · 10.9HI
      if (this.potionCd > 0) this.potionCd = Math.max(0, this.potionCd - dt); // 10.9CZ
      this.refreshPotionButton();
      this.tutorial.update(dt); // 10.5U: espaça as dicas
      this.hook.update(dt, this.player.position, this.hookTargets(), target as HookTarget | null, combatEnabled);
      this.enemies.update(dt, this.player, this.player.inSanctuary, this.enemyTargetFor()); // 10.9CM
      for (const en of this.enemies.enemies) { // 10.5J: terrestres respeitam obstáculos E paredes
        if (en.isDead || (en as { flying?: boolean }).flying === true) continue; // voadores passam por cima
        this.arena.resolveObstacles(en.position, en.radius);
        this.terrGates.blockStatic(en.position, en.radius); // 10.9R: não atravessam os muros dos territórios
        // 10.9AB — a barreira de região NÃO clampa mais inimigos: ela é o gate do PLAYER (até o boss)
        // e prendia os monstros nela (ficavam empilhados na parede de energia). As paredes reais dos
        // territórios continuam via terrGates.blockStatic acima.
      }
      this.updateWorldEvent(dt); // 10.9AC: Invasão do Vazio (roda em fase de onda E de boss)
      if (this.boss) {
        this.capBossHp(); // 10.9DU — teto de golpes do boss (vida nunca vira "1 milhao")
        this.boss.update(dt);
        // 10.9FT — BUG: o boss atravessava o muro da sala (só o player era preso). Agora o boss
        // também fica preso na arena selada da Fenda (Zona dos Bosses).
        if (this.inBossZone && this.zoneActiveRoom) this.bossZone.clampToRoom(this.boss.position, this.zoneActiveRoom, this.boss.radius);
        if (!this.inBossZone && !this.raid.active) this.sanctuary.keepOut(this.boss.position, this.boss.radius); // 10.9DS — boss nao entra na zona segura (fica de fora)
        // 10.9BF — FÚRIA: aviso quando o boss cruza o limiar de vida baixa (bate mais forte)
        if (this.bossFuryRef !== this.boss && inFury(this.boss.hpFraction)) {
          this.bossFuryRef = this.boss;
          this.banner.show(i18n.t("boss.fury"), "danger", 2600);
          this.cameraRig.impulse(0.3);
          this.audio.playSfx("boss_theme");
        }
        // arena do Colossus: mantém o player dentro (sem escapar p/ Sanctuary/Rift)
        if (this.boss instanceof AetherColossus || this.boss instanceof RaidTitan) this.cameraRig.setBossFocus(this.boss.position); // compõe player+boss (HUD não cobre) · 10.9EO Tita
        // 10.9CK — o player NÃO fica mais preso na arena (10.9AK); a coleira reseta a luta se ele for longe
        this.updateBossLeash(dt);
        // o boss pode ter sido removido (morte) OU ter matado o player (clearBoss em onPlayerDied)
        if (this.boss && this.boss.removeRequested) this.clearBoss();
      } else if (this.inBossZone || this.raid.active) { // 10.9EO — na raid nao ha spawner
        // Zona dos Bosses (10.9AJ): SÓ os bosses das salas — sem spawner normal, sem boss de
        // evento/área/auto aparecendo lá dentro.
      } else if (this.worldEventActive) {
        // 10.9EY — BUG "exploro e não acho monstro": a Invasão dura 2h a cada 6h e DESLIGAVA o
        // spawner — o mapa inteiro ficava vazio, só os 9 corrompidos em volta do Caçador. Agora os
        // territórios seguem povoados e a horda do evento vem POR CIMA.
        this.spawner.update(dt, this.player.position);
      } else {
        this.spawner.update(dt, this.player.position);
        this.updateEventBoss(dt); // 10.4R: boss de evento surge periodicamente no campo
        this.updateFixedBossCooldowns(dt); // 10.6Q: decrementa cooldowns de respawn
        this.checkAutoBossSpawn(); // 10.6Q: spawn automático com cooldown + limite diário
      }

      // se o player morreu no meio do frame (inimigo/boss), aborta o resto do frame "playing"
      if ((this.state as GameState) !== "playing") return;

      // projéteis (bow) + bolts de inimigos + loot no chão (magnet/auto-pickup) + baús
      this.projectiles.update(dt, candidates);
      this.enemyBolts.update(dt, this.player);
      this.fullBannerTimer = Math.max(0, this.fullBannerTimer - dt);
      this.groundLoot.update(
        dt,
        this.player.position,
        (inst) => {
          const ok = this.tryPickup(inst);
          if (!ok && this.fullBannerTimer <= 0) {
            this.banner.show(i18n.t("inventory.full"), "danger", 1400);
            this.fullBannerTimer = 3;
          }
          return ok;
        },
        this.lootPullBlocked,
      );
      this.chests.update(dt);

      this.updateRegionAndGate(dt);
      // base cura rápido; no campo há recuperação percentual após ficar sem dano (§ codex)
      if (this.player.inSanctuary) {
        this.player.regenInSanctuary(dt);
        this.updateHealFx(dt);
      } else if (this.player.regenInField(dt)) {
        this.updateHealFx(dt, GameConfig.fieldRegen.healFxInterval);
      }
      // absorção acumulada (10.4O): recalcula os stats em lote (debounced) após kills
      if (this.absorbDirty) {
        this.absorbRecalcTimer -= dt;
        if (this.absorbRecalcTimer <= 0) { this.absorbDirty = false; this.recalcPlayer(); }
      }
      this.sanctuary.update(dt);
      this.testPad.update(dt); // 10.7B — pad de teste pulsa
      this.trainingDummy.update(dt); // 10.9BY — boneco balança
      this.guardianArena?.update(dt); // 10.9CC — arena pulsa
      this.skillDefCd = Math.max(0, this.skillDefCd - dt); // 10.9BY
      this.trainHudTimer -= dt;
      if (this.trainHudTimer <= 0) { this.trainHudTimer = 1; this.updateTrainingHud(); } // 10.9BY — indicador 1×/s
      this.pvpPortal.update(dt); // 10.9 — portal PvP pulsa
      this.bossPortal.update(dt); // 10.9AJ — portais e salas da Zona dos Bosses pulsam
      if (this.inBossZone) { this.zoneExitPortal.update(dt); this.bossZone.update(dt); }
      this.rift.update(dt);
      this.wilds.update(dt);
      this.tempest.update(dt); // Fase 10: vento/nuvens/cristais da Região 3
      // Colossus Gate (Região 2): acende quando desbloqueado
      if (this.region.id === "shatteredWilds") {
        this.colossusGate.setActive(this.colossusUnlocked() || this.progress.bossesDefeated.aetherColossus);
        this.colossusGate.update(dt);
      }
      this.essence.update(dt, this.player.position, this.lootPullBlocked, this.progress.petOwned ? this.pet.group.position : null); // 10.9L21
      this.altar.update(dt);
      this.telegraph.update(dt);
      this.particles.update(dt);
      this.slashFx.update(dt);
      this.barrier.update(dt);
      this.damageNumbers.update(dt);
      this.cameraRig.update(this.player.position, dt);
      this.hud.update(this.player, this.xp, this.inventory.total(), this.progress.coins, this.progress.diamonds, this.renderer.camera);
      this.buffsHudTimer -= dt;
      if (this.buffsHudTimer <= 0) { this.buffsHudTimer = 0.5; this.hud.setBuffs(this.activeBuffs()); } // 10.9EJ
      this.enemyBars.update(this.enemies.enemies, target instanceof Enemy ? target : null);
      this.updateTargetRing(target);
      if (this.boss) this.bossHud.update(this.boss);
      this.save.updateAutosave(dt);

      // HUD touch: recargas nos botões + HOOK desabilitado na safe zone/buffer
      this.input.touch.setSprintCooldown(this.player.sprintCooldownFraction());
      this.input.touch.setHookCooldown(this.hook.cooldownFraction);
      // 10.9HY/HZ — era `spellsOf()` a CADA QUADRO: 3 spreads de objeto + 2 de array + 3 closures
      // de find por quadro (~500 objetos/s de lixo). Agora o resultado é memorizado e só refaz
      // quando classe, nível OU o Grimório mudam (as três entradas de `spellsOf`).
      const spellKey = `${this.progress.classId}|${this.xp.level}|${(this.progress.spellLoadout[this.progress.classId] ?? []).join(",")}`;
      if (spellKey !== this.spellsCacheKey) { this.spellsCacheKey = spellKey; this.spellsCache = this.spellsOf(); }
      const spells = this.spellsCache; // 10.9CX — 3 botões de magia
      this.input.touch.setSpellVisible(!!spells[0]);
      if (spells[0]) this.input.touch.setSpellCooldown(this.spellCds[0] / spells[0]!.cooldown);
      for (let i = 1; i < 3; i++) { const s2 = spells[i]; this.input.touch.setSpellSlotVisible(i, !!s2); if (s2) this.input.touch.setSpellSlotCooldown(i, this.spellCds[i] / s2.cooldown); }
      this.input.touch.setHookDisabled(!combatEnabled);
      this.input.touch.setHookPulse(this.boss instanceof AetherColossus && this.boss.hookOpportunity);
      this.safeTapToastTimer = Math.max(0, this.safeTapToastTimer - dt);
      if (!combatEnabled && this.input.hookPressed() && this.safeTapToastTimer <= 0) {
        this.banner.show(i18n.t("sanctuary.safeZone"), "info", 900);
        this.safeTapToastTimer = 2.5; // throttle: não repete o toast na borda
      }

      // interação contextual unificada (baú / altar / boss) — consome o toque de USAR
      this.altarPanel.setPromptVisible(false);
      this.updateContext();

      // fog of war + descobertas (baixa frequência) + minimapa (~12 Hz)
      this.fogTimer += dt;
      if (this.fogTimer >= 0.2) {
        this.fogTimer = 0;
        this.fog.reveal(this.player.position.x, this.player.position.z);
        this.checkDiscoveries();
      }
      this.minimapTimer += dt;
      if (this.minimapTimer >= 1 / quality.values.minimapHz) { // 10.9HY — taxa pelo preset (low = 6 Hz)
        this.minimapTimer = 0;
        this.updateMinimap();
        this.updateMissionHud(); // 10.5O: faixa da missão ativa
        // 10.5V — tinta de noite na tela (o tone mapping "lava" a luz 3D sozinha)
        document.documentElement.style.setProperty("--night", (1 - this.renderer.dayLight).toFixed(2));
      }
    } else if (this.state === "dead") {
      this.respawnTimer -= dt;
      this.deathScreen.setCountdown(this.respawnTimer);
      this.damageNumbers.update(dt);
      this.essence.update(dt, this.player.position);
      this.enemies.update(dt, this.player);
      this.projectiles.update(dt, this.enemies.enemies);
      this.telegraph.update(dt);
      this.particles.update(dt);
      this.slashFx.update(dt);
      this.sanctuary.update(dt);
      this.enemyBars.update(this.enemies.enemies, null);
      this.targetRing.visible = false;
      this.cameraRig.update(this.player.position, dt);
      if (this.respawnTimer <= 0) this.respawn();
    } else if (this.state === "altar") {
      this.altar.update(dt);
    }
  }

  /** 10.5M — retângulos dos territórios para o minimapa (só os perto do player).
   * 10.6X — inclui `discovered`: territórios não visitados ficam OCULTOS no minimapa. */
  private minimapTerritories(): { cx: number; cz: number; halfW: number; halfH: number; tier: number; current: boolean; discovered: boolean; col: number; row: number; ground: string }[] {
    const cur = this.territories.at(this.player.position.x, this.player.position.z);
    const disc = this.region.activeDiscoveries();
    const out: { cx: number; cz: number; halfW: number; halfH: number; tier: number; current: boolean; discovered: boolean; col: number; row: number; ground: string }[] = [];
    const range = GameConfig.minimap.rangeWorld + 60;
    for (const t of this.territories.all) {
      if (Math.abs(t.cx - this.player.position.x) > range || Math.abs(t.cz - this.player.position.z) > range) continue;
      const isCur = cur?.def.id === t.def.id;
      const discovered = isCur || disc[`terr:${t.def.id}`] === true;
      out.push({ cx: t.cx, cz: t.cz, halfW: t.halfW, halfH: t.halfH, tier: t.def.tier, current: isCur, discovered, col: t.def.col, row: t.def.row, ground: this.mapGround(t.biome.ground) }); // 10.9HL: piso
    }
    return out;
  }
  private currentTerritoryName(): string {
    const t = this.territories.at(this.player.position.x, this.player.position.z);
    return t ? i18n.t(t.def.nameKey) : "";
  }

  /**
   * 10.9EQ — ROUBO DE VIDA. Cura % do dano que o CACADOR causou (ataque, flecha, Corrente, magia).
   * Teto de cura por segundo (maxLifeStealPerSecPct da vida maxima): um golpe em area que acerta 20
   * monstros nao enche a barra inteira de uma vez.
   */
  private applyLifeSteal(amount: number): void {
    const p = this.player;
    if (p.isDead || p.health.current >= p.health.max) return;
    const now = performance.now();
    const perSec = p.health.max * GameConfig.combat.maxLifeStealPerSecPct;
    this.lsBudget = Math.min(perSec, this.lsBudget + ((now - this.lsLast) / 1000) * perSec);
    this.lsLast = now;
    const heal = Math.min(amount * this.lifeSteal, this.lsBudget);
    if (heal <= 0) return;
    this.lsBudget -= heal;
    p.health.heal(heal);
  }

  /**
   * 10.9ES — NOVIDADES: na 1a vez que o jogador esta JOGANDO nesta sessao (com classe escolhida e
   * fora de tutorial de classe), mostra o que mudou nas atualizacoes que ele ainda nao viu e
   * entrega a recompensa somada. Cada atualizacao paga 1x (o id fica no save e sobe pra nuvem).
   */
  private checkUpdateNotes(): void {
    if (this.state !== "playing" || !this.progress.classId || this.raid.active) return;
    this.updateNotesChecked = true;
    const list = unseenUpdates(this.progress.updatesSeen);
    if (list.length === 0) return;
    this.input.setEnabled(false);
    this.updateNotes.open(list, (coins, dia) => {
      for (const e of list) if (!this.progress.updatesSeen.includes(e.id)) this.progress.updatesSeen.push(e.id);
      // jogador novo so ve a mais recente: marca o historico todo como visto (nao recebe tudo de novo)
      for (const e of CHANGELOG) if (!this.progress.updatesSeen.includes(e.id)) this.progress.updatesSeen.push(e.id);
      if (coins > 0) this.addCoins(coins);
      if (dia > 0) this.progress.addDiamonds(dia);
      this.input.setEnabled(true);
      this.save.save();
      cloudPriority();
      if (coins > 0 || dia > 0) this.banner.show(i18n.t("update.got", { coins: String(coins), dia: String(dia) }), "boss", 2600);
    });
  }

  /**
   * 10.9IT — CONCESSOES DO PAINEL (itens, moedas, diamantes, nivel). O banco marca como resgatadas
   * e devolve so as que marcou; cada uma entra pelo CORREIO (persiste no save, sobe pra nuvem).
   * Nivel e aplicado direto. Roda 1x por sessao, depois das Novidades.
   */
  private checkAdminGrants(): void {
    if (this.state !== "playing" || !this.progress.classId || this.raid.active) return;
    this.grantsChecked = true;
    // 10.9IW — passa a classe ATIVA: uma concessao com `payload.classId` de outra classe (ex.:
    // "remover item" mira um personagem especifico) fica pendente ate essa classe ser carregada,
    // em vez de ser marcada resgatada e desperdicada num personagem errado.
    void claimGrants(this.progress.classId).then((list) => {
      if (list.length === 0) return;
      // 10.9K7 — ZERAR (botão do painel): apaga TODOS os personagens do aparelho e da nuvem e recarrega.
      // O resto que veio junto não se aplica — era pra conta que acabou de ser zerada.
      if (list.some((g) => g.kind === "wipe")) {
        diag.log("admin", "wipe recebido — zerando a conta");
        this.state = "paused";
        this.input.setEnabled(false);
        this.banner.show(i18n.t("admin.wiped"), "danger", 4000);
        void wipeAccount(GameConfig.save.key).then(() => window.setTimeout(() => location.reload(), 1800));
        return;
      }
      for (const gr of list) this.applyGrant(gr);
      this.updateMailDot();
      this.save.save();
      cloudPriority();
      diag.log("admin", `${list.length} concessao(oes)`);
      // 10.9IW — "veja o Correio" so faz sentido pra quem MANDA pro Correio (items/coins/diamonds).
      // "level"/"remove_item" ja mostram o proprio aviso em applyGrant; duplicar aqui confundiria
      // ("vai no Correio" quando na verdade o nivel ja mudou na hora).
      const foiPeloCorreio = list.some((g) => g.kind === "items" || g.kind === "coins" || g.kind === "diamonds");
      if (foiPeloCorreio) this.banner.show(i18n.t("admin.grantBanner", { n: String(list.filter((g) => g.kind === "items" || g.kind === "coins" || g.kind === "diamonds").length) }), "boss", 3200);
    });
  }

  private applyGrant(gr: Grant): void {
    const note = gr.note ?? "";
    const p = gr.payload;
    if (gr.kind === "items") {
      const items = (p.items ?? []).filter((it) => it && typeof it.defId === "string").map((it) => ({ defId: it.defId, rarity: it.rarity, level: Math.max(1, Math.floor(Number(it.level) || 1)), vip: it.vip === true }));
      if (items.length > 0) this.mailSystem.sendItems("mail.admin.title", "mail.admin.body", items, {}, { note });
    } else if (gr.kind === "coins") {
      const n = Math.max(0, Math.floor(Number(p.amount) || 0));
      if (n > 0) this.mailSystem.send("mail.admin.title", "mail.admin.body", { coins: n }, { note });
    } else if (gr.kind === "diamonds") {
      const n = Math.max(0, Math.floor(Number(p.amount) || 0));
      if (n > 0) this.mailSystem.send("mail.admin.title", "mail.admin.body", { diamonds: n }, { note });
    } else if (gr.kind === "level") {
      const lvl = Math.max(1, Math.floor(Number(p.level) || this.xp.level));
      this.xp.level = lvl;
      this.xp.xpForNext = GameConfig.progression.xpForLevel(lvl);
      this.recalcPlayer();
      this.updateTreeBadge();
      this.banner.show(i18n.t("admin.levelSet", { n: String(lvl) }), "boss", 3200);
    } else if (gr.kind === "remove_item") {
      // 10.9IW — remove da BOLSA por instanceId (peca equipada nao entra: e um risco maior mexer
      // na arma/roupa que o jogador esta usando AGORA sem ele estar olhando). Se o item ja saiu do
      // slot (vendido, quebrado, forjado) o `findIndex` so nao acha nada — silencioso de proposito,
      // porque o painel mostrava exatamente o que existia no momento em que o admin pediu a remocao.
      const instanceId = String(p.instanceId ?? "");
      const idx = this.equipment.bag.findIndex((it) => it.instanceId === instanceId);
      if (idx >= 0) {
        this.equipment.bag.splice(idx, 1);
        this.chain.setFromBag(this.equipment.bag, this.equipment.equipped.weapon, this.chainType, this.chainSlotLimit(), this.classDef()?.allowedWeapons, (i) => this.equipment.canEquipLevel(i, this.xp.level));
        this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
        diag.log("admin", `remove_item ok`);
        this.banner.show(i18n.t("admin.itemRemoved"), "info", 2600);
      } else {
        diag.log("admin", `remove_item nao encontrado`);
      }
    } else if (gr.kind === "item_level") {
      // 10.9J2 — forja de QUALQUER item, equipado ou não: acha por instanceId onde quer que esteja
      // (`findAnywhere` olha os 6 slots equipados e a bolsa). Diferente de "remove_item", mexer no
      // nível de forja de uma peça em uso não tem risco de "sumir sem o jogador ver": ela continua
      // exatamente onde estava, só mais forte (ou mais fraca, se o admin baixar o nível).
      const instanceId = String(p.instanceId ?? "");
      const inst = this.equipment.findAnywhere(instanceId);
      if (inst) {
        inst.level = Math.max(1, Math.min(GameConfig.equipment.upgradeMaxLevel, Math.floor(Number(p.level) || inst.level)));
        this.recalcPlayer();
        this.equipBar.update(this.equipment, this.xp.level, this.chain.weapons);
        diag.log("admin", `item_level ok L${inst.level}`);
        this.banner.show(i18n.t("admin.itemLevelSet", { n: String(inst.level) }), "info", 2600);
      } else {
        diag.log("admin", `item_level nao encontrado`);
      }
    } else if (gr.kind === "complete_area") {
      // 10.9L26 — marca a área como CONCLUÍDA (todo objetivo no alvo), o que libera a região
      // seguinte pra viagem via enforceRegionGates — mesmo caminho de quem termina de verdade.
      const regionId = String(p.regionId ?? "");
      const region = GameConfig.world.regions.find((r) => r.id === regionId);
      if (region) {
        const kinds = new Set(this.areaGoals.list(regionId).map((g) => g.kind));
        for (const k of kinds) this.areaGoals.advance(regionId, k, 999999);
        this.progress.areaGoals = this.areaGoals.snapshot();
        this.enforceRegionGates();
        this.terrGates.refresh();
        this.banner.show(i18n.t("admin.areaCompleted", { name: i18n.t(region.nameKey) }), "boss", 3200);
        this.save.save();
        diag.log("admin", `complete_area ok ${regionId}`);
      } else {
        diag.log("admin", `complete_area regiao invalida: ${regionId}`);
      }
    }
  }

  /**
   * 10.9EV — QUANTO FOI COMPRADO de vida/dano/regen (loja de moedas + atributos do VIP) e quantas
   * compras. A compra soma no mesmo lugar que a ABSORCAO de monstros, entao sem este contador nao
   * dava pra saber o que veio de compra. Valores CRUS (antes dos multiplicadores de % da vida/dano).
   */
  private boughtStats(): { boughtHp: number; boughtHpN: number; boughtDmg: number; boughtDmgN: number; boughtRegen: number; boughtRegenN: number } {
    const p = this.progress;
    const coin = (id: string): { n: number; amt: number } => {
      const it = GameConfig.shop.items.find((i) => i.id === id);
      return { n: p.shopCount(id), amt: it ? it.amount : 0 };
    };
    const hp = coin("hp");
    const pw = coin("power");
    const r = { boughtHp: hp.n * hp.amt, boughtHpN: hp.n, boughtDmg: pw.n * pw.amt, boughtDmgN: pw.n, boughtRegen: 0, boughtRegenN: 0 };
    for (const it of GameConfig.attrShop.items) {
      const n = p.shopCount(`attr:${it.id}`);
      if (n <= 0) continue;
      if (it.kind === "hp") { r.boughtHp += n * it.amount; r.boughtHpN += n; }
      else if (it.kind === "damage") { r.boughtDmg += n * it.amount; r.boughtDmgN += n; }
      else if (it.kind === "regen") { r.boughtRegen += n * it.amount; r.boughtRegenN += n; }
    }
    return r;
  }

  /** 10.9EO — leva o caçador pra arena da raid (fora do mapa), guardando onde ele estava. */
  private enterRaidArena(x: number, z: number): void {
    this.traps.length = 0; this.curses.length = 0; this.meteors.length = 0; this.spellVfx.clear(); // 10.9GO
    this.renderer.setGradeOverride(VOID_GRADE); // 10.9GR
    if (this.inBossZone) this.leaveBossZone(false);
    if (this.boss && !this.raid.active) this.clearBoss(); // boss de campo em andamento: a raid tem prioridade (10.9GX: nunca o proprio Tita)
    this.raidReturn = this.player.position.clone();
    this.enemies.clear();
    this.hook.reset();
    this.autoTarget.clear();
    this.telegraph.clear();
    const a = GameConfig.raid.arena;
    this.arena.setClampOverride({ cx: a.x, cz: a.z, half: a.r + 2 });
    this.player.teleportTo(x, z);
    this.cameraRig.snapTo(this.player.position);
    this.audio.playSfx("level_up");
  }

  /** 10.9EO — volta pro PROPRIO jogo, exatamente onde estava antes da raid. */
  private leaveRaidArena(): void {
    this.renderer.setGradeOverride(null); // 10.9GR
    this.arena.setClampOverride(null);
    const back = this.raidReturn;
    this.raidReturn = null;
    if (back) this.player.teleportTo(back.x, back.z);
    else this.player.teleportTo(this.sanctuary.center.x, this.sanctuary.center.z);
    if (this.player.isDead) this.player.respawnAt(this.player.position.x, this.player.position.z, 1);
    this.cameraRig.snapTo(this.player.position);
    this.spawner.prewarm(this.player.position);
    this.audio.playRegion(this.region.id);
  }

  /**
   * 10.9EO — RECOMPENSA DA RAID (1x por horario). Participou e venceu: XP (fracao do nivel),
   * moedas por nivel e diamantes, + bonus de diamante do 1o ao 10o. Perdeu: parte do XP/moedas.
   * Quem o servidor marcou como SUSPEITO leva so a participacao (sem bonus de posicao).
   */
  private grantRaidReward(r: RaidReward): void {
    if (this.progress.raidClaimed.includes(r.slot)) return;
    if (r.myDmg <= 0) return; // 10.9GN — entrou e nao bateu: sem recompensa E sem marcar (senao perdia a do respawn)
    this.progress.raidClaimed.push(r.slot);
    const c = GameConfig.raid.reward;
    const frac = r.win ? 1 : c.failFrac;
    const xp = Math.round(this.xp.xpForNext * c.xpFracOfLevel * frac);
    const coins = Math.round(c.coinsPerLevel * this.xp.level * frac);
    let dia = r.win ? c.diamonds : 0;
    if (r.win && !r.suspect && r.rank >= 1 && r.rank <= c.topDiamonds.length) dia += c.topDiamonds[r.rank - 1];
    this.xp.gain(xp);
    this.addCoins(coins);
    if (dia > 0) this.progress.addDiamonds(dia);
    this.banner.show(i18n.t("raid.reward", { xp: String(xp), coins: String(coins), dia: String(dia) }), "boss", 4200);
    // 10.9GF — BOSS GLOBAL solta EQUIPAMENTO (do tier do seu nível): vitória = arma + roupa; participou = 1 item
    if (r.win) {
      const w = rollBossWeapon("legendary", this.classWeaponTypes(), this.xp.level); if (w) this.collectItem(w, "raid");
      const a = rollBossArmor("legendary", this.xp.level); if (a) this.collectItem(a, "raid");
      // 10.9GG — ITEM ÚNICO do boss global (chance alta na vitória)
      if (Math.random() < 0.5) { const u = rollRaidUnique("legendary", this.classWeaponTypes()); if (u) { this.collectItem(u, "raid"); window.setTimeout(() => this.banner.show(i18n.t("raid.unique"), "boss", 3200), 1400); } }
      window.setTimeout(() => this.banner.show(i18n.t("chest.bossWeapon"), "boss", 2600), 700);
    } else if (Math.random() < 0.5) {
      const drop = Math.random() < 0.5 ? rollBossWeapon("epic", this.classWeaponTypes(), this.xp.level) : rollBossArmor("epic", this.xp.level);
      if (drop) this.collectItem(drop, "raid");
    }
    this.save.save();
    cloudPriority(); // 10.9EP
  }

  /**
   * 10.9DX — mantém a POPULAÇÃO FIXA de cada território viva mesmo longe do Caçador. Os pedaços
   * perto dele são MATERIALIZADOS (valem os inimigos reais do spawner); os de longe vivem como
   * pontos, repõem sozinhos e é isso que o mapa desenha.
   */
  private updateTerrPopulation(dt: number): void {
    if (this.terrPopRegion !== this.region.id) {
      this.terrPop.setRegion(this.territories.all.slice());
      this.terrPopRegion = this.region.id;
    }
    this.terrPop.update(dt);
    // o que você limpou perto de você tem que sumir do mapa também (senão o pedaço varrido
    // continuaria "cheio" assim que você se afastasse)
    this.terrRealCount.clear();
    for (const e of this.enemies.enemies) {
      if (e.isDead || e.removeRequested || !e.territoryId) continue;
      this.terrRealCount.set(e.territoryId, (this.terrRealCount.get(e.territoryId) ?? 0) + 1);
    }
    // territórios materializados = os que o spawner alcança (mesmo raio), calculado UMA vez por
    // frame e reaproveitado pelos blips (nada de procurar território por id em loop).
    const r = GameConfig.territories.population.materializedRadius;
    this.materializedTerrs.clear();
    for (const t of this.territories.all) {
      const dx = t.cx - this.player.position.x;
      const dz = t.cz - this.player.position.z;
      if (dx * dx + dz * dz > r * r) continue;
      this.materializedTerrs.add(t.def.id);
      this.terrPop.syncMaterialized(t.def.id, this.terrRealCount.get(t.def.id) ?? 0);
    }
  }

  /** Pontos da população virtual (só dos pedaços NÃO materializados e já descobertos). */
  private virtualBlips(): VirtualMob[] {
    this.terrPopBlips.length = 0;
    const disc = this.region.activeDiscoveries();
    const cur = this.territories.at(this.player.position.x, this.player.position.z);
    return this.terrPop.blips(
      (id) => this.materializedTerrs.has(id),
      (id) => cur?.def.id === id || disc[`terr:${id}`] === true,
      this.terrPopBlips,
    );
  }

  /**
   * 10.9EJ — BONUS ATIVOS no HUD. Todos esses efeitos ja existiam (e o jogador pagava por varios
   * deles na Loja), mas nenhum aparecia na tela: nao dava pra saber se o Contrato ainda valia,
   * quanto faltava do boost de XP, ou se havia Bencao guardada antes de arriscar um boss.
   */
  private activeBuffs(): { icon: string; text: string; tip: string; kind?: string }[] {
    const mmss = (s: number): string => {
      const t = Math.max(0, Math.ceil(s));
      const m = Math.floor(t / 60);
      return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m}:${String(t % 60).padStart(2, "0")}`;
    };
    const out: { icon: string; text: string; tip: string; kind?: string }[] = [];
    if (this.buffTimer > 0) out.push({ icon: "✨", text: mmss(this.buffTimer), tip: i18n.t("buff.spell"), kind: "spell" });
    const xpLeft = this.progress.xpBoostSecondsLeft();
    if (xpLeft > 0) out.push({ icon: "⭐", text: `×${this.progress.xpBoostMult} ${mmss(xpLeft)}`, tip: i18n.t("buff.xp") });
    const coinLeft = this.progress.coinBoostSecondsLeft();
    if (coinLeft > 0) out.push({ icon: "🪙", text: `×${this.progress.coinBoostMult} ${mmss(coinLeft)}`, tip: i18n.t("buff.coin") });
    const dropLeft = this.progress.dropBoostSecondsLeft();
    if (dropLeft > 0) out.push({ icon: "🍀", text: mmss(dropLeft), tip: i18n.t("buff.luck") });
    // (Contrato nao entra aqui: ele ja tem a faixa propria `contract-hud`, logo acima, na mesma pilha)
    if (this.eventStacks > 0) out.push({ icon: "🔥", text: `×${this.eventStacks}`, tip: i18n.t("buff.event"), kind: "event" });
    if (this.progress.bless > 0) out.push({ icon: "🙏", text: `×${this.progress.bless}`, tip: i18n.t("buff.bless") });
    if (Vip.active) out.push({ icon: "👑", text: "", tip: i18n.t("buff.vip"), kind: "vip" });
    return out;
  }

  /** Monta os dados e atualiza o minimapa (baixa frequência). */
  private updateMinimap(): void {
    this.minimapEntities.length = 0;
    for (const e of this.enemies.enemies) {
      if (e.isDead) continue;
      this.minimapEntities.push({ x: e.position.x, z: e.position.z, kind: e.elite ? "elite" : "enemy" });
    }
    // 10.9DX — a horda dos territórios de LONGE também aparece (o mapa mostra o mundo povoado)
    for (const m of this.virtualBlips()) this.minimapEntities.push({ x: m.x, z: m.z, kind: m.champion ? "elite" : "enemy" });
    if (this.boss && !this.boss.isDead) this.minimapEntities.push({ x: this.boss.position.x, z: this.boss.position.z, kind: "boss" });
    else if (this.guardianArena && this.guardianReady(this.region.id)) this.minimapEntities.push({ x: this.guardianArena.cx, z: this.guardianArena.cz, kind: "boss" }); // 10.9CC
    // 10.9AS — na Fenda dos Titãs, as SALAS dos bosses aparecem no minimapa (quadrados)
    if (this.inBossZone) for (const r of this.bossZone.rooms) this.minimapEntities.push({ x: r.cx, z: r.cz, kind: "room" });
    for (const c of GameConfig.chests.world) {
      if ((c as { region?: string }).region !== this.region.id) continue; // só baús da região ativa
      if (!this.equipment.isChestOpen(c.id)) this.minimapEntities.push({ x: c.x, z: c.z, kind: "chest" });
    }
    const den = GameConfig.boss.voidAlpha.arena.center;
    const disc = this.region.activeDiscoveries();
    const rc = this.region.current().rift;
    this.minimap.update({
      px: this.player.position.x,
      pz: this.player.position.z,
      facing: this.player.facingAngle,
      entities: this.minimapEntities,
      gates: this.inBossZone || this.raid.active ? undefined : this.terrGates.mapGates(), // 10.9EK — portas no minimapa
      divides: this.inBossZone || this.raid.active ? undefined : this.terrGates.mapDivides(), // 10.9HN
      discovered: { sanctuary: !!disc.sanctuary, altar: !!disc.altar, alphaDen: !!disc.alphaDen },
      sanctuary: { x: this.sanctuary.center.x, z: this.sanctuary.center.z },
      altar: { x: this.altar.position.x, z: this.altar.position.z },
      alphaDen: { x: den.x, z: den.z },
      gateZ: GameConfig.gate.barrierZ,
      gateOpen: this.progress.alphaGateOpen,
      sanctuaryRadius: this.region.current().sanctuary.radius, // 10.5J: anel da barreira no mapa
      bossZone: this.inBossZone, // 10.9HX — na Fenda o minimapa ignora a névoa (zona fora do grid)
      regionGround: this.mapGround(this.region.current().theme.ground), // 10.9HR — piso da região onde não há território
      territories: this.minimapTerritories(), // 10.5M: divisão do mapa
      terrName: this.currentTerritoryName(),
      forest: this.region.id === "forgottenForest",
      rift: rc ? { x: rc.x, z: rc.z } : undefined,
      riftDiscovered: !!disc.rift,
    });
  }

  /** Move o anel indicador para o alvo atual (feedback discreto). */
  private updateTargetRing(target: Damageable | null): void {
    if (!target || target.isDead) {
      this.targetRing.visible = false;
      return;
    }
    const r = (target as { radius?: number }).radius ?? 1;
    this.targetRing.visible = true;
    this.targetRing.position.set(target.position.x, 0.06, target.position.z);
    this.targetRing.scale.setScalar(Math.max(0.8, r * 1.2));
  }

  // ---- loop ----
  /** 10.9HO — vida nas divisas: brasas subindo da lava, motes do abismo, borrifos na água, faíscas no gelo (só perto). */
  private divideAmbAcc = 0;
  private updateDivideAmbient(dt: number): void {
    this.divideAmbAcc += dt;
    if (this.divideAmbAcc < 0.3 || this.inBossZone || this.raid.active) return;
    this.divideAmbAcc = 0;
    const p = this.player.position;
    for (const dv of this.terrGates.mapDivides()) {
      const mx = (dv.x0 + dv.x1) / 2, mz = (dv.z0 + dv.z1) / 2;
      if (Math.abs(mx - p.x) > 30 || Math.abs(mz - p.z) > 30) continue;
      const amb = DIVIDE_AMBIENT[dv.type];
      if (Math.random() > amb.chance) continue;
      const f = Math.random();
      const x = dv.x0 + (dv.x1 - dv.x0) * f + (Math.random() - 0.5) * 3.6, z = dv.z0 + (dv.z1 - dv.z0) * f + (Math.random() - 0.5) * 3.6;
      this.particles.burst(x, dv.type === "chasm" ? 0.2 : 0.35, z, amb.color, { count: amb.count, speed: amb.speed, dur: amb.dur });
    }
  }
  /** 10.9HL — cor do PISO do bioma pra mapa/minimapa (levanta o brilho mínimo pra ler no fundo escuro). */
  private mapGround(hex: number): string {
    let r = (hex >> 16) & 255, g = (hex >> 8) & 255, b = hex & 255;
    const l = (r * 0.3 + g * 0.59 + b * 0.11) / 255;
    if (l < 0.42) { const k = 0.42 / Math.max(0.01, l); r = Math.min(255, r * k); g = Math.min(255, g * k); b = Math.min(255, b * k); }
    return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;
  }
  /** 10.9GT — escurece a tela, executa `fn` no meio e reabre (zona dos bosses, raid, renascer). */
  private fadeThen(fn: () => void, ms = 480): void {
    this.regionFade.classList.add("active");
    window.setTimeout(fn, ms * 0.5);
    window.setTimeout(() => this.regionFade.classList.remove("active"), ms);
  }
  /** 10.9GX — só a cortina (escurece e reabre), SEM atrasar nada — pra trocas que precisam ser síncronas. */
  private fadeCover(ms = 420): void {
    this.regionFade.classList.add("active");
    window.setTimeout(() => this.regionFade.classList.remove("active"), ms);
  }
  /** 10.9GT — câmera lenta por `seconds` (volta ao normal suavemente no último 1/4 s). */
  private slowMotion(scale: number, seconds: number): void {
    this.slowMo = Math.max(this.slowMo, seconds);
    this.slowMoScale = Math.min(this.slowMoScale, scale);
  }
  /** 10.9GT — vitória sobre um boss: clarão + câmera lenta + tranco (todas as variantes de boss). */
  private bossVictoryFx(): void {
    this.renderer.screenFlash(0.3);
    this.slowMotion(0.25, 0.85);
    this.cameraRig.impulse(0.3);
  }

  private sleeping = false;
  private rafId = 0;
  /** 10.9HH — app foi pro 2º plano: para o loop, silencia e salva. Idempotente. */
  private sleep(): void {
    if (this.sleeping) return;
    this.sleeping = true;
    diag.log("app", "dorme"); // 10.9IT
    this.hiddenAt = Date.now();
    this.audio.suspend();
    if (this.state === "playing") this.pause();
    this.save.save();
    if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = 0; }
  }
  /** 10.9HH — voltou: religa o loop (dt zerado) e destrava o áudio; o jogo continua pausado até Retomar. */
  private wake(): void {
    if (!this.sleeping) return;
    this.sleeping = false;
    diag.log("app", "acorda"); // 10.9IT
    this.audio.resume();
    this.checkLongAbsence();
    // 10.9J2 — reabre a checagem de concessões do painel ao voltar do 2º plano. Sem isto, um admin
    // testando ao vivo (manda item pelo painel numa aba, minimiza o jogo, volta) so recebia na
    // PRÓXIMA sessão: `grantsChecked` já tinha virado true no boot, antes do item existir no banco.
    this.grantsChecked = false;
    // 10.9K2 — virou o dia com o jogo em 2º plano: entrega o bônus diário agora (só com um
    // personagem carregado — no menu o correio ainda não leu o save).
    if (this.progress.classId) this.checkDailyLoginNow();
    this.last = performance.now();
    if (!this.rafId) this.rafId = requestAnimationFrame(this.loop);
  }

  /**
   * 10.9IS — TRAVAMENTO COM DIAGNOSTICO. Thiago: "o jogo ta travando assim, isso nunca aconteceu"
   * (foto: mundo parado, HUD sumido, nenhuma tela aberta). Nao reproduzi no preview em nenhum
   * caminho, entao a proxima vez PRECISA vir com o erro escrito — e o jogador nao pode ficar preso.
   *
   * Duas coisas que o jogo nao tinha:
   *  1. O quadro roda dentro de um try. Antes, UMA excecao no meio do quadro pulava o
   *     `requestAnimationFrame` do fim e o loop morria pra sempre: a tela congela no ultimo
   *     quadro desenhado. E exatamente a cara da foto.
   *  2. Uma armadilha global (`error` / `unhandledrejection`) que GRAVA o erro no aparelho
   *     (`vh_lastError`), mostra a primeira linha num banner e, se o HUD estiver escondido sem
   *     nenhuma tela aberta, DEVOLVE o HUD e o toque em vez de deixar o jogador olhando o chao.
   */
  private loop = (now: number): void => {
    try {
      this.frame(now);
    } catch (err) {
      this.onCrash(err, "quadro");
      // o quadro morreu antes de rearmar o proximo: rearma aqui, senao o jogo congela pra sempre
      if (!this.rafId && !this.sleeping) this.rafId = requestAnimationFrame(this.loop);
    }
  };

  private crashCooldown = 0;

  private installCrashTrap(): void {
    window.addEventListener("error", (e) => this.onCrash(e.error ?? e.message, "erro"));
    window.addEventListener("unhandledrejection", (e) => this.onCrash((e as PromiseRejectionEvent).reason, "promessa"));
    // 10.9IT — TRILHA: toda tela que abre/fecha (atributo hidden), o HUD, os controles e a barra de
    // atalhos. Um observador so, em vez de 30 chamadas espalhadas — e pega ate o que eu esqueci.
    const ui = document.querySelector("#ui-root");
    if (ui) {
      new MutationObserver((ms) => {
        for (const m of ms) {
          const el = m.target as HTMLElement;
          const cl = el.classList;
          if (!(cl.contains("overlay") || cl.contains("hud") || cl.contains("touch-controls") || cl.contains("menu-bar") || cl.contains("tour"))) continue;
          const nome = [...cl].filter((c) => c !== "overlay").slice(0, 2).join(".") || el.tagName.toLowerCase();
          diag.log("tela", `${nome} ${el.hidden ? "fecha" : "abre"}`);
        }
      }).observe(ui, { attributes: true, attributeFilter: ["hidden"], subtree: true });
    }
    // 10.9IT — WEBGL. O celular derruba o contexto sob pressao de memoria — SO no mobile, e sem
    // lancar excecao nenhuma: o canvas simplesmente congela no ultimo quadro (o DOM continua vivo).
    // Nada tratava isso. Agora registra, avisa, salva e recarrega com contexto novo.
    const cv = this.renderer.canvas;
    cv.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
      diag.log("webgl", "contexto PERDIDO");
      this.onCrash(new Error("WebGL: o aparelho derrubou o desenho do jogo (memoria)"), "webgl");
      window.setTimeout(() => { try { this.save.save(); } catch { /* */ } location.reload(); }, 1500);
    });
  }

  /** Registra, avisa e recupera. Nunca lanca (uma armadilha que explode e pior que nenhuma). */
  private onCrash(err: unknown, onde: string): void {
    try {
      const e = err as { message?: string; stack?: string } | null;
      const msg = String(e?.message ?? err ?? "erro desconhecido").slice(0, 300);
      diag.log("erro", `${onde}: ${msg.slice(0, 120)}`); // 10.9IT
      const stack = String(e?.stack ?? "").slice(0, 1200);
      const agora = Date.now();
      try {
        localStorage.setItem("vh_lastError", JSON.stringify({ t: agora, onde, msg, stack, state: this.state, region: this.region?.id, ua: navigator.userAgent, trail: diag.trail(40) }));
      } catch { /* sem espaco: paciencia */ }
      console.error(`[VOID] travou (${onde}):`, err);
      reportCrash({ onde, msg, stack, state: this.state, region: this.region?.id ?? "", ua: navigator.userAgent, trail: diag.trail(40) }); // 10.9IS — sobe pra nuvem (le-se no painel)
      if (agora - this.crashCooldown < 3000) return; // rajada do mesmo erro: nao empilha banner
      this.crashCooldown = agora;
      this.banner.show(`⚠️ ${msg}`, "danger", 6000);
      // SELF-HEAL: HUD escondido, nenhuma tela por cima e um personagem carregado = o jogador esta
      // preso num estado de transicao que nao completou. Devolve o controle.
      const telaAberta = document.querySelector(".overlay:not([hidden])") !== null;
      const hudSumiu = this.hudRoot()?.hidden === true;
      if (!telaAberta && hudSumiu && this.progress.classId && !this.player.isDead && !this.raid?.active) {
        this.hud.show(); this.equipBar.show(); this.minimap.show(); this.menuBar.show();
        this.input.setEnabled(true);
        this.state = "playing";
      }
    } catch { /* nunca */ }
  }

  private hudRoot(): HTMLElement | null {
    return document.querySelector<HTMLElement>(".hud");
  }

  private frame = (now: number): void => {
    this.rafId = 0;
    if (this.sleeping) return; // 10.9HH — dormindo: nenhum quadro
    if (this.state !== this.diagState) { diag.log("estado", `${this.diagState} -> ${this.state}`); this.diagState = this.state; } // 10.9IT
    let dt = clamp((now - this.last) / 1000, 0, 0.05);
    this.last = now;
    // 10.9GT — câmera lenta (morte / vitória): a simulação desacelera, o relógio real segue
    if (this.slowMo > 0) {
      this.slowMo = Math.max(0, this.slowMo - dt);
      const k = Math.min(1, this.slowMo / 0.25); // 1 = lenta cheia … 0 = normal (retorno suave)
      dt *= this.slowMoScale + (1 - this.slowMoScale) * (1 - k);
      if (this.slowMo <= 0) this.slowMoScale = 1;
    }

    // 10.6B — HIT-STOP: em acerto crítico (ou quando o Caçador leva pancada) a simulação
    // roda em câmera lenta por alguns centésimos. É o truque clássico de "peso" de golpe:
    // o quadro trava de leve e o impacto parece ter força. O tempo REAL continua correndo
    // (só a simulação desacelera), então nada de física acumulando atraso.
    if (this.hitStop > 0) {
      this.hitStop = Math.max(0, this.hitStop - dt);
      dt *= 0.28;
    }

    this.fpsAccum += dt;
    this.fpsFrames++;
    if (this.fpsAccum >= 0.25) {
      this.fps = this.fpsFrames / this.fpsAccum;
      this.fpsAccum = 0;
      this.fpsFrames = 0;
    }

    // 10.8C/10.9HY — GOVERNADOR DE FPS. Se o FPS ficar baixo, baixa a qualidade sozinho. Só REDUZ
    // (não oscila) e não sobrescreve escolha MANUAL do jogador.
    //   passo 1..3: `renderer.degrade()` — bloom → sombras → resolução (barato, sem recarregar nada)
    //   passo 4+ (10.9HY): esgotados os degraus de render, baixa o PRESET INTEIRO (high→medium→low),
    //     que é o que corta CARGA DE CENA (inimigos vivos, territórios ativos, distância de corte,
    //     barras de vida, taxa do minimapa). Antes o governador parava no degrau 3 e um celular
    //     fraco seguia com 220 inimigos e 150 m de cenário — continuava travando.
    this.runTime += dt;
    if (this.runTime > 5 && this.fps > 0) {
      const bad = this.fps < 40;
      this.lowFpsTime = bad ? this.lowFpsTime + dt : Math.max(0, this.lowFpsTime - dt * 0.5);
      if (this.lowFpsTime > 3) {
        this.lowFpsTime = 0;
        if (this.renderer.perfDegradeLevel < 3) {
          this.renderer.degrade(); // baixa um degrau de render
        } else if (quality.auto && quality.name !== "low") {
          // desce o preset e reaplica em tudo que lê a carga de cena
          const next = quality.name === "high" ? "medium" : "low";
          quality.setPreset(next);
          quality.auto = true; // continua automático (não foi escolha do jogador)
          this.renderer.applyQuality();
          this.spawner.refreshQuality();
          // 10.9HZ — LEMBRA o piso: sem salvar, o aparelho fraco passava por 3 degraus + queda de
          // preset (uns 12 s travando) TODA vez que abria. O save guarda `graphics` = preset atual
          // com `graphicsAuto` ainda true; no boot o jogo usa o MENOR entre detectado e lembrado.
          this.save.save();
          this.banner.show(i18n.t("perf.autoLower", { n: i18n.t(`graphics.${next}`) }), "info", 2600);
        }
      }
    }

    // 10.9GI — TODAS AS PLATAFORMAS: só o CELULAR é travado em paisagem (pra girar pro retrato).
    // Desktop, notebook e tablet jogam em paisagem (mouse controla o joystick/botões; WASD também).
    if (viewport.state.orientation === "landscape" && Game.isPhone()) {
      this.orientationOverlay.show();
      this.input.update();
      this.renderer.render();
      this.rafId = requestAnimationFrame(this.loop);
      return;
    }
    this.orientationOverlay.hide();

    if (this.input.pausePressed()) this.handleEscape();
    if (this.input.debugTogglePressed()) this.debug.toggle();

    // 10.5I — blindagem: uma exceção num frame NÃO pode matar o requestAnimationFrame
    // (era assim que o jogo "travava": o rAF nunca era re-agendado). Loga 1x e segue.
    try {
      this.update(dt);
    } catch (err) {
      if (!this.loopErrorLogged) {
        this.loopErrorLogged = true;
        console.error("VOID HUNTER: erro no update (frame ignorado, loop segue)", err);
      }
    }

    if (this.debug.isEnabled) this.updateDebug();

    // 10.9GX — PRÉVIA 3D do Caçador na tela de equipamentos (renderiza só ele + corrente, num canto do canvas, e copia pro canvas da tela)
    const pv = this.state === "inventory" ? this.invScreen : this.state === "character" ? this.characterScreen : this.state === "wardrobe" ? this.wardrobe : null; // 10.9GZ
    if (pv && pv.isOpen) {
      const cv = pv.previewCanvas;
      if (cv) this.renderer.renderPreview([this.player.group, this.chain.group], cv, pv.tickPreview(dt), this.player.position);
    }
    this.renderer.render();
    this.input.update();
    this.rafId = requestAnimationFrame(this.loop);
  };

  private updateDebug(): void {
    const bossDbg = this.boss?.debugSnapshot();
    this.debug.update({
      fps: this.fps,
      px: this.player.position.x,
      pz: this.player.position.z,
      enemies: this.enemies.aliveCount,
      hp: this.player.health.current,
      maxHp: this.player.health.max,
      level: this.xp.level,
      xp: this.xp.xp,
      xpForNext: this.xp.xpForNext,
      damage: this.player.combatStats.baseDamage,
      moveSpeed: this.player.speed,
      armor: this.player.armor,
      critChance: this.player.combatStats.critChance,
      power: this.inventory.get("power"),
      vitality: this.inventory.get("vitality"),
      agility: this.inventory.get("agility"),
      fortitude: this.inventory.get("fortitude"),
      ferocity: this.inventory.get("ferocity"),
      hookState: this.hook.state,
      hookCd: this.hook.cooldownFraction,
      zone: this.player.combatZone,
      distSanctuary: this.sanctuary.distanceFromBoundary(this.player.position),
      combatEnabled: this.player.combatEnabled,
      targetName: this.autoTarget.current ? (this.autoTarget.current === (this.boss as unknown) ? "boss" : "enemy") : "none",
      saveStatus: "auto",
      region: `${this.region.id}/${this.region.subAreaAt(this.player.position.z).id}`,
      elitesDefeated: this.progress.elitesDefeated,
      requiredElites: GameConfig.gate.requiredElites,
      gateOpen: this.progress.alphaGateOpen,
      alphaSoul: this.progress.alphaSoul,
      saveVersion: GameConfig.save.version,
      adds: this.enemies.aliveCount,
      bossActive: !!this.boss,
      bossState: bossDbg?.state ?? "-",
      bossAttack: bossDbg?.attack ?? "-",
      bossPhase: bossDbg?.phase ?? 0,
      bossHp: bossDbg?.hp ?? 0,
    });
  }
}
